import * as i0 from '@angular/core';
import { InjectionToken, Directive, EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, Optional, SkipSelf, Inject, Output, ContentChild, ViewChild, Host, ContentChildren, Input, NgModule, TemplateRef, HostBinding, HostListener, forwardRef, Injectable, ElementRef, PLATFORM_ID, Pipe, inject } from '@angular/core';
import * as i1$2 from '@angular/common';
import { DOCUMENT, CommonModule, isPlatformBrowser, DatePipe, NgTemplateOutlet, NgIf } from '@angular/common';
import * as i2 from '@angular/cdk/portal';
import { TemplatePortal, PortalModule, BasePortalOutlet, CdkPortalOutlet, PortalInjector, ComponentPortal } from '@angular/cdk/portal';
import { CdkAccordionItem, CdkAccordion } from '@angular/cdk/accordion';
import * as i1$1 from '@angular/cdk/a11y';
import { FocusKeyManager, isFakeMousedownFromScreenReader, A11yModule } from '@angular/cdk/a11y';
import { ENTER, hasModifierKey, SPACE, HOME, END, UP_ARROW, DOWN_ARROW, ESCAPE } from '@angular/cdk/keycodes';
import { Subject, Subscription, merge, defer, of, Observable, fromEvent, race, EMPTY, timer } from 'rxjs';
import { distinctUntilChanged, startWith, filter, take, delay, map, share, takeUntil, tap, withLatestFrom, endWith, mergeMap } from 'rxjs/operators';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { trigger, state, style, transition, animate, group, query, useAnimation, animation } from '@angular/animations';
import * as i1 from '@angular/cdk/collections';
import * as i2$1 from '@gsa-sam/ngx-uswds-icons';
import { IconModule, alertError, StackedIconModule } from '@gsa-sam/ngx-uswds-icons';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import * as i1$3 from '@angular/cdk/overlay';
import { OverlayConfig, OverlayModule, Overlay } from '@angular/cdk/overlay';
import { normalizePassiveListenerOptions } from '@angular/cdk/platform';
import * as i1$4 from 'ngx-bootstrap-icons';
import { NgxBootstrapIconsModule, x, threeDotsVertical, caretDownFill, caretUpFill, check2Square, infoCircle, exclamationTriangle, exclamationCircle, checkCircle, slashCircle, search, arrowLeftCircleFill, circle, check2Circle, plusCircleFill, dashCircleFill } from 'ngx-bootstrap-icons';
import * as i2$2 from '@angular/forms';
import { FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i1$5 from '@angular/router';
import { RouterModule } from '@angular/router';
import { Directionality } from '@angular/cdk/bidi';
import * as i2$3 from '@angular/cdk/layout';
import * as i1$6 from 'ngx-toastr';
import { Toast } from 'ngx-toastr';
import * as ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import * as i1$7 from '@ckeditor/ckeditor5-angular';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { UsaTableModule } from '@gsa-sam/ngx-uswds';
import { flip, preventOverflow, arrow, createPopperLite, offset } from '@popperjs/core';

/**
 * Token used to provide a `SdsAccordion` to `SdsAccordionItem`.
 * Used primarily to avoid circular imports between `SdsAccordion` and `SdsAccordionItem`.
 */
const SDS_ACCORDION = new InjectionToken('SDS_ACCORDION');

/** Time and timing curve for accordion item animations. */
const ACCORDION_ITEM_ANIMATION_TIMING = '225ms cubic-bezier(0.4,0.0,0.2,1)';
const sdsExpansionAnimations = {
    /** Animation that expands and collapses the accordion item content. */
    bodyExpansion: trigger('bodyExpansion', [
        state('collapsed, void', style({ height: '0px', visibility: 'hidden' })),
        state('expanded', style({ height: '*', visibility: 'visible' })),
        transition('expanded <=> collapsed, void => collapsed', animate(ACCORDION_ITEM_ANIMATION_TIMING)),
    ]),
};

/**
 * Accordion Item content that will be rendered lazily
 * after the accordion item is opened for the first time.
 */
class SdsAccordionItemContentDirective {
    constructor(_template) {
        this._template = _template;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsAccordionItemContentDirective_Factory(t) { return new (t || SdsAccordionItemContentDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsAccordionItemContentDirective, selectors: [["ng-template", "sdsAccordionItemContent", ""]] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAccordionItemContentDirective, [{
        type: Directive,
        args: [{
                selector: 'ng-template[sdsAccordionItemContent]',
            }]
    }], () => [{ type: i0.TemplateRef }], null); })();

const _c0$r = ["body"];
const _c1$d = [[["sds-accordion-item-header"]], "*"];
const _c2$5 = ["sds-accordion-item-header", "*"];
function SdsAccordionItemComponent_ng_template_5_Template(rf, ctx) { }
/** Counter for generating unique element ids. */
let uniqueId$1 = 0;
class SdsAccordionItemComponent extends CdkAccordionItem {
    constructor(accordion, _changeDetectorRef, _uniqueSelectionDispatcher, _viewContainerRef, _document, _animationMode) {
        super(accordion, _changeDetectorRef, _uniqueSelectionDispatcher);
        this._viewContainerRef = _viewContainerRef;
        this._animationMode = _animationMode;
        /** An event emitted after the body's expansion animation happens. */
        this.afterExpand = new EventEmitter();
        /** An event emitted after the body's collapse animation happens. */
        this.afterCollapse = new EventEmitter();
        /** Stream that emits for changes in `@Input` properties. */
        this._inputChanges = new Subject();
        /** ID for the associated header element. Used for a11y labelling. */
        this._headerId = `sds-accordion-item-header-${uniqueId$1++}`;
        /** Stream of body animation done events. */
        this._bodyAnimationDone = new Subject();
        this.accordion = accordion;
        this._document = _document;
        // We need a Subject with distinctUntilChanged, because the `done` event
        // fires twice on some browsers. See https://github.com/angular/angular/issues/24084
        this._bodyAnimationDone
            .pipe(distinctUntilChanged((x, y) => {
            return x.fromState === y.fromState && x.toState === y.toState;
        }))
            .subscribe((event) => {
            if (event.fromState !== 'void') {
                if (event.toState === 'expanded') {
                    this.afterExpand.emit();
                }
                else if (event.toState === 'collapsed') {
                    this.afterCollapse.emit();
                }
            }
        });
        console.warn(`This is a deprectaed version of accordion component, and will be removed in future versions. 
      Please switch to using usa-accordion from @gsa-sam/ngx-uswds (https://github.com/GSA/ngx-uswds)`);
    }
    /** Gets the expanded state string. */
    _getExpandedState() {
        return this.expanded ? 'expanded' : 'collapsed';
    }
    ngAfterContentInit() {
        if (this._lazyContent) {
            // Render the content as soon as the accordion item becomes open.
            this.opened
                .pipe(startWith(null), filter(() => this.expanded && !this._portal), take(1))
                .subscribe(() => {
                this._portal = new TemplatePortal(this._lazyContent._template, this._viewContainerRef);
            });
        }
    }
    ngOnChanges(changes) {
        this._inputChanges.next(changes);
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this._bodyAnimationDone.complete();
        this._inputChanges.complete();
    }
    /** Checks whether the accordion item's content contains the currently-focused element. */
    _containsFocus() {
        if (this._body) {
            const focusedElement = this._document.activeElement;
            const bodyElement = this._body.nativeElement;
            return focusedElement === bodyElement || bodyElement.contains(focusedElement);
        }
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsAccordionItemComponent_Factory(t) { return new (t || SdsAccordionItemComponent)(i0.ɵɵdirectiveInject(SDS_ACCORDION, 12), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.UniqueSelectionDispatcher), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(ANIMATION_MODULE_TYPE, 8)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsAccordionItemComponent, selectors: [["sds-accordion-item"]], contentQueries: function SdsAccordionItemComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, SdsAccordionItemContentDirective, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._lazyContent = _t.first);
        } }, viewQuery: function SdsAccordionItemComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$r, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._body = _t.first);
        } }, hostAttrs: [1, "sds-accordion__item"], hostVars: 4, hostBindings: function SdsAccordionItemComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("sds-accordion__item--expanded", ctx.expanded)("_sds-animation-noopable", ctx._animationMode === "NoopAnimations");
        } }, inputs: { disabled: "disabled", expanded: "expanded" }, outputs: { opened: "opened", closed: "closed", expandedChange: "expandedChange", afterExpand: "afterExpand", afterCollapse: "afterCollapse" }, exportAs: ["sdsAccordionItem"], features: [i0.ɵɵInheritDefinitionFeature, i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c2$5, decls: 6, vars: 3, consts: [["body", ""], ["role", "region", 1, "sam-accordion__panel", 3, "id"], [1, "sds-accordion__panel-body"], [3, "cdkPortalOutlet"]], template: function SdsAccordionItemComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵprojectionDef(_c1$d);
            i0.ɵɵprojection(0);
            i0.ɵɵelementStart(1, "div", 1, 0);
            i0.ɵɵlistener("@bodyExpansion.done", function SdsAccordionItemComponent_Template_div_animation_bodyExpansion_done_1_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx._bodyAnimationDone.next($event)); });
            i0.ɵɵelementStart(3, "div", 2);
            i0.ɵɵprojection(4, 1);
            i0.ɵɵtemplate(5, SdsAccordionItemComponent_ng_template_5_Template, 0, 0, "ng-template", 3);
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("@bodyExpansion", ctx._getExpandedState())("id", ctx.id);
            i0.ɵɵadvance(4);
            i0.ɵɵproperty("cdkPortalOutlet", ctx._portal);
        } }, dependencies: [i2.CdkPortalOutlet], encapsulation: 2, data: { animation: [sdsExpansionAnimations.bodyExpansion] }, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAccordionItemComponent, [{
        type: Component,
        args: [{ selector: 'sds-accordion-item', exportAs: 'sdsAccordionItem', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, inputs: ['disabled', 'expanded'], outputs: ['opened', 'closed', 'expandedChange'], animations: [sdsExpansionAnimations.bodyExpansion], host: {
                    class: 'sds-accordion__item',
                    '[class.sds-accordion__item--expanded]': 'expanded',
                    '[class._sds-animation-noopable]': '_animationMode === "NoopAnimations"',
                }, template: "<ng-content select=\"sds-accordion-item-header\"></ng-content>\n<div\n  class=\"sam-accordion__panel\"\n  role=\"region\"\n  [@bodyExpansion]=\"_getExpandedState()\"\n  (@bodyExpansion.done)=\"_bodyAnimationDone.next($event)\"\n  [id]=\"id\"\n  #body\n>\n  <div class=\"sds-accordion__panel-body\">\n    <ng-content></ng-content>\n    <ng-template [cdkPortalOutlet]=\"_portal\"></ng-template>\n  </div>\n</div>\n" }]
    }], () => [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }, {
                type: Inject,
                args: [SDS_ACCORDION]
            }] }, { type: i0.ChangeDetectorRef }, { type: i1.UniqueSelectionDispatcher }, { type: i0.ViewContainerRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [ANIMATION_MODULE_TYPE]
            }] }], { afterExpand: [{
            type: Output
        }], afterCollapse: [{
            type: Output
        }], _lazyContent: [{
            type: ContentChild,
            args: [SdsAccordionItemContentDirective]
        }], _body: [{
            type: ViewChild,
            args: ['body', { static: false }]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsAccordionItemComponent, { className: "SdsAccordionItemComponent" }); })();

const _c0$q = ["*"];
class SdsAccordionItemHeaderComponent {
    constructor(accordionItem, _element, _focusMonitor, _changeDetectorRef) {
        this.accordionItem = accordionItem;
        this._element = _element;
        this._focusMonitor = _focusMonitor;
        this._changeDetectorRef = _changeDetectorRef;
        this._parentChangeSubscription = Subscription.EMPTY;
        // Since the toggle state depends on an @Input on the accordion item, we
        // need to subscribe and trigger change detection manually.
        this._parentChangeSubscription = merge(accordionItem.opened, accordionItem.closed, accordionItem._inputChanges.pipe(filter((changes) => !!changes['disabled']))).subscribe(() => this._changeDetectorRef.markForCheck());
        _focusMonitor.monitor(_element).subscribe((origin) => {
            if (origin && accordionItem.accordion) {
                accordionItem.accordion._handleHeaderFocus(this);
            }
        });
    }
    /**
     * Whether the associated accordion item is disabled.
     * Implemented as a part of `FocusableOption`.
     */
    get disabled() {
        return this.accordionItem.disabled;
    }
    /** Toggles the expanded state of the accordion item. */
    _toggle() {
        this.accordionItem.toggle();
    }
    /** Gets whether the accordion item is expanded. */
    _isExpanded() {
        return this.accordionItem.expanded;
    }
    /** Gets the accordion item id. */
    _getAccordionItemId() {
        return this.accordionItem.id;
    }
    /** Handle keydown event calling to toggle() if appropriate. */
    _keydown(event) {
        switch (event.keyCode) {
            // Toggle for space and enter keys.
            case SPACE:
            case ENTER:
                if (!hasModifierKey(event)) {
                    event.preventDefault();
                    this._toggle();
                }
                break;
            default:
                if (this.accordionItem.accordion) {
                    this.accordionItem.accordion._handleHeaderKeydown(event);
                }
                return;
        }
    }
    /**
     * Focuses the item header. Implemented as a part of `FocusableOption`.
     * @param origin Origin of the action that triggered the focus.
     * @docs-private
     */
    focus(origin = 'program') {
        this._focusMonitor.focusVia(this._element, origin);
    }
    ngOnDestroy() {
        this._parentChangeSubscription.unsubscribe();
        this._focusMonitor.stopMonitoring(this._element);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsAccordionItemHeaderComponent_Factory(t) { return new (t || SdsAccordionItemHeaderComponent)(i0.ɵɵdirectiveInject(SdsAccordionItemComponent, 1), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsAccordionItemHeaderComponent, selectors: [["sds-accordion-item-header"]], hostAttrs: ["role", "button", 1, "sds-accordion__trigger"], hostVars: 5, hostBindings: function SdsAccordionItemHeaderComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function SdsAccordionItemHeaderComponent_click_HostBindingHandler() { return ctx._toggle(); })("keydown", function SdsAccordionItemHeaderComponent_keydown_HostBindingHandler($event) { return ctx._keydown($event); });
        } if (rf & 2) {
            i0.ɵɵattribute("id", ctx.accordionItem._headerId)("tabindex", ctx.disabled ? -1 : 0)("aria-controls", ctx._getAccordionItemId())("aria-expanded", ctx._isExpanded())("aria-disabled", ctx.accordionItem.disabled);
        } }, ngContentSelectors: _c0$q, decls: 4, vars: 0, consts: [["role", "heading", "aria-level", "2", 1, "sds-accordion__title"], [1, "sds-accordion__title-text"], [1, "sds-accordion__icon"]], template: function SdsAccordionItemHeaderComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "span", 0)(1, "span", 1);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
            i0.ɵɵelement(3, "span", 2);
            i0.ɵɵelementEnd();
        } }, encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAccordionItemHeaderComponent, [{
        type: Component,
        args: [{ selector: 'sds-accordion-item-header', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                    class: 'sds-accordion__trigger',
                    role: 'button',
                    '[attr.id]': 'accordionItem._headerId',
                    '[attr.tabindex]': 'disabled ? -1 : 0',
                    '[attr.aria-controls]': '_getAccordionItemId()',
                    '[attr.aria-expanded]': '_isExpanded()',
                    '[attr.aria-disabled]': 'accordionItem.disabled',
                    '(click)': '_toggle()',
                    '(keydown)': '_keydown($event)',
                }, template: "<span class=\"sds-accordion__title\" role=\"heading\" aria-level=\"2\">\n  <span class=\"sds-accordion__title-text\">\n    <ng-content></ng-content>\n  </span>\n  <span class=\"sds-accordion__icon\"></span>\n</span>\n" }]
    }], () => [{ type: SdsAccordionItemComponent, decorators: [{
                type: Host
            }] }, { type: i0.ElementRef }, { type: i1$1.FocusMonitor }, { type: i0.ChangeDetectorRef }], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsAccordionItemHeaderComponent, { className: "SdsAccordionItemHeaderComponent" }); })();

class SdsAccordionDirective extends CdkAccordion {
    constructor() {
        super(...arguments);
        this.displayMode = 'default';
    }
    ngAfterContentInit() {
        this._keyManager = new FocusKeyManager(this._headers).withWrap();
    }
    /** Handles keyboard events coming in from the item headers. */
    _handleHeaderKeydown(event) {
        const { keyCode } = event;
        if (keyCode === HOME) {
            this._keyManager.setFirstItemActive();
            event.preventDefault();
        }
        else if (keyCode === END) {
            this._keyManager.setLastItemActive();
            event.preventDefault();
        }
        else {
            this._keyManager.onKeydown(event);
        }
    }
    _handleHeaderFocus(header) {
        this._keyManager.updateActiveItem(header);
    }
    /** @nocollapse */ static { this.ɵfac = /** @pureOrBreakMyCode */ (() => { let ɵSdsAccordionDirective_BaseFactory; return function SdsAccordionDirective_Factory(t) { return (ɵSdsAccordionDirective_BaseFactory || (ɵSdsAccordionDirective_BaseFactory = i0.ɵɵgetInheritedFactory(SdsAccordionDirective)))(t || SdsAccordionDirective); }; })(); }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsAccordionDirective, selectors: [["sds-accordion"]], contentQueries: function SdsAccordionDirective_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, SdsAccordionItemHeaderComponent, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._headers = _t);
        } }, hostAttrs: [1, "sds-accordion"], hostVars: 2, hostBindings: function SdsAccordionDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("sds-accordion--basic", ctx.displayMode === "basic");
        } }, inputs: { multi: "multi", displayMode: "displayMode" }, exportAs: ["sdsAccordion"], features: [i0.ɵɵProvidersFeature([
                {
                    provide: SDS_ACCORDION,
                    useExisting: SdsAccordionDirective,
                },
            ]), i0.ɵɵInheritDefinitionFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAccordionDirective, [{
        type: Directive,
        args: [{
                selector: 'sds-accordion',
                exportAs: 'sdsAccordion',
                inputs: ['multi'],
                providers: [
                    {
                        provide: SDS_ACCORDION,
                        useExisting: SdsAccordionDirective,
                    },
                ],
                host: {
                    class: 'sds-accordion',
                    '[class.sds-accordion--basic]': 'displayMode === "basic"',
                },
            }]
    }], null, { _headers: [{
            type: ContentChildren,
            args: [SdsAccordionItemHeaderComponent, { descendants: true }]
        }], displayMode: [{
            type: Input
        }] }); })();

class SdsAccordionModule {
    /** @nocollapse */ static { this.ɵfac = function SdsAccordionModule_Factory(t) { return new (t || SdsAccordionModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsAccordionModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, PortalModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAccordionModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, PortalModule],
                exports: [
                    SdsAccordionDirective,
                    SdsAccordionItemComponent,
                    SdsAccordionItemHeaderComponent,
                    SdsAccordionItemContentDirective,
                ],
                declarations: [
                    SdsAccordionDirective,
                    SdsAccordionItemComponent,
                    SdsAccordionItemHeaderComponent,
                    SdsAccordionItemContentDirective,
                ],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsAccordionModule, { declarations: [SdsAccordionDirective,
        SdsAccordionItemComponent,
        SdsAccordionItemHeaderComponent,
        SdsAccordionItemContentDirective], imports: [CommonModule, PortalModule], exports: [SdsAccordionDirective,
        SdsAccordionItemComponent,
        SdsAccordionItemHeaderComponent,
        SdsAccordionItemContentDirective] }); })();

var ActionMenuMode;
(function (ActionMenuMode) {
    ActionMenuMode[ActionMenuMode["SHOWN"] = 0] = "SHOWN";
    ActionMenuMode[ActionMenuMode["DISABLED"] = 1] = "DISABLED";
    ActionMenuMode[ActionMenuMode["HIDDEN"] = 2] = "HIDDEN";
})(ActionMenuMode || (ActionMenuMode = {}));

const sdsMenuAnimations = {
    transformMenu: trigger('transformMenu', [
        state('void', style({
            opacity: 0,
            transform: 'scale(0.8)',
        })),
        transition('void => enter', group([
            query('.sds-menu', animate('100ms linear', style({
                opacity: 1,
            }))),
            animate('120ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'scale(1)' })),
        ])),
        transition('* => void', animate('100ms 25ms linear', style({ opacity: 0 }))),
    ]),
};

const _c0$p = ["*"];
function SdsMenuComponent_ng_template_0_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 0);
    i0.ɵɵlistener("keydown", function SdsMenuComponent_ng_template_0_Template_div_keydown_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1._handleKeydown($event)); })("click", function SdsMenuComponent_ng_template_0_Template_div_click_0_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.closed.emit("click")); })("@transformMenu.start", function SdsMenuComponent_ng_template_0_Template_div_animation_transformMenu_start_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1._onAnimationStart($event)); })("@transformMenu.done", function SdsMenuComponent_ng_template_0_Template_div_animation_transformMenu_done_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1._onAnimationDone($event)); });
    i0.ɵɵelementStart(1, "div", 1);
    i0.ɵɵprojection(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", ctx_r1._classList)("@transformMenu", ctx_r1._panelAnimationState);
    i0.ɵɵadvance();
    i0.ɵɵclassProp("sds-menu--small", ctx_r1.size === "sm");
} }
/** Injection token used to provide the parent menu to menu items. */
const SDS_MENU_TOKEN = new InjectionToken('SDS_MENU_TOKEN');
class SdsMenuComponent {
    /** Position of the menu in the X axis. */
    get xPosition() {
        return this._xPosition;
    }
    set xPosition(value) {
        this._xPosition = value;
        this.setPositionClasses();
    }
    /** Position of the menu in the Y axis. */
    get yPosition() {
        return this._yPosition;
    }
    set yPosition(value) {
        this._yPosition = value;
        this.setPositionClasses();
    }
    /** Whether menu panel overlaps trigger element */
    get overlapTrigger() {
        return this._overlapTrigger;
    }
    set overlapTrigger(value) {
        this._overlapTrigger = coerceBooleanProperty(value);
    }
    /** Transfer classes from the sds-menu to the overlay container */
    set panelClass(classes) {
        const previousPanelClass = this._previousPanelClass;
        // Remove previous classes from current set of classes
        if (previousPanelClass && previousPanelClass.length) {
            previousPanelClass.split(' ').forEach((className) => {
                this._classList[className] = false;
            });
        }
        this._previousPanelClass = classes;
        // Adds new classes to current set of classes
        if (classes && classes.length) {
            classes.split(' ').forEach((className) => {
                this._classList[className] = true;
            });
            // Remove all classes from <sds-menu>
            this._elementRef.nativeElement.className = '';
        }
    }
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        /** After | Before the menu triger element */
        this._xPosition = 'after';
        /** Above | Below the menu triger element */
        this._yPosition = 'below';
        /** Menu items inside the current menu. */
        this._items = [];
        /** Subscription to tab events on the menu panel */
        this._tabSubscription = Subscription.EMPTY;
        /** Config object to be passed into the menu's ngClass */
        this._classList = {};
        /** Current state of the panel animation. */
        this._panelAnimationState = 'void';
        this._overlapTrigger = false;
        /** Event emitted when the menu is closed. */
        this.closed = new EventEmitter();
    }
    ngOnInit() {
        this.setPositionClasses();
    }
    ngAfterContentInit() {
        this._keyManager = new FocusKeyManager(this._items).withWrap();
        this._tabSubscription = this._keyManager.tabOut.subscribe(() => this.closed.emit('tab'));
    }
    ngOnDestroy() {
        this._tabSubscription.unsubscribe();
        this.closed.complete();
    }
    /** Focus the first item in the menu */
    focusFirstItem(origin = 'program') {
        this._keyManager.setFocusOrigin(origin).setFirstItemActive();
    }
    /** Adds classes to the menu panel based on its position */
    setPositionClasses(posX = this.xPosition, posY = this.yPosition) {
        const classes = this._classList;
        classes['sds-menu-before'] = posX === 'before';
        classes['sds-menu-after'] = posX === 'after';
        classes['sds-menu-above'] = posY === 'above';
        classes['sds-menu-below'] = posY === 'below';
    }
    /** Adds a menu item with the menu. */
    addItem(item) {
        if (this._items.indexOf(item) === -1) {
            this._items.push(item);
        }
    }
    /** Inserts a menu item at an index */
    insertItem(item, index) {
        if (this._items.indexOf(item) === -1 && index < this._items.length) {
            this._items.splice(index, 0, item);
        }
    }
    /** Removes an item from the menu. */
    removeItem(item) {
        const index = this._items.indexOf(item);
        if (this._items.indexOf(item) > -1) {
            this._items.splice(index, 1);
        }
    }
    /** Handle a keyboard event from the menu */
    _handleKeydown(event) {
        // tslint:disable-next-line: deprecation
        const keyCode = event.keyCode;
        const manager = this._keyManager;
        switch (keyCode) {
            case ESCAPE:
                this.closed.emit('keydown');
                break;
            case HOME:
            case END:
                if (!hasModifierKey(event)) {
                    keyCode === HOME ? manager.setFirstItemActive() : manager.setLastItemActive();
                    event.preventDefault();
                }
                break;
            default:
                if (keyCode === UP_ARROW || keyCode === DOWN_ARROW) {
                    manager.setFocusOrigin('keyboard');
                }
                manager.onKeydown(event);
        }
    }
    /** Starts the enter animation. */
    _startAnimation() {
        this._panelAnimationState = 'enter';
    }
    /** Callback that is invoked when the panel animation completes. */
    _onAnimationDone(event) { }
    /** Resets the panel animation to its initial state. */
    _resetAnimation() {
        this._panelAnimationState = 'void';
    }
    _onAnimationStart(event) {
        // Scroll the content element to the top as soon as the animation starts.
        if (event.toState === 'enter' && this._keyManager.activeItemIndex === 0) {
            event.element.scrollTop = 0;
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsMenuComponent_Factory(t) { return new (t || SdsMenuComponent)(i0.ɵɵdirectiveInject(i0.ElementRef)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsMenuComponent, selectors: [["sds-menu"]], viewQuery: function SdsMenuComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(TemplateRef, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.templateRef = _t.first);
        } }, inputs: { size: "size", xPosition: "xPosition", yPosition: "yPosition", overlapTrigger: "overlapTrigger", panelClass: [i0.ɵɵInputFlags.None, "class", "panelClass"] }, outputs: { closed: "closed" }, exportAs: ["sdsMenu"], features: [i0.ɵɵProvidersFeature([{ provide: SDS_MENU_TOKEN, useExisting: SdsMenuComponent }])], ngContentSelectors: _c0$p, decls: 1, vars: 0, consts: [["tabindex", "-1", "role", "menu", 1, "sds-overlay", "minw-menu", "maxw-mobile", "radius-overlay", 3, "keydown", "click", "ngClass"], [1, "sds-menu"]], template: function SdsMenuComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵtemplate(0, SdsMenuComponent_ng_template_0_Template, 3, 4, "ng-template");
        } }, dependencies: [i1$2.NgClass], encapsulation: 2, data: { animation: [sdsMenuAnimations.transformMenu] }, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsMenuComponent, [{
        type: Component,
        args: [{ selector: 'sds-menu', exportAs: 'sdsMenu', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, animations: [sdsMenuAnimations.transformMenu], providers: [{ provide: SDS_MENU_TOKEN, useExisting: SdsMenuComponent }], template: "<ng-template>\n  <div\n    class=\"sds-overlay minw-menu maxw-mobile radius-overlay\"\n    [ngClass]=\"_classList\"\n    (keydown)=\"_handleKeydown($event)\"\n    (click)=\"closed.emit('click')\"\n    [@transformMenu]=\"_panelAnimationState\"\n    (@transformMenu.start)=\"_onAnimationStart($event)\"\n    (@transformMenu.done)=\"_onAnimationDone($event)\"\n    tabindex=\"-1\"\n    role=\"menu\"\n  >\n    <div class=\"sds-menu\" [class.sds-menu--small]=\"size === 'sm'\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</ng-template>\n" }]
    }], () => [{ type: i0.ElementRef }], { templateRef: [{
            type: ViewChild,
            args: [TemplateRef, { static: false }]
        }], size: [{
            type: Input
        }], xPosition: [{
            type: Input
        }], yPosition: [{
            type: Input
        }], overlapTrigger: [{
            type: Input
        }], panelClass: [{
            type: Input,
            args: ['class']
        }], closed: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsMenuComponent, { className: "SdsMenuComponent" }); })();

const _c0$o = ["*"];
function SdsMenuHeaderComponent_button_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "button", 3);
    i0.ɵɵelement(1, "usa-icon", 4);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "x");
} }
class SdsMenuHeaderComponent {
    constructor() {
        this.hideClose = false;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsMenuHeaderComponent_Factory(t) { return new (t || SdsMenuHeaderComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsMenuHeaderComponent, selectors: [["sds-menu-header"]], inputs: { hideClose: "hideClose" }, ngContentSelectors: _c0$o, decls: 4, vars: 1, consts: [[1, "sds-menu__header"], [1, "sds-menu__header-title"], ["aria-label", "Close Menu", "sds-menu-item", "", "role", "button", 4, "ngIf"], ["aria-label", "Close Menu", "sds-menu-item", "", "role", "button"], [3, "icon"]], template: function SdsMenuHeaderComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 0)(1, "span", 1);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(3, SdsMenuHeaderComponent_button_3_Template, 2, 1, "button", 2);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("ngIf", !ctx.hideClose);
        } }, encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsMenuHeaderComponent, [{
        type: Component,
        args: [{ selector: 'sds-menu-header', template: "<div class=\"sds-menu__header\">\n  <span class=\"sds-menu__header-title\"> <ng-content></ng-content> </span>\n  <button *ngIf=\"!hideClose\" aria-label=\"Close Menu\" sds-menu-item role=\"button\">\n    <usa-icon [icon]=\"'x'\"></usa-icon>\n  </button>\n</div>\n" }]
    }], null, { hideClose: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsMenuHeaderComponent, { className: "SdsMenuHeaderComponent" }); })();

const _c0$n = ["sds-menu-item", ""];
const _c1$c = ["*"];
class SdsMenuItemComponent {
    /** Whether the menu item should be disabled */
    // @Input()
    // get disabled() {
    //   return this._disabled;
    // }
    // set disabled(value: any) {
    //   this._disabled = coerceBooleanProperty(value);
    // }
    constructor(_elementRef, _focusMonitor, _parentMenu, _parentMenuHeader) {
        this._elementRef = _elementRef;
        this._focusMonitor = _focusMonitor;
        this._parentMenu = _parentMenu;
        this._parentMenuHeader = _parentMenuHeader;
        /** Menu item class */
        this.class = this._getClass();
        /** ARIA role for the menu item. */
        this.role = 'menuitem';
        /** Menu item tab index */
        this.tabIndex = '0';
        /** Holds the disable status value */
        this._disabled = false;
        // Start listening to focus changes
        _focusMonitor.monitor(this._elementRef, false);
        // Add this menu item to its parent menu
        // If item its inside a header
        // add it as the first item in the list
        if (_parentMenuHeader) {
            _parentMenu.insertItem(this, 0);
        }
        else {
            _parentMenu.addItem(this);
        }
    }
    /** Get item class */
    _getClass() {
        const headerButtonSmall = this._parentMenu.size === 'sm' ? 'sds-button--small' : '';
        return this._parentMenuHeader ? `sds-button sds-button--circular ${headerButtonSmall}` : 'sds-menu__item';
    }
    /** Focuses the menu item. */
    focus(origin = 'program') {
        this._focusMonitor.focusVia(this._elementRef.nativeElement, origin);
    }
    /** Stop listening to focus changes and remove item from parent */
    ngOnDestroy() {
        this._focusMonitor.stopMonitoring(this._elementRef);
        this._parentMenu.removeItem(this);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsMenuItemComponent_Factory(t) { return new (t || SdsMenuItemComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(SDS_MENU_TOKEN), i0.ɵɵdirectiveInject(SdsMenuHeaderComponent, 8)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsMenuItemComponent, selectors: [["", "sds-menu-item", ""]], hostVars: 4, hostBindings: function SdsMenuItemComponent_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵattribute("role", ctx.role)("tabIndex", ctx.tabIndex);
            i0.ɵɵclassMap(ctx.class);
        } }, inputs: { role: "role" }, attrs: _c0$n, ngContentSelectors: _c1$c, decls: 1, vars: 0, template: function SdsMenuItemComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵprojection(0);
        } }, encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsMenuItemComponent, [{
        type: Component,
        args: [{
                // tslint:disable-next-line: component-selector
                selector: '[sds-menu-item]',
                template: '<ng-content></ng-content>',
                changeDetection: ChangeDetectionStrategy.OnPush,
                encapsulation: ViewEncapsulation.None,
            }]
    }], () => [{ type: i0.ElementRef }, { type: i1$1.FocusMonitor }, { type: undefined, decorators: [{
                type: Inject,
                args: [SDS_MENU_TOKEN]
            }] }, { type: SdsMenuHeaderComponent, decorators: [{
                type: Optional
            }] }], { class: [{
            type: HostBinding,
            args: ['class']
        }], role: [{
            type: HostBinding,
            args: ['attr.role']
        }, {
            type: Input
        }], tabIndex: [{
            type: HostBinding,
            args: ['attr.tabIndex']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsMenuItemComponent, { className: "SdsMenuItemComponent" }); })();

class SdsMenuTriggerForDirective {
    /** ARIA expanded for the menu trigger. */
    get menuOpen() {
        return this._menuOpen;
    }
    /** References the menu instance that the trigger is associated with. */
    get menu() {
        return this._menu;
    }
    set menu(menu) {
        if (menu === this._menu) {
            return;
        }
        this._menu = menu;
        this._menuCloseSubscription.unsubscribe();
        if (menu) {
            this._menuCloseSubscription = menu.closed.asObservable().subscribe(() => {
                this._destroyMenu();
            });
        }
    }
    /** Handles mouse presses on the trigger. */
    _handleMousedown(event) {
        if (!isFakeMousedownFromScreenReader(event)) {
            // Since right or middle button clicks won't trigger the `click` event,
            // we shouldn't consider the menu as opened by mouse in those cases.
            this._openedBy = event.button === 0 ? 'mouse' : null;
        }
    }
    /** Toggles the menu between the open and closed states. */
    toggleMenu() {
        return this._menuOpen ? this.closeMenu() : this.openMenu();
    }
    constructor(_overlay, _element, _viewContainerRef, _focusMonitor) {
        this._overlay = _overlay;
        this._element = _element;
        this._viewContainerRef = _viewContainerRef;
        this._focusMonitor = _focusMonitor;
        /** ARIA haspopup for the menu trigger. */
        this.ariaHasPopup = true;
        /** Holds value for menuOpen variable */
        this._menuOpen = false;
        /** PortalOutlet */
        this._overlayRef = null;
        /** Tracks input type  */
        this._openedBy = null;
        this._closingActionsSubscription = Subscription.EMPTY;
        this._menuCloseSubscription = Subscription.EMPTY;
        /** Event emitted when the associated menu is opened. */
        this.menuOpened = new EventEmitter();
        /** Event emitted when the associated menu is closed. */
        this.menuClosed = new EventEmitter();
        // On touch devices set _openedBy to 'touch'
        _element.nativeElement.addEventListener('touchstart', () => (this._openedBy = 'touch'), normalizePassiveListenerOptions({ passive: true }));
    }
    ngOnDestroy() {
        if (this._overlayRef) {
            this._overlayRef.dispose();
            this._overlayRef = null;
        }
        this._element.nativeElement.removeEventListener('touchstart', () => (this._openedBy = 'touch'), normalizePassiveListenerOptions({ passive: true }));
        this._menuCloseSubscription.unsubscribe();
        this._closingActionsSubscription.unsubscribe();
    }
    /** Opens the menu. */
    openMenu() {
        if (this._menuOpen) {
            return;
        }
        const overlayRef = this._createOverlay();
        const overlayConfig = overlayRef.getConfig();
        this._setPosition(overlayConfig.positionStrategy);
        overlayRef.attach(this._getPortal());
        this._closingActionsSubscription = this._menuClosingActions().subscribe(() => this.closeMenu());
        this._initMenu();
        if (this.menu instanceof SdsMenuComponent) {
            this.menu._startAnimation();
        }
    }
    /** Closes the menu. */
    closeMenu() {
        this.menu.closed.emit();
    }
    /** Focuses the menu trigger. */
    focus(origin = 'program') {
        this._focusMonitor.focusVia(this._element, origin);
    }
    /** This method sets the menu state to open and focuses the first item */
    _initMenu() {
        this._setIsMenuOpen(true);
        this.menu.focusFirstItem(this._openedBy || 'program');
    }
    /** sets open state */
    _setIsMenuOpen(isOpen) {
        this._menuOpen = isOpen;
        this._menuOpen ? this.menuOpened.emit() : this.menuClosed.emit();
    }
    /**
     * This method resets the menu when it's closed,
     * most importantly restoring focus to the menu trigger
     */
    _resetMenu() {
        this._setIsMenuOpen(false);
        if (!this._openedBy) {
            this.focus();
        }
        else {
            this.focus(this._openedBy);
        }
        this._openedBy = null;
    }
    /** Closes the menu and does the necessary cleanup. */
    _destroyMenu() {
        if (!this._overlayRef || !this.menuOpen) {
            return;
        }
        this._closingActionsSubscription.unsubscribe();
        this._overlayRef.detach();
        if (this.menu instanceof SdsMenuComponent) {
            this.menu._resetAnimation();
            this._resetMenu();
        }
    }
    /**
     * This method creates the overlay from the provided menu's template and saves its
     * OverlayRef so that it can be attached to the DOM when openMenu is called.
     */
    _createOverlay() {
        if (!this._overlayRef) {
            const config = this._getOverlayConfig();
            this._subscribeToPositions(config.positionStrategy);
            this._overlayRef = this._overlay.create(config);
            this._overlayRef.keydownEvents().subscribe();
        }
        return this._overlayRef;
    }
    /**
     * This method builds the configuration object needed to create the overlay, the OverlayState.
     * @returns OverlayConfig
     */
    _getOverlayConfig() {
        return new OverlayConfig({
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._element)
                .withLockedPosition()
                .withTransformOriginOn('.sds-overlay'),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this._overlay.scrollStrategies.reposition(),
        });
    }
    /**
     * Listens to changes in the position of the overlay and sets the correct classes
     * on the menu based on the new position.
     */
    _subscribeToPositions(position) {
        position.positionChanges.subscribe((change) => {
            const posX = change.connectionPair.overlayX === 'start' ? 'after' : 'before';
            const posY = change.connectionPair.overlayY === 'top' ? 'below' : 'above';
            this.menu.setPositionClasses(posX, posY);
        });
    }
    /**
     * Sets the appropriate positions on a position strategy
     * so the overlay connects with the trigger correctly.
     */
    _setPosition(positionStrategy) {
        const [originX, originFallbackX] = this.menu.xPosition === 'before' ? ['end', 'start'] : ['start', 'end'];
        const [overlayY, overlayFallbackY] = this.menu.yPosition === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];
        let [originY, originFallbackY] = [overlayY, overlayFallbackY];
        const [overlayX, overlayFallbackX] = [originX, originFallbackX];
        const offsetY = 0;
        if (!this.menu.overlapTrigger) {
            originY = overlayY === 'top' ? 'bottom' : 'top';
            originFallbackY = overlayFallbackY === 'top' ? 'bottom' : 'top';
        }
        // Positions from most to least desirable
        positionStrategy.withPositions([
            { originX, originY, overlayX, overlayY, offsetY },
            {
                originX: originFallbackX,
                originY,
                overlayX: overlayFallbackX,
                overlayY,
                offsetY,
            },
            {
                originX,
                originY: originFallbackY,
                overlayX,
                overlayY: overlayFallbackY,
                offsetY: -offsetY,
            },
            {
                originX: originFallbackX,
                originY: originFallbackY,
                overlayX: overlayFallbackX,
                overlayY: overlayFallbackY,
                offsetY: -offsetY,
            },
        ]);
    }
    /** Returns a stream that emits whenever an action that should close the menu occurs. */
    _menuClosingActions() {
        const backdrop = this._overlayRef.backdropClick();
        const detachments = this._overlayRef.detachments();
        return merge(backdrop, detachments);
    }
    /** Gets the portal that should be attached to the overlay. */
    _getPortal() {
        if (!this._portal || this._portal.templateRef !== this.menu.templateRef) {
            this._portal = new TemplatePortal(this.menu.templateRef, this._viewContainerRef);
        }
        return this._portal;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsMenuTriggerForDirective_Factory(t) { return new (t || SdsMenuTriggerForDirective)(i0.ɵɵdirectiveInject(i1$3.Overlay), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i1$1.FocusMonitor)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsMenuTriggerForDirective, selectors: [["", "sdsMenuTriggerFor", ""]], hostVars: 2, hostBindings: function SdsMenuTriggerForDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("mousedown", function SdsMenuTriggerForDirective_mousedown_HostBindingHandler($event) { return ctx._handleMousedown($event); })("click", function SdsMenuTriggerForDirective_click_HostBindingHandler() { return ctx.toggleMenu(); });
        } if (rf & 2) {
            i0.ɵɵattribute("aria-haspopup", ctx.ariaHasPopup)("aria-expanded", ctx.menuOpen);
        } }, inputs: { menu: [i0.ɵɵInputFlags.None, "sdsMenuTriggerFor", "menu"] }, outputs: { menuOpened: "menuOpened", menuClosed: "menuClosed" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsMenuTriggerForDirective, [{
        type: Directive,
        args: [{
                selector: '[sdsMenuTriggerFor]',
            }]
    }], () => [{ type: i1$3.Overlay }, { type: i0.ElementRef }, { type: i0.ViewContainerRef }, { type: i1$1.FocusMonitor }], { ariaHasPopup: [{
            type: HostBinding,
            args: ['attr.aria-haspopup']
        }], menuOpen: [{
            type: HostBinding,
            args: ['attr.aria-expanded']
        }], menu: [{
            type: Input,
            args: ['sdsMenuTriggerFor']
        }], menuOpened: [{
            type: Output
        }], menuClosed: [{
            type: Output
        }], _handleMousedown: [{
            type: HostListener,
            args: ['mousedown', ['$event']]
        }], toggleMenu: [{
            type: HostListener,
            args: ['click']
        }] }); })();

const _c0$m = (a0, a1, a2) => ({ "sds-button--menu usa-button usa-button--secondary-dark  text-secondary-dark": a0, "sds-button sds-button--circular": a1, "usa-button--disabled": a2 });
const _c1$b = a0 => ({ "usa-button--disabled usa-button--unstyled": a0 });
function SdsActionsMenuComponent_span_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.model.label);
} }
function SdsActionsMenuComponent_ng_container_9_button_1_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 8);
    i0.ɵɵlistener("click", function SdsActionsMenuComponent_ng_container_9_button_1_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r2); const button_r3 = i0.ɵɵnextContext().$implicit; const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.clicks.emit(button_r3.id)); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const button_r3 = i0.ɵɵnextContext().$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("disabled", button_r3.mode === ctx_r0.actionModes.DISABLED)("ngClass", i0.ɵɵpureFunction1(3, _c1$b, button_r3.mode === ctx_r0.actionModes.DISABLED));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", button_r3.text, " ");
} }
function SdsActionsMenuComponent_ng_container_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsActionsMenuComponent_ng_container_9_button_1_Template, 2, 5, "button", 7);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const button_r3 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", button_r3.mode !== ctx_r0.actionModes.HIDDEN);
} }
class SdsActionsMenuComponent {
    constructor() {
        this.actionModes = ActionMenuMode;
        this.disabled = false;
        this.screenReaderText = 'Toggle Actions';
        this.clicks = new EventEmitter();
    }
    ngOnInit() {
        if (this.model.actions.length) {
            this.model.actions.forEach((menuItem) => {
                if (!menuItem.mode)
                    menuItem.mode = this.actionModes.SHOWN;
            });
        }
    }
    getDisabled(mode) {
        return (mode = this.actionModes.DISABLED) ? true : false;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsActionsMenuComponent_Factory(t) { return new (t || SdsActionsMenuComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsActionsMenuComponent, selectors: [["sds-actions-menu"]], inputs: { model: "model", size: "size", disabled: "disabled", screenReaderText: "screenReaderText" }, outputs: { clicks: "clicks" }, decls: 10, vars: 21, consts: [["menu", "sdsMenu"], [3, "disabled", "ngClass", "sdsMenuTriggerFor"], [4, "ngIf"], [3, "icon", "size"], [1, "usa-sr-only"], ["xPosition", "before", "overlapTrigger", "true", 3, "size"], [4, "ngFor", "ngForOf"], ["sds-menu-item", "", "tabindex", "0", 3, "disabled", "ngClass", "click", 4, "ngIf"], ["sds-menu-item", "", "tabindex", "0", 3, "click", "disabled", "ngClass"]], template: function SdsActionsMenuComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "button", 1);
            i0.ɵɵtemplate(1, SdsActionsMenuComponent_span_1_Template, 2, 1, "span", 2);
            i0.ɵɵelement(2, "usa-icon", 3);
            i0.ɵɵelementStart(3, "span", 4);
            i0.ɵɵtext(4);
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(5, "sds-menu", 5, 0)(7, "sds-menu-header");
            i0.ɵɵtext(8, "Actions");
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(9, SdsActionsMenuComponent_ng_container_9_Template, 2, 1, "ng-container", 6);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            const menu_r4 = i0.ɵɵreference(6);
            i0.ɵɵclassProp("sds-button--primary", ctx.model.trigger.type === "primary" && !ctx.model.label)("sds-button--secondary", ctx.model.trigger.type === "secondary")("sds-button--shadow", ctx.model.trigger.shadow)("sds-button--small", ctx.size === "sm");
            i0.ɵɵproperty("disabled", ctx.disabled)("ngClass", i0.ɵɵpureFunction3(17, _c0$m, ctx.model.label, !ctx.model.label, ctx.disabled))("sdsMenuTriggerFor", menu_r4);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.model.label);
            i0.ɵɵadvance();
            i0.ɵɵproperty("icon", "three-dots-vertical")("size", "1x");
            i0.ɵɵadvance(2);
            i0.ɵɵtextInterpolate(ctx.screenReaderText);
            i0.ɵɵadvance();
            i0.ɵɵproperty("size", ctx.size);
            i0.ɵɵadvance(4);
            i0.ɵɵproperty("ngForOf", ctx.model.actions);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i2$1.IconComponent, SdsMenuComponent, SdsMenuItemComponent, SdsMenuTriggerForDirective, SdsMenuHeaderComponent], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsActionsMenuComponent, [{
        type: Component,
        args: [{ selector: 'sds-actions-menu', template: "<!-- Button that triggers menu (sdsMenuTriggerFor) -->\n<button\n  [disabled]=\"disabled\"\n  [ngClass]=\"{\n    'sds-button--menu usa-button usa-button--secondary-dark  text-secondary-dark': model.label,\n    'sds-button sds-button--circular': !model.label,\n    'usa-button--disabled': disabled\n  }\"\n  [class.sds-button--primary]=\"model.trigger.type === 'primary' && !model.label\"\n  [class.sds-button--secondary]=\"model.trigger.type === 'secondary'\"\n  [class.sds-button--shadow]=\"model.trigger.shadow\"\n  [class.sds-button--small]=\"size === 'sm'\"\n  [sdsMenuTriggerFor]=\"menu\"\n>\n  <span *ngIf=\"model.label\">{{ model.label }}</span>\n  <usa-icon [icon]=\"'three-dots-vertical'\" [size]=\"'1x'\"></usa-icon>\n  <span class=\"usa-sr-only\">{{ screenReaderText }}</span>\n</button>\n\n<!-- Menu content -->\n<sds-menu #menu=\"sdsMenu\" [size]=\"size\" xPosition=\"before\" overlapTrigger=\"true\">\n  <!-- Menu header (optional) -->\n  <sds-menu-header>Actions</sds-menu-header>\n  <!-- Menu items -->\n  <ng-container *ngFor=\"let button of model.actions\">\n    <button\n      *ngIf=\"button.mode !== actionModes.HIDDEN\"\n      [disabled]=\"button.mode === actionModes.DISABLED\"\n      [ngClass]=\"{ 'usa-button--disabled usa-button--unstyled': button.mode === actionModes.DISABLED }\"\n      (click)=\"clicks.emit(button.id)\"\n      sds-menu-item\n      tabindex=\"0\"\n    >\n      {{ button.text }}\n    </button>\n  </ng-container>\n</sds-menu>\n" }]
    }], () => [], { model: [{
            type: Input
        }], size: [{
            type: Input
        }], disabled: [{
            type: Input
        }], screenReaderText: [{
            type: Input
        }], clicks: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsActionsMenuComponent, { className: "SdsActionsMenuComponent" }); })();

class SdsMenuModule {
    /** @nocollapse */ static { this.ɵfac = function SdsMenuModule_Factory(t) { return new (t || SdsMenuModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsMenuModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, OverlayModule, IconModule, NgxBootstrapIconsModule.pick({ x })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsMenuModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, OverlayModule, IconModule, NgxBootstrapIconsModule.pick({ x })],
                exports: [SdsMenuComponent, SdsMenuItemComponent, SdsMenuTriggerForDirective, SdsMenuHeaderComponent],
                declarations: [SdsMenuComponent, SdsMenuItemComponent, SdsMenuTriggerForDirective, SdsMenuHeaderComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsMenuModule, { declarations: [SdsMenuComponent, SdsMenuItemComponent, SdsMenuTriggerForDirective, SdsMenuHeaderComponent], imports: [CommonModule, OverlayModule, IconModule, i1$4.NgxBootstrapIconsModule], exports: [SdsMenuComponent, SdsMenuItemComponent, SdsMenuTriggerForDirective, SdsMenuHeaderComponent] }); })();
i0.ɵɵsetComponentScope(SdsMenuHeaderComponent, function () { return [i1$2.NgIf, i2$1.IconComponent, SdsMenuItemComponent]; }, []);

class SdsActionsMenuModule {
    /** @nocollapse */ static { this.ɵfac = function SdsActionsMenuModule_Factory(t) { return new (t || SdsActionsMenuModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsActionsMenuModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, IconModule, SdsMenuModule, NgxBootstrapIconsModule.pick({ threeDotsVertical, x })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsActionsMenuModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, IconModule, SdsMenuModule, NgxBootstrapIconsModule.pick({ threeDotsVertical, x })],
                exports: [SdsActionsMenuComponent],
                declarations: [SdsActionsMenuComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsActionsMenuModule, { declarations: [SdsActionsMenuComponent], imports: [CommonModule, IconModule, SdsMenuModule, i1$4.NgxBootstrapIconsModule], exports: [SdsActionsMenuComponent] }); })();

const _c0$l = ["*"];
class SdsPageComponent {
    constructor() { }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = function SdsPageComponent_Factory(t) { return new (t || SdsPageComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsPageComponent, selectors: [["sds-page"]], ngContentSelectors: _c0$l, decls: 2, vars: 0, consts: [[1, "grid-container"]], template: function SdsPageComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵprojection(1);
            i0.ɵɵelementEnd();
        } }, encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPageComponent, [{
        type: Component,
        args: [{
                selector: 'sds-page',
                template: ` <div class="grid-container"><ng-content></ng-content></div> `,
            }]
    }], () => [], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsPageComponent, { className: "SdsPageComponent" }); })();
class SdsPageOptionsComponent {
    constructor() { }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = function SdsPageOptionsComponent_Factory(t) { return new (t || SdsPageOptionsComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsPageOptionsComponent, selectors: [["sds-page-options"]], ngContentSelectors: _c0$l, decls: 2, vars: 0, consts: [[1, "grid-row", "position-relative"]], template: function SdsPageOptionsComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵprojection(1);
            i0.ɵɵelementEnd();
        } }, encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPageOptionsComponent, [{
        type: Component,
        args: [{
                selector: 'sds-page-options',
                template: ` <div class="grid-row position-relative"><ng-content></ng-content></div> `,
            }]
    }], () => [], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsPageOptionsComponent, { className: "SdsPageOptionsComponent" }); })();

class SdsPageModule {
    /** @nocollapse */ static { this.ɵfac = function SdsPageModule_Factory(t) { return new (t || SdsPageModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsPageModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPageModule, [{
        type: NgModule,
        args: [{
                imports: [],
                exports: [SdsPageComponent, SdsPageOptionsComponent],
                declarations: [SdsPageComponent, SdsPageOptionsComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsPageModule, { declarations: [SdsPageComponent, SdsPageOptionsComponent], exports: [SdsPageComponent, SdsPageOptionsComponent] }); })();

class PaginationConfigurationModel {
}
class PaginationModel {
}

const _c0$k = ["currentPage"];
const _c1$a = a0 => ({ width: a0 });
function PaginationComponent_div_0_option_19_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 14);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r3 = ctx.$implicit;
    i0.ɵɵproperty("ngValue", item_r3.value);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", item_r3.label, " ");
} }
function PaginationComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 3)(1, "div", 4)(2, "button", 5);
    i0.ɵɵlistener("click", function PaginationComponent_div_0_Template_button_click_2_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.previousPage()); });
    i0.ɵɵtext(3, "Previous Page");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div", 6)(5, "label", 7);
    i0.ɵɵtext(6, "page");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "input", 8, 0);
    i0.ɵɵlistener("ngModelChange", function PaginationComponent_div_0_Template_input_ngModelChange_7_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.valuechange($event)); });
    i0.ɵɵtwoWayListener("ngModelChange", function PaginationComponent_div_0_Template_input_ngModelChange_7_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); i0.ɵɵtwoWayBindingSet(ctx_r1.page.pageNumber, $event) || (ctx_r1.page.pageNumber = $event); return i0.ɵɵresetView($event); })("attr.maxChange", function PaginationComponent_div_0_Template_input_attr_maxChange_7_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); i0.ɵɵtwoWayBindingSet(ctx_r1.page.totalPages, $event) || (ctx_r1.page.totalPages = $event); return i0.ɵɵresetView($event); });
    i0.ɵɵlistener("focusout", function PaginationComponent_div_0_Template_input_focusout_7_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.currentPageFocusOut()); });
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(9, "span", 9);
    i0.ɵɵtext(10, " of ");
    i0.ɵɵelementStart(11, "strong");
    i0.ɵɵtext(12);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(13, "button", 5);
    i0.ɵɵlistener("click", function PaginationComponent_div_0_Template_button_click_13_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.nextPage()); });
    i0.ɵɵtext(14, "Next Page");
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(15, "div", 10)(16, "label", 11);
    i0.ɵɵtext(17, "results per page");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(18, "select", 12);
    i0.ɵɵlistener("change", function PaginationComponent_div_0_Template_select_change_18_listener() { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.onSelectChange()); });
    i0.ɵɵtwoWayListener("ngModelChange", function PaginationComponent_div_0_Template_select_ngModelChange_18_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); i0.ɵɵtwoWayBindingSet(ctx_r1.page.pageSize, $event) || (ctx_r1.page.pageSize = $event); return i0.ɵɵresetView($event); });
    i0.ɵɵtemplate(19, PaginationComponent_div_0_option_19_Template, 2, 2, "option", 13);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵattribute("id", ctx_r1.paginationConfiguration.id + "-previousPage");
    i0.ɵɵadvance(3);
    i0.ɵɵattribute("for", ctx_r1.paginationConfiguration.id + "-currentPage");
    i0.ɵɵadvance(2);
    i0.ɵɵtwoWayProperty("ngModel", ctx_r1.page.pageNumber);
    i0.ɵɵproperty("ngStyle", i0.ɵɵpureFunction1(13, _c1$a, 20 + ctx_r1.page.totalPages.toString().length * 10 + "px"));
    i0.ɵɵattribute("id", ctx_r1.paginationConfiguration.id + "-currentPage")("aria-label", "Page " + ctx_r1.page.pageNumber + " of " + ctx_r1.page.totalPages)("max", ctx_r1.page.totalPages);
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate(ctx_r1.page.totalPages);
    i0.ɵɵadvance();
    i0.ɵɵattribute("id", ctx_r1.paginationConfiguration.id + "-nextPage");
    i0.ɵɵadvance(3);
    i0.ɵɵattribute("for", ctx_r1.paginationConfiguration.id + "-select");
    i0.ɵɵadvance(2);
    i0.ɵɵtwoWayProperty("ngModel", ctx_r1.page.pageSize);
    i0.ɵɵattribute("id", ctx_r1.paginationConfiguration.id + "-select");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r1.options);
} }
function PaginationComponent_div_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3)(1, "div", 15);
    i0.ɵɵtext(2);
    i0.ɵɵpipe(3, "number");
    i0.ɵɵpipe(4, "number");
    i0.ɵɵpipe(5, "number");
    i0.ɵɵpipe(6, "number");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate3(" Showing ", i0.ɵɵpipeBind3(3, 3, (ctx_r1.page.pageNumber - 1) * ctx_r1.page.pageSize + 1, "1.0", "en-US"), " - ", (ctx_r1.page.pageNumber - 1) * ctx_r1.page.pageSize + ctx_r1.page.pageSize > ctx_r1.totalItems ? i0.ɵɵpipeBind3(4, 7, ctx_r1.totalItems, "1.0", "en-US") : i0.ɵɵpipeBind3(5, 11, (ctx_r1.page.pageNumber - 1) * ctx_r1.page.pageSize + ctx_r1.page.pageSize, "1.0", "en-US"), " of ", i0.ɵɵpipeBind3(6, 15, ctx_r1.totalItems, "1.0", "en-US"), " results ");
} }
class PaginationComponent {
    ngOnInit() {
        this.maintainPreviousValue();
    }
    constructor(change) {
        this.change = change;
        /**
         * Output of the page model object
         */
        this.pageChange = new EventEmitter();
        /**
         * Pagination model
         */
        this.page = new PaginationModel();
        /**
         * displayMode = 'default' | 'results';
         */
        this.displayMode = 'default';
        /**
         * totalItems for display on results view;
         */
        this.totalItems = 0;
        /**
         * debounce time for current page input
         */
        this.debounceTime = 500;
        /**
         * Drop down options for page size
         */
        this.options = [
            { label: '25', value: 25 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
        ];
    }
    /**
     * Stores the previous number. Used when focus out if field empty
     */
    maintainPreviousValue() {
        this.previousNumber = this.page.pageNumber.valueOf();
    }
    /**
     * previous page lowers page number by one within range
     */
    previousPage() {
        if (this.page.pageNumber > 1) {
            this.page.pageNumber--;
            this.maintainPreviousValue();
            this.pageChange.emit(this.page);
        }
    }
    /**
     * next page increase page number by one within range
     */
    nextPage() {
        if (this.page.pageNumber < this.page.totalPages) {
            this.page.pageNumber++;
            this.maintainPreviousValue();
            this.pageChange.emit(this.page);
        }
    }
    /**
     * current page changes sets new value if within range
     * @param newValue
     */
    valuechange(newValue) {
        window.clearTimeout(this.timeoutNumber);
        this.timeoutNumber = window.setTimeout(() => {
            if (newValue || newValue === 0) {
                newValue = this.handleInputOutsideBounds(newValue);
                if (newValue >= 1 && newValue <= this.page.totalPages) {
                    this.page.pageNumber = newValue;
                    this.maintainPreviousValue();
                    this.pageChange.emit(this.page);
                    this.change.detectChanges();
                }
            }
            else {
                if (this.page.pageNumber) {
                    this.maintainPreviousValue();
                }
            }
        }, this.debounceTime);
    }
    /**
     * adjusts the value if not within the page limit above or below
     * @param newValue handles
     */
    handleInputOutsideBounds(newValue) {
        if (newValue < 1) {
            newValue = 1;
            this.currentPageField.nativeElement.value = newValue;
        }
        else if (newValue > this.page.totalPages) {
            newValue = this.page.totalPages;
            this.currentPageField.nativeElement.value = newValue;
        }
        return newValue;
    }
    /**
     * current page focus out will replace with previous valid if empty
     */
    currentPageFocusOut() {
        if (this.currentPageField.nativeElement.value === '') {
            this.currentPageField.nativeElement.value = this.page.pageNumber = this.previousNumber;
            this.change.detectChanges();
        }
    }
    /**
     * page size selection change
     */
    onSelectChange() {
        this.page.pageNumber = 1;
        this.pageChange.emit(this.page);
    }
    /** @nocollapse */ static { this.ɵfac = function PaginationComponent_Factory(t) { return new (t || PaginationComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: PaginationComponent, selectors: [["sds-pagination"]], viewQuery: function PaginationComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$k, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.currentPageField = _t.first);
        } }, inputs: { page: "page", paginationConfiguration: "paginationConfiguration", displayMode: "displayMode", totalItems: "totalItems" }, outputs: { pageChange: "pageChange" }, decls: 3, vars: 2, consts: [["currentPage", ""], ["class", "sds-pagination", 4, "ngIf"], ["aria-live", "polite"], [1, "sds-pagination"], [1, "sds-pagination__controls"], [3, "click"], [1, "current-page-container"], [1, "usa-label", "font-sans-3xs", "margin-top-1", "text-italic", "text-base"], ["type", "number", "min", "1", 1, "usa-input", "usa-input--small", "font-sans-3xs", "text-center", "border-base-light", 3, "ngModelChange", "attr.maxChange", "focusout", "ngModel", "ngStyle"], [1, "sds-pagination__total"], [1, "sds-pagination__results"], [1, "usa-label", "font-sans-3xs", "text-italic", "text-base"], [1, "usa-select", "usa-select--small", "border-base-light", 3, "change", "ngModelChange", "ngModel"], [3, "ngValue", 4, "ngFor", "ngForOf"], [3, "ngValue"], [1, "text-semibold", "font-sans-3xs"]], template: function PaginationComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, PaginationComponent_div_0_Template, 20, 15, "div", 1);
            i0.ɵɵelementStart(1, "div", 2);
            i0.ɵɵtemplate(2, PaginationComponent_div_2_Template, 7, 19, "div", 1);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.displayMode === "default");
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.totalItems && ctx.displayMode === "results");
        } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.NgStyle, i2$2.NgSelectOption, i2$2.ɵNgSelectMultipleOption, i2$2.DefaultValueAccessor, i2$2.NumberValueAccessor, i2$2.SelectControlValueAccessor, i2$2.NgControlStatus, i2$2.MinValidator, i2$2.NgModel, i1$2.DecimalPipe], styles: ["input[type=number][_ngcontent-%COMP%]::-webkit-inner-spin-button, input[type=number][_ngcontent-%COMP%]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number][_ngcontent-%COMP%]{-moz-appearance:textfield}.current-page-container[_ngcontent-%COMP%]{margin-top:-26px}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PaginationComponent, [{
        type: Component,
        args: [{ selector: 'sds-pagination', template: "<div class=\"sds-pagination\" *ngIf=\"displayMode === 'default'\">\n  <div class=\"sds-pagination__controls\">\n    <button [attr.id]=\"paginationConfiguration.id + '-previousPage'\" (click)=\"previousPage()\">Previous Page</button>\n    <div class=\"current-page-container\">\n      <label\n        class=\"usa-label font-sans-3xs margin-top-1 text-italic text-base\"\n        [attr.for]=\"paginationConfiguration.id + '-currentPage'\"\n        >page</label\n      >\n      <input\n        [attr.id]=\"paginationConfiguration.id + '-currentPage'\"\n        class=\"usa-input usa-input--small font-sans-3xs text-center border-base-light\"\n        #currentPage\n        [attr.aria-label]=\"'Page ' + page.pageNumber + ' of ' + page.totalPages\"\n        (ngModelChange)=\"valuechange($event)\"\n        [(ngModel)]=\"page.pageNumber\"\n        type=\"number\"\n        min=\"1\"\n        [(attr.max)]=\"page.totalPages\"\n        (focusout)=\"currentPageFocusOut()\"\n        [ngStyle]=\"{ width: 20 + page.totalPages.toString().length * 10 + 'px' }\"\n      />\n    </div>\n    <span class=\"sds-pagination__total\">\n      of <strong>{{ page.totalPages }}</strong>\n    </span>\n    <button [attr.id]=\"paginationConfiguration.id + '-nextPage'\" (click)=\"nextPage()\">Next Page</button>\n  </div>\n  <div class=\"sds-pagination__results\">\n    <label class=\"usa-label font-sans-3xs text-italic text-base\" [attr.for]=\"paginationConfiguration.id + '-select'\"\n      >results per page</label\n    >\n    <select\n      class=\"usa-select usa-select--small border-base-light\"\n      [attr.id]=\"paginationConfiguration.id + '-select'\"\n      (change)=\"onSelectChange()\"\n      [(ngModel)]=\"page.pageSize\"\n    >\n      <option *ngFor=\"let item of options\" [ngValue]=\"item.value\">\n        {{ item.label }}\n      </option>\n    </select>\n  </div>\n</div>\n\n<div aria-live=\"polite\">\n  <div class=\"sds-pagination\" *ngIf=\"totalItems && displayMode === 'results'\">\n    <div class=\"text-semibold font-sans-3xs\">\n      Showing {{ (page.pageNumber - 1) * page.pageSize + 1 | number: '1.0':'en-US' }} -\n      {{\n        (page.pageNumber - 1) * page.pageSize + page.pageSize > totalItems\n          ? (totalItems | number: '1.0':'en-US')\n          : ((page.pageNumber - 1) * page.pageSize + page.pageSize | number: '1.0':'en-US')\n      }}\n      of {{ totalItems | number: '1.0':'en-US' }} results\n    </div>\n  </div>\n</div>\n", styles: ["input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}input[type=number]{-moz-appearance:textfield}.current-page-container{margin-top:-26px}\n"] }]
    }], () => [{ type: i0.ChangeDetectorRef }], { currentPageField: [{
            type: ViewChild,
            args: ['currentPage', { static: false }]
        }], pageChange: [{
            type: Output
        }], page: [{
            type: Input
        }], paginationConfiguration: [{
            type: Input
        }], displayMode: [{
            type: Input
        }], totalItems: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(PaginationComponent, { className: "PaginationComponent" }); })();

class PaginationModule {
    /** @nocollapse */ static { this.ɵfac = function PaginationModule_Factory(t) { return new (t || PaginationModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: PaginationModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, FormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PaginationModule, [{
        type: NgModule,
        args: [{
                declarations: [PaginationComponent],
                imports: [CommonModule, FormsModule],
                exports: [PaginationComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PaginationModule, { declarations: [PaginationComponent], imports: [CommonModule, FormsModule], exports: [PaginationComponent] }); })();

class SDSSelectedItemModel {
    constructor(items) {
        this.items = items ? [...items] : [];
    }
}

class SDSSelectedItemModelHelper {
    /**
     *  adds an item to the collection
     * if selected mode is single it removes any existing items
     * also checks to see if that item already exists
     * keyfield is used to determine uniqueness of the item added
     * @param itemToAdd
     * @param keyField
     * @param selectionMode
     * @param items
     */
    static addItem(itemToAdd, keyField, selectionMode, model) {
        if (!SDSSelectedItemModelHelper.containsItem(itemToAdd[keyField], keyField, model.items)) {
            if (selectionMode === SelectionMode.SINGLE) {
                SDSSelectedItemModelHelper.clearItems(model.items);
            }
            model.items.push(itemToAdd);
        }
    }
    /**
     * adds an array of items to the list and will not add duplicate items
     * keyfield is used to determine uniqueness of the item added
     * @param toAddItems
     * @param keyField
     * @param selectionMode
     * @param items
     */
    static addItems(toAddItems, keyField, selectionMode, model) {
        for (let i = 0; i < toAddItems.length; i++) {
            SDSSelectedItemModelHelper.addItem(toAddItems[i], keyField, selectionMode, model);
        }
    }
    /**
     * removes the item from the list
     * keyfield is used to determine uniqueness of the item added
     * @param item
     * @param keyField
     * @param items
     */
    static removeItem(item, keyField, model) {
        if (SDSSelectedItemModelHelper.containsItem(item[keyField], keyField, model.items)) {
            model.items.splice(model.items.indexOf(item), 1);
        }
    }
    /**
     * checks to see if a particular item exists by the provied key
     * keyfield is used to determine uniqueness of the item added
     * @param key
     * @param keyField
     * @param items
     */
    static containsItem(key, keyField, items) {
        let item = items.find((o) => o[keyField] === key);
        return item !== null && item !== undefined;
    }
    /**
     * Clears the list of items
     * @param items
     */
    static clearItems(items) {
        while (items.length > 0) {
            items.pop();
        }
    }
    /**
     * updates an array of items to the list and will not add duplicate items
     * keyfield is used to determine uniqueness of the item added
     * @param selectedItems
     * @param keyField
     * @param selectionMode
     * @param items
     */
    static replaceItems(selectedItems, keyField, selectionMode, model) {
        //Clears Old List
        SDSSelectedItemModelHelper.clearItems(model.items);
        //Adds new List
        SDSSelectedItemModelHelper.addItems(selectedItems, keyField, selectionMode, model);
    }
}
var SelectionMode;
(function (SelectionMode) {
    SelectionMode[SelectionMode["SINGLE"] = 0] = "SINGLE";
    SelectionMode[SelectionMode["MULTIPLE"] = 1] = "MULTIPLE";
})(SelectionMode || (SelectionMode = {}));

const _c0$j = a0 => ({ $implicit: a0 });
function SDSSelectedResultComponent_ul_0_li_1_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 8);
} if (rf & 2) {
    const result_r1 = i0.ɵɵnextContext().$implicit;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.itemTemplate)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c0$j, result_r1));
} }
function SDSSelectedResultComponent_ul_0_li_1_ng_container_3_div_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const result_r1 = i0.ɵɵnextContext(2).$implicit;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", result_r1[ctx_r1.configuration.secondaryTextField], " ");
} }
function SDSSelectedResultComponent_ul_0_li_1_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 9)(2, "div");
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, SDSSelectedResultComponent_ul_0_li_1_ng_container_3_div_4_Template, 2, 1, "div", 6);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    const result_r1 = ctx_r2.$implicit;
    const i_r4 = ctx_r2.index;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", ctx_r1.getObjectValue(result_r1, ctx_r1.configuration.primaryTextField, i_r4), " ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.configuration.secondaryTextField && result_r1[ctx_r1.configuration.secondaryTextField]);
} }
function SDSSelectedResultComponent_ul_0_li_1_button_4_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 10);
    i0.ɵɵlistener("click", function SDSSelectedResultComponent_ul_0_li_1_button_4_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r5); const result_r1 = i0.ɵɵnextContext().$implicit; const ctx_r1 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r1.removeItem(result_r1)); })("keyup.enter", function SDSSelectedResultComponent_ul_0_li_1_button_4_Template_button_keyup_enter_0_listener() { i0.ɵɵrestoreView(_r5); const result_r1 = i0.ɵɵnextContext().$implicit; const ctx_r1 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r1.removeItem(result_r1)); });
    i0.ɵɵelement(1, "usa-icon", 11);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    const result_r1 = ctx_r2.$implicit;
    const i_r4 = ctx_r2.index;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵclassProp("text-base", ctx_r1.disabled);
    i0.ɵɵattribute("aria-label", "Remove Item " + ctx_r1.getObjectValue(result_r1, ctx_r1.configuration.primaryTextField, i_r4));
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "x");
} }
function SDSSelectedResultComponent_ul_0_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 3)(1, "div", 4);
    i0.ɵɵtemplate(2, SDSSelectedResultComponent_ul_0_li_1_ng_container_2_Template, 1, 4, "ng-container", 5)(3, SDSSelectedResultComponent_ul_0_li_1_ng_container_3_Template, 5, 2, "ng-container", 6)(4, SDSSelectedResultComponent_ul_0_li_1_button_4_Template, 2, 4, "button", 7);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", ctx_r1.disabled ? "sds-tag--disabled" : "");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.itemTemplate);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r1.itemTemplate);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r1.disabled);
} }
function SDSSelectedResultComponent_ul_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "ul", 1);
    i0.ɵɵtemplate(1, SDSSelectedResultComponent_ul_0_li_1_Template, 5, 4, "li", 2);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵattribute("aria-label", ctx_r1.configuration.labelText + " results");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r1.model.items);
} }
const SDS_SelectedResult_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SDSSelectedResultComponent),
    multi: true,
};
class SDSSelectedResultComponent {
    constructor() {
        /**
         * Stored Event for ControlValueAccessor
         */
        this.onTouchedCallback = () => null;
        /**
         * Stored Event for ControlValueAccessor
         */
        this.propogateChange = (_) => null;
    }
    /**
     * Removes item from the model
     * @param item
     */
    removeItem(item) {
        if (!this.disabled) {
            SDSSelectedItemModelHelper.removeItem(item, this.configuration.primaryKeyField, this.model);
            this.propogateChange(this.model);
            this.onTouchedCallback();
        }
    }
    writeValue(obj) {
        if (obj instanceof SDSSelectedItemModel) {
            this.model = obj;
        }
    }
    registerOnChange(fn) {
        this.propogateChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /**
     * Gets the string value from the specifed properties of an object
     * @param object
     * @param propertyFields comma seperated list with periods depth of object
     * @param index - the index location of the value in model's item list
     */
    getObjectValue(object, propertyFields, index) {
        let value = '';
        let current = object;
        let fieldSplit = propertyFields.split(',');
        for (let i = 0; i < fieldSplit.length; i++) {
            let fieldValue = fieldSplit[i];
            let fieldPartSplit = fieldValue.split('.');
            for (let j = 0; j < fieldPartSplit.length; j++) {
                let fieldCheckValue = fieldPartSplit[j];
                if (current) {
                    current = current[fieldCheckValue];
                }
            }
            if (current) {
                value += current.toString() + ' ';
            }
            current = object;
        }
        return this.configuration.displayModifierFn
            ? this.configuration.displayModifierFn(value.trim(), index)
            : value.trim();
    }
    /** @nocollapse */ static { this.ɵfac = function SDSSelectedResultComponent_Factory(t) { return new (t || SDSSelectedResultComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SDSSelectedResultComponent, selectors: [["sds-selected-result"]], inputs: { itemTemplate: "itemTemplate", configuration: "configuration", disabled: "disabled" }, features: [i0.ɵɵProvidersFeature([SDS_SelectedResult_VALUE_ACCESSOR])], decls: 1, vars: 1, consts: [["class", "usa-list usa-list--unstyled sds-autocomplete-selected", "aria-relevant", "additions", "role", "list", "aria-live", "polite", 4, "ngIf"], ["aria-relevant", "additions", "role", "list", "aria-live", "polite", 1, "usa-list", "usa-list--unstyled", "sds-autocomplete-selected"], ["role", "listitem", 4, "ngFor", "ngForOf"], ["role", "listitem"], [1, "sds-tag", "sds-tag--chip", "sds-tag--input", 3, "ngClass"], [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngIf"], [4, "ngIf"], ["aria-hidden", "false", "class", "sds-tag__close", 3, "text-base", "click", "keyup.enter", 4, "ngIf"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [1, "sds--tag__item", "padding-right-2"], ["aria-hidden", "false", 1, "sds-tag__close", 3, "click", "keyup.enter"], ["size", "lg", 3, "icon"]], template: function SDSSelectedResultComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SDSSelectedResultComponent_ul_0_Template, 2, 2, "ul", 0);
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.model);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i2$1.IconComponent] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SDSSelectedResultComponent, [{
        type: Component,
        args: [{ selector: 'sds-selected-result', providers: [SDS_SelectedResult_VALUE_ACCESSOR], template: "<ul\n  *ngIf=\"model\"\n  [attr.aria-label]=\"configuration.labelText + ' results'\"\n  class=\"usa-list usa-list--unstyled sds-autocomplete-selected\"\n  aria-relevant=\"additions\"\n  role=\"list\"\n  aria-live=\"polite\"\n>\n  <li role=\"listitem\" *ngFor=\"let result of model.items; let i = index\">\n    <div class=\"sds-tag sds-tag--chip sds-tag--input\" [ngClass]=\"disabled ? 'sds-tag--disabled' : ''\">\n      <ng-container\n        *ngIf=\"itemTemplate\"\n        [ngTemplateOutlet]=\"itemTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: result }\"\n      >\n      </ng-container>\n      <ng-container *ngIf=\"!itemTemplate\">\n        <div class=\"sds--tag__item padding-right-2\">\n          <div>\n            {{ getObjectValue(result, configuration.primaryTextField, i) }}\n          </div>\n          <div *ngIf=\"configuration.secondaryTextField && result[configuration.secondaryTextField]\">\n            {{ result[configuration.secondaryTextField] }}\n          </div>\n        </div>\n      </ng-container>\n      <button\n        *ngIf=\"!disabled\"\n        [attr.aria-label]=\"'Remove Item ' + getObjectValue(result, configuration.primaryTextField, i)\"\n        [class.text-base]=\"disabled\"\n        aria-hidden=\"false\"\n        class=\"sds-tag__close\"\n        (click)=\"removeItem(result)\"\n        (keyup.enter)=\"removeItem(result)\"\n      >\n        <usa-icon [icon]=\"'x'\" size=\"lg\"></usa-icon>\n      </button>\n    </div>\n  </li>\n</ul>\n" }]
    }], null, { itemTemplate: [{
            type: Input
        }], configuration: [{
            type: Input
        }], disabled: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SDSSelectedResultComponent, { className: "SDSSelectedResultComponent" }); })();

class KeyHelper {
    constructor(...keys) {
        this._allowedKeys = [];
        this._currentlySupported = [
            'alt',
            'enter',
            'up',
            'down',
            'left',
            'right',
            'tab',
            'esc',
            'space',
            'shift',
            'backspace',
            '1',
            '2',
            '3',
            '4',
            '5',
            '6',
            '7',
            '8',
            '9',
            '0',
            'delete',
        ];
        this._setAllowedKeys(...keys);
    }
    isAllowed(event) {
        const val = this._allowedKeys.reduce((val, key) => {
            return KeyHelper.is(key, event) || val;
        }, false);
        return val;
    }
    _setAllowedKeys(...keys) {
        keys.forEach((key) => {
            if (this._currentlySupported.indexOf(key) !== -1) {
                this._allowedKeys.push(key);
            }
            else {
                const ok = this._allowedToString();
                const msg = `Only supports ${ok} at this time`;
                throw new TypeError(msg);
            }
        });
    }
    _allowedToString() {
        return this._allowedKeys.join(', ');
    }
    static getKeyCode(event) {
        if (!event) {
            return undefined;
        }
        else if (event.key) {
            return event.key;
        }
        else if (event.code) {
            return event.code;
        }
        else if (event.keyIdentifier) {
            return event.keyIdentifier;
        }
        else {
            return undefined;
        }
    }
    static getNumberFromKey(event) {
        const tests = [
            KeyHelper._zero,
            KeyHelper._one,
            KeyHelper._two,
            KeyHelper._three,
            KeyHelper._four,
            KeyHelper._five,
            KeyHelper._six,
            KeyHelper._seven,
            KeyHelper._eight,
            KeyHelper._nine,
        ];
        return tests.reduce((val, test) => {
            return val !== undefined ? val : test(event);
        }, undefined);
    }
    static is(validKeyParam, event) {
        let lowercased = validKeyParam.toLowerCase();
        switch (lowercased) {
            case 'alt':
                return this._isAltKey(event);
            case 'enter':
                return this._isEnter(event);
            case 'up':
                return this._isArrowUp(event);
            case 'down':
                return this._isArrowDown(event);
            case 'left':
                return this._isArrowLeft(event);
            case 'right':
                return this._isArrowRight(event);
            case 'tab':
                return this._isTab(event);
            case 'esc':
                return this._isEscape(event);
            case 'space':
                return this._isSpace(event);
            case 'shift':
                return this._isShift(event);
            case 'backspace':
                return this._isBackspace(event);
            case 'delete':
                return this._isDelete(event);
            case '0':
                return this._isExpectedNumber(0, event);
            case '1':
                return this._isExpectedNumber(1, event);
            case '2':
                return this._isExpectedNumber(2, event);
            case '3':
                return this._isExpectedNumber(3, event);
            case '4':
                return this._isExpectedNumber(4, event);
            case '5':
                return this._isExpectedNumber(5, event);
            case '6':
                return this._isExpectedNumber(6, event);
            case '7':
                return this._isExpectedNumber(7, event);
            case '8':
                return this._isExpectedNumber(8, event);
            case '9':
                return this._isExpectedNumber(9, event);
            default:
                return false;
        }
    }
    static _isEnter(e) {
        if (e.code === 'Enter' ||
            e.key === 'Enter' ||
            e.keyIdentifier === 'Enter' ||
            e.which === 13 ||
            e.charCode === 13 ||
            e.keyCode === 13) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isAltKey(e) {
        if (e.code === 'Alt' ||
            e.key === 'Alt' ||
            e.keyIdentifier === 'Alt' ||
            e.which === 18 ||
            e.charCode === 18 ||
            e.keyCode === 18) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isArrowUp(e) {
        if (e.code === 'ArrowUp' ||
            e.key === 'ArrowUp' ||
            e.key === 'Up' ||
            e.keyIdentifier === 'Up' ||
            e.which === 38 ||
            e.keyCode === 38) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isArrowDown(e) {
        if (e.code === 'ArrowDown' ||
            e.key === 'ArrowDown' ||
            e.key === 'Down' ||
            e.keyIdentifier === 'Down' ||
            e.which === 40 ||
            e.keyCode === 40) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isArrowLeft(e) {
        if (e.code === 'ArrowLeft' ||
            e.key === 'ArrowLeft' ||
            e.key === 'Left' ||
            e.keyIdentifier === 'Left' ||
            e.which === 37 ||
            e.keyCode === 37) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isArrowRight(e) {
        if (e.code === 'ArrowRight' ||
            e.key === 'ArrowRight' ||
            e.key === 'Right' ||
            e.keyIdentifier === 'Right' ||
            e.which === 39 ||
            e.keyCode === 39) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isTab(e) {
        if (e.code === 'Tab' || e.key === 'Tab' || e.keyIdentifier === 'U+0009' || e.which === 9 || e.keyCode === 9) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isEscape(e) {
        if (e.code === 'Escape' ||
            e.key === 'Escape' ||
            e.key === 'Esc' ||
            e.keyIdentifier === 'U+001B' ||
            e.which === 27 ||
            e.keyCode === 27) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isSpace(e) {
        if (e.code === 'Space' ||
            e.key === ' ' ||
            e.key === 'Spacebar' ||
            e.keyIdentifier === 'U+0020' ||
            e.which === 32 ||
            e.keyCode === 32) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isShift(e) {
        if (e.code === 'ShiftLeft' ||
            e.code === 'ShiftRight' ||
            e.key === 'Shift' ||
            e.keyIdentifier === 'Shift' ||
            e.which === 16 ||
            e.keyCode === 16) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isBackspace(e) {
        if (e.code === 'Backspace' ||
            e.key === 'Backspace' ||
            e.keyIdentifier === 'U+0008' ||
            e.which === 8 ||
            e.keyCode === 8) {
            return true;
        }
        else {
            return false;
        }
    }
    static _isDelete(e) {
        if (e.code === 'Delete' ||
            e.key === 'Delete' ||
            e.keyIdentifier === 'U+007F' ||
            e.which === 46 ||
            e.keyCode === 46) {
            return true;
        }
        else {
            return false;
        }
    }
    static _zero(e) {
        if (e.code === 'Digit0' ||
            e.code === 'Numpad0' ||
            e.key === 0 ||
            e.keyCode === 48 ||
            e.keyCode === 96 ||
            e.keyIdentifier === 'U+0030' ||
            e.which === 48) {
            return 0;
        }
        else {
            return undefined;
        }
    }
    static _one(e) {
        if (e.code === 'Digit1' ||
            e.code === 'Numpad1' ||
            e.key === 1 ||
            e.keyCode === 49 ||
            e.keyCode === 97 ||
            e.keyIdentifier === 'U+0031' ||
            e.which === 49) {
            return 1;
        }
        else {
            return undefined;
        }
    }
    static _two(e) {
        if (e.code === 'Digit2' ||
            e.code === 'Numpad2' ||
            e.key === 2 ||
            e.keyCode === 50 ||
            e.keyCode === 98 ||
            e.keyIdentifier === 'U+0032' ||
            e.which === 50) {
            return 2;
        }
        else {
            return undefined;
        }
    }
    static _three(e) {
        if (e.code === 'Digit3' ||
            e.code === 'Numpad3' ||
            e.key === 3 ||
            e.keyCode === 51 ||
            e.keyCode === 99 ||
            e.keyIdentifier === 'U+0033' ||
            e.which === 51) {
            return 3;
        }
        else {
            return undefined;
        }
    }
    static _four(e) {
        if (e.code === 'Digit4' ||
            e.code === 'Numpad4' ||
            e.key === 4 ||
            e.keyCode === 52 ||
            e.keyCode === 100 ||
            e.keyIdentifier === 'U+0034' ||
            e.which === 52) {
            return 4;
        }
        else {
            return undefined;
        }
    }
    static _five(e) {
        if (e.code === 'Digit5' ||
            e.code === 'Numpad5' ||
            e.key === 5 ||
            e.keyCode === 53 ||
            e.keyCode === 101 ||
            e.keyIdentifier === 'U+0035' ||
            e.which === 53) {
            return 5;
        }
        else {
            return undefined;
        }
    }
    static _six(e) {
        if (e.code === 'Digit6' ||
            e.code === 'Numpad6' ||
            e.key === 6 ||
            e.keyCode === 54 ||
            e.keyCode === 102 ||
            e.keyIdentifier === 'U+0036' ||
            e.which === 54) {
            return 6;
        }
        else {
            return undefined;
        }
    }
    static _seven(e) {
        if (e.code === 'Digit7' ||
            e.code === 'Numpad7' ||
            e.key === 7 ||
            e.keyCode === 55 ||
            e.keyCode === 103 ||
            e.keyIdentifier === 'U+0037' ||
            e.which === 55) {
            return 7;
        }
        else {
            return undefined;
        }
    }
    static _eight(e) {
        if (e.code === 'Digit8' ||
            e.code === 'Numpad8' ||
            e.key === 8 ||
            e.keyCode === 56 ||
            e.keyCode === 104 ||
            e.keyIdentifier === 'U+0038' ||
            e.which === 56) {
            return 8;
        }
        else {
            return undefined;
        }
    }
    static _nine(e) {
        if (e.code === 'Digit9' ||
            e.code === 'Numpad9' ||
            e.key === 9 ||
            e.keyCode === 57 ||
            e.keyCode === 105 ||
            e.keyIdentifier === 'U+0039' ||
            e.which === 57) {
            return 9;
        }
        else {
            return undefined;
        }
    }
    static _isExpectedNumber(expected, event) {
        return expected === KeyHelper.getNumberFromKey(event);
    }
}
var KEYS;
(function (KEYS) {
    KEYS["ENTER"] = "enter";
    KEYS["ALT"] = "alt";
    KEYS["UP"] = "up";
    KEYS["DOWN"] = "down";
    KEYS["LEFT"] = "left";
    KEYS["RIGHT"] = "right";
    KEYS["TAB"] = "tab";
    KEYS["ESC"] = "esc";
    KEYS["SPACE"] = "space";
    KEYS["SHIFT"] = "shift";
    KEYS["BACKSPACE"] = "backspace";
    KEYS["ONE"] = "1";
    KEYS["TWO"] = "2";
    KEYS["THREE"] = "3";
    KEYS["FOUR"] = "4";
    KEYS["FIVE"] = "5";
    KEYS["SIX"] = "6";
    KEYS["SEVEN"] = "7";
    KEYS["EIGHT"] = "8";
    KEYS["NINE"] = "9";
    KEYS["ZERO"] = "0";
    KEYS["DELETE"] = "delete";
})(KEYS || (KEYS = {}));

/**
 * The <sam-click-outside> directive can detect whether a click is made inside
 * the target
 */
class SDSClickOutsideDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        /**
         * Event emitted when clicked outside the target
         */
        this.clickOutside = new EventEmitter();
    }
    onClick(event) {
        const targetElement = event.target;
        const clickedInside = this.isInside(targetElement);
        if (!clickedInside) {
            this.clickOutside.emit(undefined);
        }
    }
    isInside(targetElement) {
        return targetElement.closest('.sds-autocomplete, .usa-input') !== null;
    }
    /** @nocollapse */ static { this.ɵfac = function SDSClickOutsideDirective_Factory(t) { return new (t || SDSClickOutsideDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SDSClickOutsideDirective, selectors: [["", "sds-click-outside", ""]], hostBindings: function SDSClickOutsideDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function SDSClickOutsideDirective_click_HostBindingHandler($event) { return ctx.onClick($event); }, false, i0.ɵɵresolveDocument);
        } }, outputs: { clickOutside: "clickOutside" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SDSClickOutsideDirective, [{
        type: Directive,
        args: [{
                selector: '[sds-click-outside]',
            }]
    }], () => [{ type: i0.ElementRef }], { clickOutside: [{
            type: Output
        }], onClick: [{
            type: HostListener,
            args: ['document:click', ['$event']]
        }] }); })();

class SDSTabOutsideDirective {
    constructor(_elementRef) {
        this._elementRef = _elementRef;
        /**
         * Emitter for tabOutside event
         */
        this.tabOutside = new EventEmitter();
    }
    hasFocusChanged(target) {
        const isInsideHost = this._elementRef.nativeElement.contains(target);
        if (!isInsideHost) {
            this.tabOutside.emit(undefined);
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SDSTabOutsideDirective_Factory(t) { return new (t || SDSTabOutsideDirective)(i0.ɵɵdirectiveInject(i0.ElementRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SDSTabOutsideDirective, selectors: [["", "sds-tab-outside", ""]], hostBindings: function SDSTabOutsideDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("keyup", function SDSTabOutsideDirective_keyup_HostBindingHandler($event) { return ctx.hasFocusChanged($event.target); }, false, i0.ɵɵresolveDocument);
        } }, outputs: { tabOutside: "tabOutside" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SDSTabOutsideDirective, [{
        type: Directive,
        args: [{
                selector: '[sds-tab-outside]',
            }]
    }], () => [{ type: i0.ElementRef }], { tabOutside: [{
            type: Output
        }], hasFocusChanged: [{
            type: HostListener,
            args: ['document:keyup', ['$event.target']]
        }] }); })();

const _c0$i = ["resultsList"];
const _c1$9 = ["input"];
const _c2$4 = a0 => ({ "sds-selected__label--disabled": a0 });
const _c3$3 = a0 => ({ "sds-autocomplete__checkbox--highlighted": a0 });
const _c4$2 = (a0, a1, a2, a3) => ({ "sds-autocomplete__group": a0, "sds-autocomplete__item": a1, "sds-autocomplete__item--highlighted": a2, "sds-autocomplete__item--selected": a3 });
const _c5$1 = a0 => ({ $implicit: a0 });
const _c6 = (a0, a1, a2, a3) => ({ "sds-autocomplete__item": a0, "sds-autocomplete__item--highlighted": a1, "sds-autocomplete__item--disabled": a2, "sds-autocomplete__item--selected": a3 });
const _c7 = a0 => ({ "text-semibold": a0 });
const _c8 = (a0, a1) => ({ "sds-autocomplete__item--selected": a0, "sds-autocomplete__item--highlighted": a1 });
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_li_5_ng_container_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 18);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const result_r5 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", result_r5[ctx_r2.configuration.secondaryTextField], " ");
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_li_5_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 14);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_li_5_Template_li_click_0_listener() { const result_r5 = i0.ɵɵrestoreView(_r4).$implicit; const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r2.selectItem(result_r5)); });
    i0.ɵɵelementStart(1, "div", 15)(2, "input", 16);
    i0.ɵɵlistener("keydown.esc", function SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_li_5_Template_input_keydown_esc_2_listener() { i0.ɵɵrestoreView(_r4); const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r2.showResults = false); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "label", 17);
    i0.ɵɵtext(4);
    i0.ɵɵtemplate(5, SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_li_5_ng_container_5_Template, 3, 1, "ng-container", 8);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const result_r5 = ctx.$implicit;
    const i_r6 = ctx.index;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(6, _c3$3, result_r5["highlighted"]));
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-resultItem-" + i_r6);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("checked", ctx_r2.checkItemSelected(result_r5));
    i0.ɵɵattribute("aria-label", "Select " + ctx_r2.getObjectValue(result_r5, ctx_r2.configuration.primaryTextField));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r2.getObjectValue(result_r5, ctx_r2.configuration.primaryTextField), " ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.configuration.secondaryTextField && result_r5[ctx_r2.configuration.secondaryTextField]);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "label", 11);
    i0.ɵɵelement(2, "usa-icon", 12);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div");
    i0.ɵɵtemplate(5, SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_li_5_Template, 6, 8, "li", 13);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(5, _c2$4, ctx_r2.model.items.length <= 0));
    i0.ɵɵattribute("aria-label", ctx_r2.model.items.length + "Selected");
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "check2-square");
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.model.items.length, " SELECTED ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", ctx_r2.results);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 20);
} if (rf & 2) {
    const result_r8 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r2.itemTemplate)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c5$1, result_r8));
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const result_r8 = i0.ɵɵnextContext(2).$implicit;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(result_r8[ctx_r2.configuration.secondaryTextField]);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_div_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const child_r10 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext(5);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", child_r10[ctx_r2.configuration.secondaryTextField], " ");
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_li_7_div_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const subchild_r12 = i0.ɵɵnextContext().$implicit;
    const ctx_r2 = i0.ɵɵnextContext(6);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", subchild_r12[ctx_r2.configuration.secondaryTextField], " ");
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_li_7_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 24);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_li_7_Template_li_click_0_listener() { const subchild_r12 = i0.ɵɵrestoreView(_r11).$implicit; const ctx_r2 = i0.ɵɵnextContext(6); return i0.ɵɵresetView(ctx_r2.selectItem(subchild_r12)); });
    i0.ɵɵelementStart(1, "div")(2, "div", 21);
    i0.ɵɵtext(3);
    i0.ɵɵtemplate(4, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_li_7_div_4_Template, 2, 1, "div", 8);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const subchild_r12 = ctx.$implicit;
    const k_r13 = ctx.index;
    const ctx_r2 = i0.ɵɵnextContext(6);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(5, _c8, ctx_r2.checkItemSelected(subchild_r12), subchild_r12["highlighted"]));
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-grandchildren-" + k_r13);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(8, _c7, ctx_r2.configuration.secondaryTextField));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.getObjectValue(subchild_r12, ctx_r2.configuration.primaryTextField), " ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.configuration.secondaryTextField && subchild_r12[ctx_r2.configuration.secondaryTextField]);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_Template(rf, ctx) { if (rf & 1) {
    const _r9 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 14);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_Template_li_click_0_listener() { const child_r10 = i0.ɵɵrestoreView(_r9).$implicit; const ctx_r2 = i0.ɵɵnextContext(5); return i0.ɵɵresetView(ctx_r2.selectItem(child_r10)); });
    i0.ɵɵelementStart(1, "div", 21)(2, "div", 21);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_div_4_Template, 2, 1, "div", 8);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "ul", 22, 3);
    i0.ɵɵtemplate(7, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_li_7_Template, 5, 10, "li", 23);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const child_r10 = ctx.$implicit;
    const j_r14 = ctx.index;
    const ctx_r2 = i0.ɵɵnextContext(5);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction4(8, _c4$2, child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length, !(child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length), child_r10["highlighted"] && !(child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length), ctx_r2.checkItemSelected(child_r10) && !(child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length)));
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-childItem-" + j_r14);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction4(13, _c6, child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length, (child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length) && child_r10["highlighted"], !ctx_r2.configuration.isSelectableGroup && (child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length), ctx_r2.checkItemSelected(child_r10) && (child_r10[ctx_r2.configuration.groupByChild] == null ? null : child_r10[ctx_r2.configuration.groupByChild].length)));
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(18, _c7, ctx_r2.configuration.secondaryTextField));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.getObjectValue(child_r10, ctx_r2.configuration.primaryTextField), " ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.configuration.secondaryTextField && child_r10[ctx_r2.configuration.secondaryTextField]);
    i0.ɵɵadvance();
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-grandchildren-");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", child_r10[ctx_r2.configuration.groupByChild]);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 21)(2, "div", 21);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(4, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_ng_container_4_Template, 3, 1, "ng-container", 8);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "ul", 22, 2);
    i0.ɵɵtemplate(7, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_li_7_Template, 8, 20, "li", 13);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r14 = i0.ɵɵnextContext();
    const result_r8 = ctx_r14.$implicit;
    const i_r16 = ctx_r14.index;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction4(6, _c6, ctx_r2.configuration.isGroupingEnabled, ctx_r2.configuration.isGroupingEnabled && result_r8["highlighted"], !ctx_r2.configuration.isSelectableGroup && ctx_r2.configuration.isGroupingEnabled, ctx_r2.checkItemSelected(result_r8) && ctx_r2.configuration.isGroupingEnabled));
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(11, _c7, ctx_r2.configuration.secondaryTextField));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r2.getObjectValue(result_r8, ctx_r2.configuration.primaryTextField), " ");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.configuration.secondaryTextField && result_r8[ctx_r2.configuration.secondaryTextField]);
    i0.ɵɵadvance();
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-childrenlist-" + i_r16);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", result_r8[ctx_r2.configuration.groupByChild]);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 14);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_Template_li_click_0_listener() { const result_r8 = i0.ɵɵrestoreView(_r7).$implicit; const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(!ctx_r2.configuration.isSelectableGroup ? "" : ctx_r2.selectItem(result_r8)); });
    i0.ɵɵtemplate(1, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_1_Template, 1, 4, "ng-container", 19)(2, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_ng_container_2_Template, 8, 13, "ng-container", 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const result_r8 = ctx.$implicit;
    const i_r16 = ctx.index;
    const ctx_r2 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction4(4, _c4$2, ctx_r2.configuration.isGroupingEnabled, !ctx_r2.configuration.isGroupingEnabled, result_r8["highlighted"] && !ctx_r2.configuration.isGroupingEnabled, ctx_r2.checkItemSelected(result_r8) && !ctx_r2.configuration.isGroupingEnabled));
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-resultItem-" + i_r16);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.itemTemplate);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r2.itemTemplate && !ctx_r2.configuration.useCheckBoxes);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_li_1_Template, 3, 9, "li", 13);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r2.results);
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "li", 25);
    i0.ɵɵtext(2, "No results found");
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} }
function SDSAutocompleteSearchComponent_ng_template_6_ng_container_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "li", 26);
    i0.ɵɵelement(2, "div", 27)(3, "div", 28);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "li", 26);
    i0.ɵɵelement(5, "div", 27)(6, "div", 28);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} }
function SDSAutocompleteSearchComponent_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ul", 9, 1);
    i0.ɵɵlistener("scroll", function SDSAutocompleteSearchComponent_ng_template_6_Template_ul_scroll_0_listener() { i0.ɵɵrestoreView(_r2); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.onScroll()); });
    i0.ɵɵtemplate(2, SDSAutocompleteSearchComponent_ng_template_6_ng_container_2_Template, 6, 7, "ng-container", 8)(3, SDSAutocompleteSearchComponent_ng_template_6_ng_container_3_Template, 2, 1, "ng-container", 8)(4, SDSAutocompleteSearchComponent_ng_template_6_ng_container_4_Template, 3, 0, "ng-container", 8)(5, SDSAutocompleteSearchComponent_ng_template_6_ng_container_5_Template, 7, 0, "ng-container", 8);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "ul", 10)(7, "li");
    i0.ɵɵtext(8);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵattribute("id", ctx_r2.configuration.id + "-list");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r2.configuration.useCheckBoxes);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.results && ctx_r2.results.length && !ctx_r2.configuration.useCheckBoxes);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r2.results || ctx_r2.results.length == 0);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.showLoad);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(ctx_r2.srOnlyText);
} }
function SDSAutocompleteSearchComponent_ng_container_7_span_2_Template(rf, ctx) { if (rf & 1) {
    const _r17 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 32);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_container_7_span_2_Template_span_click_0_listener() { i0.ɵɵrestoreView(_r17); const ctx_r2 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r2.clearInput()); })("keyup.enter", function SDSAutocompleteSearchComponent_ng_container_7_span_2_Template_span_keyup_enter_0_listener() { i0.ɵɵrestoreView(_r17); const ctx_r2 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r2.clearInput()); });
    i0.ɵɵelement(1, "usa-icon", 33);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "x");
} }
function SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_1_Template(rf, ctx) { if (rf & 1) {
    const _r18 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "usa-icon", 37);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_1_Template_usa_icon_click_0_listener() { i0.ɵɵrestoreView(_r18); const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r2.openOptions()); })("keyup.enter", function SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_1_Template_usa_icon_keyup_enter_0_listener() { i0.ɵɵrestoreView(_r18); const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r2.openOptions()); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵproperty("icon", "caret-down-fill");
} }
function SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_2_Template(rf, ctx) { if (rf & 1) {
    const _r19 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "usa-icon", 38);
    i0.ɵɵlistener("click", function SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_2_Template_usa_icon_click_0_listener($event) { i0.ɵɵrestoreView(_r19); const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r2.checkForFocus($event)); })("keyup.enter", function SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_2_Template_usa_icon_keyup_enter_0_listener($event) { i0.ɵɵrestoreView(_r19); const ctx_r2 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r2.checkForFocus($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵproperty("icon", "caret-up-fill");
} }
function SDSAutocompleteSearchComponent_ng_container_7_span_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 34);
    i0.ɵɵtemplate(1, SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_1_Template, 1, 1, "usa-icon", 35)(2, SDSAutocompleteSearchComponent_ng_container_7_span_3_usa_icon_2_Template, 1, 1, "usa-icon", 36);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r2.showResults && !ctx_r2.disabled);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.showResults && !ctx_r2.disabled);
} }
function SDSAutocompleteSearchComponent_ng_container_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "span", 29);
    i0.ɵɵtemplate(2, SDSAutocompleteSearchComponent_ng_container_7_span_2_Template, 2, 1, "span", 30)(3, SDSAutocompleteSearchComponent_ng_container_7_span_3_Template, 3, 2, "span", 31);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", ctx_r2.isClearIconVisible());
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r2.configuration.isTagModeEnabled);
} }
const Autocomplete_Autocomplete_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SDSAutocompleteSearchComponent),
    multi: true,
};
class SDSAutocompleteSearchComponent {
    constructor(_changeDetectorRef) {
        this._changeDetectorRef = _changeDetectorRef;
        /**
         * Model contain only the primary key, primary value, and secondary value.
         */
        this.essentialModelFields = false;
        /**
         * selected index
         */
        this.highlightedIndex = 0;
        /**
         * selected child index
         */
        this.highlightedChildIndex = 0;
        /**
         * value of the input field
         */
        this.inputValue = '';
        /**
         * Proprty being set on the object is highlighted
         */
        this.HighlightedPropertyName = 'highlighted';
        this.showLoad = true;
        /**
         * Search string
         */
        this.searchString = null;
        /**
         * To make input readonly
         */
        this.inputReadOnly = false;
        /**
         * Stored Event for ControlValueAccessor
         */
        this.onTouchedCallback = () => null;
        /**
         * Stored Event for ControlValueAccessor
         */
        this.propogateChange = (_) => null;
        this.resultsAvailableMessage = ' results available. Use up and down arrows\
  to scroll through results. Hit enter to select.';
        this.index = 0;
        /**
         * Determines if the dropdown should be shown
         */
        this.showResults = false;
    }
    /**
     * Gets the string value from the specifed properties of an object
     * @param object
     * @param propertyFields comma seperated list with periods depth of object
     */
    getObjectValue(object, propertyFields) {
        let value = '';
        let current = object;
        let fieldSplit = propertyFields.split(',');
        for (let i = 0; i < fieldSplit.length; i++) {
            let fieldValue = fieldSplit[i];
            let fieldPartSplit = fieldValue.split('.');
            for (let j = 0; j < fieldPartSplit.length; j++) {
                let fieldCheckValue = fieldPartSplit[j];
                if (current) {
                    current = current[fieldCheckValue];
                }
            }
            if (current) {
                value += current.toString() + ' ';
            }
            current = object;
        }
        return value.trim();
    }
    /**
     * Clears the input fields and value
     */
    clearInput() {
        this.inputValue = '';
        if (this.configuration.selectionMode === SelectionMode.SINGLE) {
            if (this.model.items.length > 0) {
                SDSSelectedItemModelHelper.clearItems(this.model.items);
                this.input.nativeElement.focus();
                this.propogateChange(this.model);
            }
        }
        this.onTouchedCallback();
        this.clearAndHideResults();
    }
    /**
     * Triggered when user clicks or enters on the up carat OR clicks outside this component
     * @param event
     */
    checkForFocus(event) {
        this.focusRemoved();
        this.showResults = false;
    }
    tabOutside(event) {
        if (!this.configuration.useCheckBoxes) {
            this.focusRemoved();
            this.showResults = false;
        }
    }
    /**
     *
     */
    focusRemoved() {
        if (this.configuration?.selectionMode === SelectionMode.SINGLE) {
            if (this.configuration.isFreeTextEnabled) {
                // Only one option allowed and that one option has been made so delete to user input.
                if (this.model.items.length > 0 && this.model.items[0]['name'] !== this.inputValue) {
                    SDSSelectedItemModelHelper.clearItems(this.model.items);
                    this.propogateChange(this.model);
                }
                if (this.inputValue === '') {
                }
                else if (this.model.items.length === 0 && this.inputValue !== '') {
                    const customItem = this.createFreeTextItem();
                    this.selectItem(customItem);
                }
                else if (this.model.items.length > 0 && this.model.items[0]['name'] !== this.inputValue) {
                    const customItem = this.createFreeTextItem();
                    this.selectItem(customItem);
                }
            }
            else if (this.model.items.length > 0) {
                this.inputValue = this.getObjectValue(this.model.items[0], this.configuration.primaryTextField);
            }
            else {
                this.inputValue = '';
            }
        }
        else {
            this.inputValue = '';
        }
    }
    onkeypress(ev) {
        return this.configuration.inputReadOnly ? false : true;
    }
    textChange(event) {
        if (!this.configuration.isTagModeEnabled) {
            // ie 11 placeholders will incorrectly trigger input events (known bug)
            // if input isn't active element then don't do anything
            if (event.target != document.activeElement) {
                event.preventDefault();
                return;
            }
            const searchString = event.target.value || '';
            this.getResults(searchString);
        }
    }
    isClearIconVisible() {
        if (this.model && this.model.items.length > 0 && this.configuration.hideCloseIcon)
            return false;
        return this.inputValue && !this.disabled;
    }
    //
    /**
     * Event method used when focus is gained to the input
     */
    inputFocusHandler() {
        if (!this.configuration.isTagModeEnabled) {
            if (this.configuration.focusInSearch) {
                this.highlightedIndex = 0;
                this.highlightedChildIndex = this.configuration.isSelectableGroup ? 0 : null;
                this.getResults(this.inputValue || '');
            }
            this.onTouchedCallback();
            if (this.isAutocompleteWithinModal()) {
                this.addListener();
            }
        }
    }
    isAutocompleteWithinModal() {
        return document.getElementsByClassName('sds-dialog-content').length > 0;
    }
    /**
     * Key event
     * @param event
     */
    onKeydown(event) {
        if (KeyHelper.is(KEYS.ALT, event)) {
            event.preventDefault();
            this.inputFocusHandler();
        }
        if (KeyHelper.is(KEYS.TAB, event)) {
            return;
        }
        else if (KeyHelper.is(KEYS.BACKSPACE, event)) {
            if (this.configuration.inputReadOnly) {
                event.preventDefault();
            }
        }
        else if (KeyHelper.is(KEYS.DOWN, event)) {
            event.preventDefault();
            this.onArrowGroupDown();
        }
        else if (KeyHelper.is(KEYS.UP, event)) {
            event.preventDefault();
            this.onArrowGroupUp();
        }
        else if (KeyHelper.is(KEYS.ENTER, event) && this.highlightedIndex >= 0) {
            if (this.configuration.isTagModeEnabled && this.inputValue.length > 0) {
                const val = this.createFreeTextItem();
                this.selectItem(val);
            }
            else {
                this.selectItem(this.highlightedItem);
            }
        }
        else if (KeyHelper.is(KEYS.ENTER, event) && this.highlightedIndex < 0) {
            if (this.configuration.isFreeTextEnabled && this.inputValue.length > 0) {
                const val = this.createFreeTextItem();
                this.selectItem(val);
                this.clearAndHideResults();
            }
        }
        else if (KeyHelper.is(KEYS.SPACE, event) &&
            this.configuration.useCheckBoxes &&
            this.highlightedIndex >= 0 &&
            this.inputValue === '') {
            event.preventDefault();
            this.selectItem(this.highlightedItem);
        }
        else if (KeyHelper.is(KEYS.ESC, event)) {
            if (this.showResults) {
                this.clearAndHideResults();
                if (event.stopPropagation) {
                    event.stopPropagation();
                }
            }
        }
    }
    /**
     * selects the item adding it to the model and closes the results
     * @param item
     */
    selectItem(item) {
        let filterItem = {};
        if (this.essentialModelFields) {
            filterItem[this.configuration.primaryKeyField] = item[this.configuration.primaryKeyField];
            filterItem[this.configuration.primaryTextField] = item[this.configuration.primaryTextField];
            if (this.configuration.secondaryTextField) {
                filterItem[this.configuration.secondaryTextField] = item[this.configuration.secondaryTextField];
            }
        }
        else {
            filterItem = item;
        }
        if (this.configuration.useCheckBoxes) {
            const isSelected = this.checkItemSelected(item);
            if (!isSelected) {
                this.addItemToModel(filterItem);
            }
            else if (this.configuration.selectionMode === SelectionMode.MULTIPLE) {
                this.unselectItem(item);
            }
        }
        else {
            this.addItemToModel(filterItem);
        }
        if (this.configuration.selectionMode === SelectionMode.MULTIPLE && !this.configuration.isTagModeEnabled) {
            this.showResults = true;
            this.input.nativeElement.focus();
            const flat = this.getFlatElements();
            const index = flat.findIndex((element) => element[this.configuration.primaryKeyField] === item[this.configuration.primaryKeyField]);
            this.highlightedIndex = index;
            this.setHighlightedItem(flat[this.highlightedIndex]);
        }
        else {
            this.showResults = false;
            if (this.configuration.selectionMode === SelectionMode.MULTIPLE) {
                this.inputValue = '';
            }
            this.input.nativeElement.focus();
        }
    }
    addItemToModel(item) {
        SDSSelectedItemModelHelper.addItem(item, this.configuration.primaryKeyField, this.configuration.selectionMode, this.model);
        this.propogateChange(this.model);
        let message = this.getObjectValue(item, this.configuration.primaryTextField);
        if (this.configuration.selectionMode === SelectionMode.SINGLE) {
            this.inputValue = message;
        }
    }
    unselectItem(item) {
        SDSSelectedItemModelHelper.removeItem(item, this.configuration.primaryKeyField, this.model);
        this.propogateChange(this.model);
    }
    /**
     *  clears the results and closes result drop down
     */
    clearAndHideResults() {
        this.results = [];
        this.showResults = false;
        this.focusRemoved();
    }
    openOptions() {
        this.input.nativeElement.focus();
        this.inputFocusHandler();
    }
    getFlatElements() {
        const results = this.results;
        const flat = [];
        const flatten = (array) => {
            for (let i in array) {
                const item = array[i];
                flat.push(item);
                if (item[this.configuration.groupByChild] && item[this.configuration.groupByChild].length) {
                    flatten(item[this.configuration.groupByChild]);
                }
            }
        };
        flatten(results);
        return flat;
    }
    /**
     * When paging up and down with arrow key it sets the highlighted item into view
     */
    scrollToSelectedItem() {
        if (this.highlightedIndex >= 0) {
            this._changeDetectorRef.detectChanges();
            const dom = this.resultsListElement.nativeElement;
            let selectedClass = this.configuration.useCheckBoxes
                ? '.sds-autocomplete__checkbox--highlighted'
                : '.sds-autocomplete__item--highlighted';
            const selectedChild = dom.querySelector(selectedClass);
            if (selectedChild) {
                // Manually set scroll top rather than invoke scroll functions for browser compatibility
                const containerCenter = this.resultsListElement.nativeElement.getBoundingClientRect().height / 2;
                this.resultsListElement.nativeElement.scrollTop = selectedChild.offsetTop - containerCenter;
            }
        }
    }
    /**
     *  handles the arrow down key event
     */
    onArrowGroupDown() {
        if (this.results && this.results.length > 0) {
            const flat = this.getFlatElements();
            if (this.highlightedIndex < flat.length - 1) {
                this.highlightedIndex++;
            }
            this.setHighlightedItem(flat[this.highlightedIndex]);
            this.scrollToSelectedItem();
        }
    }
    /**
     *  handles the arrow up key event
     */
    onArrowGroupUp() {
        if (this.results && this.results.length > 0) {
            const flat = this.getFlatElements();
            if (this.highlightedIndex != 0) {
                this.highlightedIndex--;
            }
            this.setHighlightedItem(flat[this.highlightedIndex]);
            this.scrollToSelectedItem();
        }
    }
    showFreeText() {
        if (this.configuration.isFreeTextEnabled) {
            if (this.inputValue) {
                if (this.inputValue.length !== 0) {
                    let foundItem = false;
                    if (this.results) {
                        for (var i = 0; i < this.results.length && !foundItem; i++) {
                            let item = this.results[i];
                            foundItem = item[this.configuration.primaryTextField] === this.inputValue;
                        }
                    }
                    if (this.model.items.length > 0 && !foundItem) {
                        for (var i = 0; i < this.model.items.length && !foundItem; i++) {
                            let item = this.model.items[i];
                            foundItem = item[this.configuration.primaryTextField] === this.inputValue;
                        }
                    }
                    return !foundItem;
                }
                else {
                    return false;
                }
            }
            else {
                return false;
            }
        }
        else {
            return this.configuration.isFreeTextEnabled;
        }
    }
    createFreeTextItem() {
        let item = { type: 'custom' };
        item[this.configuration.primaryTextField] = this.inputValue;
        item[this.configuration.primaryKeyField] = this.inputValue;
        return item;
    }
    /**
     *  return Item is already selected or not
     * @param result
     */
    checkItemSelected(result) {
        const selectedItem = this.model.items.filter((item) => item[this.configuration.primaryKeyField] === result[this.configuration.primaryKeyField]);
        return selectedItem.length > 0 ? true : false;
    }
    /**
     *  gets the inital results
     * @param searchString
     */
    getResults(searchString) {
        if (searchString.length >= this.configuration.minimumCharacterCountSearch) {
            if (!this.matchPastSearchString(searchString) ||
                (this.matchPastSearchString(searchString) && !this.showResults) ||
                this.matchPastSearchString('')) {
                this.searchString = searchString;
                window.clearTimeout(this.timeoutNumber);
                this.timeoutNumber = window.setTimeout(() => {
                    this.showLoad = true;
                    this.service.getDataByText(0, searchString).subscribe((result) => {
                        this.results = result.items;
                        this.showLoad = false;
                        this.maxResults = result.totalItems;
                        this.highlightedIndex =
                            this.configuration.isFreeTextEnabled || this.maxResults == 0
                                ? -1
                                : this.highlightedIndex >= 0
                                    ? this.highlightedIndex
                                    : 0;
                        if (!this.configuration.isFreeTextEnabled) {
                            this.setHighlightedItem(this.results[this.highlightedIndex]);
                        }
                        this.showResults = true;
                        this.addScreenReaderMessage(this.maxResults + ' ' + this.resultsAvailableMessage);
                        this._changeDetectorRef.markForCheck();
                    });
                }, this.configuration.debounceTime);
            }
        }
    }
    /**
     * Checks if the new search string matches the old search string
     * @param searchString
     */
    matchPastSearchString(searchString) {
        return this.searchString === searchString;
    }
    /**
     * Scroll Event Handler (Calculates if mpre items should be asked for from service on scrolling down)
     */
    onScroll() {
        if (this.maxResults > this.results.length) {
            let scrollAreaHeight = this.resultsListElement.nativeElement.offsetHeight;
            let scrollTopPos = this.resultsListElement.nativeElement.scrollTop;
            let scrollAreaMaxHeight = this.resultsListElement.nativeElement.scrollHeight;
            if (scrollTopPos + scrollAreaHeight * 2 >= scrollAreaMaxHeight) {
                this.getAdditionalResults();
            }
        }
    }
    /**
     * gets more results based when scrolling and adds the items
     */
    getAdditionalResults() {
        this.showLoad = true;
        this.service.getDataByText(this.results.length, this.searchString).subscribe((result) => {
            for (let i = 0; i < result.items.length; i++) {
                this.addResult(result.items[i]);
            }
            this.showLoad = false;
            this.maxResults = result.totalItems;
        });
    }
    /**
     * adds a single item to the list
     * @param item
     */
    addResult(item) {
        //add check to make sure item does not exist
        this.results.push(item);
    }
    /**
     * Sets the highlighted item by keyboard or mouseover
     * @param item
     */
    setHighlightedItem(item) {
        if (this.results && this.results.length > 0) {
            if (this.highlightedItem) {
                this.highlightedItem[this.HighlightedPropertyName] = false;
            }
            let message = '';
            if (item) {
                this.highlightedItem = item;
                this.highlightedItem[this.HighlightedPropertyName] = true;
                message = item[this.configuration.primaryTextField];
                if (this.configuration.secondaryTextField && item[this.configuration.secondaryTextField]) {
                    message += ': ' + item[this.configuration.secondaryTextField];
                }
            }
            else {
                this.highlightedItem = undefined;
                message = 'No item selected';
            }
            this.addScreenReaderMessage(message);
        }
    }
    /**
     * Adds message to be read by screen reader
     * @param message
     */
    addScreenReaderMessage(message) {
        this.srOnlyText = message;
    }
    writeValue(obj) {
        if (obj instanceof SDSSelectedItemModel) {
            this.model = obj;
            this._changeDetectorRef.markForCheck();
            if (this.model.items.length === 0) {
                this.inputValue = '';
            }
            else if (this.configuration && this.configuration.selectionMode === SelectionMode.SINGLE) {
                this.inputValue = this.getObjectValue(this.model.items[0], this.configuration.primaryTextField);
            }
        }
    }
    addListener() {
        const autocompleteElement = document.getElementById(this.configuration.id);
        const dialogContainer = document.getElementsByClassName('sds-dialog-content');
        const resultsDropdown = document.getElementsByClassName('sds-autocomplete');
        let inputHeight = autocompleteElement.getBoundingClientRect().height;
        let modalHeight = dialogContainer[0].getBoundingClientRect().height;
        dialogContainer[0].addEventListener('scroll', function (event) {
            if (resultsDropdown.length > 0) {
                let inputTopValue = autocompleteElement.getBoundingClientRect().top;
                let inputBottomValue = modalHeight - inputTopValue;
                let listHeight = resultsDropdown[0].getBoundingClientRect().height;
                let element = resultsDropdown[0].parentElement;
                if (element && element.style.bottom) {
                    element.style.bottom = inputBottomValue + listHeight - inputHeight + 30 + 'px';
                }
                else {
                    element.style.top = inputTopValue + inputHeight + 'px';
                }
            }
        });
    }
    getClass() {
        return this.configuration.inputReadOnly ? 'hide-cursor' : '';
    }
    registerOnChange(fn) {
        this.propogateChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = function SDSAutocompleteSearchComponent_Factory(t) { return new (t || SDSAutocompleteSearchComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SDSAutocompleteSearchComponent, selectors: [["sds-search-autocomplete"]], viewQuery: function SDSAutocompleteSearchComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$i, 5);
            i0.ɵɵviewQuery(_c1$9, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.resultsListElement = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.input = _t.first);
        } }, inputs: { itemTemplate: "itemTemplate", essentialModelFields: "essentialModelFields", configuration: "configuration", service: "service", inputReadOnly: "inputReadOnly", disabled: "disabled" }, features: [i0.ɵɵProvidersFeature([Autocomplete_Autocomplete_VALUE_ACCESSOR])], decls: 8, vars: 16, consts: [["trigger", "cdkOverlayOrigin", "input", ""], ["resultsList", ""], ["childrenList", ""], ["childrenItems", ""], ["sds-click-outside", "", "sds-tab-outside", "", 3, "clickOutside", "tabOutside"], [1, "maxw-mobile-lg", "position-relative"], ["role", "combobox", "cdkOverlayOrigin", "", "aria-haspopup", "listbox", "type", "text", "aria-autocomplete", "list", "autocomplete", "off", 1, "usa-input", "padding-right-3", 3, "keypress", "input", "click", "keydown", "ngModelChange", "disabled", "ngClass", "ngModel"], ["cdkConnectedOverlay", "", 3, "cdkConnectedOverlayOrigin", "cdkConnectedOverlayOpen", "cdkConnectedOverlayDisableClose", "cdkConnectedOverlayMinWidth"], [4, "ngIf"], ["role", "list", 1, "usa-list", "usa-list--unstyled", "sds-autocomplete", 3, "scroll"], ["aria-live", "assertive", 1, "usa-sr-only"], [1, "sds-selected__label", 3, "ngClass"], ["size", "1x", 1, "margin-right-1", 3, "icon"], ["role", "listitem", 3, "ngClass", "click", 4, "ngFor", "ngForOf"], ["role", "listitem", 3, "click", "ngClass"], [1, "usa-checkbox", "sds-autocomplete__checkbox"], ["type", "checkbox", 1, "usa-checkbox__input", 3, "keydown.esc", "checked"], [1, "usa-checkbox__label", "sds-autocomplete__checkbox--item"], [1, "sds-autocomplete__checkbox--secondary"], [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngIf"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [3, "ngClass"], ["role", "list", 1, "usa-list", "usa-list--unstyled"], ["role", "listitem", "class", "sds-autocomplete__item", 3, "ngClass", "click", 4, "ngFor", "ngForOf"], ["role", "listitem", 1, "sds-autocomplete__item", 3, "click", "ngClass"], [1, "emptyResults", "margin-1"], [1, "autoLoading"], [1, "lightToGrayBackgroundGradiate"], [1, "grayToLightBackgroundGradiate"], [1, "position-absolute", "right-105", "top-1", "cursor-pointer", "bg-white"], ["role", "button", "aria-label", "Clear input", "tabindex", "0", "class", "icon-container", 3, "click", "keyup.enter", 4, "ngIf"], ["class", "margin-left-1 margin-top-05 icon-container", 4, "ngIf"], ["role", "button", "aria-label", "Clear input", "tabindex", "0", 1, "icon-container", 3, "click", "keyup.enter"], ["size", "lg", 1, "font-sans-xs", 3, "icon"], [1, "margin-left-1", "margin-top-05", "icon-container"], ["role", "button", "aria-label", "Display Options", "tabindex", "0", "class", "font-sans-xs", "size", "1x", 3, "icon", "click", "keyup.enter", 4, "ngIf"], ["role", "button", "aria-label", "Hide Options", "tabindex", "0", "class", "font-sans-xs", "size", "1x", 3, "icon", "click", "keyup.enter", 4, "ngIf"], ["role", "button", "aria-label", "Display Options", "tabindex", "0", "size", "1x", 1, "font-sans-xs", 3, "click", "keyup.enter", "icon"], ["role", "button", "aria-label", "Hide Options", "tabindex", "0", "size", "1x", 1, "font-sans-xs", 3, "click", "keyup.enter", "icon"]], template: function SDSAutocompleteSearchComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelementStart(0, "div", 4);
            i0.ɵɵlistener("clickOutside", function SDSAutocompleteSearchComponent_Template_div_clickOutside_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.checkForFocus($event)); })("tabOutside", function SDSAutocompleteSearchComponent_Template_div_tabOutside_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.tabOutside($event)); });
            i0.ɵɵelementStart(1, "div", 5)(2, "div")(3, "input", 6, 0);
            i0.ɵɵlistener("keypress", function SDSAutocompleteSearchComponent_Template_input_keypress_3_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.onkeypress($event)); })("input", function SDSAutocompleteSearchComponent_Template_input_input_3_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.textChange($event)); })("click", function SDSAutocompleteSearchComponent_Template_input_click_3_listener() { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.inputFocusHandler()); })("keydown", function SDSAutocompleteSearchComponent_Template_input_keydown_3_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.onKeydown($event)); });
            i0.ɵɵtwoWayListener("ngModelChange", function SDSAutocompleteSearchComponent_Template_input_ngModelChange_3_listener($event) { i0.ɵɵrestoreView(_r1); i0.ɵɵtwoWayBindingSet(ctx.inputValue, $event) || (ctx.inputValue = $event); return i0.ɵɵresetView($event); });
            i0.ɵɵelementEnd()();
            i0.ɵɵtemplate(6, SDSAutocompleteSearchComponent_ng_template_6_Template, 9, 6, "ng-template", 7)(7, SDSAutocompleteSearchComponent_ng_container_7_Template, 4, 2, "ng-container", 8);
            i0.ɵɵelementEnd()();
        } if (rf & 2) {
            const trigger_r20 = i0.ɵɵreference(4);
            const input_r21 = i0.ɵɵreference(5);
            i0.ɵɵadvance(2);
            i0.ɵɵattribute("id", ctx.configuration.id + "-container");
            i0.ɵɵadvance();
            i0.ɵɵproperty("disabled", ctx.disabled)("ngClass", ctx.getClass());
            i0.ɵɵtwoWayProperty("ngModel", ctx.inputValue);
            i0.ɵɵattribute("aria-expanded", ctx.showResults)("aria-owns", ctx.showResults ? ctx.configuration.id + "-list" : "")("aria-label", ctx.configuration.ariaLabelText ? ctx.configuration.ariaLabelText : ctx.configuration.labelText)("id", ctx.configuration.id)("placeholder", ctx.configuration.autocompletePlaceHolderText)("aria-activedescendant", ctx.showResults ? ctx.configuration.id + "-resultItem-" + ctx.highlightedIndex : "")("aria-controls", ctx.showResults ? ctx.configuration.id + "-list" : "");
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("cdkConnectedOverlayOrigin", trigger_r20)("cdkConnectedOverlayOpen", ctx.showResults)("cdkConnectedOverlayDisableClose", true)("cdkConnectedOverlayMinWidth", input_r21.offsetWidth);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", !input_r21.disabled);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i2$2.DefaultValueAccessor, i2$2.NgControlStatus, i2$2.NgModel, SDSClickOutsideDirective, SDSTabOutsideDirective, i1$3.CdkConnectedOverlay, i1$3.CdkOverlayOrigin, i2$1.IconComponent], styles: [".grayToLightBackgroundGradiate[_ngcontent-%COMP%]{background:linear-gradient(to right,#c4c4c4,#f7f7f7);background-size:400% 400%;height:10px;padding:2.5px 0;text-align:center;color:#888;font-size:15px;font-weight:700;margin:7px 10px 0;-webkit-animation:_ngcontent-%COMP%_gradiantShiftGray 5s ease infinite;-moz-animation:gradiantShiftGray 5s ease infinite;animation:_ngcontent-%COMP%_gradiantShiftGray 5s ease infinite}@-webkit-keyframes _ngcontent-%COMP%_gradiantShiftGray{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@-moz-keyframes gradiantShiftGray{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@keyframes _ngcontent-%COMP%_gradiantShiftGray{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}.lightToGrayBackgroundGradiate[_ngcontent-%COMP%]{background:linear-gradient(to right,#f7f7f7,#c4c4c4);background-size:400% 400%;height:15px;padding:2.5px 0;text-align:center;color:#888;font-size:15px;font-weight:700;margin:10px 10px 0;-webkit-animation:_ngcontent-%COMP%_gradiantShift 5s ease infinite;-moz-animation:gradiantShift 5s ease infinite;animation:_ngcontent-%COMP%_gradiantShift 5s ease infinite}@-webkit-keyframes _ngcontent-%COMP%_gradiantShift{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@-moz-keyframes gradiantShift{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@keyframes _ngcontent-%COMP%_gradiantShift{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}.autoLoading[_ngcontent-%COMP%]{height:60px}.hide-cursor[_ngcontent-%COMP%]{caret-color:transparent}input[_ngcontent-%COMP%]::-ms-clear{display:none}span.icon-container[_ngcontent-%COMP%]{display:inline-block}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SDSAutocompleteSearchComponent, [{
        type: Component,
        args: [{ selector: 'sds-search-autocomplete', providers: [Autocomplete_Autocomplete_VALUE_ACCESSOR], template: "<div sds-click-outside (clickOutside)=\"checkForFocus($event)\" sds-tab-outside (tabOutside)=\"tabOutside($event)\">\n  <div class=\"maxw-mobile-lg position-relative\">\n    <div [attr.id]=\"configuration.id + '-container'\">\n      <input\n        role=\"combobox\"\n        [attr.aria-expanded]=\"showResults\"\n        cdkOverlayOrigin\n        #trigger=\"cdkOverlayOrigin\"\n        [attr.aria-owns]=\"showResults ? configuration.id + '-list' : ''\"\n        aria-haspopup=\"listbox\"\n        [disabled]=\"disabled\"\n        (keypress)=\"onkeypress($event)\"\n        (input)=\"textChange($event)\"\n        class=\"usa-input padding-right-3\"\n        [ngClass]=\"getClass()\"\n        #input\n        [attr.aria-label]=\"configuration.ariaLabelText ? configuration.ariaLabelText : configuration.labelText\"\n        [attr.id]=\"configuration.id\"\n        type=\"text\"\n        (click)=\"inputFocusHandler()\"\n        (keydown)=\"onKeydown($event)\"\n        aria-autocomplete=\"list\"\n        [(ngModel)]=\"inputValue\"\n        [attr.placeholder]=\"configuration.autocompletePlaceHolderText\"\n        [attr.aria-activedescendant]=\"showResults ? configuration.id + '-resultItem-' + highlightedIndex : ''\"\n        [attr.aria-controls]=\"showResults ? configuration.id + '-list' : ''\"\n        autocomplete=\"off\"\n      />\n    </div>\n    <ng-template\n      cdkConnectedOverlay\n      [cdkConnectedOverlayOrigin]=\"trigger\"\n      [cdkConnectedOverlayOpen]=\"showResults\"\n      [cdkConnectedOverlayDisableClose]=\"true\"\n      [cdkConnectedOverlayMinWidth]=\"input.offsetWidth\"\n    >\n      <ul\n        #resultsList\n        [attr.id]=\"configuration.id + '-list'\"\n        role=\"list\"\n        class=\"usa-list usa-list--unstyled sds-autocomplete\"\n        (scroll)=\"onScroll()\"\n      >\n        <ng-container *ngIf=\"configuration.useCheckBoxes\">\n          <label\n            class=\"sds-selected__label\"\n            [ngClass]=\"{ 'sds-selected__label--disabled': model.items.length <= 0 }\"\n            [attr.aria-label]=\"model.items.length + 'Selected'\"\n          >\n            <usa-icon [icon]=\"'check2-square'\" size=\"1x\" class=\"margin-right-1\"> </usa-icon>\n            {{ model.items.length }} SELECTED\n          </label>\n          <div>\n            <li\n              [attr.id]=\"configuration.id + '-resultItem-' + i\"\n              role=\"listitem\"\n              *ngFor=\"let result of results; let i = index\"\n              (click)=\"selectItem(result)\"\n              [ngClass]=\"{\n                'sds-autocomplete__checkbox--highlighted': result['highlighted']\n              }\"\n            >\n              <div class=\"usa-checkbox sds-autocomplete__checkbox\">\n                <input\n                  class=\"usa-checkbox__input\"\n                  type=\"checkbox\"\n                  (keydown.esc)=\"showResults = false\"\n                  [checked]=\"checkItemSelected(result)\"\n                  [attr.aria-label]=\"'Select ' + getObjectValue(result, configuration.primaryTextField)\"\n                />\n                <label class=\"usa-checkbox__label sds-autocomplete__checkbox--item\">\n                  {{ getObjectValue(result, configuration.primaryTextField) }}\n                  <ng-container *ngIf=\"configuration.secondaryTextField && result[configuration.secondaryTextField]\">\n                    <div class=\"sds-autocomplete__checkbox--secondary\">\n                      {{ result[configuration.secondaryTextField] }}\n                    </div>\n                  </ng-container>\n                </label>\n              </div>\n            </li>\n          </div>\n        </ng-container>\n        <ng-container *ngIf=\"results && results.length && !configuration.useCheckBoxes\">\n          <li\n            [attr.id]=\"configuration.id + '-resultItem-' + i\"\n            role=\"listitem\"\n            [ngClass]=\"{\n              'sds-autocomplete__group': configuration.isGroupingEnabled,\n              'sds-autocomplete__item': !configuration.isGroupingEnabled,\n              'sds-autocomplete__item--highlighted': result['highlighted'] && !configuration.isGroupingEnabled,\n              'sds-autocomplete__item--selected': checkItemSelected(result) && !configuration.isGroupingEnabled\n            }\"\n            *ngFor=\"let result of results; let i = index\"\n            (click)=\"!configuration.isSelectableGroup ? '' : selectItem(result)\"\n          >\n            <ng-container\n              *ngIf=\"itemTemplate\"\n              [ngTemplateOutlet]=\"itemTemplate\"\n              [ngTemplateOutletContext]=\"{ $implicit: result }\"\n            >\n            </ng-container>\n            <ng-container *ngIf=\"!itemTemplate && !configuration.useCheckBoxes\">\n              <div\n                [ngClass]=\"{\n                  'sds-autocomplete__item': configuration.isGroupingEnabled,\n                  'sds-autocomplete__item--highlighted': configuration.isGroupingEnabled && result['highlighted'],\n                  'sds-autocomplete__item--disabled':\n                    !configuration.isSelectableGroup && configuration.isGroupingEnabled,\n                  'sds-autocomplete__item--selected': checkItemSelected(result) && configuration.isGroupingEnabled\n                }\"\n              >\n                <div\n                  [ngClass]=\"{\n                    'text-semibold': configuration.secondaryTextField\n                  }\"\n                >\n                  {{ getObjectValue(result, configuration.primaryTextField) }}\n                </div>\n                <ng-container *ngIf=\"configuration.secondaryTextField && result[configuration.secondaryTextField]\">\n                  <div>{{ result[configuration.secondaryTextField] }}</div>\n                </ng-container>\n              </div>\n\n              <ul\n                class=\"usa-list usa-list--unstyled\"\n                #childrenList\n                [attr.id]=\"configuration.id + '-childrenlist-' + i\"\n                role=\"list\"\n              >\n                <li\n                  [attr.id]=\"configuration.id + '-childItem-' + j\"\n                  role=\"listitem\"\n                  [ngClass]=\"{\n                    'sds-autocomplete__group': child[configuration.groupByChild]?.length,\n                    'sds-autocomplete__item': !child[configuration.groupByChild]?.length,\n                    'sds-autocomplete__item--highlighted':\n                      child['highlighted'] && !child[configuration.groupByChild]?.length,\n                    'sds-autocomplete__item--selected':\n                      checkItemSelected(child) && !child[configuration.groupByChild]?.length\n                  }\"\n                  *ngFor=\"let child of result[configuration.groupByChild]; let j = index\"\n                  (click)=\"selectItem(child)\"\n                >\n                  <div\n                    [ngClass]=\"{\n                      'sds-autocomplete__item': child[configuration.groupByChild]?.length,\n                      'sds-autocomplete__item--highlighted':\n                        child[configuration.groupByChild]?.length && child['highlighted'],\n                      'sds-autocomplete__item--disabled':\n                        !configuration.isSelectableGroup && child[configuration.groupByChild]?.length,\n                      'sds-autocomplete__item--selected':\n                        checkItemSelected(child) && child[configuration.groupByChild]?.length\n                    }\"\n                  >\n                    <div\n                      [ngClass]=\"{\n                        'text-semibold': configuration.secondaryTextField\n                      }\"\n                    >\n                      {{ getObjectValue(child, configuration.primaryTextField) }}\n                    </div>\n\n                    <div *ngIf=\"configuration.secondaryTextField && child[configuration.secondaryTextField]\">\n                      {{ child[configuration.secondaryTextField] }}\n                    </div>\n                  </div>\n                  <ul\n                    class=\"usa-list usa-list--unstyled\"\n                    #childrenItems\n                    [attr.id]=\"configuration.id + '-grandchildren-'\"\n                    role=\"list\"\n                  >\n                    <li\n                      [attr.id]=\"configuration.id + '-grandchildren-' + k\"\n                      role=\"listitem\"\n                      class=\"sds-autocomplete__item\"\n                      [ngClass]=\"{\n                        'sds-autocomplete__item--selected': checkItemSelected(subchild),\n                        'sds-autocomplete__item--highlighted': subchild['highlighted']\n                      }\"\n                      *ngFor=\"let subchild of child[configuration.groupByChild]; let k = index\"\n                      (click)=\"selectItem(subchild)\"\n                    >\n                      <div>\n                        <div\n                          [ngClass]=\"{\n                            'text-semibold': configuration.secondaryTextField\n                          }\"\n                        >\n                          {{ getObjectValue(subchild, configuration.primaryTextField) }}\n\n                          <div *ngIf=\"configuration.secondaryTextField && subchild[configuration.secondaryTextField]\">\n                            {{ subchild[configuration.secondaryTextField] }}\n                          </div>\n                        </div>\n                      </div>\n                    </li>\n                  </ul>\n                </li>\n              </ul>\n            </ng-container>\n          </li>\n        </ng-container>\n        <ng-container *ngIf=\"!results || results.length == 0\">\n          <li class=\"emptyResults margin-1\">No results found</li>\n        </ng-container>\n        <ng-container *ngIf=\"showLoad\">\n          <li class=\"autoLoading\">\n            <div class=\"lightToGrayBackgroundGradiate\"></div>\n            <div class=\"grayToLightBackgroundGradiate\"></div>\n          </li>\n          <li class=\"autoLoading\">\n            <div class=\"lightToGrayBackgroundGradiate\"></div>\n            <div class=\"grayToLightBackgroundGradiate\"></div>\n          </li>\n        </ng-container>\n      </ul>\n      <ul class=\"usa-sr-only\" aria-live=\"assertive\">\n        <li>{{ srOnlyText }}</li>\n      </ul>\n    </ng-template>\n\n    <ng-container *ngIf=\"!input.disabled\">\n      <span class=\"position-absolute right-105 top-1 cursor-pointer bg-white\">\n        <span\n          *ngIf=\"isClearIconVisible()\"\n          role=\"button\"\n          aria-label=\"Clear input\"\n          (click)=\"clearInput()\"\n          (keyup.enter)=\"clearInput()\"\n          tabindex=\"0\"\n          class=\"icon-container\"\n        >\n          <usa-icon [icon]=\"'x'\" size=\"lg\" class=\"font-sans-xs\"></usa-icon>\n        </span>\n        <span *ngIf=\"!configuration.isTagModeEnabled\" class=\"margin-left-1 margin-top-05 icon-container\">\n          <usa-icon\n            role=\"button\"\n            aria-label=\"Display Options\"\n            tabindex=\"0\"\n            *ngIf=\"!showResults && !disabled\"\n            (click)=\"openOptions()\"\n            class=\"font-sans-xs\"\n            (keyup.enter)=\"openOptions()\"\n            [icon]=\"'caret-down-fill'\"\n            size=\"1x\"\n          >\n          </usa-icon>\n          <usa-icon\n            role=\"button\"\n            aria-label=\"Hide Options\"\n            tabindex=\"0\"\n            *ngIf=\"showResults && !disabled\"\n            (click)=\"checkForFocus($event)\"\n            class=\"font-sans-xs\"\n            (keyup.enter)=\"checkForFocus($event)\"\n            [icon]=\"'caret-up-fill'\"\n            size=\"1x\"\n          >\n          </usa-icon>\n        </span>\n      </span>\n    </ng-container>\n  </div>\n</div>\n", styles: [".grayToLightBackgroundGradiate{background:linear-gradient(to right,#c4c4c4,#f7f7f7);background-size:400% 400%;height:10px;padding:2.5px 0;text-align:center;color:#888;font-size:15px;font-weight:700;margin:7px 10px 0;-webkit-animation:gradiantShiftGray 5s ease infinite;-moz-animation:gradiantShiftGray 5s ease infinite;animation:gradiantShiftGray 5s ease infinite}@-webkit-keyframes gradiantShiftGray{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@-moz-keyframes gradiantShiftGray{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@keyframes gradiantShiftGray{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}.lightToGrayBackgroundGradiate{background:linear-gradient(to right,#f7f7f7,#c4c4c4);background-size:400% 400%;height:15px;padding:2.5px 0;text-align:center;color:#888;font-size:15px;font-weight:700;margin:10px 10px 0;-webkit-animation:gradiantShift 5s ease infinite;-moz-animation:gradiantShift 5s ease infinite;animation:gradiantShift 5s ease infinite}@-webkit-keyframes gradiantShift{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@-moz-keyframes gradiantShift{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}@keyframes gradiantShift{0%{background-position:0% 50%}50%{background-position:100% 50%}to{background-position:0% 50%}}.autoLoading{height:60px}.hide-cursor{caret-color:transparent}input::-ms-clear{display:none}span.icon-container{display:inline-block}\n"] }]
    }], () => [{ type: i0.ChangeDetectorRef }], { resultsListElement: [{
            type: ViewChild,
            args: ['resultsList']
        }], input: [{
            type: ViewChild,
            args: ['input']
        }], itemTemplate: [{
            type: Input
        }], essentialModelFields: [{
            type: Input
        }], configuration: [{
            type: Input
        }], service: [{
            type: Input
        }], inputReadOnly: [{
            type: Input
        }], disabled: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SDSAutocompleteSearchComponent, { className: "SDSAutocompleteSearchComponent" }); })();

const _c0$h = ["autocompleteSearch"];
function SDSAutocompleteComponent_sds_selected_result_2_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "sds-selected-result", 3);
    i0.ɵɵlistener("ngModelChange", function SDSAutocompleteComponent_sds_selected_result_2_Template_sds_selected_result_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r2); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.updateItems($event)); });
    i0.ɵɵtwoWayListener("ngModelChange", function SDSAutocompleteComponent_sds_selected_result_2_Template_sds_selected_result_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r2); const ctx_r2 = i0.ɵɵnextContext(); i0.ɵɵtwoWayBindingSet(ctx_r2.model, $event) || (ctx_r2.model = $event); return i0.ɵɵresetView($event); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("disabled", ctx_r2.disabled);
    i0.ɵɵtwoWayProperty("ngModel", ctx_r2.model);
    i0.ɵɵproperty("configuration", ctx_r2.configuration)("itemTemplate", ctx_r2.selectedItemTemplate);
} }
const Autocomplete_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => SDSAutocompleteComponent),
    multi: true,
};
class SDSAutocompleteComponent {
    constructor(cd) {
        this.cd = cd;
        /**
         * The data model that has the selected item
         */
        this.model = new SDSSelectedItemModel();
        /**
         * Model contain only the primary key, primary value, and secondary value.
         */
        this.essentialModelFields = false;
        this._subscriptions = new Subscription();
        /**
         * Stored Event for ControlValueAccessor
         */
        this.onTouched = () => { };
        /**
         * Stored Event for ControlValueAccessor
         */
        this.onChange = (_) => { };
    }
    ngOnInit() {
        if (!this.configuration.registerChanges$) {
            return;
        }
        const changesSubscription = this.configuration.registerChanges$.subscribe(() => {
            this.cd.detectChanges();
        });
        this._subscriptions.add(changesSubscription);
    }
    ngOnDestroy() {
        this._subscriptions.unsubscribe();
    }
    // ControlValueAccessor (and Formly) is trying to update the value of the FormControl (our custom component) programatically
    // If there is a value we will just overwrite items
    // If there is no value we reset the items array to be empty
    writeValue(value) {
        if (value instanceof SDSSelectedItemModel && value.items && value.items.length) {
            this.model.items = [...value.items];
            this.cd.markForCheck();
        }
        else if (value && value.length) {
            this.model.items = value;
            this.cd.markForCheck();
        }
        else {
            if (!this.model || !(this.model instanceof SDSSelectedItemModel)) {
                this.model = new SDSSelectedItemModel();
            }
            this.model.items = value && value.items ? value.items : [];
            this.cd.markForCheck();
        }
        this.autocompleteSearch.writeValue(this.model);
    }
    // Method that is fired when the child component event notifies us that the items array has been modified within the child component
    updateItems($event) {
        this.updateModel();
    }
    // Helper method that gets a new instance of the model and notifies ControlValueAccessor that we have a new model for this FormControl (our custom component)
    updateModel() {
        const model = this.getModel();
        this.onChange(model);
    }
    // Helper method to return a new instance of an array that contains our items
    getModel() {
        return [...this.model.items];
    }
    // ControlValueAccessor hook that lets us call this._onChange(var) to let the form know our variable has changed (in this case model)
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // ControlValueAccessor hook (not used)
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.cd.detectChanges();
    }
    isSingleMode() {
        if (this.configuration) {
            return this.configuration.selectionMode === SelectionMode.SINGLE;
        }
        else {
            return false;
        }
    }
    addItem(item) {
        this.autocompleteSearch.selectItem(item);
    }
    addItems(list) {
        list.forEach((item) => {
            this.addItem(item);
        });
    }
    /** @nocollapse */ static { this.ɵfac = function SDSAutocompleteComponent_Factory(t) { return new (t || SDSAutocompleteComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SDSAutocompleteComponent, selectors: [["sds-autocomplete"]], viewQuery: function SDSAutocompleteComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$h, 7);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.autocompleteSearch = _t.first);
        } }, hostBindings: function SDSAutocompleteComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("focusout", function SDSAutocompleteComponent_focusout_HostBindingHandler() { return ctx.onTouched(); });
        } }, inputs: { suggestionTemplate: "suggestionTemplate", selectedItemTemplate: "selectedItemTemplate", configuration: "configuration", essentialModelFields: "essentialModelFields", service: "service" }, features: [i0.ɵɵProvidersFeature([Autocomplete_VALUE_ACCESSOR])], decls: 3, vars: 7, consts: [["autocompleteSearch", ""], [3, "ngModelChange", "disabled", "essentialModelFields", "service", "ngModel", "configuration", "itemTemplate"], [3, "disabled", "ngModel", "configuration", "itemTemplate", "ngModelChange", 4, "ngIf"], [3, "ngModelChange", "disabled", "ngModel", "configuration", "itemTemplate"]], template: function SDSAutocompleteComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelementStart(0, "sds-search-autocomplete", 1, 0);
            i0.ɵɵlistener("ngModelChange", function SDSAutocompleteComponent_Template_sds_search_autocomplete_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateItems($event)); });
            i0.ɵɵtwoWayListener("ngModelChange", function SDSAutocompleteComponent_Template_sds_search_autocomplete_ngModelChange_0_listener($event) { i0.ɵɵrestoreView(_r1); i0.ɵɵtwoWayBindingSet(ctx.model, $event) || (ctx.model = $event); return i0.ɵɵresetView($event); });
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, SDSAutocompleteComponent_sds_selected_result_2_Template, 1, 4, "sds-selected-result", 2);
        } if (rf & 2) {
            i0.ɵɵproperty("disabled", ctx.disabled)("essentialModelFields", ctx.essentialModelFields)("service", ctx.service);
            i0.ɵɵtwoWayProperty("ngModel", ctx.model);
            i0.ɵɵproperty("configuration", ctx.configuration)("itemTemplate", ctx.suggestionTemplate);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", !ctx.isSingleMode() && !(ctx.configuration == null ? null : ctx.configuration.hideChips));
        } }, dependencies: [i1$2.NgIf, i2$2.NgControlStatus, i2$2.NgModel, SDSSelectedResultComponent, SDSAutocompleteSearchComponent], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SDSAutocompleteComponent, [{
        type: Component,
        args: [{ selector: 'sds-autocomplete', providers: [Autocomplete_VALUE_ACCESSOR], changeDetection: ChangeDetectionStrategy.OnPush, template: "<sds-search-autocomplete\n  [disabled]=\"disabled\"\n  [essentialModelFields]=\"essentialModelFields\"\n  (ngModelChange)=\"updateItems($event)\"\n  #autocompleteSearch\n  [service]=\"service\"\n  [(ngModel)]=\"model\"\n  [configuration]=\"configuration\"\n  [itemTemplate]=\"suggestionTemplate\"\n>\n</sds-search-autocomplete>\n<sds-selected-result\n  (ngModelChange)=\"updateItems($event)\"\n  [disabled]=\"disabled\"\n  *ngIf=\"!isSingleMode() && !configuration?.hideChips\"\n  [(ngModel)]=\"model\"\n  [configuration]=\"configuration\"\n  [itemTemplate]=\"selectedItemTemplate\"\n></sds-selected-result>\n" }]
    }], () => [{ type: i0.ChangeDetectorRef }], { suggestionTemplate: [{
            type: Input
        }], selectedItemTemplate: [{
            type: Input
        }], configuration: [{
            type: Input
        }], essentialModelFields: [{
            type: Input
        }], service: [{
            type: Input
        }], autocompleteSearch: [{
            type: ViewChild,
            args: ['autocompleteSearch', { static: true }]
        }], onTouched: [{
            type: HostListener,
            args: ['focusout']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SDSAutocompleteComponent, { className: "SDSAutocompleteComponent" }); })();

class SdsSelectedResultsModule {
    /** @nocollapse */ static { this.ɵfac = function SdsSelectedResultsModule_Factory(t) { return new (t || SdsSelectedResultsModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsSelectedResultsModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, FormsModule, RouterModule, IconModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSelectedResultsModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, FormsModule, RouterModule, IconModule],
                declarations: [SDSSelectedResultComponent],
                exports: [SDSSelectedResultComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsSelectedResultsModule, { declarations: [SDSSelectedResultComponent], imports: [CommonModule, FormsModule, RouterModule, IconModule], exports: [SDSSelectedResultComponent] }); })();

class SDSClickOutsideModule {
    /** @nocollapse */ static { this.ɵfac = function SDSClickOutsideModule_Factory(t) { return new (t || SDSClickOutsideModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SDSClickOutsideModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, FormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SDSClickOutsideModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, FormsModule],
                declarations: [SDSClickOutsideDirective],
                exports: [SDSClickOutsideDirective],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SDSClickOutsideModule, { declarations: [SDSClickOutsideDirective], imports: [CommonModule, FormsModule], exports: [SDSClickOutsideDirective] }); })();

class SdsTabOutsideModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTabOutsideModule_Factory(t) { return new (t || SdsTabOutsideModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTabOutsideModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, FormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTabOutsideModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, FormsModule],
                declarations: [SDSTabOutsideDirective],
                exports: [SDSTabOutsideDirective],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTabOutsideModule, { declarations: [SDSTabOutsideDirective], imports: [CommonModule, FormsModule], exports: [SDSTabOutsideDirective] }); })();

class SdsAutocompleteSearchModule {
    /** @nocollapse */ static { this.ɵfac = function SdsAutocompleteSearchModule_Factory(t) { return new (t || SdsAutocompleteSearchModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsAutocompleteSearchModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule,
            FormsModule,
            SDSClickOutsideModule,
            SdsTabOutsideModule,
            OverlayModule,
            IconModule,
            NgxBootstrapIconsModule.pick({ x, caretDownFill, caretUpFill, check2Square })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAutocompleteSearchModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    SDSClickOutsideModule,
                    SdsTabOutsideModule,
                    OverlayModule,
                    IconModule,
                    NgxBootstrapIconsModule.pick({ x, caretDownFill, caretUpFill, check2Square }),
                ],
                declarations: [SDSAutocompleteSearchComponent],
                exports: [SDSAutocompleteSearchComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsAutocompleteSearchModule, { declarations: [SDSAutocompleteSearchComponent], imports: [CommonModule,
        FormsModule,
        SDSClickOutsideModule,
        SdsTabOutsideModule,
        OverlayModule,
        IconModule, i1$4.NgxBootstrapIconsModule], exports: [SDSAutocompleteSearchComponent] }); })();

class SdsAutocompleteModule {
    /** @nocollapse */ static { this.ɵfac = function SdsAutocompleteModule_Factory(t) { return new (t || SdsAutocompleteModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsAutocompleteModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, FormsModule, RouterModule, SdsSelectedResultsModule, SdsAutocompleteSearchModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsAutocompleteModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, FormsModule, RouterModule, SdsSelectedResultsModule, SdsAutocompleteSearchModule],
                declarations: [SDSAutocompleteComponent],
                exports: [SDSAutocompleteComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsAutocompleteModule, { declarations: [SDSAutocompleteComponent], imports: [CommonModule, FormsModule, RouterModule, SdsSelectedResultsModule, SdsAutocompleteSearchModule], exports: [SDSAutocompleteComponent] }); })();

class SDSAutocompletelConfiguration {
    /**
     * sets the default debounce time to 250 milliseconds
     */
    constructor() {
        /**
         * Mode of the model either allows a single item or multiple items
         */
        this.selectionMode = SelectionMode.SINGLE;
        /**
         * Allows option to allow user text not in the standard results
         */
        this.isFreeTextEnabled = false;
        /**
         * Text appeneded ad the end of free text
         */
        this.freeTextSubtext = 'search';
        /**
         * Focus into autocomplete search
         */
        this.focusInSearch = true;
        /**
         * To enable the tag mode
         */
        this.isTagModeEnabled = false;
        /**
         * To make input readonly
         */
        this.inputReadOnly = false;
        /**
         * Name of the children filed
         */
        this.groupByChild = 'children';
        /**
         * To enable the Grouping mode
         */
        this.isGroupingEnabled = false;
        /**
         * To enable the Group item selectable
         */
        this.isSelectableGroup = true;
        /**
         * Toggle whether or not to display chips. This can be useful if some custom UI is used
         * for rendering autocomplete values in multi-select mode
         * @default false
         */
        this.hideChips = false;
        this.hideCloseIcon = false;
        this.useCheckBoxes = false;
        this.debounceTime = 250;
        this.minimumCharacterCountSearch = 0;
    }
}

class SDSHiercarchicalServiceSearchItem {
}

class SDSSelectedResultConfiguration {
    constructor() {
        /**
         * Mode of the model either allows a single item or multiple items
         */
        this.selectionMode = SelectionMode.SINGLE;
    }
}

var NavigationMode;
(function (NavigationMode) {
    NavigationMode[NavigationMode["INTERNAL"] = 0] = "INTERNAL";
    NavigationMode[NavigationMode["EXTERNAL"] = 1] = "EXTERNAL";
    NavigationMode[NavigationMode["EVENT"] = 2] = "EVENT";
    NavigationMode[NavigationMode["LABEL"] = 3] = "LABEL";
})(NavigationMode || (NavigationMode = {}));

class NavigationHelper {
    /**
     * checks if link is internal
     * @param link
     */
    isLinkInternal(link) {
        return link.mode === NavigationMode.INTERNAL;
    }
    /**
     * checks if link is external
     * @param link
     */
    isLinkExternal(link) {
        return link.mode === NavigationMode.EXTERNAL;
    }
}

const _c0$g = ["sideNavRouteLinkTemplate"];
const _c1$8 = ["sideNavLabelLinkTemplate"];
const _c2$3 = ["sideNavHREFLinkTemplate"];
const _c3$2 = ["sideNavEVENTLinkTemplate"];
const _c4$1 = a0 => ({ $implicit: a0 });
const _c5 = a0 => [a0];
function SdsSideNavigationComponent_li_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 7);
    i0.ɵɵelementContainer(1, 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const link_r1 = ctx.$implicit;
    i0.ɵɵnextContext();
    const sideNavLinkTemplate_r2 = i0.ɵɵreference(4);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", sideNavLinkTemplate_r2)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c4$1, link_r1));
} }
function SdsSideNavigationComponent_ng_template_3_ul_1_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 7);
    i0.ɵɵelementContainer(1, 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const link_r3 = ctx.$implicit;
    i0.ɵɵnextContext(3);
    const sideNavLinkTemplate_r2 = i0.ɵɵreference(4);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", sideNavLinkTemplate_r2)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c4$1, link_r3));
} }
function SdsSideNavigationComponent_ng_template_3_ul_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "ul", 10);
    i0.ɵɵtemplate(1, SdsSideNavigationComponent_ng_template_3_ul_1_li_1_Template, 2, 4, "li", 6);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const link_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", link_r4.children);
} }
function SdsSideNavigationComponent_ng_template_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 8);
    i0.ɵɵtemplate(1, SdsSideNavigationComponent_ng_template_3_ul_1_Template, 2, 1, "ul", 9);
} if (rf & 2) {
    const link_r4 = ctx.$implicit;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r4.getItemTemplate(link_r4))("ngTemplateOutletContext", i0.ɵɵpureFunction1(3, _c4$1, link_r4));
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", link_r4.children);
} }
function SdsSideNavigationComponent_ng_template_5_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "a", 11);
    i0.ɵɵlistener("click", function SdsSideNavigationComponent_ng_template_5_Template_a_click_0_listener() { const link_r7 = i0.ɵɵrestoreView(_r6).$implicit; const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.linkClickEvent(link_r7)); });
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const link_r7 = ctx.$implicit;
    i0.ɵɵproperty("routerLink", i0.ɵɵpureFunction1(5, _c5, link_r7.route))("queryParams", link_r7.queryParams)("queryParamsHandling", link_r7.queryParamsHandling);
    i0.ɵɵattribute("class", link_r7.selected ? " usa-current" : "");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(link_r7.text);
} }
function SdsSideNavigationComponent_ng_template_7_Template(rf, ctx) { if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "a", 12);
    i0.ɵɵlistener("click", function SdsSideNavigationComponent_ng_template_7_Template_a_click_0_listener() { const link_r9 = i0.ɵɵrestoreView(_r8).$implicit; const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.linkClickEvent(link_r9)); });
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const link_r9 = ctx.$implicit;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵattribute("class", link_r9.selected ? " usa-current" : "")("href", ctx_r4.urlBuilder(link_r9), i0.ɵɵsanitizeUrl);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(link_r9.text);
} }
function SdsSideNavigationComponent_ng_template_9_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span")(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const link_r10 = ctx.$implicit;
    i0.ɵɵattribute("class", link_r10.selected ? " usa-current" : "");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(link_r10.text);
} }
function SdsSideNavigationComponent_ng_template_11_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "a", 13);
    i0.ɵɵlistener("click", function SdsSideNavigationComponent_ng_template_11_Template_a_click_0_listener() { const link_r12 = i0.ɵɵrestoreView(_r11).$implicit; const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.linkClickEvent(link_r12)); });
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const link_r12 = ctx.$implicit;
    i0.ɵɵattribute("class", link_r12.selected ? " usa-current" : "");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(link_r12.text);
} }
class SdsSideNavigationComponent {
    constructor() {
        /**
         * Navigation helper
         */
        this.navigationHelper = new NavigationHelper();
        /**
         * event for event based
         */
        this.linkEvent = new EventEmitter();
    }
    /**
     * Takes the navigation item and returns the template to be used
     * @param item navigation item
     */
    getItemTemplate(item) {
        let template = null;
        switch (item.mode) {
            case NavigationMode.EVENT:
                template = this.sideNavEVENTLinkTemplate;
                break;
            case NavigationMode.EXTERNAL:
                template = this.sideNavHREFLinkTemplate;
                break;
            case NavigationMode.INTERNAL:
                template = this.sideNavRouteLinkTemplate;
                break;
            case NavigationMode.LABEL:
                template = this.sideNavLabelLinkTemplate;
                break;
            default:
                template = null;
                break;
        }
        return template;
    }
    /**
     * Selects new item and parents and deselects previous
     * @param id
     */
    select(id) {
        this.deselect();
        for (let i = 0; i < this.model.navigationLinks.length; i++) {
            let item = this.model.navigationLinks[i];
            this.selectItem(id, item, null);
        }
    }
    /**
     * Selects item if matches passed in id and will select parent
     * @param id
     * @param item
     * @param parent
     */
    selectItem(id, item, parent) {
        if (item.id === id) {
            item.selected = true;
            if (parent) {
                parent.selected = true;
            }
        }
        else {
            if (item.children) {
                for (let i = 0; i < item.children.length; i++) {
                    let childItem = item.children[i];
                    this.selectItem(id, childItem, item);
                }
                if (item.selected) {
                    if (parent) {
                        parent.selected = true;
                    }
                }
            }
        }
    }
    /**
     * Deselects all the items in the side navigation model
     */
    deselect() {
        if (this.model) {
            if (this.model.navigationLinks) {
                for (let i = 0; i < this.model.navigationLinks.length; i++) {
                    this.deselectItem(this.model.navigationLinks[i]);
                }
            }
        }
    }
    /**
     * checks if item is selected and if selcted will check children and will unselect
     */
    deselectItem(item) {
        if (item.selected) {
            item.selected = false;
            if (item.children) {
                for (let i = 0; i < item.children.length; i++) {
                    this.deselectItem(item.children[i]);
                }
            }
        }
    }
    /**
     * Link clicked and emits the link data into an event
     * @param link
     */
    linkClickEvent(link) {
        this.linkEvent.emit(link);
        return false;
    }
    /**
     * creates url from provided route and query params
     * @param item
     */
    urlBuilder(item) {
        let url = item.route;
        let queryParams = this.queryStringBuilder(item);
        if (queryParams) {
            if (url.indexOf('?') === -1) {
                url += '?' + queryParams;
            }
            else if (url.indexOf('?') === url.length - 1) {
                url += queryParams;
            }
            else {
                url += '&' + queryParams;
            }
        }
        return url;
    }
    /**
     * creates query string
     * @param item
     */
    queryStringBuilder(item) {
        const ret = [];
        for (let d in item.queryParams) {
            ret.push(encodeURIComponent(d) + '=' + encodeURIComponent(item.queryParams[d]));
        }
        return ret.join('&');
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSideNavigationComponent_Factory(t) { return new (t || SdsSideNavigationComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSideNavigationComponent, selectors: [["sds-side-navigation"]], viewQuery: function SdsSideNavigationComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$g, 5);
            i0.ɵɵviewQuery(_c1$8, 5);
            i0.ɵɵviewQuery(_c2$3, 5);
            i0.ɵɵviewQuery(_c3$2, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.sideNavRouteLinkTemplate = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.sideNavLabelLinkTemplate = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.sideNavHREFLinkTemplate = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.sideNavEVENTLinkTemplate = _t.first);
        } }, inputs: { ariaLabel: "ariaLabel", model: "model" }, outputs: { linkEvent: "linkEvent" }, decls: 13, vars: 2, consts: [["sideNavLinkTemplate", ""], ["sideNavRouteLinkTemplate", ""], ["sideNavHREFLinkTemplate", ""], ["sideNavLabelLinkTemplate", ""], ["sideNavEVENTLinkTemplate", ""], [1, "usa-sidenav"], ["class", "sidenav__item", 4, "ngFor", "ngForOf"], [1, "sidenav__item"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], ["class", "usa-sidenav__sublist", 4, "ngIf"], [1, "usa-sidenav__sublist"], [3, "click", "routerLink", "queryParams", "queryParamsHandling"], [3, "click"], ["href", "javascript:void(0)", 3, "click"]], template: function SdsSideNavigationComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "nav")(1, "ul", 5);
            i0.ɵɵtemplate(2, SdsSideNavigationComponent_li_2_Template, 2, 4, "li", 6);
            i0.ɵɵelementEnd()();
            i0.ɵɵtemplate(3, SdsSideNavigationComponent_ng_template_3_Template, 2, 5, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(5, SdsSideNavigationComponent_ng_template_5_Template, 3, 7, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor)(7, SdsSideNavigationComponent_ng_template_7_Template, 3, 3, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor)(9, SdsSideNavigationComponent_ng_template_9_Template, 3, 2, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor)(11, SdsSideNavigationComponent_ng_template_11_Template, 3, 2, "ng-template", null, 4, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵattribute("aria-label", ctx.ariaLabel);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngForOf", ctx.model.navigationLinks);
        } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i1$5.RouterLink] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSideNavigationComponent, [{
        type: Component,
        args: [{ selector: 'sds-side-navigation', template: "<nav [attr.aria-label]=\"ariaLabel\">\n  <ul class=\"usa-sidenav\">\n    <li *ngFor=\"let link of model.navigationLinks\" class=\"sidenav__item\">\n      <ng-container [ngTemplateOutlet]=\"sideNavLinkTemplate\" [ngTemplateOutletContext]=\"{ $implicit: link }\">\n      </ng-container>\n    </li>\n  </ul>\n</nav>\n\n<ng-template #sideNavLinkTemplate let-link>\n  <ng-container [ngTemplateOutlet]=\"getItemTemplate(link)\" [ngTemplateOutletContext]=\"{ $implicit: link }\">\n  </ng-container>\n  <ul *ngIf=\"link.children\" class=\"usa-sidenav__sublist\">\n    <li *ngFor=\"let link of link.children\" class=\"sidenav__item\">\n      <ng-container [ngTemplateOutlet]=\"sideNavLinkTemplate\" [ngTemplateOutletContext]=\"{ $implicit: link }\">\n      </ng-container>\n    </li>\n  </ul>\n</ng-template>\n\n<ng-template #sideNavRouteLinkTemplate let-link>\n  <a\n    [attr.class]=\"link.selected ? ' usa-current' : ''\"\n    [routerLink]=\"[link.route]\"\n    (click)=\"linkClickEvent(link)\"\n    [queryParams]=\"link.queryParams\"\n    [queryParamsHandling]=\"link.queryParamsHandling\"\n  >\n    <span>{{ link.text }}</span>\n  </a>\n</ng-template>\n\n<ng-template #sideNavHREFLinkTemplate let-link>\n  <a [attr.class]=\"link.selected ? ' usa-current' : ''\" [attr.href]=\"urlBuilder(link)\" (click)=\"linkClickEvent(link)\"\n    ><span>{{ link.text }}</span></a\n  >\n</ng-template>\n\n<ng-template #sideNavLabelLinkTemplate let-link>\n  <span [attr.class]=\"link.selected ? ' usa-current' : ''\"\n    ><span>{{ link.text }}</span></span\n  >\n</ng-template>\n\n<ng-template #sideNavEVENTLinkTemplate let-link>\n  <a [attr.class]=\"link.selected ? ' usa-current' : ''\" href=\"javascript:void(0)\" (click)=\"linkClickEvent(link)\"\n    ><span>{{ link.text }}</span></a\n  >\n</ng-template>\n" }]
    }], null, { sideNavRouteLinkTemplate: [{
            type: ViewChild,
            args: ['sideNavRouteLinkTemplate', { static: false }]
        }], sideNavLabelLinkTemplate: [{
            type: ViewChild,
            args: ['sideNavLabelLinkTemplate', { static: false }]
        }], sideNavHREFLinkTemplate: [{
            type: ViewChild,
            args: ['sideNavHREFLinkTemplate', { static: false }]
        }], sideNavEVENTLinkTemplate: [{
            type: ViewChild,
            args: ['sideNavEVENTLinkTemplate', { static: false }]
        }], ariaLabel: [{
            type: Input
        }], model: [{
            type: Input
        }], linkEvent: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSideNavigationComponent, { className: "SdsSideNavigationComponent" }); })();

class SdsSideNavigationModule {
    /** @nocollapse */ static { this.ɵfac = function SdsSideNavigationModule_Factory(t) { return new (t || SdsSideNavigationModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsSideNavigationModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, FormsModule, RouterModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSideNavigationModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, FormsModule, RouterModule],
                declarations: [SdsSideNavigationComponent],
                exports: [SdsSideNavigationComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsSideNavigationModule, { declarations: [SdsSideNavigationComponent], imports: [CommonModule, FormsModule, RouterModule], exports: [SdsSideNavigationComponent] }); })();

/**
 * Configuration for opening a modal dialog with the SdsDialog service.
 */
class SdsDialogConfig {
    constructor() {
        /** The ARIA role of the dialog element. */
        this.role = 'dialog';
        /** Custom class for the overlay pane. */
        this.panelClass = '';
        /** Whether the dialog has a backdrop. */
        this.hasBackdrop = true;
        /** Custom class for the backdrop, */
        this.backdropClass = '';
        /** Whether the user can use escape or clicking on the backdrop to close the modal. */
        this.disableClose = false;
        /** Width of the dialog. */
        this.width = '';
        /** Height of the dialog. */
        this.height = '';
        /** Max-width of the dialog. If a number is provided, pixel units are assumed. Defaults to 80vw */
        this.maxWidth = '80vw';
        /** Data being injected into the child component. */
        this.data = null;
        /** ID of the element that describes the dialog. */
        this.ariaDescribedBy = null;
        /** Aria label to assign to the dialog element */
        this.ariaLabel = null;
        /** Whether the dialog should focus the first focusable element on open. */
        this.autoFocus = true;
        /**
         * Whether the dialog should restore focus to the
         * previously-focused element, after it's closed.
         */
        this.restoreFocus = true;
        /**
         * Whether the dialog should close when the user goes backwards/forwards in history.
         * Note that this usually doesn't include clicking on links (unless the user is using
         * the `HashLocationStrategy`).
         */
        this.closeOnNavigation = true;
        /**
         * Whether to display the close button in dialog header
         */
        this.displayCloseBtn = true;
        /**
         * Whether dialog is a slide-out. Changes enter and exit animation, and adds
         * class `dialog-slide-out` to container
         */
        this.slideOut = false;
    }
}

const animationBody = [
    // Note: The `enter` animation transitions to `transform: none`, because for some reason
    // specifying the transform explicitly, causes IE both to blur the dialog content and
    // decimate the animation performance. Leaving it as `none` solves both issues.
    transition('* => enter', [
        style({ opacity: 0, transform: 'scale(0.7)' }),
        animate('150ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 })),
    ]),
    transition('* => void, * => exit', [
        style({ transform: 'none' }),
        animate('75ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0 })),
    ]),
    transition('* => slideEnter', [
        useAnimation(animation([
            style({ right: '-{{ width }}' }),
            animate('{{ time }} cubic-bezier(0, 0, 0.2, 1)', style({ right: '0rem' })),
        ])),
    ]),
    transition('* => slideExit', [
        useAnimation(animation([
            style({ right: '0rem' }),
            animate('{{ time }} cubic-bezier(0, 0, 0.2, 1)', style({ right: '-{{ width }}' })),
        ])),
    ]),
];
/**
 * Animations used by SdsDialog.
 * @docs-private
 */
const sdsDialogAnimations = {
    /** Animation that is applied on the dialog container by defalt. */
    dialogContainer: trigger('dialogContainer', animationBody),
};

const _c0$f = (a0, a1) => ({ width: a0, time: a1 });
const _c1$7 = () => ({ width: "15rem", time: "1s" });
const _c2$2 = (a0, a1) => ({ value: a0, params: a1 });
function SdsDialogContainerComponent_button_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "button", 3);
    i0.ɵɵelement(1, "usa-icon", 4);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "x");
} }
function SdsDialogContainerComponent_div_1_usa_icon_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "usa-icon", 8);
} if (rf & 2) {
    i0.ɵɵproperty("icon", "slash-circle");
} }
function SdsDialogContainerComponent_div_1_usa_icon_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "usa-icon", 8);
} if (rf & 2) {
    i0.ɵɵproperty("icon", "exclamation-triangle");
} }
function SdsDialogContainerComponent_div_1_usa_icon_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "usa-icon", 8);
} if (rf & 2) {
    i0.ɵɵproperty("icon", "info-circle");
} }
function SdsDialogContainerComponent_div_1_usa_icon_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "usa-icon", 8);
} if (rf & 2) {
    i0.ɵɵproperty("icon", "check-circle");
} }
function SdsDialogContainerComponent_div_1_div_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1, " Alert ");
    i0.ɵɵelementStart(2, "em");
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵtext(4, " is not valid ");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(ctx_r0._config.alert);
} }
function SdsDialogContainerComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtemplate(1, SdsDialogContainerComponent_div_1_usa_icon_1_Template, 1, 1, "usa-icon", 6)(2, SdsDialogContainerComponent_div_1_usa_icon_2_Template, 1, 1, "usa-icon", 6)(3, SdsDialogContainerComponent_div_1_usa_icon_3_Template, 1, 1, "usa-icon", 6)(4, SdsDialogContainerComponent_div_1_usa_icon_4_Template, 1, 1, "usa-icon", 6)(5, SdsDialogContainerComponent_div_1_div_5_Template, 5, 1, "div", 7);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngSwitch", ctx_r0._config.alert);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "error");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "warning");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "info");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngSwitchCase", "success");
} }
function SdsDialogContainerComponent_ng_template_2_Template(rf, ctx) { }
/**
 * Throws an exception for the case when a ComponentPortal is
 * attached to a DomPortalOutlet without an origin.
 * @docs-private
 */
function throwSdsDialogContentAlreadyAttachedError() {
    throw Error('Attempting to attach dialog content after content is already attached');
}
/**
 * Internal component that wraps user-provided dialog content.
 * @docs-private
 */
class SdsDialogContainerComponent extends BasePortalOutlet {
    constructor(_elementRef, _focusTrapFactory, _changeDetectorRef, _document, 
    /** The dialog configuration. */
    _config) {
        super();
        this._elementRef = _elementRef;
        this._focusTrapFactory = _focusTrapFactory;
        this._changeDetectorRef = _changeDetectorRef;
        this._document = _document;
        this._config = _config;
        /** Element that was focused before the dialog was opened. Save this to restore upon close. */
        this._elementFocusedBeforeDialogWasOpened = null;
        /** State of the dialog animation. */
        this._state = this._config.slideOut ? 'slideEnter' : 'enter';
        /** Emits when an animation state changes. */
        this._animationStateChanged = new EventEmitter();
        /** ID of the element that should be considered as the dialog's label. */
        this._ariaLabelledBy = null;
    }
    /**
     * Attach a ComponentPortal as content to this dialog container.
     * @param portal Portal to be attached as the dialog content.
     */
    attachComponentPortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throwSdsDialogContentAlreadyAttachedError();
        }
        this._savePreviouslyFocusedElement();
        return this._portalOutlet.attachComponentPortal(portal);
    }
    /**
     * Attach a TemplatePortal as content to this dialog container.
     * @param portal Portal to be attached as the dialog content.
     */
    attachTemplatePortal(portal) {
        if (this._portalOutlet.hasAttached()) {
            throwSdsDialogContentAlreadyAttachedError();
        }
        this._savePreviouslyFocusedElement();
        return this._portalOutlet.attachTemplatePortal(portal);
    }
    /** Moves the focus inside the focus trap. */
    _trapFocus() {
        if (!this._focusTrap) {
            this._focusTrap = this._focusTrapFactory.create(this._elementRef.nativeElement);
        }
        // If were to attempt to focus immediately, then the content of the dialog would not yet be
        // ready in instances where change detection has to run first. To deal with this, we simply
        // wait for the microtask queue to be empty.
        if (this._config.autoFocus) {
            this._focusTrap.focusInitialElementWhenReady();
        }
    }
    /** Restores focus to the element that was focused before the dialog opened. */
    _restoreFocus() {
        const toFocus = this._elementFocusedBeforeDialogWasOpened;
        // We need the extra check, because IE can set the `activeElement` to null in some cases.
        if (this._config.restoreFocus && toFocus && typeof toFocus.focus === 'function') {
            toFocus.focus();
        }
        if (this._focusTrap) {
            this._focusTrap.destroy();
        }
    }
    /** Saves a reference to the element that was focused before the dialog was opened. */
    _savePreviouslyFocusedElement() {
        if (this._document) {
            this._elementFocusedBeforeDialogWasOpened = this._document.activeElement;
            // Note that there is no focus method when rendering on the server.
            if (this._elementRef.nativeElement.focus) {
                // Move focus onto the dialog immediately in order to prevent the user from accidentally
                // opening multiple dialogs at the same time. Needs to be async, because the element
                // may not be focusable immediately.
                Promise.resolve().then(() => this._elementRef.nativeElement.focus());
            }
        }
    }
    /** Callback, invoked whenever an animation on the host completes. */
    _onAnimationDone(event) {
        if (event.toState === 'enter' || event.toState === 'slideEnter') {
            this._trapFocus();
        }
        else if (event.toState === 'exit' || event.toState === 'slideExit') {
            this._restoreFocus();
        }
        this._animationStateChanged.emit(event);
    }
    /** Callback, invoked when an animation on the host starts. */
    _onAnimationStart(event) {
        this._animationStateChanged.emit(event);
    }
    /** Starts the dialog exit animation. */
    _startExitAnimation() {
        this._state = this._config.slideOut ? 'slideExit' : 'exit';
        // Mark the container for check so it can react if the
        // view container is using OnPush change detection.
        this._changeDetectorRef.markForCheck();
    }
    /** Checks if the value is a Slide Out panel configuration or a boolean. */
    /** A configuration indicates that a user wants a custom Slide Out Panel */
    isSlideOutConfig(val) {
        return val.width !== undefined || val.time !== undefined;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsDialogContainerComponent_Factory(t) { return new (t || SdsDialogContainerComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$1.FocusTrapFactory), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(DOCUMENT, 8), i0.ɵɵdirectiveInject(SdsDialogConfig)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsDialogContainerComponent, selectors: [["sds-dialog-container"]], viewQuery: function SdsDialogContainerComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(CdkPortalOutlet, 7);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._portalOutlet = _t.first);
        } }, hostAttrs: ["tabindex", "-1", "aria-modal", "true", 1, "sds-dialog__container"], hostVars: 27, hostBindings: function SdsDialogContainerComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵsyntheticHostListener("@dialogContainer.start", function SdsDialogContainerComponent_animation_dialogContainer_start_HostBindingHandler($event) { return ctx._onAnimationStart($event); })("@dialogContainer.done", function SdsDialogContainerComponent_animation_dialogContainer_done_HostBindingHandler($event) { return ctx._onAnimationDone($event); });
        } if (rf & 2) {
            i0.ɵɵsyntheticHostProperty("@dialogContainer", i0.ɵɵpureFunction2(24, _c2$2, ctx._state, ctx._config.slideOut && ctx.isSlideOutConfig(ctx._config.slideOut) ? i0.ɵɵpureFunction2(20, _c0$f, ctx._config.slideOut.width || "15rem", ctx._config.slideOut.time || "1s") : i0.ɵɵpureFunction0(23, _c1$7)));
            i0.ɵɵattribute("id", ctx._id)("role", ctx._config.role)("aria-labelledby", ctx._config.ariaLabel ? null : ctx._ariaLabelledBy)("aria-label", ctx._config.ariaLabel)("aria-describedby", ctx._config.ariaDescribedBy || null);
            i0.ɵɵstyleProp("width", ctx._config.slideOut && ctx.isSlideOutConfig(ctx._config.slideOut) ? ctx._config.slideOut.width : null);
            i0.ɵɵclassProp("sds-dialog--alert", ctx._config.alert)("sds-dialog--alert-error", ctx._config.alert === "error")("sds-dialog--alert-warning", ctx._config.alert === "warning")("sds-dialog--alert-info", ctx._config.alert === "info")("sds-dialog--alert-success", ctx._config.alert === "success")("dialog-slide-out", ctx._config.slideOut);
        } }, features: [i0.ɵɵInheritDefinitionFeature], decls: 3, vars: 2, consts: [["sds-dialog-close", "", "class", "close-btn", "aria-label", "Close Modal", 4, "ngIf"], ["class", "sds-dialog-icon", 3, "ngSwitch", 4, "ngIf"], ["cdkPortalOutlet", ""], ["sds-dialog-close", "", "aria-label", "Close Modal", 1, "close-btn"], [3, "icon"], [1, "sds-dialog-icon", 3, "ngSwitch"], ["size", "3x", 3, "icon", 4, "ngSwitchCase"], [4, "ngSwitchDefault"], ["size", "3x", 3, "icon"]], template: function SdsDialogContainerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SdsDialogContainerComponent_button_0_Template, 2, 1, "button", 0)(1, SdsDialogContainerComponent_div_1_Template, 6, 5, "div", 1)(2, SdsDialogContainerComponent_ng_template_2_Template, 0, 0, "ng-template", 2);
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx._config.displayCloseBtn);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx._config.alert);
        } }, encapsulation: 2, data: { animation: [sdsDialogAnimations.dialogContainer] } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogContainerComponent, [{
        type: Component,
        args: [{ selector: 'sds-dialog-container', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.Default, animations: [sdsDialogAnimations.dialogContainer], host: {
                    class: 'sds-dialog__container',
                    '[class.sds-dialog--alert]': '_config.alert',
                    '[class.sds-dialog--alert-error]': '_config.alert === "error"',
                    '[class.sds-dialog--alert-warning]': '_config.alert === "warning"',
                    '[class.sds-dialog--alert-info]': '_config.alert === "info"',
                    '[class.sds-dialog--alert-success]': '_config.alert === "success"',
                    '[class.dialog-slide-out]': '_config.slideOut',
                    tabindex: '-1',
                    'aria-modal': 'true',
                    '[attr.id]': '_id',
                    '[attr.role]': '_config.role',
                    '[attr.aria-labelledby]': '_config.ariaLabel ? null : _ariaLabelledBy',
                    '[attr.aria-label]': '_config.ariaLabel',
                    '[attr.aria-describedby]': '_config.ariaDescribedBy || null',
                    '[style.width]': '_config.slideOut && isSlideOutConfig(_config.slideOut) ? _config.slideOut.width : null',
                    '[@dialogContainer]': '{ value: _state, params: _config.slideOut && isSlideOutConfig(_config.slideOut) ? { width: _config.slideOut.width || "15rem", time: _config.slideOut.time || "1s" } : { width: "15rem", time: "1s" } }',
                    '(@dialogContainer.start)': '_onAnimationStart($event)',
                    '(@dialogContainer.done)': '_onAnimationDone($event)',
                }, template: "<button *ngIf=\"_config.displayCloseBtn\" sds-dialog-close class=\"close-btn\" aria-label=\"Close Modal\">\n  <usa-icon [icon]=\"'x'\"></usa-icon>\n</button>\n\n<!-- Alert Icons -->\n<div *ngIf=\"_config.alert\" [ngSwitch]=\"_config.alert\" class=\"sds-dialog-icon\">\n  <usa-icon *ngSwitchCase=\"'error'\" [icon]=\"'slash-circle'\" size=\"3x\"></usa-icon>\n  <usa-icon *ngSwitchCase=\"'warning'\" [icon]=\"'exclamation-triangle'\" size=\"3x\"></usa-icon>\n  <usa-icon *ngSwitchCase=\"'info'\" [icon]=\"'info-circle'\" size=\"3x\"></usa-icon>\n  <usa-icon *ngSwitchCase=\"'success'\" [icon]=\"'check-circle'\" size=\"3x\"></usa-icon>\n  <div *ngSwitchDefault>\n    Alert <em>{{ _config.alert }}</em> is not valid\n  </div>\n</div>\n\n<ng-template cdkPortalOutlet></ng-template>\n" }]
    }], () => [{ type: i0.ElementRef }, { type: i1$1.FocusTrapFactory }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: SdsDialogConfig }], { _portalOutlet: [{
            type: ViewChild,
            args: [CdkPortalOutlet, { static: true }]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsDialogContainerComponent, { className: "SdsDialogContainerComponent" }); })();

// Counter for unique dialog ids.
let uniqueId = 0;
/**
 * Reference to a dialog opened via the SdsDialog service.
 */
class SdsDialogRef {
    constructor(_overlayRef, _containerInstance, _location, id = `sds-dialog-${uniqueId++}`) {
        this._overlayRef = _overlayRef;
        this._containerInstance = _containerInstance;
        this.id = id;
        /** Whether the user is allowed to close the dialog. */
        this.disableClose = this._containerInstance._config.disableClose;
        /** Subject for notifying the user that the dialog has finished opening. */
        this._afterOpened = new Subject();
        /** Subject for notifying the user that the dialog has finished closing. */
        this._afterClosed = new Subject();
        /** Subject for notifying the user that the dialog has started closing. */
        this._beforeClosed = new Subject();
        // Pass the id along to the container.
        _containerInstance._id = id;
        // Emit when opening animation completes
        _containerInstance._animationStateChanged
            .pipe(filter((event) => event.phaseName === 'done' && (event.toState === 'enter' || event.toState === 'slideEnter')), take(1))
            .subscribe(() => {
            this._afterOpened.next();
            this._afterOpened.complete();
        });
        // Dispose overlay when closing animation is complete
        _containerInstance._animationStateChanged
            .pipe(filter((event) => event.phaseName === 'done' && (event.toState === 'exit' || event.toState === 'slideExit')), take(1))
            .subscribe(() => this._overlayRef.dispose());
        _overlayRef.detachments().subscribe(() => {
            this._beforeClosed.next(this._result);
            this._beforeClosed.complete();
            this._afterClosed.next(this._result);
            this._afterClosed.complete();
            this.componentInstance = null;
            this._overlayRef.dispose();
        });
        _overlayRef
            .keydownEvents()
            .pipe(filter((event) => event.keyCode === ESCAPE && !this.disableClose))
            .subscribe(() => this.close());
    }
    /**
     * Close the dialog.
     * @param dialogResult Optional result to return to the dialog opener.
     */
    close(dialogResult) {
        this._result = dialogResult;
        // Transition the backdrop in parallel to the dialog.
        this._containerInstance._animationStateChanged
            .pipe(filter((event) => event.phaseName === 'start'), take(1))
            .subscribe(() => {
            this._beforeClosed.next(dialogResult);
            this._beforeClosed.complete();
            this._overlayRef.detachBackdrop();
        });
        this._containerInstance._startExitAnimation();
    }
    /**
     * Gets an observable that is notified when the dialog is finished opening.
     */
    afterOpened() {
        return this._afterOpened.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog is finished closing.
     */
    afterClosed() {
        return this._afterClosed.asObservable();
    }
    /**
     * Gets an observable that is notified when the dialog has started closing.
     */
    beforeClosed() {
        return this._beforeClosed.asObservable();
    }
    /**
     * Gets an observable that emits when the overlay's backdrop has been clicked.
     */
    backdropClick() {
        return this._overlayRef.backdropClick();
    }
    /**
     * Gets an observable that emits when keydown events are targeted on the overlay.
     */
    keydownEvents() {
        return this._overlayRef.keydownEvents();
    }
    /**
     * Updates the dialog's position.
     * @param position New dialog position.
     */
    updatePosition(position) {
        const strategy = this._getPositionStrategy();
        if (position && (position.left || position.right)) {
            position.left ? strategy.left(position.left) : strategy.right(position.right);
        }
        else {
            strategy.centerHorizontally();
        }
        if (position && (position.top || position.bottom)) {
            position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
        }
        else {
            strategy.centerVertically();
        }
        this._overlayRef.updatePosition();
        return this;
    }
    /**
     * Updates the dialog's width and height.
     * @param width New width of the dialog.
     * @param height New height of the dialog.
     */
    updateSize(width = '', height = '') {
        this._getPositionStrategy().width(width).height(height);
        this._overlayRef.updatePosition();
        return this;
    }
    /** Add a CSS class or an array of classes to the overlay pane. */
    addPanelClass(classes) {
        this._overlayRef.addPanelClass(classes);
        return this;
    }
    /** Remove a CSS class or an array of classes from the overlay pane. */
    removePanelClass(classes) {
        this._overlayRef.removePanelClass(classes);
        return this;
    }
    /** Fetches the position strategy object from the overlay ref. */
    _getPositionStrategy() {
        return this._overlayRef.getConfig().positionStrategy;
    }
}

/** Injection token that can be used to access the data that was passed in to a dialog. */
const SDS_DIALOG_DATA = new InjectionToken('SdsDialogData');
/** Injection token that can be used to specify default dialog options. */
const SDS_DIALOG_DEFAULT_OPTIONS = new InjectionToken('sds-dialog-default-options');
/** Injection token that determines the scroll handling while the dialog is open. */
const SDS_DIALOG_SCROLL_STRATEGY = new InjectionToken('sds-dialog-scroll-strategy');
const SDS_SLIDE_OUT_SCROLL_STRATEGY = new InjectionToken('sds-slide-out-scroll-strategy');
/** @docs-private */
function SDS_DIALOG_SCROLL_STRATEGY_FACTORY(overlay) {
    return () => overlay.scrollStrategies.block();
}
/** @docs-private */
function SDS_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay) {
    return () => overlay.scrollStrategies.block();
}
/** @docs-private */
function SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay) {
    return () => overlay.scrollStrategies.reposition();
}
/** @docs-private */
const SDS_DIALOG_SCROLL_STRATEGY_PROVIDER = {
    provide: SDS_DIALOG_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: SDS_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY,
};
const SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER = {
    provide: SDS_SLIDE_OUT_SCROLL_STRATEGY,
    deps: [Overlay],
    useFactory: SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER_FACTORY,
};
/**
 * Service to open modal dialogs.
 */
class SdsDialogService {
    /** Keeps track of the currently-open dialogs. */
    get openDialogs() {
        return this._parentDialog ? this._parentDialog.openDialogs : this._openDialogsAtThisLevel;
    }
    /** Stream that emits when a dialog has been opened. */
    get afterOpened() {
        return this._parentDialog ? this._parentDialog.afterOpened : this._afterOpenedAtThisLevel;
    }
    get _afterAllClosed() {
        const parent = this._parentDialog;
        return parent ? parent._afterAllClosed : this._afterAllClosedAtThisLevel;
    }
    constructor(_overlay, _injector, _location, _defaultOptions, scrollStrategy, slideOutScrollStrategy, _parentDialog, _overlayContainer) {
        this._overlay = _overlay;
        this._injector = _injector;
        this._location = _location;
        this._defaultOptions = _defaultOptions;
        this._parentDialog = _parentDialog;
        this._overlayContainer = _overlayContainer;
        this._openDialogsAtThisLevel = [];
        this._afterAllClosedAtThisLevel = new Subject();
        this._afterOpenedAtThisLevel = new Subject();
        this._ariaHiddenElements = new Map();
        /**
         * Stream that emits when all open dialog have finished closing.
         * Will emit on subscribe if there are no open dialogs to begin with.
         */
        this.afterAllClosed = defer(() => this.openDialogs.length ? this._afterAllClosed : this._afterAllClosed.pipe(startWith(undefined)));
        this._scrollStrategy = scrollStrategy;
        this._slideOutScrollStrategy = slideOutScrollStrategy;
    }
    /**
     * Opens a modal dialog containing the given component.
     * @param componentOrTemplateRef Type of the component to load into the dialog,
     *     or a TemplateRef to instantiate as the dialog content.
     * @param config Extra configuration options.
     * @returns Reference to the newly-opened dialog.
     */
    open(componentOrTemplateRef, config) {
        // Convenience widths names: small | medium | large
        // added to help with standardization
        if (config && config.width) {
            switch (config.width) {
                case 'small': {
                    config.width = '370px';
                    break;
                }
                case 'medium': {
                    config.width = '730px';
                    break;
                }
                case 'large': {
                    config.width = '960px';
                    break;
                }
                default: {
                    break;
                }
            }
        }
        config = _applyConfigDefaults(config, this._defaultOptions || new SdsDialogConfig());
        if (config.id && this.getDialogById(config.id)) {
            throw Error(`Dialog with id "${config.id}" exists already. The dialog id must be unique.`);
        }
        const overlayRef = this._createOverlay(config);
        const dialogContainer = this._attachDialogContainer(overlayRef, config);
        const dialogRef = this._attachDialogContent(componentOrTemplateRef, dialogContainer, overlayRef, config);
        // If this is the first dialog that we're opening, hide all the non-overlay content.
        if (!this.openDialogs.length) {
            this._hideNonDialogContentFromAssistiveTechnology();
        }
        this.openDialogs.push(dialogRef);
        dialogRef.afterClosed().subscribe(() => this._removeOpenDialog(dialogRef));
        this.afterOpened.next(dialogRef);
        return dialogRef;
    }
    /**
     * Closes all of the currently-open dialogs.
     */
    closeAll() {
        this._closeDialogs(this.openDialogs);
    }
    /**
     * Finds an open dialog by its id.
     * @param id ID to use when looking up the dialog.
     */
    getDialogById(id) {
        return this.openDialogs.find((dialog) => dialog.id === id);
    }
    ngOnDestroy() {
        // Only close the dialogs at this level on destroy
        // since the parent service may still be active.
        this._closeDialogs(this._openDialogsAtThisLevel);
        this._afterAllClosedAtThisLevel.complete();
        this._afterOpenedAtThisLevel.complete();
    }
    /**
     * Creates the overlay into which the dialog will be loaded.
     * @param config The dialog configuration.
     * @returns A promise resolving to the OverlayRef for the created overlay.
     */
    _createOverlay(config) {
        const overlayConfig = this._getOverlayConfig(config);
        return this._overlay.create(overlayConfig);
    }
    /**
     * Creates an overlay config from a dialog config.
     * @param dialogConfig The dialog configuration.
     * @returns The overlay configuration.
     */
    _getOverlayConfig(dialogConfig) {
        const state = new OverlayConfig({
            positionStrategy: this._overlay.position().global(),
            scrollStrategy: dialogConfig.scrollStrategy ||
                (dialogConfig.slideOut ? this._slideOutScrollStrategy() : this._scrollStrategy()),
            panelClass: dialogConfig.panelClass,
            hasBackdrop: dialogConfig.hasBackdrop,
            direction: dialogConfig.direction,
            minWidth: dialogConfig.minWidth,
            minHeight: dialogConfig.minHeight,
            maxWidth: dialogConfig.maxWidth,
            maxHeight: dialogConfig.maxHeight,
            disposeOnNavigation: dialogConfig.closeOnNavigation,
        });
        if (dialogConfig.backdropClass) {
            state.backdropClass = dialogConfig.backdropClass;
        }
        return state;
    }
    /**
     * Attaches an SdsDialogContainerComponent to a dialog's already-created overlay.
     * @param overlay Reference to the dialog's underlying overlay.
     * @param config The dialog configuration.
     * @returns A promise resolving to a ComponentRef for the attached container.
     */
    _attachDialogContainer(overlay, config) {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        const injector = new PortalInjector(userInjector || this._injector, new WeakMap([[SdsDialogConfig, config]]));
        const containerPortal = new ComponentPortal(SdsDialogContainerComponent, config.viewContainerRef, injector);
        const containerRef = overlay.attach(containerPortal);
        return containerRef.instance;
    }
    /**
     * Attaches the user-provided component to the already-created SdsDialogContainerComponent.
     * @param componentOrTemplateRef The type of component being loaded into the dialog,
     *     or a TemplateRef to instantiate as the content.
     * @param dialogContainer Reference to the wrapping SdsDialogContainerComponent.
     * @param overlayRef Reference to the overlay in which the dialog resides.
     * @param config The dialog configuration.
     * @returns A promise resolving to the SdsDialogRef that should be returned to the user.
     */
    _attachDialogContent(componentOrTemplateRef, dialogContainer, overlayRef, config) {
        // Create a reference to the dialog we're creating in order to give the user a handle
        // to modify and close it.
        const dialogRef = new SdsDialogRef(overlayRef, dialogContainer, this._location, config.id);
        // When the dialog backdrop is clicked, we want to close it.
        if (config.hasBackdrop) {
            overlayRef.backdropClick().subscribe(() => {
                if (!dialogRef.disableClose) {
                    dialogRef.close();
                }
            });
        }
        if (componentOrTemplateRef instanceof TemplateRef) {
            dialogContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplateRef, null, { $implicit: config.data, dialogRef }));
        }
        else {
            const injector = this._createInjector(config, dialogRef, dialogContainer);
            const contentRef = dialogContainer.attachComponentPortal(new ComponentPortal(componentOrTemplateRef, undefined, injector));
            dialogRef.componentInstance = contentRef.instance;
        }
        dialogRef.updateSize(config.width, config.height).updatePosition(config.position);
        return dialogRef;
    }
    /**
     * Creates a custom injector to be used inside the dialog. This allows a component loaded inside
     * of a dialog to close itself and, optionally, to return a value.
     * @param config Config object that is used to construct the dialog.
     * @param dialogRef Reference to the dialog.
     * @param container Dialog container element that wraps all of the contents.
     * @returns The custom injector that can be used inside the dialog.
     */
    _createInjector(config, dialogRef, dialogContainer) {
        const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
        // The SdsDialogContainerComponent is injected in the portal as the SdsDialogContainerComponent and the dialog's
        // content are created out of the same ViewContainerRef and as such, are siblings for injector
        // purposes. To allow the hierarchy that is expected, the SdsDialogContainerComponent is explicitly
        // added to the injection tokens.
        const injectionTokens = new WeakMap([
            [SdsDialogContainerComponent, dialogContainer],
            [SDS_DIALOG_DATA, config.data],
            [SdsDialogRef, dialogRef],
        ]);
        if (config.direction && (!userInjector || !userInjector.get(Directionality, null))) {
            injectionTokens.set(Directionality, {
                value: config.direction,
                change: of(),
            });
        }
        return new PortalInjector(userInjector || this._injector, injectionTokens);
    }
    /**
     * Removes a dialog from the array of open dialogs.
     * @param dialogRef Dialog to be removed.
     */
    _removeOpenDialog(dialogRef) {
        const index = this.openDialogs.indexOf(dialogRef);
        if (index > -1) {
            this.openDialogs.splice(index, 1);
            // If all the dialogs were closed, remove/restore the `aria-hidden`
            // to a the siblings and emit to the `afterAllClosed` stream.
            if (!this.openDialogs.length) {
                this._ariaHiddenElements.forEach((previousValue, element) => {
                    if (previousValue) {
                        element.setAttribute('aria-hidden', previousValue);
                    }
                    else {
                        element.removeAttribute('aria-hidden');
                    }
                });
                this._ariaHiddenElements.clear();
                this._afterAllClosed.next();
            }
        }
    }
    /**
     * Hides all of the content that isn't an overlay from assistive technology.
     */
    _hideNonDialogContentFromAssistiveTechnology() {
        const overlayContainer = this._overlayContainer.getContainerElement();
        // Ensure that the overlay container is attached to the DOM.
        if (overlayContainer.parentElement) {
            const siblings = overlayContainer.parentElement.children;
            for (let i = siblings.length - 1; i > -1; i--) {
                const sibling = siblings[i];
                if (sibling !== overlayContainer &&
                    sibling.nodeName !== 'SCRIPT' &&
                    sibling.nodeName !== 'STYLE' &&
                    !sibling.hasAttribute('aria-live')) {
                    this._ariaHiddenElements.set(sibling, sibling.getAttribute('aria-hidden'));
                    sibling.setAttribute('aria-hidden', 'true');
                }
            }
        }
    }
    /** Closes all of the dialogs in an array. */
    _closeDialogs(dialogs) {
        let i = dialogs.length;
        while (i--) {
            // The `_openDialogs` property isn't updated after close until the rxjs subscription
            // runs on the next microtask, in addition to modifying the array as we're going
            // through it. We loop through all of them and call close without assuming that
            // they'll be removed from the list instantaneously.
            dialogs[i].close();
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsDialogService_Factory(t) { return new (t || SdsDialogService)(i0.ɵɵinject(i1$3.Overlay), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(i1$2.Location, 8), i0.ɵɵinject(SDS_DIALOG_DEFAULT_OPTIONS, 8), i0.ɵɵinject(SDS_DIALOG_SCROLL_STRATEGY), i0.ɵɵinject(SDS_SLIDE_OUT_SCROLL_STRATEGY), i0.ɵɵinject(SdsDialogService, 12), i0.ɵɵinject(i1$3.OverlayContainer)); }; }
    /** @nocollapse */ static { this.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: SdsDialogService, factory: SdsDialogService.ɵfac }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogService, [{
        type: Injectable
    }], () => [{ type: i1$3.Overlay }, { type: i0.Injector }, { type: i1$2.Location, decorators: [{
                type: Optional
            }] }, { type: SdsDialogConfig, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [SDS_DIALOG_DEFAULT_OPTIONS]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [SDS_DIALOG_SCROLL_STRATEGY]
            }] }, { type: undefined, decorators: [{
                type: Inject,
                args: [SDS_SLIDE_OUT_SCROLL_STRATEGY]
            }] }, { type: SdsDialogService, decorators: [{
                type: Optional
            }, {
                type: SkipSelf
            }] }, { type: i1$3.OverlayContainer }], null); })();
/**
 * Applies default options to the dialog config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
function _applyConfigDefaults(config, defaultOptions) {
    return { ...defaultOptions, ...config };
}

const _c0$e = ["mobile"];
function SdsSideToolbarComponent_div_0_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function SdsSideToolbarComponent_div_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, SdsSideToolbarComponent_div_0_ng_container_1_Template, 1, 0, "ng-container", 2);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.template);
} }
function SdsSideToolbarComponent_div_1_usa_icon_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "usa-icon", 5);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("icon", ctx_r0.responsiveButtonIcon)("size", "1x");
} }
function SdsSideToolbarComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div")(1, "button", 3);
    i0.ɵɵlistener("click", function SdsSideToolbarComponent_div_1_Template_button_click_1_listener() { i0.ɵɵrestoreView(_r2); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.onResponsiveViewButtonClicked()); });
    i0.ɵɵtemplate(2, SdsSideToolbarComponent_div_1_usa_icon_2_Template, 1, 2, "usa-icon", 4);
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵattribute("aria-label", ctx_r0.responsiveButtonText);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0.responsiveButtonIcon);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", ctx_r0.responsiveButtonText, " ");
} }
function SdsSideToolbarComponent_ng_template_2_ng_container_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function SdsSideToolbarComponent_ng_template_2_div_8_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 6)(1, "div", 13)(2, "button", 14);
    i0.ɵɵlistener("click", function SdsSideToolbarComponent_ng_template_2_div_8_Template_button_click_2_listener() { i0.ɵɵrestoreView(_r4); const ctx_r0 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r0.onApplyClicked()); });
    i0.ɵɵtext(3, "Apply");
    i0.ɵɵelementEnd()()();
} }
function SdsSideToolbarComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 6)(1, "div", 7)(2, "button", 8);
    i0.ɵɵlistener("click", function SdsSideToolbarComponent_ng_template_2_Template_button_click_2_listener() { i0.ɵɵrestoreView(_r3); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.onCancelClicked()); });
    i0.ɵɵelement(3, "usa-icon", 9);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div", 10)(5, "h2", 11);
    i0.ɵɵtext(6);
    i0.ɵɵelementEnd()()()();
    i0.ɵɵtemplate(7, SdsSideToolbarComponent_ng_template_2_ng_container_7_Template, 1, 0, "ng-container", 2)(8, SdsSideToolbarComponent_ng_template_2_div_8_Template, 4, 0, "div", 12);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵattribute("aria-label", ctx_r0.backButtonAria);
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "chevron-left");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(ctx_r0.dialogTitleText);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.template);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0.showApply);
} }
class SdsSideToolbarComponent {
    constructor(sdsDialogService, breakpointObserver // Will watch for changes between mobile and non-mobile screen size
    ) {
        this.sdsDialogService = sdsDialogService;
        this.breakpointObserver = breakpointObserver;
        // default value is size of mobile view in px
        this.responsiveSize = 480;
        this.showApply = false;
        this.onCancel = new EventEmitter();
        this.onApply = new EventEmitter();
        this.responsiveDialog = new EventEmitter();
        this.responsiveView = new EventEmitter();
        this.isResponsiveView = false;
        this.subscription = new Subscription();
    }
    ngOnInit() {
        this.observeViewChange();
        if (this.dialogTitleText === undefined) {
            this.dialogTitleText = this.responsiveButtonText;
        }
        if (this.backButtonAria === undefined) {
            this.backButtonAria = `Cancel ${this.dialogTitleText}`;
        }
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    onResponsiveViewButtonClicked() {
        let dialogOptions = {
            height: '100vh',
            width: '100vw',
            maxWidth: '100vw',
            maxHeight: '100vh',
            hasBackdrop: false,
            displayCloseBtn: false,
            panelClass: ['sds-dialog--full'],
        };
        let allOptions = this.responsiveDialogOptions
            ? { ...dialogOptions, ...this.responsiveDialogOptions }
            : dialogOptions;
        this.openResponsiveDialog = this.sdsDialogService.open(this.mobile, allOptions);
        this.responsiveDialog.emit(this.openResponsiveDialog);
        this.openResponsiveDialog.afterClosed().subscribe(() => {
            this.openResponsiveDialog = undefined;
        });
    }
    observeViewChange() {
        const breakpointUnsubscription = this.breakpointObserver
            .observe([`(max-width: ${this.responsiveSize}px)`])
            .subscribe((result) => {
            if (result.matches) {
                this.isResponsiveView = true;
            }
            else {
                this.isResponsiveView = false;
                if (this.openResponsiveDialog) {
                    this.openResponsiveDialog.close();
                    this.openResponsiveDialog = undefined;
                    this.responsiveDialog.emit(this.openResponsiveDialog);
                }
            }
            this.responsiveView.emit(this.isResponsiveView);
        });
        this.subscription.add(breakpointUnsubscription);
    }
    onCancelClicked() {
        this.openResponsiveDialog.close();
        this.openResponsiveDialog = undefined;
        this.onCancel.emit();
    }
    onApplyClicked() {
        this.openResponsiveDialog.close();
        this.openResponsiveDialog = undefined;
        this.onApply.emit();
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSideToolbarComponent_Factory(t) { return new (t || SdsSideToolbarComponent)(i0.ɵɵdirectiveInject(SdsDialogService), i0.ɵɵdirectiveInject(i2$3.BreakpointObserver)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSideToolbarComponent, selectors: [["sds-side-toolbar"]], contentQueries: function SdsSideToolbarComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TemplateRef, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.template = _t.first);
        } }, viewQuery: function SdsSideToolbarComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$e, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.mobile = _t.first);
        } }, inputs: { responsiveButtonText: "responsiveButtonText", responsiveButtonIcon: "responsiveButtonIcon", dialogTitleText: "dialogTitleText", backButtonAria: "backButtonAria", responsiveDialogOptions: "responsiveDialogOptions", responsiveSize: "responsiveSize", showApply: "showApply" }, outputs: { onCancel: "onCancel", onApply: "onApply", responsiveDialog: "responsiveDialog", responsiveView: "responsiveView" }, decls: 4, vars: 2, consts: [["mobile", ""], [4, "ngIf"], [4, "ngTemplateOutlet"], ["id", "responsiveViewButton", 1, "usa-button", "usa-button--accent-cool", "usa-button--hover", "text-secondary-dar", "width-full", 3, "click"], ["class", "padding-right-1", 3, "icon", "size", 4, "ngIf"], [1, "padding-right-1", 3, "icon", "size"], [1, "sds-card"], [1, "sds-card__header", "sds-card__header--accent-cool", "grid-row"], ["id", "cancelButton", 1, "sds-button", "sds-button--circular", "sds-button--white", 3, "click"], [3, "icon"], [1, "margin-x-auto"], [1, "sds-card__title", "margin-top-1", "margin-left-neg-2"], ["class", "sds-card", 4, "ngIf"], [1, "sds-card__body", "sds-card__body--accent-cool"], ["id", "applyButton", "aria-label", "Apply", 1, "usa-button", 3, "click"]], template: function SdsSideToolbarComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SdsSideToolbarComponent_div_0_Template, 2, 1, "div", 1)(1, SdsSideToolbarComponent_div_1_Template, 4, 3, "div", 1)(2, SdsSideToolbarComponent_ng_template_2_Template, 9, 5, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", !ctx.isResponsiveView);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.isResponsiveView);
        } }, dependencies: [i1$2.NgIf, i1$2.NgTemplateOutlet, i2$1.IconComponent], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSideToolbarComponent, [{
        type: Component,
        args: [{ selector: 'sds-side-toolbar', template: "<div *ngIf=\"!isResponsiveView\">\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n</div>\n\n<div *ngIf=\"isResponsiveView\">\n  <button\n    class=\"usa-button usa-button--accent-cool usa-button--hover text-secondary-dar width-full\"\n    id=\"responsiveViewButton\"\n    (click)=\"onResponsiveViewButtonClicked()\"\n    [attr.aria-label]=\"responsiveButtonText\"\n  >\n    <usa-icon\n      *ngIf=\"responsiveButtonIcon\"\n      class=\"padding-right-1\"\n      [icon]=\"responsiveButtonIcon\"\n      [size]=\"'1x'\"\n    ></usa-icon>\n    {{ responsiveButtonText }}\n  </button>\n</div>\n\n<ng-template #mobile>\n  <div class=\"sds-card\">\n    <div class=\"sds-card__header sds-card__header--accent-cool grid-row\">\n      <button\n        id=\"cancelButton\"\n        class=\"sds-button sds-button--circular sds-button--white\"\n        (click)=\"onCancelClicked()\"\n        [attr.aria-label]=\"backButtonAria\"\n      >\n        <usa-icon [icon]=\"'chevron-left'\"></usa-icon>\n      </button>\n      <div class=\"margin-x-auto\">\n        <h2 class=\"sds-card__title margin-top-1 margin-left-neg-2\">{{ dialogTitleText }}</h2>\n      </div>\n    </div>\n  </div>\n  <ng-container *ngTemplateOutlet=\"template\"></ng-container>\n  <div class=\"sds-card\" *ngIf=\"showApply\">\n    <div class=\"sds-card__body sds-card__body--accent-cool\">\n      <button class=\"usa-button\" id=\"applyButton\" aria-label=\"Apply\" (click)=\"onApplyClicked()\">Apply</button>\n    </div>\n  </div>\n</ng-template>\n" }]
    }], () => [{ type: SdsDialogService }, { type: i2$3.BreakpointObserver }], { template: [{
            type: ContentChild,
            args: [TemplateRef]
        }], mobile: [{
            type: ViewChild,
            args: ['mobile']
        }], responsiveButtonText: [{
            type: Input
        }], responsiveButtonIcon: [{
            type: Input
        }], dialogTitleText: [{
            type: Input
        }], backButtonAria: [{
            type: Input
        }], responsiveDialogOptions: [{
            type: Input
        }], responsiveSize: [{
            type: Input
        }], showApply: [{
            type: Input
        }], onCancel: [{
            type: Output
        }], onApply: [{
            type: Output
        }], responsiveDialog: [{
            type: Output
        }], responsiveView: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSideToolbarComponent, { className: "SdsSideToolbarComponent" }); })();

/** Counter used to generate unique IDs for dialog elements. */
let dialogElementUid = 0;
/**
 * Button that will close the current dialog.
 */
class SdsDialogCloseDirective {
    constructor(dialogRef, _elementRef, _dialog) {
        this.dialogRef = dialogRef;
        this._elementRef = _elementRef;
        this._dialog = _dialog;
    }
    ngOnInit() {
        if (!this.dialogRef) {
            // When this directive is included in a dialog via TemplateRef (rather than being
            // in a Component), the DialogRef isn't available via injection because embedded
            // views cannot be given a custom injector. Instead, we look up the DialogRef by
            // ID. This must occur in `onInit`, as the ID binding for the dialog container won't
            // be resolved at constructor time.
            this.dialogRef = getClosestDialog(this._elementRef, this._dialog.openDialogs);
        }
    }
    ngOnChanges(changes) {
        const proxiedChange = changes['_sdsDialogClose'] || changes['_sdsDialogCloseResult'];
        if (proxiedChange) {
            this.dialogResult = proxiedChange.currentValue;
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsDialogCloseDirective_Factory(t) { return new (t || SdsDialogCloseDirective)(i0.ɵɵdirectiveInject(SdsDialogRef, 8), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SdsDialogService)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsDialogCloseDirective, selectors: [["button", "sds-dialog-close", ""], ["button", "sdsDialogClose", ""]], hostAttrs: ["type", "button"], hostVars: 1, hostBindings: function SdsDialogCloseDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function SdsDialogCloseDirective_click_HostBindingHandler() { return ctx.dialogRef.close(ctx.dialogResult); });
        } if (rf & 2) {
            i0.ɵɵattribute("aria-label", ctx.ariaLabel || null);
        } }, inputs: { ariaLabel: [i0.ɵɵInputFlags.None, "aria-label", "ariaLabel"], dialogResult: [i0.ɵɵInputFlags.None, "sds-dialog-close", "dialogResult"], _sdsDialogClose: [i0.ɵɵInputFlags.None, "sdsDialogClose", "_sdsDialogClose"] }, exportAs: ["sdsDialogClose"], features: [i0.ɵɵNgOnChangesFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogCloseDirective, [{
        type: Directive,
        args: [{
                selector: `button[sds-dialog-close], button[sdsDialogClose]`,
                exportAs: 'sdsDialogClose',
                // tslint:disable-next-line: use-host-property-decorator
                host: {
                    '(click)': 'dialogRef.close(dialogResult)',
                    '[attr.aria-label]': 'ariaLabel || null',
                    type: 'button', // Prevents accidental form submits.
                },
            }]
    }], () => [{ type: SdsDialogRef, decorators: [{
                type: Optional
            }] }, { type: i0.ElementRef }, { type: SdsDialogService }], { ariaLabel: [{
            type: Input,
            args: ['aria-label']
        }], dialogResult: [{
            type: Input,
            args: ['sds-dialog-close']
        }], _sdsDialogClose: [{
            type: Input,
            args: ['sdsDialogClose']
        }] }); })();
/**
 * Title of a dialog element. Stays fixed to the top of the dialog when scrolling.
 */
class SdsDialogTitleDirective {
    constructor(_dialogRef, _elementRef, _dialog) {
        this._dialogRef = _dialogRef;
        this._elementRef = _elementRef;
        this._dialog = _dialog;
        this.id = `sds-dialog-title-${dialogElementUid++}`;
    }
    ngOnInit() {
        if (!this._dialogRef) {
            this._dialogRef = getClosestDialog(this._elementRef, this._dialog.openDialogs);
        }
        if (this._dialogRef) {
            Promise.resolve().then(() => {
                const container = this._dialogRef._containerInstance;
                if (container && !container._ariaLabelledBy) {
                    container._ariaLabelledBy = this.id;
                }
            });
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsDialogTitleDirective_Factory(t) { return new (t || SdsDialogTitleDirective)(i0.ɵɵdirectiveInject(SdsDialogRef, 8), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SdsDialogService)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsDialogTitleDirective, selectors: [["", "sds-dialog-title", ""], ["", "sdsDialogTitle", ""]], hostVars: 3, hostBindings: function SdsDialogTitleDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵhostProperty("id", ctx.id);
            i0.ɵɵclassProp("sds-dialog-title", true);
        } }, inputs: { id: "id" }, exportAs: ["sdsDialogTitle"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogTitleDirective, [{
        type: Directive,
        args: [{
                selector: '[sds-dialog-title], [sdsDialogTitle]',
                exportAs: 'sdsDialogTitle',
                // tslint:disable-next-line: use-host-property-decorator
                host: {
                    '[class.sds-dialog-title]': 'true',
                    '[id]': 'id',
                },
            }]
    }], () => [{ type: SdsDialogRef, decorators: [{
                type: Optional
            }] }, { type: i0.ElementRef }, { type: SdsDialogService }], { id: [{
            type: Input
        }] }); })();
/**
 * SubTitle of a dialog element
 */
class SdsDialogSubtitleDirective {
    /** @nocollapse */ static { this.ɵfac = function SdsDialogSubtitleDirective_Factory(t) { return new (t || SdsDialogSubtitleDirective)(); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsDialogSubtitleDirective, selectors: [["", "sds-dialog-subtitle", ""], ["sds-dialog-subtitle"], ["", "sdsDialogSubtitle", ""]], hostVars: 2, hostBindings: function SdsDialogSubtitleDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("sds-dialog-subtitle", true);
        } } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogSubtitleDirective, [{
        type: Directive,
        args: [{
                selector: `[sds-dialog-subtitle], sds-dialog-subtitle, [sdsDialogSubtitle]`,
                // tslint:disable-next-line: use-host-property-decorator
                host: { '[class.sds-dialog-subtitle]': 'true' },
            }]
    }], null, null); })();
/**
 * Scrollable content container of a dialog.
 */
class SdsDialogContentDirective {
    /** @nocollapse */ static { this.ɵfac = function SdsDialogContentDirective_Factory(t) { return new (t || SdsDialogContentDirective)(); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsDialogContentDirective, selectors: [["", "sds-dialog-content", ""], ["sds-dialog-content"], ["", "sdsDialogContent", ""]], hostVars: 2, hostBindings: function SdsDialogContentDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("sds-dialog-content", true);
        } } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogContentDirective, [{
        type: Directive,
        args: [{
                selector: `[sds-dialog-content], sds-dialog-content, [sdsDialogContent]`,
                // tslint:disable-next-line: use-host-property-decorator
                host: { '[class.sds-dialog-content]': 'true' },
            }]
    }], null, null); })();
/**
 * Container for the bottom action buttons in a dialog.
 * Stays fixed to the bottom when scrolling.
 */
class SdsDialogActionsDirective {
    /** @nocollapse */ static { this.ɵfac = function SdsDialogActionsDirective_Factory(t) { return new (t || SdsDialogActionsDirective)(); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsDialogActionsDirective, selectors: [["", "sds-dialog-actions", ""], ["sds-dialog-actions"], ["", "sdsDialogActions", ""]], hostVars: 2, hostBindings: function SdsDialogActionsDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("sds-dialog-actions", true);
        } } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogActionsDirective, [{
        type: Directive,
        args: [{
                selector: `[sds-dialog-actions], sds-dialog-actions, [sdsDialogActions]`,
                // tslint:disable-next-line: use-host-property-decorator
                host: { '[class.sds-dialog-actions]': 'true' },
            }]
    }], null, null); })();
/**
 * Finds the closest SdsDialogRef to an element by looking at the DOM.
 * @param element Element relative to which to look for a dialog.
 * @param openDialogs References to the currently-open dialogs.
 */
function getClosestDialog(element, openDialogs) {
    let parent = element.nativeElement.parentElement;
    while (parent && !parent.classList.contains('sds-dialog__container')) {
        parent = parent.parentElement;
    }
    return parent ? openDialogs.find((dialog) => dialog.id === parent.id) : null;
}

class SdsDialogModule {
    /** @nocollapse */ static { this.ɵfac = function SdsDialogModule_Factory(t) { return new (t || SdsDialogModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsDialogModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ providers: [SdsDialogService, SDS_DIALOG_SCROLL_STRATEGY_PROVIDER, SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER], imports: [CommonModule,
            OverlayModule,
            PortalModule,
            IconModule,
            NgxBootstrapIconsModule.pick({
                x,
                alertError,
                infoCircle,
                exclamationTriangle,
                exclamationCircle,
                checkCircle,
                slashCircle,
            })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDialogModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    OverlayModule,
                    PortalModule,
                    IconModule,
                    NgxBootstrapIconsModule.pick({
                        x,
                        alertError,
                        infoCircle,
                        exclamationTriangle,
                        exclamationCircle,
                        checkCircle,
                        slashCircle,
                    }),
                ],
                exports: [
                    SdsDialogContainerComponent,
                    SdsDialogCloseDirective,
                    SdsDialogTitleDirective,
                    SdsDialogSubtitleDirective,
                    SdsDialogContentDirective,
                    SdsDialogActionsDirective,
                ],
                declarations: [
                    SdsDialogContainerComponent,
                    SdsDialogCloseDirective,
                    SdsDialogTitleDirective,
                    SdsDialogSubtitleDirective,
                    SdsDialogActionsDirective,
                    SdsDialogContentDirective,
                ],
                providers: [SdsDialogService, SDS_DIALOG_SCROLL_STRATEGY_PROVIDER, SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsDialogModule, { declarations: [SdsDialogContainerComponent,
        SdsDialogCloseDirective,
        SdsDialogTitleDirective,
        SdsDialogSubtitleDirective,
        SdsDialogActionsDirective,
        SdsDialogContentDirective], imports: [CommonModule,
        OverlayModule,
        PortalModule,
        IconModule, i1$4.NgxBootstrapIconsModule], exports: [SdsDialogContainerComponent,
        SdsDialogCloseDirective,
        SdsDialogTitleDirective,
        SdsDialogSubtitleDirective,
        SdsDialogContentDirective,
        SdsDialogActionsDirective] }); })();
i0.ɵɵsetComponentScope(SdsDialogContainerComponent, function () { return [i1$2.NgIf, i1$2.NgSwitch, i1$2.NgSwitchCase, i1$2.NgSwitchDefault, i2.CdkPortalOutlet, i2$1.IconComponent, SdsDialogCloseDirective]; }, []);

class SdsSideToolbarModule {
    /** @nocollapse */ static { this.ɵfac = function SdsSideToolbarModule_Factory(t) { return new (t || SdsSideToolbarModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsSideToolbarModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, SdsDialogModule, IconModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSideToolbarModule, [{
        type: NgModule,
        args: [{
                declarations: [SdsSideToolbarComponent],
                imports: [CommonModule, SdsDialogModule, IconModule],
                exports: [SdsSideToolbarComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsSideToolbarModule, { declarations: [SdsSideToolbarComponent], imports: [CommonModule, SdsDialogModule, IconModule], exports: [SdsSideToolbarComponent] }); })();

class SearchModel {
}
class Message {
}
class Button {
}

const _c0$d = ["resultContent"];
const _c1$6 = a0 => ({ $implicit: a0 });
const _c2$1 = (a0, a1) => [a0, a1];
const _c3$1 = () => ["sds", "alert-error"];
function SdsSearchResultListComponent_ng_container_0_div_1_hr_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "hr", 7);
} }
function SdsSearchResultListComponent_ng_container_0_div_1_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function SdsSearchResultListComponent_ng_container_0_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_0_div_1_hr_1_Template, 1, 0, "hr", 5)(2, SdsSearchResultListComponent_ng_container_0_div_1_ng_container_2_Template, 1, 0, "ng-container", 6);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r1 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.divider);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.resultContentTemplate)("ngTemplateOutletContext", i0.ɵɵpureFunction1(3, _c1$6, item_r1));
} }
function SdsSearchResultListComponent_ng_container_0_hr_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "hr", 7);
} }
function SdsSearchResultListComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_0_div_1_Template, 3, 5, "div", 4)(2, SdsSearchResultListComponent_ng_container_0_hr_2_Template, 1, 0, "hr", 5);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r1.updateModel.results);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.divider && (ctx_r1.updateModel == null ? null : ctx_r1.updateModel.results == null ? null : ctx_r1.updateModel.results.length));
} }
function SdsSearchResultListComponent_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 8);
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.customResultsTemplate)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c1$6, ctx_r1.updateModel));
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 9)(2, "div", 10)(3, "usa-stacked-icon", 11);
    i0.ɵɵelement(4, "usa-icon", 12)(5, "usa-icon", 12);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(6, "div", 13)(7, "h1", 14);
    i0.ɵɵtext(8, "No matches found");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(9, "p");
    i0.ɵɵtext(10, "We couldn't find a match for your search criteria.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(11, "p");
    i0.ɵɵtext(12, "Please try another search or go back to previous results.");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(13, "div", 15)(14, "button", 16);
    i0.ɵɵlistener("click", function SdsSearchResultListComponent_ng_container_3_ng_container_1_ng_container_1_Template_button_click_14_listener() { i0.ɵɵrestoreView(_r3); const ctx_r1 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r1.goBack()); });
    i0.ɵɵtext(15, "Go Back");
    i0.ɵɵelementEnd()()()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("size", "2x");
    i0.ɵɵadvance();
    i0.ɵɵproperty("size", "3x")("icon", "circle");
    i0.ɵɵadvance();
    i0.ɵɵproperty("size", "2x")("icon", "search");
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_1_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 9)(2, "div", 17);
    i0.ɵɵelement(3, "usa-icon", 18);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "div", 13)(5, "h1", 14);
    i0.ɵɵtext(6, "Select Criteria");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(7, "p");
    i0.ɵɵtext(8, "Choose your filters and run your report to begin.");
    i0.ɵɵelementEnd()()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("icon", "arrow-left-circle-fill")("size", "5x");
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_3_ng_container_1_ng_container_1_Template, 16, 5, "ng-container", 1)(2, SdsSearchResultListComponent_ng_container_3_ng_container_1_ng_container_2_Template, 9, 2, "ng-container", 1);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r1.isDefaultModel);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.isDefaultModel);
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_1_ng_container_1_Template, 1, 0, "ng-container", 19);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r1.updateModel == null ? null : ctx_r1.updateModel.metadata == null ? null : ctx_r1.updateModel.metadata.messages);
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_ng_container_1_button_8_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 26);
    i0.ɵɵlistener("click", function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_ng_container_1_button_8_Template_button_click_0_listener() { const button_r5 = i0.ɵɵrestoreView(_r4).$implicit; return i0.ɵɵresetView(button_r5.action()); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const button_r5 = ctx.$implicit;
    i0.ɵɵproperty("ngClass", button_r5.classes ? button_r5.classes : "usa-button--outline");
    i0.ɵɵattribute("aria-label", button_r5.ariaLabel);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", button_r5.text, " ");
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "div", 17);
    i0.ɵɵelement(2, "usa-icon", 18);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 13)(4, "h1", 14);
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(6, "p", 23);
    i0.ɵɵelementStart(7, "div", 24);
    i0.ɵɵtemplate(8, SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_ng_container_1_button_8_Template, 2, 3, "button", 25);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const msg_r6 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("icon", msg_r6.type === "error" ? i0.ɵɵpureFunction0(5, _c3$1) : msg_r6.type === "info" ? "info-circle" : msg_r6.type === "initial" ? "arrow-left-circle-fill" : "search")("size", "5x");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate(msg_r6.title);
    i0.ɵɵadvance();
    i0.ɵɵproperty("innerHTML", msg_r6.message, i0.ɵɵsanitizeHtml);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", msg_r6.buttons);
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 21);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_ng_container_1_Template, 9, 6, "ng-container", 22);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const msg_r6 = ctx.$implicit;
    i0.ɵɵnextContext(4);
    const loading_r7 = i0.ɵɵreference(5);
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(3, _c2$1, msg_r6.type !== "loading" ? "sds-card--vertical" : "", msg_r6.classes ? msg_r6.classes : ""));
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", msg_r6.type !== "loading")("ngIfElse", loading_r7);
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_div_1_Template, 2, 6, "div", 20);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r1.updateModel.metadata.messages);
} }
function SdsSearchResultListComponent_ng_container_3_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_1_Template, 2, 1, "ng-container", 1)(2, SdsSearchResultListComponent_ng_container_3_ng_container_2_ng_container_2_Template, 2, 1, "ng-container", 1);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r1.isTemplate());
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.isTemplate());
} }
function SdsSearchResultListComponent_ng_container_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchResultListComponent_ng_container_3_ng_container_1_Template, 3, 2, "ng-container", 1)(2, SdsSearchResultListComponent_ng_container_3_ng_container_2_Template, 3, 2, "ng-container", 1);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.updateModel == null || !(ctx_r1.updateModel == null ? null : ctx_r1.updateModel.metadata == null ? null : ctx_r1.updateModel.metadata.messages) && (ctx_r1.updateModel == null ? null : ctx_r1.updateModel.results == null ? null : ctx_r1.updateModel.results.length) === 0);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.updateModel == null ? null : ctx_r1.updateModel.metadata == null ? null : ctx_r1.updateModel.metadata.messages);
} }
function SdsSearchResultListComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 27)(1, "div", 28);
    i0.ɵɵelement(2, "div", 29);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 30);
    i0.ɵɵelement(4, "div", 31);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div", 30);
    i0.ɵɵelement(6, "div", 32);
    i0.ɵɵelementEnd()();
} }
class SdsSearchResultListComponent {
    /**
     * Model for search results
     */
    set model(value) {
        if (Array.isArray(value)) {
            const items = value;
            this.updateModel = new SearchModel();
            this.updateModel.results = items;
        }
        else {
            this.updateModel = value;
        }
    }
    constructor(_location) {
        this._location = _location;
        this.updateModel = new SearchModel();
        /**
         * Show divider between results
         */
        this.divider = true;
    }
    goBack() {
        this._location.back();
    }
    isTemplate() {
        if (this.updateModel && this.updateModel.metadata) {
            return Array.isArray(this.updateModel.metadata.messages);
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSearchResultListComponent_Factory(t) { return new (t || SdsSearchResultListComponent)(i0.ɵɵdirectiveInject(i1$2.Location)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSearchResultListComponent, selectors: [["sds-search-result-list"]], contentQueries: function SdsSearchResultListComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, _c0$d, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.resultContentTemplate = _t.first);
        } }, inputs: { customResultsTemplate: "customResultsTemplate", isDefaultModel: "isDefaultModel", model: "model", divider: "divider" }, decls: 6, vars: 3, consts: [["loading", ""], [4, "ngIf"], [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngIf"], ["aria-live", "polite", "aria-atomic", "true"], [4, "ngFor", "ngForOf"], ["class", "fine", 4, "ngIf"], [4, "ngTemplateOutlet", "ngTemplateOutletContext"], [1, "fine"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], [1, "sds-card", "sds-card--vertical", "sds-card--shadowed", "sds-card--bordered", "bg-base-lightest"], [1, "sds-card__header", "padding-3"], ["aria-label", "search results", 3, "size"], [1, "usa-stack-icon", 3, "size", "icon"], [1, "sds-card__body", "margin-2"], [1, "sds-card__title"], [1, "margin-top-3"], [1, "usa-button", "usa-button--outline", 3, "click"], [1, "sds-card__header"], [3, "icon", "size"], [4, "ngTemplateOutlet"], ["class", "sds-card sds-card--shadowed sds-card--bordered", 3, "ngClass", 4, "ngFor", "ngForOf"], [1, "sds-card", "sds-card--shadowed", "sds-card--bordered", 3, "ngClass"], [4, "ngIf", "ngIfElse"], [3, "innerHTML"], [1, "sds-card__buttons"], ["class", "usa-button", 3, "ngClass", "click", 4, "ngFor", "ngForOf"], [1, "usa-button", 3, "click", "ngClass"], [1, "grid-container", "padding-left-0"], [1, "sds-load", "grid-row"], [1, "grid-col-5", "sds-load__title"], [1, "sds-load", "margin-top-1"], [1, "tablet:grid-col-fill", "sds-load__element"], [1, "grid-col-8", "height-4", "sds-load__content"]], template: function SdsSearchResultListComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SdsSearchResultListComponent_ng_container_0_Template, 3, 2, "ng-container", 1)(1, SdsSearchResultListComponent_ng_container_1_Template, 1, 4, "ng-container", 2);
            i0.ɵɵelementStart(2, "div", 3);
            i0.ɵɵtemplate(3, SdsSearchResultListComponent_ng_container_3_Template, 3, 2, "ng-container", 1);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(4, SdsSearchResultListComponent_ng_template_4_Template, 7, 0, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.updateModel == null ? null : ctx.updateModel.results == null ? null : ctx.updateModel.results.length);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.customResultsTemplate && !(ctx.updateModel == null ? null : ctx.updateModel.results == null ? null : ctx.updateModel.results.length));
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", !ctx.customResultsTemplate);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i2$1.IconComponent, i2$1.StackedIconComponent] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSearchResultListComponent, [{
        type: Component,
        args: [{ selector: 'sds-search-result-list', template: "<ng-container *ngIf=\"updateModel?.results?.length\">\n  <div *ngFor=\"let item of updateModel.results\">\n    <hr *ngIf=\"divider\" class=\"fine\" />\n    <ng-container *ngTemplateOutlet=\"resultContentTemplate; context: { $implicit: item }\"></ng-container>\n  </div>\n  <hr *ngIf=\"divider && updateModel?.results?.length\" class=\"fine\" />\n</ng-container>\n\n<ng-container\n  *ngIf=\"customResultsTemplate && !updateModel?.results?.length\"\n  [ngTemplateOutlet]=\"customResultsTemplate\"\n  [ngTemplateOutletContext]=\"{ $implicit: updateModel }\"\n>\n</ng-container>\n\n<!-- <ng-container *ngIf=\"(!updateModel?.metadata?.messages && updateModel?.results?.length === 0)\">\n  <div class=\"sds-card sds-card--vertical sds-card--shadowed sds-card--bordered\">\n    This is initial template\n  </div>\n</ng-container> -->\n<div aria-live=\"polite\" aria-atomic=\"true\">\n  <ng-container *ngIf=\"!customResultsTemplate\">\n    <ng-container\n      *ngIf=\"updateModel == null || (!updateModel?.metadata?.messages && updateModel?.results?.length === 0)\"\n    >\n      <ng-container *ngIf=\"!isDefaultModel\">\n        <div class=\"sds-card sds-card--vertical sds-card--shadowed sds-card--bordered bg-base-lightest\">\n          <div class=\"sds-card__header padding-3\">\n            <usa-stacked-icon [size]=\"'2x'\" aria-label=\"search results\">\n              <usa-icon class=\"usa-stack-icon\" [size]=\"'3x'\" [icon]=\"'circle'\"></usa-icon>\n              <usa-icon class=\"usa-stack-icon\" [size]=\"'2x'\" [icon]=\"'search'\"></usa-icon>\n            </usa-stacked-icon>\n          </div>\n          <div class=\"sds-card__body margin-2\">\n            <h1 class=\"sds-card__title\">No matches found</h1>\n            <p>We couldn't find a match for your search criteria.</p>\n            <p>Please try another search or go back to previous results.</p>\n            <div class=\"margin-top-3\">\n              <button class=\"usa-button usa-button--outline\" (click)=\"goBack()\">Go Back</button>\n            </div>\n          </div>\n        </div>\n      </ng-container>\n      <ng-container *ngIf=\"isDefaultModel\">\n        <div class=\"sds-card sds-card--vertical sds-card--shadowed sds-card--bordered bg-base-lightest\">\n          <div class=\"sds-card__header\">\n            <usa-icon [icon]=\"'arrow-left-circle-fill'\" [size]=\"'5x'\"></usa-icon>\n          </div>\n          <div class=\"sds-card__body margin-2\">\n            <h1 class=\"sds-card__title\">Select Criteria</h1>\n            <p>Choose your filters and run your report to begin.</p>\n          </div>\n        </div>\n      </ng-container>\n    </ng-container>\n\n    <ng-container *ngIf=\"updateModel?.metadata?.messages\">\n      <ng-container *ngIf=\"!isTemplate()\">\n        <ng-container *ngTemplateOutlet=\"updateModel?.metadata?.messages\"></ng-container>\n      </ng-container>\n      <ng-container *ngIf=\"isTemplate()\">\n        <div\n          *ngFor=\"let msg of updateModel.metadata.messages\"\n          class=\"sds-card sds-card--shadowed sds-card--bordered\"\n          [ngClass]=\"[msg.type !== 'loading' ? 'sds-card--vertical' : '', msg.classes ? msg.classes : '']\"\n        >\n          <ng-container *ngIf=\"msg.type !== 'loading'; else loading\">\n            <div class=\"sds-card__header\">\n              <usa-icon\n                [icon]=\"\n                  msg.type === 'error'\n                    ? ['sds', 'alert-error']\n                    : msg.type === 'info'\n                    ? 'info-circle'\n                    : msg.type === 'initial'\n                    ? 'arrow-left-circle-fill'\n                    : 'search'\n                \"\n                [size]=\"'5x'\"\n              >\n              </usa-icon>\n            </div>\n            <div class=\"sds-card__body margin-2\">\n              <h1 class=\"sds-card__title\">{{ msg.title }}</h1>\n              <p [innerHTML]=\"msg.message\"></p>\n              <div class=\"sds-card__buttons\">\n                <button\n                  *ngFor=\"let button of msg.buttons\"\n                  class=\"usa-button\"\n                  [attr.aria-label]=\"button.ariaLabel\"\n                  [ngClass]=\"button.classes ? button.classes : 'usa-button--outline'\"\n                  (click)=\"button.action()\"\n                >\n                  {{ button.text }}\n                </button>\n              </div>\n            </div>\n          </ng-container>\n        </div>\n      </ng-container>\n    </ng-container>\n  </ng-container>\n</div>\n\n<ng-template #loading>\n  <div class=\"grid-container padding-left-0\">\n    <div class=\"sds-load grid-row\">\n      <div class=\"grid-col-5 sds-load__title\"></div>\n    </div>\n    <div class=\"sds-load margin-top-1\">\n      <div class=\"tablet:grid-col-fill sds-load__element\"></div>\n    </div>\n    <div class=\"sds-load margin-top-1\">\n      <div class=\"grid-col-8 height-4 sds-load__content\"></div>\n    </div>\n  </div>\n</ng-template>\n" }]
    }], () => [{ type: i1$2.Location }], { customResultsTemplate: [{
            type: Input
        }], isDefaultModel: [{
            type: Input
        }], model: [{
            type: Input,
            args: ['model']
        }], divider: [{
            type: Input
        }], resultContentTemplate: [{
            type: ContentChild,
            args: ['resultContent']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSearchResultListComponent, { className: "SdsSearchResultListComponent" }); })();

class SdsSearchResultListModule {
    /** @nocollapse */ static { this.ɵfac = function SdsSearchResultListModule_Factory(t) { return new (t || SdsSearchResultListModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsSearchResultListModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule,
            FormsModule,
            IconModule,
            StackedIconModule,
            NgxBootstrapIconsModule.pick({
                search,
                arrowLeftCircleFill,
                infoCircle,
                alertError,
                circle,
            })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSearchResultListModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    IconModule,
                    StackedIconModule,
                    NgxBootstrapIconsModule.pick({
                        search,
                        arrowLeftCircleFill,
                        infoCircle,
                        alertError,
                        circle,
                    }),
                ],
                declarations: [SdsSearchResultListComponent],
                exports: [SdsSearchResultListComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsSearchResultListModule, { declarations: [SdsSearchResultListComponent], imports: [CommonModule,
        FormsModule,
        IconModule,
        StackedIconModule, i1$4.NgxBootstrapIconsModule], exports: [SdsSearchResultListComponent] }); })();

class SideNavigationModel {
}
class NavigationLink {
}

const _c0$c = ["inputEl"];
const _c1$5 = ["selectEl"];
const _c2 = ["buttonEl"];
const _c3 = a0 => ({ "tablet:grid-col": a0 });
const _c4 = (a0, a1) => [a0, a1];
function SdsSearchComponent_label_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "label", 8);
    i0.ɵɵtext(1, "Dropdown label");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵattribute("for", ctx_r0.searchSettings.dropdown.id);
} }
function SdsSearchComponent_select_2_ng_container_2_optgroup_1_option_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 15);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const child_r3 = ctx.$implicit;
    const ctx_r0 = i0.ɵɵnextContext(4);
    i0.ɵɵproperty("value", child_r3.value)("selected", ctx_r0.model.searchCategory == child_r3.value)("disabled", child_r3.disabled);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", child_r3.label, " ");
} }
function SdsSearchComponent_select_2_ng_container_2_optgroup_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "optgroup", 13);
    i0.ɵɵtemplate(1, SdsSearchComponent_select_2_ng_container_2_optgroup_1_option_1_Template, 2, 4, "option", 14);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵpropertyInterpolate("label", item_r4.label);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", item_r4.group);
} }
function SdsSearchComponent_select_2_ng_container_2_option_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 15);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r4 = i0.ɵɵnextContext().$implicit;
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("value", item_r4.value)("selected", ctx_r0.model.searchCategory == item_r4.value)("disabled", item_r4.disabled);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(item_r4.label);
} }
function SdsSearchComponent_select_2_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsSearchComponent_select_2_ng_container_2_optgroup_1_Template, 2, 2, "optgroup", 11)(2, SdsSearchComponent_select_2_ng_container_2_option_2_Template, 2, 4, "option", 12);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const item_r4 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", item_r4.group);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !item_r4.group);
} }
function SdsSearchComponent_select_2_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "select", 9, 1);
    i0.ɵɵlistener("change", function SdsSearchComponent_select_2_Template_select_change_0_listener() { i0.ɵɵrestoreView(_r2); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.writeValueToModel()); });
    i0.ɵɵtemplate(2, SdsSearchComponent_select_2_ng_container_2_Template, 3, 2, "ng-container", 10);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("value", ctx_r0.model.searchCategory ? ctx_r0.model.searchCategory : "");
    i0.ɵɵattribute("id", ctx_r0.searchSettings.dropdown.id);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", ctx_r0.searchSettings.dropdown.options);
} }
function SdsSearchComponent_ng_template_4_span_6_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 22);
    i0.ɵɵlistener("click", function SdsSearchComponent_ng_template_4_span_6_Template_span_click_0_listener() { i0.ɵɵrestoreView(_r6); const ctx_r0 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r0.clearInput()); })("keyup.enter", function SdsSearchComponent_ng_template_4_span_6_Template_span_keyup_enter_0_listener() { i0.ɵɵrestoreView(_r6); const ctx_r0 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r0.clearInput()); });
    i0.ɵɵelement(1, "usa-icon", 23);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "x")("size", ctx_r0.searchSettings.size === "large" ? "1x" : "xs");
} }
function SdsSearchComponent_ng_template_4_span_7_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 24);
    i0.ɵɵlistener("click", function SdsSearchComponent_ng_template_4_span_7_Template_span_click_0_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r0 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r0.handleClick($event)); })("keyup.enter", function SdsSearchComponent_ng_template_4_span_7_Template_span_keyup_enter_0_listener($event) { i0.ɵɵrestoreView(_r7); const ctx_r0 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r0.handleClick($event)); });
    i0.ɵɵelement(1, "usa-icon", 25);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "search")("size", ctx_r0.searchSettings.size === "large" ? "1x" : "xs");
} }
function SdsSearchComponent_ng_template_4_button_8_Template(rf, ctx) { if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 26, 3);
    i0.ɵɵlistener("click", function SdsSearchComponent_ng_template_4_button_8_Template_button_click_0_listener($event) { i0.ɵɵrestoreView(_r8); const ctx_r0 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r0.handleClick($event)); });
    i0.ɵɵelement(2, "usa-icon", 27);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵattribute("aria-label", ctx_r0.searchSettings.ariaLabel ? ctx_r0.searchSettings.ariaLabel : "Search");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("icon", "search");
} }
function SdsSearchComponent_ng_template_4_Template(rf, ctx) { if (rf & 1) {
    const _r5 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 16)(1, "label", 8);
    i0.ɵɵtext(2, "Search");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "input", 17, 2);
    i0.ɵɵlistener("blur", function SdsSearchComponent_ng_template_4_Template_input_blur_3_listener() { i0.ɵɵrestoreView(_r5); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.focusChange()); })("input", function SdsSearchComponent_ng_template_4_Template_input_input_3_listener() { i0.ɵɵrestoreView(_r5); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.writeValueToModel()); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "span", 18);
    i0.ɵɵtemplate(6, SdsSearchComponent_ng_template_4_span_6_Template, 2, 2, "span", 19)(7, SdsSearchComponent_ng_template_4_span_7_Template, 2, 2, "span", 20);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(8, SdsSearchComponent_ng_template_4_button_8_Template, 3, 2, "button", 21);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(11, _c3, ctx_r0.hasDropdown()));
    i0.ɵɵadvance();
    i0.ɵɵattribute("for", ctx_r0.searchSettings.id);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("value", ctx_r0.model.searchText ? ctx_r0.model.searchText : "")("ngClass", i0.ɵɵpureFunction2(13, _c4, (ctx_r0.searchSettings == null ? null : ctx_r0.searchSettings.inputClass) ? ctx_r0.searchSettings == null ? null : ctx_r0.searchSettings.inputClass : "", !ctx_r0.hasDropdown() ? "no-drop" : ""))("placeholder", ctx_r0.searchSettings.placeholder ? ctx_r0.searchSettings.placeholder : "type here");
    i0.ɵɵattribute("id", ctx_r0.searchSettings.id)("aria-label", ctx_r0.searchSettings.ariaLabel);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngClass", ctx_r0.searchSettings.size === "large" ? "right-3 top-2 margin-right-4" : "right-105 top-1");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0.model.searchText);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r0.searchSettings.isSuffixSearchIcon);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", !ctx_r0.searchSettings.isSuffixSearchIcon);
} }
class SearchSettings {
    constructor() {
        this.placeholder = 'Search';
        this.ariaLabel = 'Search';
        this.dropdown = {
            id: 'searchOptions',
            placeholder: '-Select-',
            options: [],
            inverse: false,
        };
        this.isSuffixSearchIcon = false;
    }
}
class SdsSearchComponent {
    constructor(cd, focusMonitor, viewportRuler) {
        this.cd = cd;
        this.focusMonitor = focusMonitor;
        this.viewportRuler = viewportRuler;
        this.searchSettings = new SearchSettings();
        this.submit = new EventEmitter(null);
        this.model = {};
        this.inputState = {
            initial: { visible: undefined },
            visible: undefined,
        };
        this.onChange = (_) => { };
        this.onTouched = () => { };
    }
    ngAfterViewInit() {
        this.inputState.initial.visible = this.isInputVisible();
        this.inputState.visible = this.inputState.initial.visible;
        this.viewportRuler.change(0).subscribe(() => {
            this.inputState.initial.visible = this.isInputVisible();
            this.inputState.visible = this.inputState.initial.visible;
        });
    }
    hasDropdown() {
        if (this.searchSettings &&
            this.searchSettings.dropdown &&
            this.searchSettings.dropdown.options &&
            this.searchSettings.dropdown.options.length) {
            return true;
        }
        else {
            return false;
        }
    }
    handleClick(event) {
        event.preventDefault();
        if (!this.inputState.visible) {
            this.setInputVisibleStyles();
            this.focusMonitor.focusVia(this.inputEl, 'program');
        }
        else if (this.inputEl || this.selectEl) {
            this.submit.emit(this.model);
        }
    }
    writeValueToModel() {
        this.model.searchText = this.inputEl ? this.inputEl.nativeElement.value : '';
        if (this.selectEl && this.selectEl.nativeElement.value) {
            this.model.searchCategory = this.selectEl.nativeElement.value;
        }
        this.onChange(Object.assign({}, this.model));
    }
    writeValue(value) {
        if (value && this.model !== value) {
            this.model = value;
            this.cd.markForCheck();
        }
        else {
            this.model = {};
            this.cd.markForCheck();
        }
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    isInputVisible() {
        return this.inputEl.nativeElement.getBoundingClientRect().width ? true : false;
    }
    setInputVisibleStyles() {
        const inputWidth = this.calculateInputWidth();
        this.inputEl.nativeElement.style.setProperty('display', 'block', 'important');
        this.inputEl.nativeElement.style.position = 'absolute';
        this.inputEl.nativeElement.style.left = `-${inputWidth}px`;
        this.inputEl.nativeElement.style.setProperty('width', `${inputWidth}px`, 'important');
        this.inputState.visible = true;
    }
    removeInputVisibleStyles() {
        this.inputEl.nativeElement.style.display = '';
        this.inputEl.nativeElement.style.position = '';
        this.inputEl.nativeElement.style.left = '';
        this.inputEl.nativeElement.style.width = '';
        this.inputState.visible = false;
    }
    focusChange() {
        if (!this.inputState.initial.visible) {
            this.removeInputVisibleStyles();
        }
    }
    calculateInputWidth() {
        const leftPadding = 20;
        const buttonElement = this.buttonEl.nativeElement;
        const inputElement = this.inputEl.nativeElement;
        const rightPosition = buttonElement.getBoundingClientRect().left;
        const leftPosition = this.searchSettings.parentSelector
            ? inputElement.closest(this.searchSettings.parentSelector).getBoundingClientRect().left
            : 0;
        return Math.floor(rightPosition - leftPosition - leftPadding);
    }
    getClass() {
        const cls = this.searchSettings && this.searchSettings.size === 'large' ? 'usa-search--big' : 'usa-search--small';
        return this.searchSettings.dropdown && this.searchSettings.dropdown.inverse ? `${cls} sds-inverse` : cls;
    }
    clearInput() {
        this.inputEl.nativeElement.value = '';
        this.inputEl.nativeElement.focus();
        this.writeValueToModel();
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSearchComponent_Factory(t) { return new (t || SdsSearchComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$1.FocusMonitor), i0.ɵɵdirectiveInject(i1$3.ViewportRuler)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSearchComponent, selectors: [["sds-search"]], viewQuery: function SdsSearchComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$c, 5, ElementRef);
            i0.ɵɵviewQuery(_c1$5, 5, ElementRef);
            i0.ɵɵviewQuery(_c2, 5, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputEl = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.selectEl = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.buttonEl = _t.first);
        } }, inputs: { searchSettings: "searchSettings" }, outputs: { submit: "submit" }, features: [i0.ɵɵProvidersFeature([
                {
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef((() => SdsSearchComponent)),
                    multi: true,
                },
            ])], decls: 6, vars: 4, consts: [["inputTemplate", ""], ["selectEl", ""], ["inputEl", ""], ["buttonEl", ""], ["role", "search", 1, "usa-form", "usa-search", "grid-row", 3, "ngClass"], ["class", "usa-sr-only", 4, "ngIf"], ["name", "search options", "aria-label", "Search Options", "class", "usa-select grid-col-12 tablet:grid-col-auto", 3, "value", "change", 4, "ngIf"], [3, "ngTemplateOutlet"], [1, "usa-sr-only"], ["name", "search options", "aria-label", "Search Options", 1, "usa-select", "grid-col-12", "tablet:grid-col-auto", 3, "change", "value"], [4, "ngFor", "ngForOf"], [3, "label", 4, "ngIf"], [3, "value", "selected", "disabled", 4, "ngIf"], [3, "label"], [3, "value", "selected", "disabled", 4, "ngFor", "ngForOf"], [3, "value", "selected", "disabled"], [1, "position-relative", "grid-col-12", "suffix-icon", 3, "ngClass"], ["type", "search", "name", "search", 1, "usa-input", "sds-min-width-160", 3, "blur", "input", "value", "ngClass", "placeholder"], [1, "cursor-pointer", "usa-input-suffix", 3, "ngClass"], ["class", "postfix close-icon", "role", "button", "aria-label", "Clear input", "tabindex", "0", 3, "click", "keyup.enter", 4, "ngIf"], ["class", "postfix right-0", "role", "button", "aria-label", "search input", "tabindex", "0", 3, "click", "keyup.enter", 4, "ngIf"], ["class", "usa-button", "type", "submit", 3, "click", 4, "ngIf"], ["role", "button", "aria-label", "Clear input", "tabindex", "0", 1, "postfix", "close-icon", 3, "click", "keyup.enter"], [1, "text-ink", 3, "icon", "size"], ["role", "button", "aria-label", "search input", "tabindex", "0", 1, "postfix", "right-0", 3, "click", "keyup.enter"], [3, "icon", "size"], ["type", "submit", 1, "usa-button", 3, "click"], [1, "text-white", 3, "icon"]], template: function SdsSearchComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "form", 4);
            i0.ɵɵtemplate(1, SdsSearchComponent_label_1_Template, 2, 1, "label", 5)(2, SdsSearchComponent_select_2_Template, 3, 3, "select", 6);
            i0.ɵɵelementContainer(3, 7);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(4, SdsSearchComponent_ng_template_4_Template, 9, 16, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            const inputTemplate_r9 = i0.ɵɵreference(5);
            i0.ɵɵproperty("ngClass", ctx.getClass());
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.hasDropdown());
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.hasDropdown());
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngTemplateOutlet", inputTemplate_r9);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i2$2.ɵNgNoValidate, i2$2.NgSelectOption, i2$2.ɵNgSelectMultipleOption, i2$2.NgControlStatusGroup, i2$2.NgForm, i2$1.IconComponent], styles: ["input[_ngcontent-%COMP%]::-ms-clear{display:none}.suffix-icon[_ngcontent-%COMP%]   input[_ngcontent-%COMP%]::-webkit-search-cancel-button{display:none}.suffix-icon[_ngcontent-%COMP%]   .postfix[_ngcontent-%COMP%] ~ input[_ngcontent-%COMP%]{width:80%;width:calc(100% - 8rem);margin-left:0;margin-right:6rem!important}.suffix-icon[_ngcontent-%COMP%]   .postfix[_ngcontent-%COMP%]{color:#3f57a6;position:absolute;font-size:2rem;-webkit-transition:color .2s;transition:color .2s;margin-bottom:2px}.suffix-icon[_ngcontent-%COMP%]   .close-icon[_ngcontent-%COMP%]{right:36px}.no-drop[_ngcontent-%COMP%]{width:100%}@media only screen and (min-width: 480px){.usa-form.usa-search[_ngcontent-%COMP%]   select[_ngcontent-%COMP%]{max-width:unset}}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSearchComponent, [{
        type: Component,
        args: [{ selector: 'sds-search', providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef((() => SdsSearchComponent)),
                        multi: true,
                    },
                ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<form class=\"usa-form usa-search grid-row\" [ngClass]=\"getClass()\" role=\"search\">\n  <label *ngIf=\"hasDropdown()\" class=\"usa-sr-only\" [attr.for]=\"searchSettings.dropdown.id\">Dropdown label</label>\n  <select\n    *ngIf=\"hasDropdown()\"\n    [value]=\"model.searchCategory ? model.searchCategory : ''\"\n    #selectEl\n    name=\"search options\"\n    aria-label=\"Search Options\"\n    class=\"usa-select grid-col-12 tablet:grid-col-auto\"\n    [attr.id]=\"searchSettings.dropdown.id\"\n    (change)=\"writeValueToModel()\"\n  >\n    <ng-container *ngFor=\"let item of searchSettings.dropdown.options\">\n      <optgroup *ngIf=\"item.group\" label=\"{{ item.label }}\">\n        <option\n          *ngFor=\"let child of item.group\"\n          [value]=\"child.value\"\n          [selected]=\"model.searchCategory == child.value\"\n          [disabled]=\"child.disabled\"\n        >\n          {{ child.label }}\n        </option>\n      </optgroup>\n      <option\n        *ngIf=\"!item.group\"\n        [value]=\"item.value\"\n        [selected]=\"model.searchCategory == item.value\"\n        [disabled]=\"item.disabled\"\n        >{{ item.label }}</option\n      >\n    </ng-container>\n  </select>\n  <ng-container [ngTemplateOutlet]=\"inputTemplate\"></ng-container>\n</form>\n\n<ng-template #inputTemplate>\n  <div\n    class=\"position-relative grid-col-12 suffix-icon\"\n    [ngClass]=\"{\n      'tablet:grid-col': hasDropdown()\n    }\"\n  >\n    <label class=\"usa-sr-only\" [attr.for]=\"searchSettings.id\">Search</label>\n    <input\n      #inputEl\n      [value]=\"model.searchText ? model.searchText : ''\"\n      [ngClass]=\"[searchSettings?.inputClass ? searchSettings?.inputClass : '', !hasDropdown() ? 'no-drop' : '']\"\n      [attr.id]=\"searchSettings.id\"\n      type=\"search\"\n      class=\"usa-input sds-min-width-160\"\n      name=\"search\"\n      [placeholder]=\"searchSettings.placeholder ? searchSettings.placeholder : 'type here'\"\n      [attr.aria-label]=\"searchSettings.ariaLabel\"\n      (blur)=\"focusChange()\"\n      (input)=\"writeValueToModel()\"\n    />\n\n    <span\n      class=\"cursor-pointer usa-input-suffix\"\n      [ngClass]=\"searchSettings.size === 'large' ? 'right-3 top-2 margin-right-4' : 'right-105 top-1'\"\n    >\n      <span\n        *ngIf=\"model.searchText\"\n        class=\"postfix close-icon\"\n        role=\"button\"\n        aria-label=\"Clear input\"\n        (click)=\"clearInput()\"\n        (keyup.enter)=\"clearInput()\"\n        tabindex=\"0\"\n      >\n        <usa-icon [icon]=\"'x'\" class=\"text-ink\" [size]=\"searchSettings.size === 'large' ? '1x' : 'xs'\"></usa-icon>\n      </span>\n      <span\n        *ngIf=\"searchSettings.isSuffixSearchIcon\"\n        class=\"postfix right-0\"\n        role=\"button\"\n        aria-label=\"search input\"\n        (click)=\"handleClick($event)\"\n        (keyup.enter)=\"handleClick($event)\"\n        tabindex=\"0\"\n      >\n        <usa-icon [icon]=\"'search'\" [size]=\"searchSettings.size === 'large' ? '1x' : 'xs'\"> </usa-icon>\n      </span>\n    </span>\n    <button\n      *ngIf=\"!searchSettings.isSuffixSearchIcon\"\n      #buttonEl\n      class=\"usa-button\"\n      type=\"submit\"\n      [attr.aria-label]=\"searchSettings.ariaLabel ? searchSettings.ariaLabel : 'Search'\"\n      (click)=\"handleClick($event)\"\n    >\n      <usa-icon class=\"text-white\" [icon]=\"'search'\"></usa-icon>\n    </button>\n  </div>\n</ng-template>\n", styles: ["input::-ms-clear{display:none}.suffix-icon input::-webkit-search-cancel-button{display:none}.suffix-icon .postfix~input{width:80%;width:calc(100% - 8rem);margin-left:0;margin-right:6rem!important}.suffix-icon .postfix{color:#3f57a6;position:absolute;font-size:2rem;-webkit-transition:color .2s;transition:color .2s;margin-bottom:2px}.suffix-icon .close-icon{right:36px}.no-drop{width:100%}@media only screen and (min-width: 480px){.usa-form.usa-search select{max-width:unset}}\n"] }]
    }], () => [{ type: i0.ChangeDetectorRef }, { type: i1$1.FocusMonitor }, { type: i1$3.ViewportRuler }], { inputEl: [{
            type: ViewChild,
            args: ['inputEl', { read: ElementRef, static: false }]
        }], selectEl: [{
            type: ViewChild,
            args: ['selectEl', { read: ElementRef, static: false }]
        }], buttonEl: [{
            type: ViewChild,
            args: ['buttonEl', { read: ElementRef, static: false }]
        }], searchSettings: [{
            type: Input
        }], submit: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSearchComponent, { className: "SdsSearchComponent" }); })();

class SdsSearchModule {
    /** @nocollapse */ static { this.ɵfac = function SdsSearchModule_Factory(t) { return new (t || SdsSearchModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsSearchModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, A11yModule, FormsModule, IconModule, NgxBootstrapIconsModule.pick({ x, search })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSearchModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, A11yModule, FormsModule, IconModule, NgxBootstrapIconsModule.pick({ x, search })],
                exports: [SdsSearchComponent],
                declarations: [SdsSearchComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsSearchModule, { declarations: [SdsSearchComponent], imports: [CommonModule, A11yModule, FormsModule, IconModule, i1$4.NgxBootstrapIconsModule], exports: [SdsSearchComponent] }); })();

/**
 * Directive that triggers a callback whenever the width of
 * its associated element has changed.
 */
class SdsObserveWidthDirective {
    constructor(_elementRef, viewportRuler) {
        this._elementRef = _elementRef;
        this.viewportRuler = viewportRuler;
        /** Event emitted for each change in the element's width. */
        this.elementWidth = new EventEmitter();
    }
    ngOnInit() {
        this.windowResize$ = this.viewportRuler
            .change(0)
            .pipe(startWith(this._getElementWidth()))
            .subscribe(() => this._emitEvent());
    }
    ngOnDestroy() {
        this.windowResize$.unsubscribe();
    }
    _emitEvent() {
        const width = this._getElementWidth();
        this.elementWidth.emit(width);
    }
    _getElementWidth() {
        return this._elementRef.nativeElement.offsetWidth;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsObserveWidthDirective_Factory(t) { return new (t || SdsObserveWidthDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$3.ViewportRuler)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsObserveWidthDirective, selectors: [["", "sdsObserveWidth", ""]], outputs: { elementWidth: "sdsObserveWidth" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsObserveWidthDirective, [{
        type: Directive,
        args: [{ selector: '[sdsObserveWidth]' }]
    }], () => [{ type: i0.ElementRef }, { type: i1$3.ViewportRuler }], { elementWidth: [{
            type: Output,
            args: ['sdsObserveWidth']
        }] }); })();

class SdsObserversModule {
    /** @nocollapse */ static { this.ɵfac = function SdsObserversModule_Factory(t) { return new (t || SdsObserversModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsObserversModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsObserversModule, [{
        type: NgModule,
        args: [{
                imports: [],
                exports: [SdsObserveWidthDirective],
                declarations: [SdsObserveWidthDirective],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsObserversModule, { declarations: [SdsObserveWidthDirective], exports: [SdsObserveWidthDirective] }); })();

/**
 * Used primarily to avoid circular imports between `SdsAccordion` and `SdsAccordionItem`.
 */
const SDS_TRUNCATED_TEXT_DATA = new InjectionToken('SdsTruncatedTextData');

const sdsTruncateTextAnimations = {
    container: trigger('container', [
        state('void', style({
            opacity: 0,
            transform: 'scale(0.8)',
        })),
        transition('void => enter', group([
            query('.sds-overlay', animate('100ms linear', style({
                opacity: 1,
            }))),
            animate('120ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'scale(1)' })),
        ])),
        transition('* => void', animate('100ms 25ms linear', style({ opacity: 0 }))),
    ]),
};

class SdsTruncatedTextContainerComponent {
    constructor(data) {
        this.data = data;
        this._animationState = 'void';
    }
    /** Starts the animation. */
    startAnimation() {
        this._animationState = 'enter';
    }
    /** Resets the animation to its initial state. */
    resetAnimation() {
        this._animationState = 'void';
    }
    /** Intentionally left empty to trigger change detection */
    _onAnimationDone() { }
    /** @nocollapse */ static { this.ɵfac = function SdsTruncatedTextContainerComponent_Factory(t) { return new (t || SdsTruncatedTextContainerComponent)(i0.ɵɵdirectiveInject(SDS_TRUNCATED_TEXT_DATA)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsTruncatedTextContainerComponent, selectors: [["sds-truncated-text-container"]], hostVars: 1, hostBindings: function SdsTruncatedTextContainerComponent_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵsyntheticHostListener("@container.done", function SdsTruncatedTextContainerComponent_animation_container_done_HostBindingHandler() { return ctx._onAnimationDone(); });
        } if (rf & 2) {
            i0.ɵɵsyntheticHostProperty("@container", ctx._animationState);
        } }, decls: 2, vars: 1, consts: [[1, "sds-overlay", "maxw-mobile", "radius-overlay", "padding-2"]], template: function SdsTruncatedTextContainerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵtext(1);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵtextInterpolate(ctx.data.text);
        } }, encapsulation: 2, data: { animation: [sdsTruncateTextAnimations.container] } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTruncatedTextContainerComponent, [{
        type: Component,
        args: [{
                selector: 'sds-truncated-text-container',
                template: ` <div class="sds-overlay maxw-mobile radius-overlay padding-2">{{ data.text }}</div> `,
                animations: [sdsTruncateTextAnimations.container],
            }]
    }], () => [{ type: undefined, decorators: [{
                type: Inject,
                args: [SDS_TRUNCATED_TEXT_DATA]
            }] }], { _animationState: [{
            type: HostBinding,
            args: ['@container']
        }], _onAnimationDone: [{
            type: HostListener,
            args: ['@container.done']
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsTruncatedTextContainerComponent, { className: "SdsTruncatedTextContainerComponent" }); })();

class SdsTruncateTextByLineDirective {
    /** Maximum lines of text limit */
    get textLinesLimit() {
        return this._textLinesLimit;
    }
    set textLinesLimit(_textLinesLimit) {
        _textLinesLimit = coerceNumberProperty(_textLinesLimit);
        if (this._textLinesLimit !== _textLinesLimit) {
            this._textLinesLimit = _textLinesLimit;
        }
    }
    constructor(_overlay, _injector, _element, _viewportRuler, _viewContainerRef) {
        this._overlay = _overlay;
        this._injector = _injector;
        this._element = _element;
        this._viewportRuler = _viewportRuler;
        this._viewContainerRef = _viewContainerRef;
        /** PortalOutlet */
        this._overlayRef = null;
        /** Holds subscription to stream of overlay closing events */
        this._closingActionsSubscription = Subscription.EMPTY;
    }
    ngOnInit() {
        this.initialText = this._element.nativeElement.innerText.trim();
        // Clone element to facilitate calculations
        const hostCloneEl = this._element.nativeElement.cloneNode();
        // Add 1 character to calculate character width
        hostCloneEl.innerHTML = 'x';
        // Render the clone to get character width
        this._element.nativeElement.parentElement.appendChild(hostCloneEl);
        // Set the clone to inline to prevent cases where the clone
        // expands to 100% width of the container
        hostCloneEl.setAttribute('style', 'display: inline');
        // These are close approximations that are used to better guess
        // how many characters fit in X number of lines
        this.approximatedCharacterWidth = hostCloneEl.offsetWidth;
        // Remove clone after calculations
        hostCloneEl.remove();
    }
    ngAfterViewInit() {
        this.windowResize$ = this._viewportRuler
            .change(0)
            .pipe(startWith('Start'))
            .subscribe(() => this.updateUI());
    }
    ngOnDestroy() {
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
        this._closingActionsSubscription.unsubscribe();
        this.windowResize$.unsubscribe();
    }
    /** Configures and creates the CDK overlay */
    _createOverlay() {
        const overlayPositions = {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
        };
        const config = new OverlayConfig({
            positionStrategy: this._overlay
                .position()
                .flexibleConnectedTo(this._element)
                .withLockedPosition()
                .withPositions([overlayPositions])
                .withTransformOriginOn('.sds-overlay'),
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
            scrollStrategy: this._overlay.scrollStrategies.close(),
        });
        return this._overlay.create(config);
    }
    /** Attach a ComponentPortal to the overlay **/
    _attachContainer(overlay) {
        const injector = new PortalInjector(this._injector, new WeakMap([[SDS_TRUNCATED_TEXT_DATA, { text: this.initialText }]]));
        const containerPortal = new ComponentPortal(SdsTruncatedTextContainerComponent, this._viewContainerRef, injector);
        const containerRef = overlay.attach(containerPortal);
        return containerRef.instance;
    }
    /** Returns a stream that emits whenever an action that should close the overlay occurs. */
    _overlayClosingActions() {
        const backdrop = this._overlayRef.backdropClick();
        const detachments = this._overlayRef.detachments();
        return merge(backdrop, detachments);
    }
    /** Width of host element */
    _getHostWidth() {
        return this._element.nativeElement.offsetWidth;
    }
    /** Approximated number of characters that are visible in the container */
    _getVisibleCharacters() {
        return Math.floor((this._getHostWidth() / this.approximatedCharacterWidth) * this.textLinesLimit);
    }
    _isNotLongEnough() {
        return this._getVisibleCharacters() > this.initialText.length;
    }
    openOverlay() {
        // Exit if all text can be visible in container
        if (this._isNotLongEnough())
            return;
        this._overlayRef = this._createOverlay();
        const container = this._attachContainer(this._overlayRef);
        this._closingActionsSubscription = this._overlayClosingActions().subscribe(() => this.closeOverlay());
        // Wait for the next event loop tick to start the animation
        setTimeout(() => {
            container.startAnimation();
        });
    }
    updateUI() {
        // Exit if all text can be visible in container
        if (this._isNotLongEnough())
            return;
        const wordCut = false;
        const ellipsis = '...';
        const limit = this._getVisibleCharacters() - ellipsis.length;
        let visibleText = this.initialText.slice(0, limit);
        if (!wordCut) {
            const isEndofWord = this.initialText.substr(limit, limit + 1) === ' ';
            if (!isEndofWord) {
                const previousWord = visibleText.lastIndexOf(' ');
                visibleText = visibleText.slice(0, previousWord);
            }
        }
        this._element.nativeElement.innerText = visibleText + ellipsis;
    }
    closeOverlay() {
        this._closingActionsSubscription.unsubscribe();
        this._overlayRef.detach();
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTruncateTextByLineDirective_Factory(t) { return new (t || SdsTruncateTextByLineDirective)(i0.ɵɵdirectiveInject(i1$3.Overlay), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1$3.ViewportRuler), i0.ɵɵdirectiveInject(i0.ViewContainerRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsTruncateTextByLineDirective, selectors: [["", "sdsTruncateTextByLine", ""]], hostBindings: function SdsTruncateTextByLineDirective_HostBindings(rf, ctx) { if (rf & 1) {
            i0.ɵɵlistener("click", function SdsTruncateTextByLineDirective_click_HostBindingHandler() { return ctx.openOverlay(); });
        } }, inputs: { textLinesLimit: [i0.ɵɵInputFlags.None, "sdsTruncateTextByLine", "textLinesLimit"] } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTruncateTextByLineDirective, [{
        type: Directive,
        args: [{ selector: '[sdsTruncateTextByLine]' }]
    }], () => [{ type: i1$3.Overlay }, { type: i0.Injector }, { type: i0.ElementRef }, { type: i1$3.ViewportRuler }, { type: i0.ViewContainerRef }], { textLinesLimit: [{
            type: Input,
            args: ['sdsTruncateTextByLine']
        }], openOverlay: [{
            type: HostListener,
            args: ['click']
        }] }); })();

class SdsTruncateModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTruncateModule_Factory(t) { return new (t || SdsTruncateModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTruncateModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTruncateModule, [{
        type: NgModule,
        args: [{
                imports: [],
                exports: [SdsTruncateTextByLineDirective],
                declarations: [SdsTruncateTextByLineDirective, SdsTruncatedTextContainerComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTruncateModule, { declarations: [SdsTruncateTextByLineDirective, SdsTruncatedTextContainerComponent], exports: [SdsTruncateTextByLineDirective] }); })();

/**
 * Define expiration date for a given element. If current date is
 * past expiration date, the given element will not be displayed
 * to the user
 * usage:
 * <div *expires="'2021-01-13T22:30:59">
 * </div>
 * The element above expires after January 13 2021 10:30:59pm at user's local timezone.
 *
 * You may also define desired timezone in following format:
 * YYYY-MM-DDThh:mm:ss.sssZ
 * example: 2021:01013T22:30:59.000+5:00 // Eastern time
 */
class SdsExpiresDirective {
    /**
     * Expiration date string. This string should be parsable
     * through native Date object
     * */
    set expires(date) {
        const expirationDate = new Date(date);
        const today = new Date();
        const isExpired = expirationDate.valueOf() < today.valueOf();
        if (this.initialized) {
            this.viewContainer.clear();
        }
        if (isExpired) {
            this.viewContainer.clear();
        }
        else {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
        this.initialized = true;
    }
    constructor(templateRef, viewContainer) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        // Track to allow expires to be updated without creating multiple instances of expiring element
        this.initialized = false;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsExpiresDirective_Factory(t) { return new (t || SdsExpiresDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsExpiresDirective, selectors: [["", "expires", ""]], inputs: { expires: "expires" } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsExpiresDirective, [{
        type: Directive,
        args: [{
                selector: '[expires]',
            }]
    }], () => [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }], { expires: [{
            type: Input
        }] }); })();

class SdsExpiresModule {
    /** @nocollapse */ static { this.ɵfac = function SdsExpiresModule_Factory(t) { return new (t || SdsExpiresModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsExpiresModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsExpiresModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                declarations: [SdsExpiresDirective],
                exports: [SdsExpiresDirective],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsExpiresModule, { declarations: [SdsExpiresDirective], imports: [CommonModule], exports: [SdsExpiresDirective] }); })();

class ExternalLinkDirective {
    constructor(platformId, el, vc, renderer) {
        this.platformId = platformId;
        this.el = el;
        this.vc = vc;
        this.renderer = renderer;
        this.relAttr = '';
        this.targetAttr = '';
        this.hrefAttr = '';
        this.hideIcon = false;
        /** Treat these domains as internal links */
        this.internalLinks = [
        /** 'fsd.gov' - Removed until fsd.gov contains proper route back to sam.gov */
        ];
        this.emailLinkKeyword = 'mailto';
    }
    ngOnChanges() {
        this.hrefAttr = this.href;
        this.targetAttr = this.target;
        if (!this.isExternalLink) {
            return;
        }
        this.relAttr = 'noopener';
        this.targetAttr = '_blank';
        const ariaLabel = this._getAriaLabel();
        this.el.nativeElement.setAttribute('aria-label', ariaLabel);
        if (!this.hideIcon && !this.iconVisible) {
            this.createIcon();
        }
        else if (this.hideIcon && this.iconVisible) {
            this.removeIcon();
        }
    }
    /**
     * Appends indication that the link will open in a separate window to the aria label.
     * If link does not contain any aria label, then an aria label will be generated using either the inner text
     *  or href value based on whether the anchor element contains children elements or not
     * If link contains aria label, but the label does not contain key words 'new' or 'window',
     *  then the text 'opens in a new window' will be appended to the end of the aria label
     * If link contains aria label as well as the key words 'new' and 'window', then aria label will
     *  be kept as is
     */
    _getAriaLabel() {
        const anchorElement = this.el.nativeElement;
        const currentAriaLabel = anchorElement.getAttribute('aria-label');
        /** No aria label, attach a default one using inner text if anchor does not contain additional
         * html element as children. If anchor does contain additional html element as children, then use href
         */
        if (!currentAriaLabel || currentAriaLabel.length === 0) {
            let label = anchorElement.firstElementChild ? this.href : anchorElement.innerText;
            label = label.trim();
            return `Open ${label} in a new window`;
        }
        const lowerCaseAriaLabel = currentAriaLabel.toLowerCase();
        /** Aria label already indicates link will open in a new window, set to defined aria label */
        if (lowerCaseAriaLabel.indexOf('new') != -1 && lowerCaseAriaLabel.indexOf('window') != -1) {
            return currentAriaLabel;
        }
        /** Aria label is attached, but does not indicate link will open in new window.
           Add opens in new window keyword to aria label */
        return `${currentAriaLabel} - opens in a new window`;
    }
    get isExternalLink() {
        const link = this.href
            .replace(/^https?:\/\//, '')
            .replace(/^www\./, '')
            .split('/')[0];
        return (isPlatformBrowser(this.platformId) &&
            !link.includes(location.hostname) &&
            link.indexOf(this.emailLinkKeyword) !== 0 &&
            !this.internalLinks.includes(link));
    }
    get iconVisible() {
        return this.el.nativeElement.classList.contains('usa-link--external');
    }
    createIcon() {
        this.el.nativeElement.classList.add('usa-link--external');
        this.el.nativeElement.classList.add('display-inline-block');
    }
    removeIcon() {
        this.renderer.removeClass(this.el.nativeElement, 'usa-link--external');
        this.renderer.removeClass(this.el.nativeElement, 'display-inline-block');
    }
    /** @nocollapse */ static { this.ɵfac = function ExternalLinkDirective_Factory(t) { return new (t || ExternalLinkDirective)(i0.ɵɵdirectiveInject(PLATFORM_ID), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: ExternalLinkDirective, selectors: [["a", "href", ""]], hostVars: 3, hostBindings: function ExternalLinkDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵattribute("rel", ctx.relAttr)("target", ctx.targetAttr)("href", ctx.hrefAttr, i0.ɵɵsanitizeUrl);
        } }, inputs: { href: "href", target: "target", hideIcon: "hideIcon" }, features: [i0.ɵɵNgOnChangesFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ExternalLinkDirective, [{
        type: Directive,
        args: [{
                selector: 'a[href]',
            }]
    }], () => [{ type: undefined, decorators: [{
                type: Inject,
                args: [PLATFORM_ID]
            }] }, { type: i0.ElementRef }, { type: i0.ViewContainerRef }, { type: i0.Renderer2 }], { relAttr: [{
            type: HostBinding,
            args: ['attr.rel']
        }], targetAttr: [{
            type: HostBinding,
            args: ['attr.target']
        }], hrefAttr: [{
            type: HostBinding,
            args: ['attr.href']
        }], href: [{
            type: Input
        }], target: [{
            type: Input
        }], hideIcon: [{
            type: Input
        }] }); })();

class SdsExternalLinkDirectivesModule {
    /** @nocollapse */ static { this.ɵfac = function SdsExternalLinkDirectivesModule_Factory(t) { return new (t || SdsExternalLinkDirectivesModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsExternalLinkDirectivesModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, IconModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsExternalLinkDirectivesModule, [{
        type: NgModule,
        args: [{
                declarations: [ExternalLinkDirective],
                imports: [CommonModule, IconModule],
                exports: [ExternalLinkDirective],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsExternalLinkDirectivesModule, { declarations: [ExternalLinkDirective], imports: [CommonModule, IconModule], exports: [ExternalLinkDirective] }); })();

const _c0$b = ["videoWrapper"];
const _c1$4 = ["video"];
function SdsVideoPlayerComponent_source_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "source", 10);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵpropertyInterpolate("src", ctx_r0.VPConfiguration.sourceMp4, i0.ɵɵsanitizeUrl);
} }
function SdsVideoPlayerComponent_source_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "source", 11);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵpropertyInterpolate("src", ctx_r0.VPConfiguration.sourceWebm, i0.ɵɵsanitizeUrl);
} }
class SdsVideoPlayerComponent {
    constructor(elementRef, renderer2, cdr, document) {
        this.elementRef = elementRef;
        this.renderer2 = renderer2;
        this.cdr = cdr;
        this.document = document;
        this.crossorigin = '';
        this.loadVideoSource = false;
    }
    ngOnDestroy() {
        let element = this.document.getElementById('px-video-aria-announce');
        if (element) {
            element.remove();
        }
    }
    ngOnInit() {
        if (this.VPConfiguration.preload != 'none') {
            this.loadVideoSource = true;
        }
    }
    ngAfterViewInit() {
        if (this.crossorigin) {
            const id = this.video.nativeElement;
            id.setAttribute('crossorigin', this.crossorigin);
        }
        this.config = {
            videoId: this.VPConfiguration.id,
            captionsOnDefault: false,
            seekInterval: this.VPConfiguration.seekInterval,
            videoTitle: 'Video Player',
            debug: this.VPConfiguration.debug,
        };
        const video = new InitPxVideo(this.config); // Required to ensure that browser controls are replaced with custom controls
        this.videoWrapper.nativeElement.setAttribute('style', 'width:' + this.VPConfiguration.width + ';');
        const progressElement = this.elementRef.nativeElement.querySelector('progress');
        if (progressElement) {
            this.renderer2.setAttribute(progressElement, 'aria-label', this.VPConfiguration.description + ' progress bar');
        }
        if (this.VPConfiguration.preload === 'none') {
            this._loadVideoSourceOnDemand();
        }
    }
    ngOnChanges(changes) {
        if (this.video && changes && changes.crossorigin) {
            const id = this.video.nativeElement;
            if (id) {
                id.setAttribute('crossorigin', this.crossorigin);
            }
        }
    }
    /**
     * IE and Edge ignore preload attribute and load video data eagerly. In order to
     * workaround those such browsers, we add video source only after user clicks
     * on play or restart button of the video.
     */
    _loadVideoSourceOnDemand() {
        const playButton = this.elementRef.nativeElement.querySelector('.px-video-play');
        const restartButton = this.elementRef.nativeElement.querySelector('.px-video-restart');
        const video = this.video.nativeElement;
        const loadVideo = ($event) => {
            if (this.loadVideoSource) {
                return;
            }
            this.loadVideoSource = true;
            // Due to event handler timing issues in safari, the browser does not load the source
            // when play and source are set at the same time. So we first set the source, wait for
            // an event loop, pause, then play the video to trigger source loading
            setTimeout(() => {
                video.pause();
                video.play();
            });
        };
        if (!playButton || !restartButton) {
            // Edge case - if the button to toggle video source does not exist in dom, then add in the
            // video source and let the browser decide when to fetch video data
            this.loadVideoSource = true;
        }
        else {
            playButton.onclick = loadVideo;
            restartButton.onclick = loadVideo;
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsVideoPlayerComponent_Factory(t) { return new (t || SdsVideoPlayerComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(DOCUMENT)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsVideoPlayerComponent, selectors: [["sds-video-player"]], viewQuery: function SdsVideoPlayerComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$b, 5);
            i0.ɵɵviewQuery(_c1$4, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.videoWrapper = _t.first);
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.video = _t.first);
        } }, inputs: { VPConfiguration: "VPConfiguration", crossorigin: "crossorigin" }, features: [i0.ɵɵNgOnChangesFeature], decls: 10, vars: 14, consts: [["videoWrapper", ""], ["video", ""], [1, "px-video-container", 3, "id"], [1, "px-video-img-captions-container"], ["aria-hidden", "true", 1, "px-video-captions", "hide"], ["controls", "", 3, "id", "poster", "preload"], ["type", "video/mp4", 3, "src", 4, "ngIf"], ["type", "video/webm", 3, "src", 4, "ngIf"], ["kind", "subtitles", "kind", "captions", "label", "English captions", "srclang", "en", "default", "", 3, "src"], [1, "px-video-controls"], ["type", "video/mp4", 3, "src"], ["type", "video/webm", 3, "src"]], template: function SdsVideoPlayerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 2, 0)(2, "div", 3);
            i0.ɵɵelement(3, "div", 4);
            i0.ɵɵelementStart(4, "video", 5, 1);
            i0.ɵɵtemplate(6, SdsVideoPlayerComponent_source_6_Template, 1, 1, "source", 6)(7, SdsVideoPlayerComponent_source_7_Template, 1, 1, "source", 7);
            i0.ɵɵelement(8, "track", 8);
            i0.ɵɵelementEnd()();
            i0.ɵɵelement(9, "div", 9);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵstyleProp("width", ctx.VPConfiguration.width);
            i0.ɵɵpropertyInterpolate("id", ctx.VPConfiguration.id);
            i0.ɵɵadvance(4);
            i0.ɵɵstyleProp("width", ctx.VPConfiguration.width)("height", ctx.VPConfiguration.height);
            i0.ɵɵpropertyInterpolate("poster", ctx.VPConfiguration.poster, i0.ɵɵsanitizeUrl);
            i0.ɵɵpropertyInterpolate("preload", ctx.VPConfiguration.preload);
            i0.ɵɵproperty("id", "videoPlayer" + ctx.VPConfiguration.id);
            i0.ɵɵattribute("aria-label", ctx.VPConfiguration.description);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngIf", ctx.loadVideoSource);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.loadVideoSource);
            i0.ɵɵadvance();
            i0.ɵɵpropertyInterpolate("src", ctx.VPConfiguration.caption, i0.ɵɵsanitizeUrl);
        } }, dependencies: [i1$2.NgIf], styles: [".pull-left{float:left}.sr-only{position:absolute!important;clip:rect(1px,1px,1px,1px);padding:0!important;border:0!important;height:1px!important;width:1px!important;overflow:hidden}.hide{display:none}.show-inline{display:inline-block}.px-video-img-captions-container *{box-sizing:border-box}.px-video-img-captions-container{position:relative}.px-video-progress{width:100%;height:10px;cursor:pointer}.px-video-progress[value]{-webkit-appearance:none;border:none}.px-video-progress[value]::-webkit-progress-bar{background-color:#e6e6e6}.px-video-progress[value]::-webkit-progress-value{background-color:#009cdf}.px-video-time{font-family:Helvetica Neue,Helvetica,Arial,sans-serif;float:right;margin-top:2px;font-size:14px}.px-video-captions{position:absolute;top:0;left:0;width:100%;padding:.5em;min-height:2.5em;background-color:#000;color:#fff;font-size:1.1em;text-align:center;opacity:.75}.px-video-controls button{border:1px #fff solid;background:transparent;padding:0;margin:0 5px;width:25px;height:20px;overflow:hidden;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>')}.px-video-controls button{cursor:pointer}.px-video-controls button.px-video-restart{background-position:-6px -333px;margin-left:0}.px-video-controls button.px-video-restart:hover,.px-video-controls button.px-video-restart:focus{background-position:-6px -297px}.px-video-controls button.px-video-rewind{background-position:-6px -189px}.px-video-controls button.px-video-rewind:hover,.px-video-controls button.px-video-rewind:focus{background-position:-6px -153px}.px-video-controls button.px-video-play{background-position:-6px -45px}.px-video-controls button.px-video-play:hover,.px-video-controls button.px-video-play:focus{background-position:-6px -9px}.px-video-controls button.px-video-pause{background-position:-6px -117px}.px-video-controls button.px-video-pause:hover,.px-video-controls button.px-video-pause:focus{background-position:-6px -81px}.px-video-controls button.px-video-forward{background-position:-6px -261px}.px-video-controls button.px-video-forward:hover,.px-video-controls button.px-video-forward:focus{background-position:-6px -225px}.px-video-fullscreen-btn-container label{display:inline-block;width:25px;height:20px;margin-left:10px;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>');background-position:-6px -907px}.px-video-fullscreen-btn-container input[type=checkbox]:hover+label{background-position:-6px -943px;cursor:pointer}.px-video-fullscreen-btn-container input[type=checkbox]:focus+label{outline:.25rem solid #2491ff;outline-offset:0;background-position:-6px -943px}.px-video-fullscreen-btn-container input[type=checkbox]:checked+label{background-position:-6px -979px}.px-video-fullscreen-btn-container input[type=checkbox]:checked:hover+label{background-position:-6px -1015px}.px-video-captions-btn-container label{display:inline-block;width:25px;height:20px;margin-left:10px;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>');background-position:-6px -835px}.px-video-captions-btn-container input[type=checkbox]:hover+label{background-position:-6px -799px;cursor:pointer}.px-video-captions-btn-container input[type=checkbox]:focus+label{outline:.25rem solid #2491ff;outline-offset:0;background-position:-6px -799px}.px-video-captions-btn-container input[type=checkbox]:checked+label{background-position:-6px -871px}.px-video-mute-btn-container label{display:inline-block;width:25px;height:20px;margin-left:240px;margin-top:2px;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>');background-position:-6px -476px}.px-video-mute-btn-container input[type=checkbox]:hover+label{background-position:-6px -440px;cursor:pointer}.px-video-mute-btn-container input[type=checkbox]:focus+label{outline:.25rem solid #2491ff;outline-offset:0;background-position:-6px -440px}.px-video-mute-btn-container input[type=checkbox]:checked+label{background-position:-6px -692px}.px-video-mute-btn-container input[type=checkbox]:checked:hover+label,.px-video-mute-btn-container input[type=checkbox]:checked:focus+label{background-position:-6px -656px}.px-video-controls input[type=range]{-webkit-appearance:none;height:6px;width:100px;margin-top:8px;background-color:#e6e6e6;outline:none}.px-video-controls input[type=range]:focus::-webkit-slider-thumb{outline:.25rem solid #2491ff;outline-offset:0}.px-video-controls input[type=range]::-moz-range-track{-moz-appearance:none;height:6px;background-color:#e6e6e6;border:none}.px-video-controls input[type=range]::-webkit-slider-thumb{-webkit-appearance:none!important;height:10px;width:6px;background-color:#666}.px-video-controls input[type=range]::-moz-range-thumb{height:12px;width:8px;background-color:#666}@media screen and (-ms-high-contrast: active),(-ms-high-contrast: none){.px-video-controls input[type=range]{position:relative;padding:0;height:8px;top:-3px}.px-video-time{margin-top:4px}.px-video-captions{padding:8px;min-height:36px}}.px-video-container{position:relative;min-width:200px}#videoPlayer{min-width:200px}.px-video-container.fullscreen{position:fixed;inset:0;-webkit-cursor-visibility:auto-hide}.px-video-controls.js-fullscreen-controls{position:absolute;bottom:0;width:100%;z-index:940;background:#fff}.px-video-captions.js-fullscreen-captions{min-height:3.5em;font-size:2.5em;padding:1em}.px-timetip{padding:5px 10px;font-family:Helvetica Neue,Helvetica,Arial,sans-serif;font-size:14px;background:#000000d4;border-radius:3px;color:#fff;width:auto;bottom:20px}.px-timetip:after{top:100%;left:50%;border:solid transparent;content:\" \";height:0;width:0;position:absolute;pointer-events:none;border-color:#2b2b2b00;border-top-color:#2b2b2b;border-width:10px;margin-left:-10px}\n"], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsVideoPlayerComponent, [{
        type: Component,
        args: [{ selector: 'sds-video-player', encapsulation: ViewEncapsulation.None, template: "<div #videoWrapper class=\"px-video-container\" id=\"{{ VPConfiguration.id }}\" [style.width]=\"VPConfiguration.width\">\n  <div class=\"px-video-img-captions-container\">\n    <div class=\"px-video-captions hide\" aria-hidden=\"true\"></div>\n    <video\n      #video\n      [id]=\"'videoPlayer' + VPConfiguration.id\"\n      [style.width]=\"VPConfiguration.width\"\n      [style.height]=\"VPConfiguration.height\"\n      poster=\"{{ VPConfiguration.poster }}\"\n      controls\n      preload=\"{{ VPConfiguration.preload }}\"\n      [attr.aria-label]=\"VPConfiguration.description\"\n    >\n      <!-- if Safari/Chrome-->\n      <source *ngIf=\"loadVideoSource\" src=\"{{ VPConfiguration.sourceMp4 }}\" type=\"video/mp4\" />\n      <!-- If Firefox/Opera/Chrome/IE -->\n      <source *ngIf=\"loadVideoSource\" src=\"{{ VPConfiguration.sourceWebm }}\" type=\"video/webm\" />\n      <track\n        kind=\"subtitles\"\n        kind=\"captions\"\n        label=\"English captions\"\n        src=\"{{ VPConfiguration.caption }}\"\n        srclang=\"en\"\n        default\n      />\n    </video>\n  </div>\n  <div class=\"px-video-controls\"></div>\n</div>\n", styles: [".pull-left{float:left}.sr-only{position:absolute!important;clip:rect(1px,1px,1px,1px);padding:0!important;border:0!important;height:1px!important;width:1px!important;overflow:hidden}.hide{display:none}.show-inline{display:inline-block}.px-video-img-captions-container *{box-sizing:border-box}.px-video-img-captions-container{position:relative}.px-video-progress{width:100%;height:10px;cursor:pointer}.px-video-progress[value]{-webkit-appearance:none;border:none}.px-video-progress[value]::-webkit-progress-bar{background-color:#e6e6e6}.px-video-progress[value]::-webkit-progress-value{background-color:#009cdf}.px-video-time{font-family:Helvetica Neue,Helvetica,Arial,sans-serif;float:right;margin-top:2px;font-size:14px}.px-video-captions{position:absolute;top:0;left:0;width:100%;padding:.5em;min-height:2.5em;background-color:#000;color:#fff;font-size:1.1em;text-align:center;opacity:.75}.px-video-controls button{border:1px #fff solid;background:transparent;padding:0;margin:0 5px;width:25px;height:20px;overflow:hidden;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>')}.px-video-controls button{cursor:pointer}.px-video-controls button.px-video-restart{background-position:-6px -333px;margin-left:0}.px-video-controls button.px-video-restart:hover,.px-video-controls button.px-video-restart:focus{background-position:-6px -297px}.px-video-controls button.px-video-rewind{background-position:-6px -189px}.px-video-controls button.px-video-rewind:hover,.px-video-controls button.px-video-rewind:focus{background-position:-6px -153px}.px-video-controls button.px-video-play{background-position:-6px -45px}.px-video-controls button.px-video-play:hover,.px-video-controls button.px-video-play:focus{background-position:-6px -9px}.px-video-controls button.px-video-pause{background-position:-6px -117px}.px-video-controls button.px-video-pause:hover,.px-video-controls button.px-video-pause:focus{background-position:-6px -81px}.px-video-controls button.px-video-forward{background-position:-6px -261px}.px-video-controls button.px-video-forward:hover,.px-video-controls button.px-video-forward:focus{background-position:-6px -225px}.px-video-fullscreen-btn-container label{display:inline-block;width:25px;height:20px;margin-left:10px;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>');background-position:-6px -907px}.px-video-fullscreen-btn-container input[type=checkbox]:hover+label{background-position:-6px -943px;cursor:pointer}.px-video-fullscreen-btn-container input[type=checkbox]:focus+label{outline:.25rem solid #2491ff;outline-offset:0;background-position:-6px -943px}.px-video-fullscreen-btn-container input[type=checkbox]:checked+label{background-position:-6px -979px}.px-video-fullscreen-btn-container input[type=checkbox]:checked:hover+label{background-position:-6px -1015px}.px-video-captions-btn-container label{display:inline-block;width:25px;height:20px;margin-left:10px;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>');background-position:-6px -835px}.px-video-captions-btn-container input[type=checkbox]:hover+label{background-position:-6px -799px;cursor:pointer}.px-video-captions-btn-container input[type=checkbox]:focus+label{outline:.25rem solid #2491ff;outline-offset:0;background-position:-6px -799px}.px-video-captions-btn-container input[type=checkbox]:checked+label{background-position:-6px -871px}.px-video-mute-btn-container label{display:inline-block;width:25px;height:20px;margin-left:240px;margin-top:2px;background:no-repeat url('data:image/svg+xml,<?xml version=\"1.0\" encoding=\"UTF-8\" standalone=\"no\"?>%0A<svg width=\"36px\" height=\"1044px\" viewBox=\"0 0 36 1044\" version=\"1.1\" xmlns=\"http://www.w3.org/2000/svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" xmlns:sketch=\"http://www.bohemiancoding.com/sketch/ns\">%0A    <!-- Generator: Sketch 3.3.2 (12043) - http://www.bohemiancoding.com/sketch -->%0A    <title>px-video-sprite</title>%0A    <desc>Created with Sketch.</desc>%0A    <defs></defs>%0A    <g id=\"Page-1\" stroke=\"none\" stroke-width=\"1\" fill=\"none\" fill-rule=\"evenodd\" sketch:type=\"MSPage\">%0A        <g id=\"vector-sprite-(by-Laura)\" sketch:type=\"MSArtboardGroup\">%0A            <path d=\"M12,11 L12,25 L25,18 L12,11 Z\" id=\"play---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M12,47 L12,61 L25,54 L12,47 Z\" id=\"play---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,160.214286 L18,157 L11,161.5 L18,166 L18,162.785714 L23,166 L23,157 L18,160.214286 Z\" id=\"rewind---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M18,196.214286 L18,193 L11,197.5 L18,202 L18,198.785714 L23,202 L23,193 L18,196.214286 Z\" id=\"rewind---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M20,232.214286 L20,229 L13,233.5 L20,238 L20,234.785714 L25,238 L25,229 L20,232.214286 Z\" id=\"forward---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 233.500000) scale(-1, 1) translate(-19.000000, -233.500000) \"></path>%0A            <path d=\"M20,268.214286 L20,265 L13,269.5 L20,274 L20,270.785714 L25,274 L25,265 L20,268.214286 Z\" id=\"forward---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" transform=\"translate(19.000000, 269.500000) scale(-1, 1) translate(-19.000000, -269.500000) \"></path>%0A            <path d=\"M11,83.4982674 C11,83.2230819 11.2148438,83 11.4976988,83 L15.5023012,83 C15.7771727,83 16,83.2199066 16,83.4982674 L16,96.5017326 C16,96.7769181 15.7851562,97 15.5023012,97 L11.4976988,97 C11.2228273,97 11,96.7800934 11,96.5017326 L11,83.4982674 Z M19,83.4982674 C19,83.2230819 19.2148438,83 19.4976988,83 L23.5023012,83 C23.7771727,83 24,83.2199066 24,83.4982674 L24,96.5017326 C24,96.7769181 23.7851562,97 23.5023012,97 L19.4976988,97 C19.2228273,97 19,96.7800934 19,96.5017326 L19,83.4982674 Z\" id=\"pause---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M11,119.498267 C11,119.223082 11.2148438,119 11.4976988,119 L15.5023012,119 C15.7771727,119 16,119.219907 16,119.498267 L16,132.501733 C16,132.776918 15.7851562,133 15.5023012,133 L11.4976988,133 C11.2228273,133 11,132.780093 11,132.501733 L11,119.498267 Z M19,119.498267 C19,119.223082 19.2148438,119 19.4976988,119 L23.5023012,119 C23.7771727,119 24,119.219907 24,119.498267 L24,132.501733 C24,132.776918 23.7851562,133 23.5023012,133 L19.4976988,133 C19.2228273,133 19,132.780093 19,132.501733 L19,119.498267 Z\" id=\"pause---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14.8788722,340.579989 C14.529028,340.180304 13.7630993,339.305255 13.7630993,339.305255 C15.4484869,336.790203 17.9867342,337.004546 17.9867342,337.004546 C21.1630396,337.004546 23.011872,339.500201 22.9999426,341.985538 C22.9879638,344.481173 20.9903924,346.966531 18.0009091,346.966531 C15.2817522,346.966531 14.006078,344.871931 14.006078,344.871931 L15.5482843,344.02802 C15.5482843,344.02802 16.3548248,345.183319 17.9867342,345.183319 C22.3229096,345.183319 22.329138,338.903202 17.9867342,338.73248 C17.9816127,338.732279 16.1077241,338.732475 15.1984535,340.413156 C15.1865801,340.435103 15.0721341,340.724596 15.0516928,340.77028 L16.4383545,342.297119 L12.0793458,342.976807 L12.4146975,337.866712 L14.8788722,340.579989 Z\" id=\"repeat---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M13.7995145,303.251529 C15.4847868,300.793268 17.9867342,301.004546 17.9867342,301.004546 C21.1630396,301.004546 23.011872,303.500201 22.9999426,305.985538 C22.9879638,308.481173 20.9903924,310.966531 18.0009091,310.966531 C15.2817522,310.966531 14.006078,308.871931 14.006078,308.871931 L15.5482843,308.02802 C15.5482843,308.02802 16.3548248,309.183319 17.9867342,309.183319 C22.3229096,309.183319 22.329138,302.903202 17.9867342,302.73248 C17.9815912,302.732278 16.0919986,302.732476 15.1870825,304.434356 C15.1825343,304.44291 15.1570316,304.516075 15.1329553,304.58497 L16.8779967,306.330011 L12.0739746,306.958984 L12.4146975,301.866712 L13.7995145,303.251529 Z\" id=\"repeat---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,382.501231 C14,382.224409 14.2329025,382 14.5034227,382 L20.4965773,382 C20.77461,382 21,382.229751 21,382.501231 L21,392.498769 C21,392.775591 20.837471,393.161293 20.6396722,393.35592 C20.6396722,393.35592 18,396 17.555542,396 C17.111084,396 14.3487713,393.332515 14.3487713,393.332515 C14.1561502,393.148872 14,392.770249 14,392.498769 L14,382.501231 Z\" id=\"pointer---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M14,418.501231 C14,418.224409 14.2329025,418 14.5034227,418 L20.4965773,418 C20.77461,418 21,418.229751 21,418.501231 L21,428.498769 C21,428.775591 20.837471,429.161293 20.6396722,429.35592 C20.6396722,429.35592 18,432 17.555542,432 C17.111084,432 14.3487713,429.332515 14.3487713,429.332515 C14.1561502,429.148872 14,428.770249 14,428.498769 L14,418.501231 Z\" id=\"pointer---dark\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,666 L26.8822641,663.53195 C27.0816659,663.332548 27.0828664,663.011799 26.8857749,662.814707 L26.1852929,662.114225 C25.9797727,661.908705 25.667081,661.918705 25.4680505,662.117736 L23,664.585786 L20.5319495,662.117736 C20.3325477,661.918334 20.0117986,661.917134 19.8147071,662.114225 L19.1142251,662.814707 C18.9087049,663.020227 18.9187054,663.332919 19.1177359,663.53195 L21.5857864,666 L19.1177359,668.46805 C18.9183341,668.667452 18.9171336,668.988201 19.1142251,669.185293 L19.8147071,669.885775 C20.0202273,670.091295 20.332919,670.081295 20.5319495,669.882264 L23,667.414214 L25.4680505,669.882264 C25.6674523,670.081666 25.9882014,670.082866 26.1852929,669.885775 L26.8857749,669.185293 C27.0912951,668.979773 27.0812946,668.667081 26.8822641,668.46805 L24.4142136,666 L24.4142136,666 Z M9.25,663.142354 C8.56,663.142354 8,663.702354 8,664.392354 L8,667.761475 C8,668.451475 8.56,669.011475 9.25,669.011475 L13.5,669.011475 L17,673.033203 L17,659 L13.5,663.142354 L9.25,663.142354 Z\" id=\"mute---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M24.4142136,702 L26.8822641,699.53195 C27.0816659,699.332548 27.0828664,699.011799 26.8857749,698.814707 L26.1852929,698.114225 C25.9797727,697.908705 25.667081,697.918705 25.4680505,698.117736 L23,700.585786 L20.5319495,698.117736 C20.3325477,697.918334 20.0117986,697.917134 19.8147071,698.114225 L19.1142251,698.814707 C18.9087049,699.020227 18.9187054,699.332919 19.1177359,699.53195 L21.5857864,702 L19.1177359,704.46805 C18.9183341,704.667452 18.9171336,704.988201 19.1142251,705.185293 L19.8147071,705.885775 C20.0202273,706.091295 20.332919,706.081295 20.5319495,705.882264 L23,703.414214 L25.4680505,705.882264 C25.6674523,706.081666 25.9882014,706.082866 26.1852929,705.885775 L26.8857749,705.185293 C27.0912951,704.979773 27.0812946,704.667081 26.8822641,704.46805 L24.4142136,702 L24.4142136,702 Z M9.25,699.142354 C8.56,699.142354 8,699.702354 8,700.392354 L8,703.761475 C8,704.451475 8.56,705.011475 9.25,705.011475 L13.5,705.011475 L17,709.033203 L17,695 L13.5,699.142354 L9.25,699.142354 Z\" id=\"mute---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,447.13625 C8.56,447.13625 8,447.69625 8,448.38625 L8,451.755372 C8,452.445372 8.56,453.005372 9.25,453.005372 L13.5,453.005372 L17,457.0271 L17,442.993896 L13.5,447.13625 L9.25,447.13625 Z M21.2519839,450.016602 C21.2519839,448.834686 20.5783592,447.822479 19.6067964,447.326643 L19.0773926,448.402264 C19.6608051,448.700007 20.0649799,449.307573 20.0649799,450.016602 C20.0649799,450.72563 19.6608051,451.333196 19.0773926,451.630939 L19.6067964,452.706561 C20.5783592,452.211328 21.2519839,451.198517 21.2519839,450.016602 L21.2519839,450.016602 Z M21.7321271,443 L21.2015363,444.079849 C23.341111,445.170569 24.812996,447.416026 24.812996,450.016602 C24.812996,452.617177 23.341111,454.862635 21.2015363,455.953354 L21.7321271,457.033203 C24.2598521,455.74439 26,453.090064 26,450.016602 C26,446.943139 24.2598521,444.289417 21.7321271,443 L21.7321271,443 Z M23.625992,450.016602 C23.625992,447.888913 22.4217764,446.05172 20.6709455,445.159094 L20.1350132,446.250417 C21.4959133,446.944347 22.438988,448.362404 22.438988,450.016602 C22.438988,451.6708 21.4959133,453.088856 20.1350132,453.782786 L20.6709455,454.874109 C22.4217764,453.982087 23.625992,452.144894 23.625992,450.016602 L23.625992,450.016602 Z\" id=\"volume-full---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,483.13625 C8.56,483.13625 8,483.69625 8,484.38625 L8,487.755372 C8,488.445372 8.56,489.005372 9.25,489.005372 L13.5,489.005372 L17,493.0271 L17,478.993896 L13.5,483.13625 L9.25,483.13625 Z M21.2519839,486.016602 C21.2519839,484.834686 20.5783592,483.822479 19.6067964,483.326643 L19.0773926,484.402264 C19.6608051,484.700007 20.0649799,485.307573 20.0649799,486.016602 C20.0649799,486.72563 19.6608051,487.333196 19.0773926,487.630939 L19.6067964,488.706561 C20.5783592,488.211328 21.2519839,487.198517 21.2519839,486.016602 L21.2519839,486.016602 Z M21.7321271,479 L21.2015363,480.079849 C23.341111,481.170569 24.812996,483.416026 24.812996,486.016602 C24.812996,488.617177 23.341111,490.862635 21.2015363,491.953354 L21.7321271,493.033203 C24.2598521,491.74439 26,489.090064 26,486.016602 C26,482.943139 24.2598521,480.289417 21.7321271,479 L21.7321271,479 Z M23.625992,486.016602 C23.625992,483.888913 22.4217764,482.05172 20.6709455,481.159094 L20.1350132,482.250417 C21.4959133,482.944347 22.438988,484.362404 22.438988,486.016602 C22.438988,487.6708 21.4959133,489.088856 20.1350132,489.782786 L20.6709455,490.874109 C22.4217764,489.982087 23.625992,488.144894 23.625992,486.016602 L23.625992,486.016602 Z\" id=\"volume-full---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,519.13625 C8.56,519.13625 8,519.69625 8,520.38625 L8,523.755372 C8,524.445372 8.56,525.005372 9.25,525.005372 L13.5,525.005372 L17,529.0271 L17,514.993896 L13.5,519.13625 L9.25,519.13625 Z M21.4035818,522.011475 C21.4035818,520.791859 20.6643542,519.747365 19.5981731,519.235712 L19.0172119,520.345643 C19.6574416,520.652884 20.1009781,521.27983 20.1009781,522.011475 C20.1009781,522.743119 19.6574416,523.370065 19.0172119,523.677306 L19.5981731,524.787237 C20.6643542,524.276208 21.4035818,523.23109 21.4035818,522.011475 L21.4035818,522.011475 Z M24.0087891,522.011475 C24.0087891,519.815918 22.6872977,517.920123 20.7659573,516.999023 L20.1778318,518.125158 C21.6712668,518.841222 22.7061854,520.304512 22.7061854,522.011475 C22.7061854,523.718438 21.6712668,525.181727 20.1778318,525.897791 L20.7659573,527.023926 C22.6872977,526.10345 24.0087891,524.207655 24.0087891,522.011475 L24.0087891,522.011475 Z\" id=\"volume-mid---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,555.13625 C8.56,555.13625 8,555.69625 8,556.38625 L8,559.755372 C8,560.445372 8.56,561.005372 9.25,561.005372 L13.5,561.005372 L17,565.0271 L17,550.993896 L13.5,555.13625 L9.25,555.13625 Z M21.4035818,558.011475 C21.4035818,556.791859 20.6643542,555.747365 19.5981731,555.235712 L19.0172119,556.345643 C19.6574416,556.652884 20.1009781,557.27983 20.1009781,558.011475 C20.1009781,558.743119 19.6574416,559.370065 19.0172119,559.677306 L19.5981731,560.787237 C20.6643542,560.276208 21.4035818,559.23109 21.4035818,558.011475 L21.4035818,558.011475 Z M24.0087891,558.011475 C24.0087891,555.815918 22.6872977,553.920123 20.7659573,552.999023 L20.1778318,554.125158 C21.6712668,554.841222 22.7061854,556.304512 22.7061854,558.011475 C22.7061854,559.718438 21.6712668,561.181727 20.1778318,561.897791 L20.7659573,563.023926 C22.6872977,562.10345 24.0087891,560.207655 24.0087891,558.011475 L24.0087891,558.011475 Z\" id=\"volume-mid---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,627.13625 C8.56,627.13625 8,627.69625 8,628.38625 L8,631.755372 C8,632.445372 8.56,633.005372 9.25,633.005372 L13.5,633.005372 L17,637.0271 L17,622.993896 L13.5,627.13625 L9.25,627.13625 Z M21,630.011475 C21,628.791859 20.3857903,627.747365 19.4999212,627.235712 L19.0172119,628.345643 C19.5491663,628.652884 19.9176921,629.27983 19.9176921,630.011475 C19.9176921,630.743119 19.5491663,631.370065 19.0172119,631.677306 L19.4999212,632.787237 C20.3857903,632.276208 21,631.23109 21,630.011475 L21,630.011475 Z\" id=\"volume-low---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M9.25,591.13625 C8.56,591.13625 8,591.69625 8,592.38625 L8,595.755372 C8,596.445372 8.56,597.005372 9.25,597.005372 L13.5,597.005372 L17,601.0271 L17,586.993896 L13.5,591.13625 L9.25,591.13625 Z M21,594.011475 C21,592.791859 20.3857903,591.747365 19.4999212,591.235712 L19.0172119,592.345643 C19.5491663,592.652884 19.9176921,593.27983 19.9176921,594.011475 C19.9176921,594.743119 19.5491663,595.370065 19.0172119,595.677306 L19.4999212,596.787237 C20.3857903,596.276208 21,595.23109 21,594.011475 L21,594.011475 Z\" id=\"volume-low---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <rect id=\"stopper---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"734\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <rect id=\"stopper---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\" x=\"15\" y=\"770\" width=\"5\" height=\"8\" rx=\"0.5\"></rect>%0A            <path d=\"M10,807.995826 C10,805.788992 11.7888304,804 13.9962027,804 L22.0037973,804 C24.2108391,804 26,805.796521 26,807.995826 L26,812.004174 C26,814.211008 24.2111696,816 22.0037973,816 L13.9962027,816 C11.7891609,816 10,814.203479 10,812.004174 L10,807.995826 Z M11.9901733,810.005005 C11.9901733,806.623535 16.6294556,805.807068 17.8296507,809.013977 C17.8329163,809.022703 16.4950694,809.438808 16.4888916,809.426453 C15.5649414,807.578613 13.3978882,808.480789 13.3978882,810.005005 C13.3978882,811.017029 14.34021,811.607361 15.0359497,811.607361 C16.0014648,811.607361 16.4400005,810.681824 16.4400005,810.681824 C16.4400005,810.681824 17.8296509,811.017029 17.8296509,811.017029 C17.8296507,811.132446 16.9962766,813.009705 15.0359497,813.009705 C13.1976929,813.009705 11.9901733,811.505127 11.9901733,810.005005 Z M17.9901733,810.005005 C17.9901733,806.623535 22.6294556,805.807068 23.8296507,809.013977 C23.8329163,809.022703 22.4950694,809.438808 22.4888916,809.426453 C21.5649414,807.578613 19.3978882,808.480789 19.3978882,810.005005 C19.3978882,811.017029 20.34021,811.607361 21.0359497,811.607361 C22.0014648,811.607361 22.4400005,810.681824 22.4400005,810.681824 C22.4400005,810.681824 23.8296509,811.017029 23.8296509,811.017029 C23.8296507,811.132446 22.9962766,813.009705 21.0359497,813.009705 C19.1976929,813.009705 17.9901733,811.505127 17.9901733,810.005005 Z\" id=\"closed-captions---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,843.995826 C10,841.788992 11.7888304,840 13.9962027,840 L22.0037973,840 C24.2108391,840 26,841.796521 26,843.995826 L26,848.004174 C26,850.211008 24.2111696,852 22.0037973,852 L13.9962027,852 C11.7891609,852 10,850.203479 10,848.004174 L10,843.995826 Z M11.9901733,846.005005 C11.9901733,842.623535 16.6294556,841.807068 17.8296507,845.013977 C17.8329163,845.022703 16.4950694,845.438808 16.4888916,845.426453 C15.5649414,843.578613 13.3978882,844.480789 13.3978882,846.005005 C13.3978882,847.017029 14.34021,847.607361 15.0359497,847.607361 C16.0014648,847.607361 16.4400005,846.681824 16.4400005,846.681824 C16.4400005,846.681824 17.8296509,847.017029 17.8296509,847.017029 C17.8296507,847.132446 16.9962766,849.009705 15.0359497,849.009705 C13.1976929,849.009705 11.9901733,847.505127 11.9901733,846.005005 Z M17.9901733,846.005005 C17.9901733,842.623535 22.6294556,841.807068 23.8296507,845.013977 C23.8329163,845.022703 22.4950694,845.438808 22.4888916,845.426453 C21.5649414,843.578613 19.3978882,844.480789 19.3978882,846.005005 C19.3978882,847.017029 20.34021,847.607361 21.0359497,847.607361 C22.0014648,847.607361 22.4400005,846.681824 22.4400005,846.681824 C22.4400005,846.681824 23.8296509,847.017029 23.8296509,847.017029 C23.8296507,847.132446 22.9962766,849.009705 21.0359497,849.009705 C19.1976929,849.009705 17.9901733,847.505127 17.9901733,846.005005 Z\" id=\"closed-captions---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M10,879.995826 C10,877.788992 11.7888304,876 13.9962027,876 L22.0037973,876 C24.2108391,876 26,877.796521 26,879.995826 L26,884.004174 C26,886.211008 24.2111696,888 22.0037973,888 L13.9962027,888 C11.7891609,888 10,886.203479 10,884.004174 L10,879.995826 Z M11.9901733,882.005005 C11.9901733,878.623535 16.6294556,877.807068 17.8296507,881.013977 C17.8329163,881.022703 16.4950694,881.438808 16.4888916,881.426453 C15.5649414,879.578613 13.3978882,880.480789 13.3978882,882.005005 C13.3978882,883.017029 14.34021,883.607361 15.0359497,883.607361 C16.0014648,883.607361 16.4400005,882.681824 16.4400005,882.681824 C16.4400005,882.681824 17.8296509,883.017029 17.8296509,883.017029 C17.8296507,883.132446 16.9962766,885.009705 15.0359497,885.009705 C13.1976929,885.009705 11.9901733,883.505127 11.9901733,882.005005 Z M17.9901733,882.005005 C17.9901733,878.623535 22.6294556,877.807068 23.8296507,881.013977 C23.8329163,881.022703 22.4950694,881.438808 22.4888916,881.426453 C21.5649414,879.578613 19.3978882,880.480789 19.3978882,882.005005 C19.3978882,883.017029 20.34021,883.607361 21.0359497,883.607361 C22.0014648,883.607361 22.4400005,882.681824 22.4400005,882.681824 C22.4400005,882.681824 23.8296509,883.017029 23.8296509,883.017029 C23.8296507,883.132446 22.9962766,885.009705 21.0359497,885.009705 C19.1976929,885.009705 17.9901733,883.505127 17.9901733,882.005005 Z\" id=\"closed-captions---colour\" fill=\"%23009CDE\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,912 L10,912 L10,917 L12.5,917 L12.5,914.5 L15,914.5 L15,912 Z M26,917 L26,912 L21,912 L21,914.5 L23.5,914.5 L23.5,917 L26,917 Z M15,924 L10,924 L10,919 L12.5,919 L12.5,921.5 L15,921.5 L15,924 Z M26,919 L26,924 L21,924 L21,921.5 L23.5,921.5 L23.5,919 L26,919 Z\" id=\"fullscreen-light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M15,948 L10,948 L10,953 L12.5,953 L12.5,950.5 L15,950.5 L15,948 Z M26,953 L26,948 L21,948 L21,950.5 L23.5,950.5 L23.5,953 L26,953 Z M15,960 L10,960 L10,955 L12.5,955 L12.5,957.5 L15,957.5 L15,960 Z M26,955 L26,960 L21,960 L21,957.5 L23.5,957.5 L23.5,955 L26,955 Z\" id=\"fullscreen-dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,984 L21,989 L26,989 L26,987 L23,987 L23,984 L21,984 Z M10,989 L15,989 L15,984 L13,984 L13,987 L10,987 L10,989 Z M15,996 L15,991 L10,991 L10,993 L13,993 L13,996 L15,996 Z M26,991 L21,991 L21,996 L23,996 L23,993 L26,993 L26,991 Z\" id=\"in-window---light\" fill=\"%23666666\" sketch:type=\"MSShapeGroup\"></path>%0A            <path d=\"M21,1020 L21,1025 L26,1025 L26,1023 L23,1023 L23,1020 L21,1020 Z M10,1025 L15,1025 L15,1020 L13,1020 L13,1023 L10,1023 L10,1025 Z M15,1032 L15,1027 L10,1027 L10,1029 L13,1029 L13,1032 L15,1032 Z M26,1027 L21,1027 L21,1032 L23,1032 L23,1029 L26,1029 L26,1027 Z\" id=\"in-window---dark\" fill=\"%23444444\" sketch:type=\"MSShapeGroup\"></path>%0A        </g>%0A    </g>%0A</svg>');background-position:-6px -476px}.px-video-mute-btn-container input[type=checkbox]:hover+label{background-position:-6px -440px;cursor:pointer}.px-video-mute-btn-container input[type=checkbox]:focus+label{outline:.25rem solid #2491ff;outline-offset:0;background-position:-6px -440px}.px-video-mute-btn-container input[type=checkbox]:checked+label{background-position:-6px -692px}.px-video-mute-btn-container input[type=checkbox]:checked:hover+label,.px-video-mute-btn-container input[type=checkbox]:checked:focus+label{background-position:-6px -656px}.px-video-controls input[type=range]{-webkit-appearance:none;height:6px;width:100px;margin-top:8px;background-color:#e6e6e6;outline:none}.px-video-controls input[type=range]:focus::-webkit-slider-thumb{outline:.25rem solid #2491ff;outline-offset:0}.px-video-controls input[type=range]::-moz-range-track{-moz-appearance:none;height:6px;background-color:#e6e6e6;border:none}.px-video-controls input[type=range]::-webkit-slider-thumb{-webkit-appearance:none!important;height:10px;width:6px;background-color:#666}.px-video-controls input[type=range]::-moz-range-thumb{height:12px;width:8px;background-color:#666}@media screen and (-ms-high-contrast: active),(-ms-high-contrast: none){.px-video-controls input[type=range]{position:relative;padding:0;height:8px;top:-3px}.px-video-time{margin-top:4px}.px-video-captions{padding:8px;min-height:36px}}.px-video-container{position:relative;min-width:200px}#videoPlayer{min-width:200px}.px-video-container.fullscreen{position:fixed;inset:0;-webkit-cursor-visibility:auto-hide}.px-video-controls.js-fullscreen-controls{position:absolute;bottom:0;width:100%;z-index:940;background:#fff}.px-video-captions.js-fullscreen-captions{min-height:3.5em;font-size:2.5em;padding:1em}.px-timetip{padding:5px 10px;font-family:Helvetica Neue,Helvetica,Arial,sans-serif;font-size:14px;background:#000000d4;border-radius:3px;color:#fff;width:auto;bottom:20px}.px-timetip:after{top:100%;left:50%;border:solid transparent;content:\" \";height:0;width:0;position:absolute;pointer-events:none;border-color:#2b2b2b00;border-top-color:#2b2b2b;border-width:10px;margin-left:-10px}\n"] }]
    }], () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }], { VPConfiguration: [{
            type: Input
        }], videoWrapper: [{
            type: ViewChild,
            args: ['videoWrapper', { static: false }]
        }], video: [{
            type: ViewChild,
            args: ['video', { static: false }]
        }], crossorigin: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsVideoPlayerComponent, { className: "SdsVideoPlayerComponent" }); })();

class SdsVideoPlayerModule {
    /** @nocollapse */ static { this.ɵfac = function SdsVideoPlayerModule_Factory(t) { return new (t || SdsVideoPlayerModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsVideoPlayerModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsVideoPlayerModule, [{
        type: NgModule,
        args: [{
                declarations: [SdsVideoPlayerComponent],
                exports: [SdsVideoPlayerComponent],
                imports: [CommonModule],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsVideoPlayerModule, { declarations: [SdsVideoPlayerComponent], imports: [CommonModule], exports: [SdsVideoPlayerComponent] }); })();

class SdsPopupDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.renderer.addClass(this.el.nativeElement, 'sds-popup');
        this.sdsPopupDiv = document.createElement('div');
        this.renderer.addClass(this.sdsPopupDiv, 'sds-popup__content');
    }
    ngAfterViewInit() {
        this.renderer.appendChild(this.el.nativeElement, this.sdsPopup);
        this.renderer.addClass(this.sdsPopupDiv, this.placement);
        this.renderer.addClass(this.sdsPopupDiv, this.position);
        this.renderer.appendChild(this.sdsPopupDiv, this.el.nativeElement.children[0]);
        this.renderer.appendChild(this.el.nativeElement, this.sdsPopupDiv);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsPopupDirective_Factory(t) { return new (t || SdsPopupDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsPopupDirective, selectors: [["", "sdsPopup", ""]], inputs: { sdsPopup: "sdsPopup", position: "position", placement: "placement" }, exportAs: ["sdsPopup"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPopupDirective, [{
        type: Directive,
        args: [{
                selector: '[sdsPopup]',
                exportAs: 'sdsPopup',
            }]
    }], () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], { sdsPopup: [{
            type: Input
        }], position: [{
            type: Input
        }], placement: [{
            type: Input
        }] }); })();

class SdsPopupModule {
    /** @nocollapse */ static { this.ɵfac = function SdsPopupModule_Factory(t) { return new (t || SdsPopupModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsPopupModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPopupModule, [{
        type: NgModule,
        args: [{
                declarations: [SdsPopupDirective],
                imports: [CommonModule],
                exports: [SdsPopupDirective],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsPopupModule, { declarations: [SdsPopupDirective], imports: [CommonModule], exports: [SdsPopupDirective] }); })();

class CollapseDirective {
    constructor() {
        this.collapsed = true;
    }
    /** @nocollapse */ static { this.ɵfac = function CollapseDirective_Factory(t) { return new (t || CollapseDirective)(); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: CollapseDirective, selectors: [["", "sdsCollapse", ""]], hostVars: 2, hostBindings: function CollapseDirective_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵclassProp("display-none", ctx.collapsed);
        } }, inputs: { collapsed: [i0.ɵɵInputFlags.None, "sdsCollapse", "collapsed"] }, exportAs: ["sdsCollapse"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CollapseDirective, [{
        type: Directive,
        args: [{
                selector: '[sdsCollapse]',
                exportAs: 'sdsCollapse',
                host: { '[class.display-none]': 'collapsed' },
            }]
    }], () => [], { collapsed: [{
            type: Input,
            args: ['sdsCollapse']
        }] }); })();

class SdsCollapseModule {
    /** @nocollapse */ static { this.ɵfac = function SdsCollapseModule_Factory(t) { return new (t || SdsCollapseModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsCollapseModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsCollapseModule, [{
        type: NgModule,
        args: [{
                declarations: [CollapseDirective],
                imports: [CommonModule],
                exports: [CollapseDirective],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsCollapseModule, { declarations: [CollapseDirective], imports: [CommonModule], exports: [CollapseDirective] }); })();

function SdsTextChildComponent_ng_container_0_span_1_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "span", 2);
    i0.ɵɵlistener("click", function SdsTextChildComponent_ng_container_0_span_1_Template_span_click_0_listener() { const i_r2 = i0.ɵɵrestoreView(_r1).index; const ctx_r2 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r2.removeItem(i_r2)); });
    i0.ɵɵtext(1);
    i0.ɵɵelementStart(2, "small", 3);
    i0.ɵɵtext(3, "(x)");
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const item_r4 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1("", item_r4, " ");
} }
function SdsTextChildComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsTextChildComponent_ng_container_0_span_1_Template, 4, 1, "span", 1);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", ctx_r2.items);
} }
class SdsTextChildComponent {
    constructor() {
        this.itemsChange = new EventEmitter();
    }
    // Method to remove an item from the items array, emits an event that the parent component is listening for
    removeItem(index) {
        this.items.splice(index, 1);
        this.itemsChange.emit(this.items);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTextChildComponent_Factory(t) { return new (t || SdsTextChildComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsTextChildComponent, selectors: [["sds-text-child"]], inputs: { items: "items" }, outputs: { itemsChange: "itemsChange" }, decls: 1, vars: 1, consts: [[4, "ngIf"], ["class", "sds-tag sds-tag--chip margin-x-05", "style", "cursor:pointer;", 3, "click", 4, "ngFor", "ngForOf"], [1, "sds-tag", "sds-tag--chip", "margin-x-05", 2, "cursor", "pointer", 3, "click"], [1, "margin-left-05"]], template: function SdsTextChildComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SdsTextChildComponent_ng_container_0_Template, 2, 1, "ng-container", 0);
        } if (rf & 2) {
            i0.ɵɵproperty("ngIf", ctx.items);
        } }, dependencies: [i1$2.NgForOf, i1$2.NgIf], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTextChildComponent, [{
        type: Component,
        args: [{
                selector: 'sds-text-child',
                template: `
    <ng-container *ngIf="items">
      <span
        class="sds-tag sds-tag--chip margin-x-05"
        style="cursor:pointer;"
        *ngFor="let item of items; let i = index"
        (click)="removeItem(i)"
        >{{ item }} <small class="margin-left-05">(x)</small></span
      >
    </ng-container>
  `,
            }]
    }], null, { items: [{
            type: Input
        }], itemsChange: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsTextChildComponent, { className: "SdsTextChildComponent" }); })();

class SdsTextComponent {
    constructor(cd) {
        this.cd = cd;
        this.items = [];
        this.multiple = true;
        this._onChange = (_) => { };
        this._onTouched = () => { };
    }
    // Helper method to programatically add a value to the existing items array
    addItem(val) {
        if (this.multiple) {
            this.items = [...this.items, val];
            this.updateModel();
        }
    }
    // Method that is fired when the child component event notifies us that the items array has been modified within the child component
    updateItems($event) {
        this.updateModel();
    }
    // Helper method that gets a new instance of the model and notifies ControlValueAccessor that we have a new model for this FormControl (our custom component)
    updateModel() {
        const model = this.getModel();
        this._onChange(model);
    }
    // Helper method to return a new instance of an array that contains our items
    getModel() {
        return [...this.items];
    }
    // ControlValueAccessor (and Formly) is trying to update the value of the FormControl (our custom component) programatically
    // If there is a value we will just overwrite items
    // If there is no value we reset the items array to be empty
    writeValue(value) {
        if (value && value.length && this.items !== value) {
            this.items = value;
            this.cd.markForCheck();
        }
        else {
            this.items = [];
            this.cd.markForCheck();
        }
    }
    // ControlValueAccessor hook that lets us call this._onChange(var) to let the form know our variable has changed (in this case model)
    registerOnChange(fn) {
        this._onChange = fn;
    }
    // ControlValueAccessor hook (not used)
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTextComponent_Factory(t) { return new (t || SdsTextComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsTextComponent, selectors: [["sds-text"]], inputs: { label: "label" }, features: [i0.ɵɵProvidersFeature([
                {
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef((() => SdsTextComponent)),
                    multi: true,
                },
            ])], decls: 16, vars: 5, consts: [["searchInput", ""], [1, "usa-input", "display-inline-block"], [1, "usa-button", "margin-left-05", "display-inline-block", 3, "click"], [3, "itemsChange", "items"]], template: function SdsTextComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelementStart(0, "div");
            i0.ɵɵelement(1, "input", 1, 0);
            i0.ɵɵelementStart(3, "button", 2);
            i0.ɵɵlistener("click", function SdsTextComponent_Template_button_click_3_listener() { i0.ɵɵrestoreView(_r1); const searchInput_r2 = i0.ɵɵreference(2); ctx.addItem(searchInput_r2.value); return i0.ɵɵresetView(searchInput_r2.value = ""); });
            i0.ɵɵtext(4, " Add Item ");
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(5, "h4");
            i0.ɵɵtext(6, "Component Items");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(7, "pre");
            i0.ɵɵtext(8);
            i0.ɵɵpipe(9, "json");
            i0.ɵɵelementEnd();
            i0.ɵɵelement(10, "hr");
            i0.ɵɵelementStart(11, "h4");
            i0.ɵɵtext(12, "Child Component Items ");
            i0.ɵɵelementStart(13, "small");
            i0.ɵɵtext(14, "(click to remove)");
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(15, "sds-text-child", 3);
            i0.ɵɵtwoWayListener("itemsChange", function SdsTextComponent_Template_sds_text_child_itemsChange_15_listener($event) { i0.ɵɵrestoreView(_r1); i0.ɵɵtwoWayBindingSet(ctx.items, $event) || (ctx.items = $event); return i0.ɵɵresetView($event); });
            i0.ɵɵlistener("itemsChange", function SdsTextComponent_Template_sds_text_child_itemsChange_15_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.updateItems($event)); });
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵattribute("aria-label", ctx.label);
            i0.ɵɵadvance(7);
            i0.ɵɵtextInterpolate(i0.ɵɵpipeBind1(9, 3, ctx.items));
            i0.ɵɵadvance(7);
            i0.ɵɵtwoWayProperty("items", ctx.items);
        } }, dependencies: [SdsTextChildComponent, i1$2.JsonPipe], encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTextComponent, [{
        type: Component,
        args: [{
                selector: 'sds-text',
                template: `
    <div>
      <input #searchInput class="usa-input display-inline-block" [attr.aria-label]="label" />
      <button
        class="usa-button margin-left-05 display-inline-block"
        (click)="addItem(searchInput.value); searchInput.value = ''"
      >
        Add Item
      </button>
    </div>

    <h4>Component Items</h4>
    <pre>{{ items | json }}</pre>

    <hr />

    <h4>Child Component Items <small>(click to remove)</small></h4>
    <sds-text-child [(items)]="items" (itemsChange)="updateItems($event)"></sds-text-child>
  `,
                providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef((() => SdsTextComponent)),
                        multi: true,
                    },
                ],
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], () => [{ type: i0.ChangeDetectorRef }], { label: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsTextComponent, { className: "SdsTextComponent" }); })();

class SdsTextModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTextModule_Factory(t) { return new (t || SdsTextModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTextModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, A11yModule, FormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTextModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, A11yModule, FormsModule],
                exports: [SdsTextComponent, SdsTextChildComponent],
                declarations: [SdsTextComponent, SdsTextChildComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTextModule, { declarations: [SdsTextComponent, SdsTextChildComponent], imports: [CommonModule, A11yModule, FormsModule], exports: [SdsTextComponent, SdsTextChildComponent] }); })();

function SdsToastComponent_span_5_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelement(0, "span", 7);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("innerHTML", ctx_r0.message, i0.ɵɵsanitizeHtml);
} }
function SdsToastComponent_span_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 8);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.message);
} }
function SdsToastComponent_div_7_Template(rf, ctx) { if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 9);
    i0.ɵɵlistener("click", function SdsToastComponent_div_7_Template_div_click_0_listener() { i0.ɵɵrestoreView(_r2); const ctx_r0 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r0.remove()); });
    i0.ɵɵelement(1, "usa-icon", 10);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", "x");
} }
class SdsToastComponent extends Toast {
    constructor(toastrService, toastPackage) {
        super(toastrService, toastPackage);
        this.toastrService = toastrService;
        this.toastPackage = toastPackage;
    }
    action(event) {
        event.stopPropagation();
        this.toastPackage.triggerAction();
        return false;
    }
    getIcon(toastType) {
        switch (toastType) {
            case 'sds-toast--success':
                return 'check2-circle';
            case 'sds-toast--info':
                return 'info-circle';
            case 'sds-toast--warning':
                return 'exclamation-triangle';
            case 'sds-toast--error':
                return 'slash-circle';
            default:
                return 'info-circle';
        }
    }
    getToastSr(toastType) {
        switch (toastType) {
            case 'sds-toast--success':
                return 'Success';
            case 'sds-toast--info':
                return 'Info';
            case 'sds-toast--warning':
                return 'Warning';
            case 'sds-toast--error':
                return 'Error';
            default:
                return 'Info';
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsToastComponent_Factory(t) { return new (t || SdsToastComponent)(i0.ɵɵdirectiveInject(i1$6.ToastrService), i0.ɵɵdirectiveInject(i1$6.ToastPackage)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsToastComponent, selectors: [["sds-toast"]], features: [i0.ɵɵInheritDefinitionFeature], decls: 8, vars: 5, consts: [["role", "alert", "aria-live", "assertive", 1, "sds-toast__content"], [1, "usa-sr-only"], [1, "sds-toast__content__icon"], ["size", "2x", 3, "icon"], ["class", "sds-toast__content__text", 3, "innerHTML", 4, "ngIf"], ["class", "sds-toast__content__text", 4, "ngIf"], ["class", "sds-toast__content__close", 3, "click", 4, "ngIf"], [1, "sds-toast__content__text", 3, "innerHTML"], [1, "sds-toast__content__text"], [1, "sds-toast__content__close", 3, "click"], [3, "icon"]], template: function SdsToastComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
            i0.ɵɵtext(2);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(3, "div", 2);
            i0.ɵɵelement(4, "usa-icon", 3);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(5, SdsToastComponent_span_5_Template, 1, 1, "span", 4)(6, SdsToastComponent_span_6_Template, 2, 1, "span", 5)(7, SdsToastComponent_div_7_Template, 2, 1, "div", 6);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵtextInterpolate(ctx.getToastSr(ctx.toastPackage.toastType));
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("icon", ctx.getIcon(ctx.toastPackage.toastType));
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.message && ctx.options.enableHtml);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.message && !ctx.options.enableHtml);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.options.closeButton);
        } }, dependencies: [i1$2.NgIf, i2$1.IconComponent], styles: ["[_nghost-%COMP%]{position:relative}"], data: { animation: [
                trigger('flyInOut', [
                    state('inactive', style({ opacity: 0 })),
                    state('active', style({ opacity: 1 })),
                    state('removed', style({ opacity: 0 })),
                    transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
                    transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
                ]),
            ] } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsToastComponent, [{
        type: Component,
        args: [{ selector: 'sds-toast', animations: [
                    trigger('flyInOut', [
                        state('inactive', style({ opacity: 0 })),
                        state('active', style({ opacity: 1 })),
                        state('removed', style({ opacity: 0 })),
                        transition('inactive => active', animate('{{ easeTime }}ms {{ easing }}')),
                        transition('active => removed', animate('{{ easeTime }}ms {{ easing }}')),
                    ]),
                ], preserveWhitespaces: false, template: "<div class=\"sds-toast__content\" role=\"alert\" aria-live=\"assertive\">\n  <div class=\"usa-sr-only\">{{ getToastSr(toastPackage.toastType) }}</div>\n  <div class=\"sds-toast__content__icon\">\n    <usa-icon [icon]=\"getIcon(toastPackage.toastType)\" size=\"2x\"></usa-icon>\n  </div>\n  <span *ngIf=\"message && options.enableHtml\" class=\"sds-toast__content__text\" [innerHTML]=\"message\"></span>\n  <span *ngIf=\"message && !options.enableHtml\" class=\"sds-toast__content__text\">{{ message }}</span>\n  <div *ngIf=\"options.closeButton\" class=\"sds-toast__content__close\" (click)=\"remove()\">\n    <usa-icon [icon]=\"'x'\"></usa-icon>\n  </div>\n</div>\n", styles: [":host{position:relative}\n"] }]
    }], () => [{ type: i1$6.ToastrService }, { type: i1$6.ToastPackage }], null); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsToastComponent, { className: "SdsToastComponent" }); })();

let SdsToastSettings = {
    toastComponent: SdsToastComponent,
    timeOut: 6000,
    toastClass: 'sds-toast',
    positionClass: 'toast-bottom-left',
};
class SdsToastModule {
    /** @nocollapse */ static { this.ɵfac = function SdsToastModule_Factory(t) { return new (t || SdsToastModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsToastModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule,
            IconModule,
            NgxBootstrapIconsModule.pick({ check2Circle, infoCircle, exclamationTriangle, slashCircle, x })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsToastModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    IconModule,
                    NgxBootstrapIconsModule.pick({ check2Circle, infoCircle, exclamationTriangle, slashCircle, x }),
                ],
                exports: [SdsToastComponent],
                declarations: [SdsToastComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsToastModule, { declarations: [SdsToastComponent], imports: [CommonModule,
        IconModule, i1$4.NgxBootstrapIconsModule], exports: [SdsToastComponent] }); })();

class SdsRichTextComponent {
    constructor() {
        this._onChange = (_) => { };
        this._onTouched = (_) => { };
        this.editor = ClassicEditor.default ?? ClassicEditor;
        this.model = '';
    }
    get minHeightClass() {
        return this.minHeight ? `min-height-${this.minHeight}` : '';
    }
    get maxHeightClass() {
        return this.maxHeight ? `max-height-${this.maxHeight}` : '';
    }
    writeValue(value) {
        this.model = value;
    }
    registerOnChange(fn) {
        this._onChange = fn;
    }
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsRichTextComponent_Factory(t) { return new (t || SdsRichTextComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsRichTextComponent, selectors: [["sds-rich-text"]], inputs: { minHeight: "minHeight", maxHeight: "maxHeight" }, features: [i0.ɵɵProvidersFeature([
                {
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef((() => SdsRichTextComponent)),
                    multi: true,
                },
            ])], decls: 1, vars: 6, consts: [[3, "ngModelChange", "change", "blur", "ngModel", "editor"]], template: function SdsRichTextComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "ckeditor", 0);
            i0.ɵɵtwoWayListener("ngModelChange", function SdsRichTextComponent_Template_ckeditor_ngModelChange_0_listener($event) { i0.ɵɵtwoWayBindingSet(ctx.model, $event) || (ctx.model = $event); return $event; });
            i0.ɵɵlistener("change", function SdsRichTextComponent_Template_ckeditor_change_0_listener() { return ctx._onChange(ctx.model); })("blur", function SdsRichTextComponent_Template_ckeditor_blur_0_listener() { return ctx._onTouched(ctx.model); });
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵclassMapInterpolate2("", ctx.minHeightClass, " ", ctx.maxHeightClass, "");
            i0.ɵɵtwoWayProperty("ngModel", ctx.model);
            i0.ɵɵproperty("editor", ctx.editor);
        } }, dependencies: [i1$7.CKEditorComponent, i2$2.NgControlStatus, i2$2.NgModel], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsRichTextComponent, [{
        type: Component,
        args: [{ selector: 'sds-rich-text', providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef((() => SdsRichTextComponent)),
                        multi: true,
                    },
                ], template: "<ckeditor\n  [(ngModel)]=\"model\"\n  [editor]=\"editor\"\n  (change)=\"_onChange(model)\"\n  (blur)=\"_onTouched(model)\"\n  class=\"{{ minHeightClass }} {{ maxHeightClass }}\"\n></ckeditor>\n" }]
    }], null, { minHeight: [{
            type: Input
        }], maxHeight: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsRichTextComponent, { className: "SdsRichTextComponent" }); })();

class SdsRichTextModule {
    /** @nocollapse */ static { this.ɵfac = function SdsRichTextModule_Factory(t) { return new (t || SdsRichTextModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsRichTextModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, CKEditorModule, ReactiveFormsModule, FormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsRichTextModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, CKEditorModule, ReactiveFormsModule, FormsModule],
                exports: [SdsRichTextComponent],
                declarations: [SdsRichTextComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsRichTextModule, { declarations: [SdsRichTextComponent], imports: [CommonModule, CKEditorModule, ReactiveFormsModule, FormsModule], exports: [SdsRichTextComponent] }); })();

class SdsTopBannerComponent {
    constructor() {
        this.showDetail = false;
        this.description = '';
        this.flagPath = 'assets/uswds/img/us_flag_small.png';
    }
    toggleDetails() {
        this.showDetail = !this.showDetail;
    }
    ngOnInit() {
        if (!this.flagPath) {
            this.flagPath = 'assets/uswds/img/us_flag_small.png';
        }
    }
    closeDetail() {
        if (this.showDetail) {
            this.showDetail = false;
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTopBannerComponent_Factory(t) { return new (t || SdsTopBannerComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsTopBannerComponent, selectors: [["sds-top-banner"]], inputs: { description: "description", flagPath: "flagPath" }, decls: 38, vars: 6, consts: [[1, "usa-banner"], [1, "usa-accordion"], [1, "usa-banner__header"], [1, "usa-banner__inner"], [1, "grid-col-auto"], ["aria-hidden", "true", "alt", "", 1, "usa-banner__header-flag", 3, "src"], [1, "grid-col-fill", "tablet:grid-col-auto"], [1, "usa-banner__header-text"], ["aria-hidden", "true", 1, "usa-banner__header-action"], ["aria-controls", "gov-banner", 1, "usa-accordion__button", "usa-banner__button", 3, "click", "blur"], [1, "usa-banner__button-text"], [1, "usa-banner__header-description"], ["id", "gov-banner", 1, "usa-banner__content", "usa-accordion__content", 3, "hidden"], [1, "grid-row", "grid-gap-lg"], [1, "usa-banner__guidance", "tablet:grid-col-6"], [1, "usa-banner__icon", "usa-media-block__img"], [1, "usa-media-block__body"]], template: function SdsTopBannerComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "header", 2)(3, "div", 3)(4, "div", 4);
            i0.ɵɵelement(5, "img", 5);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(6, "div", 6)(7, "p", 7);
            i0.ɵɵtext(8, " An official website of the United States government ");
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(9, "p", 8);
            i0.ɵɵtext(10, " Here\u2019s how you know ");
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(11, "button", 9);
            i0.ɵɵlistener("click", function SdsTopBannerComponent_Template_button_click_11_listener() { return ctx.toggleDetails(); })("blur", function SdsTopBannerComponent_Template_button_blur_11_listener() { return ctx.closeDetail(); });
            i0.ɵɵelementStart(12, "span", 10);
            i0.ɵɵtext(13, "Here\u2019s how you know");
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(14, "div", 11)(15, "em");
            i0.ɵɵtext(16);
            i0.ɵɵelementEnd()()()();
            i0.ɵɵelementStart(17, "div", 12)(18, "div", 13)(19, "div", 14);
            i0.ɵɵelement(20, "span", 15);
            i0.ɵɵelementStart(21, "div", 16)(22, "p")(23, "strong");
            i0.ɵɵtext(24, "The .gov means it\u2019s official.");
            i0.ɵɵelementEnd();
            i0.ɵɵelement(25, "br");
            i0.ɵɵtext(26, " Federal government websites often end in .gov or .mil. Before sharing sensitive information, make sure you\u2019re on a federal government site. ");
            i0.ɵɵelementEnd()()();
            i0.ɵɵelementStart(27, "div", 14);
            i0.ɵɵelement(28, "span", 15);
            i0.ɵɵelementStart(29, "div", 16)(30, "p")(31, "strong");
            i0.ɵɵtext(32, "The site is secure.");
            i0.ɵɵelementEnd();
            i0.ɵɵelement(33, "br");
            i0.ɵɵtext(34, " The ");
            i0.ɵɵelementStart(35, "strong");
            i0.ɵɵtext(36, "https://");
            i0.ɵɵelementEnd();
            i0.ɵɵtext(37, " ensures that you are connecting to the official website and that any information you provide is encrypted and transmitted securely. ");
            i0.ɵɵelementEnd()()()()()()();
        } if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵclassProp("sam-banner__header--expanded", ctx.showDetail);
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("src", ctx.flagPath, i0.ɵɵsanitizeUrl);
            i0.ɵɵadvance(6);
            i0.ɵɵattribute("aria-expanded", ctx.showDetail);
            i0.ɵɵadvance(5);
            i0.ɵɵtextInterpolate(ctx.description);
            i0.ɵɵadvance();
            i0.ɵɵproperty("hidden", !ctx.showDetail);
        } } }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTopBannerComponent, [{
        type: Component,
        args: [{ selector: 'sds-top-banner', template: "<div class=\"usa-banner\">\n  <div class=\"usa-accordion\">\n    <header class=\"usa-banner__header\" [class.sam-banner__header--expanded]=\"showDetail\">\n      <div class=\"usa-banner__inner\">\n        <div class=\"grid-col-auto\">\n          <img aria-hidden=\"true\" class=\"usa-banner__header-flag\" [src]=\"flagPath\" alt=\"\" />\n        </div>\n        <div class=\"grid-col-fill tablet:grid-col-auto\">\n          <p class=\"usa-banner__header-text\">\n            An official website of the United States government\n          </p>\n          <p class=\"usa-banner__header-action\" aria-hidden=\"true\">\n            Here\u2019s how you know\n          </p>\n        </div>\n        <button\n          class=\"usa-accordion__button usa-banner__button\"\n          (click)=\"toggleDetails()\"\n          (blur)=\"closeDetail()\"\n          [attr.aria-expanded]=\"showDetail\"\n          aria-controls=\"gov-banner\"\n        >\n          <span class=\"usa-banner__button-text\">Here\u2019s how you know</span>\n        </button>\n        <div class=\"usa-banner__header-description\">\n          <em>{{ description }}</em>\n        </div>\n      </div>\n    </header>\n    <div class=\"usa-banner__content usa-accordion__content\" id=\"gov-banner\" [hidden]=\"!showDetail\">\n      <div class=\"grid-row grid-gap-lg\">\n        <div class=\"usa-banner__guidance tablet:grid-col-6\">\n          <span class=\"usa-banner__icon usa-media-block__img\"></span>\n          <div class=\"usa-media-block__body\">\n            <p>\n              <strong>The .gov means it\u2019s official.</strong> <br />\n              Federal government websites often end in .gov or .mil. Before sharing sensitive information, make sure\n              you\u2019re on a federal government site.\n            </p>\n          </div>\n        </div>\n        <div class=\"usa-banner__guidance tablet:grid-col-6\">\n          <span class=\"usa-banner__icon usa-media-block__img\"></span>\n          <div class=\"usa-media-block__body\">\n            <p>\n              <strong>The site is secure.</strong> <br />\n              The <strong>https://</strong> ensures that you are connecting to the official website and that any\n              information you provide is encrypted and transmitted securely.\n            </p>\n          </div>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n" }]
    }], null, { description: [{
            type: Input
        }], flagPath: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsTopBannerComponent, { className: "SdsTopBannerComponent" }); })();

class SdsTopBannerModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTopBannerModule_Factory(t) { return new (t || SdsTopBannerModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTopBannerModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTopBannerModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                exports: [SdsTopBannerComponent],
                declarations: [SdsTopBannerComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTopBannerModule, { declarations: [SdsTopBannerComponent], imports: [CommonModule], exports: [SdsTopBannerComponent] }); })();

const _c0$a = a0 => ({ "usa-current": a0 });
function SdsSelectionPanelSelectionModeComponent_li_1_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 2)(1, "a", 3);
    i0.ɵɵlistener("click", function SdsSelectionPanelSelectionModeComponent_li_1_Template_a_click_1_listener() { const panelItem_r2 = i0.ɵɵrestoreView(_r1).$implicit; const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.onPanelItemClick(panelItem_r2)); });
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const panelItem_r2 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(2, _c0$a, ctx_r2.currentSelection && panelItem_r2.id === ctx_r2.currentSelection.id));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(panelItem_r2.text);
} }
class SdsSelectionPanelSelectionModeComponent {
    constructor() {
        this.panelSelected = new EventEmitter();
    }
    onPanelItemClick(panelItem) {
        this.currentSelection = panelItem;
        this.panelSelected.emit(panelItem);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSelectionPanelSelectionModeComponent_Factory(t) { return new (t || SdsSelectionPanelSelectionModeComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSelectionPanelSelectionModeComponent, selectors: [["sds-selection-panel-selection-mode"]], inputs: { model: "model", currentSelection: "currentSelection" }, outputs: { panelSelected: "panelSelected" }, decls: 2, vars: 1, consts: [[1, "usa-sidenav", "usa-sidenav--styled"], ["class", "usa-sidenav__item", 3, "ngClass", 4, "ngFor", "ngForOf"], [1, "usa-sidenav__item", 3, "ngClass"], ["href", "javascript:void(0);", 3, "click"]], template: function SdsSelectionPanelSelectionModeComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "ul", 0);
            i0.ɵɵtemplate(1, SdsSelectionPanelSelectionModeComponent_li_1_Template, 3, 4, "li", 1);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngForOf", ctx.model.navigationLinks);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSelectionPanelSelectionModeComponent, [{
        type: Component,
        args: [{ selector: 'sds-selection-panel-selection-mode', template: "<ul class=\"usa-sidenav usa-sidenav--styled\">\n  <li\n    *ngFor=\"let panelItem of model.navigationLinks\"\n    class=\"usa-sidenav__item\"\n    [ngClass]=\"{ 'usa-current': currentSelection && panelItem.id === currentSelection.id }\"\n  >\n    <a href=\"javascript:void(0);\" (click)=\"onPanelItemClick(panelItem)\">{{ panelItem.text }}</a>\n  </li>\n</ul>\n" }]
    }], null, { model: [{
            type: Input
        }], currentSelection: [{
            type: Input
        }], panelSelected: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSelectionPanelSelectionModeComponent, { className: "SdsSelectionPanelSelectionModeComponent" }); })();

const _c0$9 = a0 => ({ $implicit: a0 });
const _c1$3 = a0 => ({ "usa-current": a0 });
function SdsSelectionPanelNavigationModeComponent_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li");
    i0.ɵɵelementContainer(1, 3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const panelItem_r1 = ctx.$implicit;
    i0.ɵɵnextContext();
    const subPanelTemplate_r2 = i0.ɵɵreference(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", subPanelTemplate_r2)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c0$9, panelItem_r1));
} }
function SdsSelectionPanelNavigationModeComponent_ng_template_2_ul_3_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li");
    i0.ɵɵelementContainer(1, 3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const panelItem_r6 = ctx.$implicit;
    i0.ɵɵnextContext(3);
    const subPanelTemplate_r2 = i0.ɵɵreference(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", subPanelTemplate_r2)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c0$9, panelItem_r6));
} }
function SdsSelectionPanelNavigationModeComponent_ng_template_2_ul_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "ul", 6);
    i0.ɵɵtemplate(1, SdsSelectionPanelNavigationModeComponent_ng_template_2_ul_3_li_1_Template, 2, 4, "li", 2);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const panelItem_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", panelItem_r4.children);
} }
function SdsSelectionPanelNavigationModeComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "a", 4);
    i0.ɵɵlistener("click", function SdsSelectionPanelNavigationModeComponent_ng_template_2_Template_a_click_0_listener() { const panelItem_r4 = i0.ɵɵrestoreView(_r3).$implicit; const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.onPanelItemClick(panelItem_r4)); });
    i0.ɵɵelementStart(1, "span");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd()();
    i0.ɵɵtemplate(3, SdsSelectionPanelNavigationModeComponent_ng_template_2_ul_3_Template, 2, 1, "ul", 5);
} if (rf & 2) {
    const panelItem_r4 = ctx.$implicit;
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(3, _c1$3, panelItem_r4.selected));
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(panelItem_r4.text);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", panelItem_r4.children);
} }
class SdsSelectionPanelNavigationModeComponent {
    constructor(router, activatedRoute) {
        this.router = router;
        this.activatedRoute = activatedRoute;
        this.navigateOnClick = true;
        this.panelSelected = new EventEmitter();
    }
    ngOnChanges(changes) {
        if (this.model && this.currentSelection && changes.currentSelection) {
            this.selectPanel(this.currentSelection);
            if (this.navigateOnClick) {
                this.navigateToSelectedItem(this.currentSelection);
            }
        }
    }
    onPanelItemClick(panelItem) {
        this.selectPanel(panelItem);
        this.currentSelection = panelItem;
        if (this.navigateOnClick) {
            this.navigateToSelectedItem(panelItem);
        }
        this.panelSelected.emit(panelItem);
    }
    navigateToSelectedItem(selectedPanel) {
        const navigationExtras = {
            queryParams: selectedPanel.queryParams,
            relativeTo: this.activatedRoute,
        };
        this.router.navigateByUrl(selectedPanel.route, navigationExtras);
    }
    /** Public interface to select panel item */
    selectPanel(panelItem) {
        this.deselectSideNav(this.model.navigationLinks);
        this.selectSideNav(panelItem, this.model.navigationLinks);
    }
    /**
     * Deselects any previously selected sidenav item
     * @param sidenavItems
     */
    deselectSideNav(sidenavItems) {
        sidenavItems.forEach((sideNavItem) => {
            if (sideNavItem.children) {
                this.deselectSideNav(sideNavItem.children);
            }
            sideNavItem.selected = false;
        });
    }
    /**
     * Selects the clicked sidenav item as we as any parent
     * @param selectedItem
     * @param allNavItems
     */
    selectSideNav(selectedItem, allNavItems) {
        for (const item of allNavItems) {
            if (item === selectedItem) {
                item.selected = true;
                return true;
            }
            else if (item.children) {
                const isChildSelected = this.selectSideNav(selectedItem, item.children);
                if (isChildSelected) {
                    item.selected = true;
                    return true;
                }
            }
        }
        return false;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSelectionPanelNavigationModeComponent_Factory(t) { return new (t || SdsSelectionPanelNavigationModeComponent)(i0.ɵɵdirectiveInject(i1$5.Router), i0.ɵɵdirectiveInject(i1$5.ActivatedRoute)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSelectionPanelNavigationModeComponent, selectors: [["sds-selection-panel-navigation-mode"]], inputs: { model: "model", navigateOnClick: "navigateOnClick", currentSelection: "currentSelection" }, outputs: { panelSelected: "panelSelected" }, features: [i0.ɵɵNgOnChangesFeature], decls: 4, vars: 1, consts: [["subPanelTemplate", ""], [1, "usa-sidenav", "usa-sidenav--styled"], [4, "ngFor", "ngForOf"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], ["href", "javascript:void(0);", 1, "usa-sidenav__item", 3, "click", "ngClass"], ["class", "usa-sidenav__sublist", 4, "ngIf"], [1, "usa-sidenav__sublist"]], template: function SdsSelectionPanelNavigationModeComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "ul", 1);
            i0.ɵɵtemplate(1, SdsSelectionPanelNavigationModeComponent_li_1_Template, 2, 4, "li", 2);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, SdsSelectionPanelNavigationModeComponent_ng_template_2_Template, 4, 5, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngForOf", ctx.model.navigationLinks);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSelectionPanelNavigationModeComponent, [{
        type: Component,
        args: [{ selector: 'sds-selection-panel-navigation-mode', template: "<ul class=\"usa-sidenav usa-sidenav--styled\">\n  <li *ngFor=\"let panelItem of model.navigationLinks\">\n    <ng-container [ngTemplateOutlet]=\"subPanelTemplate\" [ngTemplateOutletContext]=\"{ $implicit: panelItem }\">\n    </ng-container>\n  </li>\n</ul>\n\n<ng-template #subPanelTemplate let-panelItem>\n  <a\n    href=\"javascript:void(0);\"\n    class=\"usa-sidenav__item\"\n    (click)=\"onPanelItemClick(panelItem)\"\n    [ngClass]=\"{ 'usa-current': panelItem.selected }\"\n  >\n    <span>{{ panelItem.text }}</span>\n  </a>\n  <ul *ngIf=\"panelItem.children\" class=\"usa-sidenav__sublist\">\n    <li *ngFor=\"let panelItem of panelItem.children\">\n      <ng-container [ngTemplateOutlet]=\"subPanelTemplate\" [ngTemplateOutletContext]=\"{ $implicit: panelItem }\">\n      </ng-container>\n    </li>\n  </ul>\n</ng-template>\n" }]
    }], () => [{ type: i1$5.Router }, { type: i1$5.ActivatedRoute }], { model: [{
            type: Input
        }], navigateOnClick: [{
            type: Input
        }], currentSelection: [{
            type: Input
        }], panelSelected: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSelectionPanelNavigationModeComponent, { className: "SdsSelectionPanelNavigationModeComponent" }); })();

function SdsSelectionPanelComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelementStart(1, "sds-selection-panel-selection-mode", 2);
    i0.ɵɵlistener("panelSelected", function SdsSelectionPanelComponent_ng_container_0_Template_sds_selection_panel_selection_mode_panelSelected_1_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.panelSelected.emit($event)); });
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("model", ctx_r1.model)("currentSelection", ctx_r1.currentSelection);
} }
function SdsSelectionPanelComponent_ng_template_1_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "sds-selection-panel-navigation-mode", 3);
    i0.ɵɵlistener("panelSelected", function SdsSelectionPanelComponent_ng_template_1_Template_sds_selection_panel_navigation_mode_panelSelected_0_listener($event) { i0.ɵɵrestoreView(_r3); const ctx_r1 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r1.panelSelected.emit($event)); });
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("model", ctx_r1.model)("navigateOnClick", ctx_r1.navigateOnClick)("currentSelection", ctx_r1.currentSelection);
} }
class SdsSelectionPanelComponent {
    constructor() {
        /**
         * Outputs a NavigationLink item when it is selected from the panel
         */
        this.panelSelected = new EventEmitter();
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSelectionPanelComponent_Factory(t) { return new (t || SdsSelectionPanelComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSelectionPanelComponent, selectors: [["sds-selection-panel"]], inputs: { model: "model", navigateOnClick: "navigateOnClick", currentSelection: "currentSelection" }, outputs: { panelSelected: "panelSelected" }, decls: 3, vars: 2, consts: [["selectionMode", ""], [4, "ngIf", "ngIfElse"], [3, "panelSelected", "model", "currentSelection"], [3, "panelSelected", "model", "navigateOnClick", "currentSelection"]], template: function SdsSelectionPanelComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵtemplate(0, SdsSelectionPanelComponent_ng_container_0_Template, 2, 2, "ng-container", 1)(1, SdsSelectionPanelComponent_ng_template_1_Template, 1, 3, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            const selectionMode_r4 = i0.ɵɵreference(2);
            i0.ɵɵproperty("ngIf", ctx.model.selectionMode && ctx.model.selectionMode === "SELECTION")("ngIfElse", selectionMode_r4);
        } }, dependencies: [i1$2.NgIf, SdsSelectionPanelSelectionModeComponent, SdsSelectionPanelNavigationModeComponent], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSelectionPanelComponent, [{
        type: Component,
        args: [{ selector: 'sds-selection-panel', template: "<ng-container *ngIf=\"model.selectionMode && model.selectionMode === 'SELECTION'; else selectionMode\">\n  <sds-selection-panel-selection-mode\n    [model]=\"model\"\n    [currentSelection]=\"currentSelection\"\n    (panelSelected)=\"panelSelected.emit($event)\"\n  >\n  </sds-selection-panel-selection-mode>\n</ng-container>\n\n<!-- Default mode if no selectionMode is passed in -->\n<ng-template #selectionMode>\n  <sds-selection-panel-navigation-mode\n    [model]=\"model\"\n    [navigateOnClick]=\"navigateOnClick\"\n    [currentSelection]=\"currentSelection\"\n    (panelSelected)=\"panelSelected.emit($event)\"\n  >\n  </sds-selection-panel-navigation-mode>\n</ng-template>\n" }]
    }], null, { model: [{
            type: Input
        }], navigateOnClick: [{
            type: Input
        }], currentSelection: [{
            type: Input
        }], panelSelected: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSelectionPanelComponent, { className: "SdsSelectionPanelComponent" }); })();

const _c0$8 = a0 => ({ $implicit: a0 });
const _c1$2 = a0 => ({ "usa-link--active": a0 });
function SdsSubPanelComponent_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 3);
    i0.ɵɵelementContainer(1, 4);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const panelItem_r1 = ctx.$implicit;
    i0.ɵɵnextContext();
    const subPanelTemplate_r2 = i0.ɵɵreference(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", subPanelTemplate_r2)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c0$8, panelItem_r1));
} }
function SdsSubPanelComponent_ng_template_2_ul_2_li_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "li", 9);
    i0.ɵɵelementContainer(1, 4);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const panelItem_r6 = ctx.$implicit;
    i0.ɵɵnextContext(3);
    const subPanelTemplate_r2 = i0.ɵɵreference(3);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", subPanelTemplate_r2)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c0$8, panelItem_r6));
} }
function SdsSubPanelComponent_ng_template_2_ul_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "ul", 7);
    i0.ɵɵtemplate(1, SdsSubPanelComponent_ng_template_2_ul_2_li_1_Template, 2, 4, "li", 8);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const panelItem_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", panelItem_r4.children);
} }
function SdsSubPanelComponent_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "a", 5);
    i0.ɵɵlistener("click", function SdsSubPanelComponent_ng_template_2_Template_a_click_0_listener() { const panelItem_r4 = i0.ɵɵrestoreView(_r3).$implicit; const ctx_r4 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r4.onSubPanelItemClicked(panelItem_r4)); });
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(2, SdsSubPanelComponent_ng_template_2_ul_2_Template, 2, 1, "ul", 6);
} if (rf & 2) {
    const panelItem_r4 = ctx.$implicit;
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(3, _c1$2, ctx_r4.currentSelection && panelItem_r4.id === ctx_r4.currentSelection.id));
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", panelItem_r4.text, "");
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", panelItem_r4.children);
} }
class SdsSubPanelComponent {
    constructor() {
        this.subPanelClicked = new EventEmitter();
    }
    onSubPanelItemClicked(item) {
        this.currentSelection = item;
        this.subPanelClicked.emit(item);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsSubPanelComponent_Factory(t) { return new (t || SdsSubPanelComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsSubPanelComponent, selectors: [["sds-sub-panel"]], inputs: { model: "model", currentSelection: "currentSelection" }, outputs: { subPanelClicked: "subPanelClicked" }, decls: 4, vars: 1, consts: [["subPanelTemplate", ""], [1, "usa-sidenav", "sds-subpanel", "sds-list"], ["class", "usa-sidenav__item", 4, "ngFor", "ngForOf"], [1, "usa-sidenav__item"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], ["href", "javascript:void(0);", 1, "usa-link", "margin-right-neg-2", 3, "click", "ngClass"], ["class", "usa-sidenav__sublist", 4, "ngIf"], [1, "usa-sidenav__sublist"], ["class", "sidenav__item", 4, "ngFor", "ngForOf"], [1, "sidenav__item"]], template: function SdsSubPanelComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "ul", 1);
            i0.ɵɵtemplate(1, SdsSubPanelComponent_li_1_Template, 2, 4, "li", 2);
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(2, SdsSubPanelComponent_ng_template_2_Template, 3, 5, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngForOf", ctx.model);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSubPanelComponent, [{
        type: Component,
        args: [{ selector: `sds-sub-panel`, template: "<ul class=\"usa-sidenav sds-subpanel sds-list\">\n  <li *ngFor=\"let panelItem of model\" class=\"usa-sidenav__item\">\n    <ng-container [ngTemplateOutlet]=\"subPanelTemplate\" [ngTemplateOutletContext]=\"{ $implicit: panelItem }\">\n    </ng-container>\n  </li>\n</ul>\n\n<ng-template #subPanelTemplate let-panelItem>\n  <a\n    href=\"javascript:void(0);\"\n    class=\"usa-link margin-right-neg-2\"\n    [ngClass]=\"{\n      'usa-link--active': currentSelection && panelItem.id === currentSelection.id\n    }\"\n    (click)=\"onSubPanelItemClicked(panelItem)\"\n  >\n    {{ panelItem.text }}</a\n  >\n  <ul *ngIf=\"panelItem.children\" class=\"usa-sidenav__sublist\">\n    <li *ngFor=\"let panelItem of panelItem.children\" class=\"sidenav__item\">\n      <ng-container [ngTemplateOutlet]=\"subPanelTemplate\" [ngTemplateOutletContext]=\"{ $implicit: panelItem }\">\n      </ng-container>\n    </li>\n  </ul>\n</ng-template>\n" }]
    }], null, { model: [{
            type: Input
        }], currentSelection: [{
            type: Input
        }], subPanelClicked: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsSubPanelComponent, { className: "SdsSubPanelComponent" }); })();

class SdsSelectionPanelModule {
    /** @nocollapse */ static { this.ɵfac = function SdsSelectionPanelModule_Factory(t) { return new (t || SdsSelectionPanelModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsSelectionPanelModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, RouterModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsSelectionPanelModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, RouterModule],
                declarations: [
                    SdsSelectionPanelComponent,
                    SdsSubPanelComponent,
                    SdsSelectionPanelSelectionModeComponent,
                    SdsSelectionPanelNavigationModeComponent,
                ],
                exports: [SdsSelectionPanelComponent, SdsSubPanelComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsSelectionPanelModule, { declarations: [SdsSelectionPanelComponent,
        SdsSubPanelComponent,
        SdsSelectionPanelSelectionModeComponent,
        SdsSelectionPanelNavigationModeComponent], imports: [CommonModule, RouterModule], exports: [SdsSelectionPanelComponent, SdsSubPanelComponent] }); })();

class SelectionPanelModel extends SideNavigationModel {
}

const _c0$7 = ["*"];
const _c1$1 = (a0, a1) => ({ "display-none": a0, "display-block": a1 });
class TabPanelComponent {
    /** @nocollapse */ static { this.ɵfac = function TabPanelComponent_Factory(t) { return new (t || TabPanelComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: TabPanelComponent, selectors: [["sds-tab-panel"]], inputs: { id: "id", selected: "selected", tabHeader: "tabHeader", ariaLabel: "ariaLabel", disabled: "disabled" }, ngContentSelectors: _c0$7, decls: 2, vars: 6, consts: [["role", "tabpanel", 3, "ngClass"]], template: function TabPanelComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵprojection(1);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(3, _c1$1, !ctx.selected, ctx.selected));
            i0.ɵɵattribute("aria-hidden", !ctx.selected)("id", ctx.id);
        } }, dependencies: [i1$2.NgClass], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TabPanelComponent, [{
        type: Component,
        args: [{ selector: `sds-tab-panel`, template: "<div\n  [attr.aria-hidden]=\"!selected\"\n  [attr.id]=\"id\"\n  role=\"tabpanel\"\n  [ngClass]=\"{ 'display-none': !selected, 'display-block': selected }\"\n>\n  <ng-content></ng-content>\n</div>\n" }]
    }], null, { id: [{
            type: Input
        }], selected: [{
            type: Input
        }], tabHeader: [{
            type: Input
        }], ariaLabel: [{
            type: Input
        }], disabled: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(TabPanelComponent, { className: "TabPanelComponent" }); })();

const _c0$6 = ["*"];
function TabsComponent_button_1_ng_container_1_1_ng_template_0_Template(rf, ctx) { }
function TabsComponent_button_1_ng_container_1_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, TabsComponent_button_1_ng_container_1_1_ng_template_0_Template, 0, 0, "ng-template");
} }
function TabsComponent_button_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, TabsComponent_button_1_ng_container_1_1_Template, 1, 0, null, 6);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const tabPanel_r2 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", tabPanel_r2.tabHeader);
} }
function TabsComponent_button_1_ng_template_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtext(0);
} if (rf & 2) {
    const tabPanel_r2 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵtextInterpolate1(" ", tabPanel_r2.tabHeader, " ");
} }
function TabsComponent_button_1_Template(rf, ctx) { if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 4);
    i0.ɵɵlistener("click", function TabsComponent_button_1_Template_button_click_0_listener($event) { const tabPanel_r2 = i0.ɵɵrestoreView(_r1).$implicit; const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.onTabClicked($event, tabPanel_r2)); })("keydown", function TabsComponent_button_1_Template_button_keydown_0_listener($event) { i0.ɵɵrestoreView(_r1); const ctx_r2 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r2.onKeyDown($event)); });
    i0.ɵɵtemplate(1, TabsComponent_button_1_ng_container_1_Template, 2, 1, "ng-container", 5)(2, TabsComponent_button_1_ng_template_2_Template, 1, 1, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const tabPanel_r2 = ctx.$implicit;
    const textHeader_r4 = i0.ɵɵreference(3);
    const ctx_r2 = i0.ɵɵnextContext();
    i0.ɵɵproperty("disabled", tabPanel_r2.disabled);
    i0.ɵɵattribute("aria-selected", tabPanel_r2.selected)("tabindex", tabPanel_r2.selected ? 0 : -1)("aria-controls", tabPanel_r2.id)("aria-label", tabPanel_r2.ariaLabel);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r2.isObj(tabPanel_r2.tabHeader))("ngIfElse", textHeader_r4);
} }
/** CONSTANTS
 * Adding in the constant values for keycodes
 * to handle onKeyDown events
 */
const LEFT_ARROW = 37;
const RIGHT_ARROW = 39;
class TabsComponent {
    constructor() {
        /**
         * Enable automatic activation for tabs when cycling through tabs using
         * Left or Right arrow keys. Automatic activated tabs will automatically
         * be selected on focus as user presses left or right arrow to move
         * through each tab. By default, tabs will need to be manually selected
         * when using keyboard only by pressing either Enter or Space to select
         * the focused tab
         */
        this.automaticActivation = false;
        /**
         * CSS styling class for tabs
         */
        this.tabClass = 'sds-tabs--default';
        this.interceptTabChange = false;
        /**
         * Emits an event whenever a tab is selected by the user containing the selected
         * TabPanelComponent. Please note that because this output contains `Change` suffix
         * to the `tabSelected` input, users can 2-way bind to the `selectedTab` input
         */
        this.selectedTabChange = new EventEmitter();
        this.preTabChange = new EventEmitter();
    }
    /**
     * Assigns selected tab value was given. Otherwise, the first
     * tab panel in the list will be selected after content has initialized.
     */
    ngOnInit() {
        if (this.selectedTab) {
            this.selectedTab.selected = true;
            this.focusedTab = this.selectedTab;
        }
    }
    /**
     * Listens for programmatic change to selected tab. When a tab is changed
     * programmatically, we must disable previously selected tab, set selected
     * property of the new tab to true, and adjust bookkeeping values.
     */
    ngOnChanges(changes) {
        if (changes.selectedTab && changes.selectedTab.currentValue) {
            this.tabPanels.forEach((tab) => (tab.selected = false));
            changes.selectedTab.currentValue.selected = true;
            this.focusedTab = this.selectedTab;
        }
    }
    /**
     * If user has not given any tabs to select initially,
     * select the first tab panel.
     */
    ngAfterContentInit() {
        if (!this.selectedTab) {
            this.tabPanels.first.selected = true;
            this.selectedTab = this.tabPanels.first;
            this.focusedTab = this.selectedTab;
        }
    }
    onTabClicked(clickEvent, tabPanel) {
        this.changeSelectedTabPanel(tabPanel);
    }
    /**
     * Looks for Left and Right arrow presses and moves selected tab accordingly.
     * @param $event - Keyboard Event
     */
    onKeyDown($event) {
        console.log($event);
        const tabPanelArray = this.tabPanels.toArray();
        let selectedTabIndex = tabPanelArray.findIndex((tabPanel) => tabPanel === this.focusedTab);
        switch ($event.keyCode) {
            case LEFT_ARROW:
                selectedTabIndex = this.getNextTabLeft(tabPanelArray, selectedTabIndex);
                break;
            case RIGHT_ARROW:
                selectedTabIndex = this.getNextTabRight(tabPanelArray, selectedTabIndex);
                break;
        }
        // Move focus to newly selected panel
        $event.target.parentElement.children[selectedTabIndex].focus();
        this.focusedTab = tabPanelArray[selectedTabIndex];
        if (this.automaticActivation) {
            this.changeSelectedTabPanel(this.focusedTab);
        }
    }
    isObj(obj) {
        if (typeof obj === 'object' && obj.elementRef) {
            return true;
        }
        else if (typeof obj === 'string') {
            return false;
        }
        else {
            throw new Error('Tab header must be either a string or a template reference');
        }
    }
    /**
     * Internal method to change currently selected tab panel and display another panel
     * to the user. External components should not use this method, but rather
     * two-way bind the selectedTab input and modify that value.
     * @param newTabPanel
     */
    changeSelectedTabPanel(newTabPanel) {
        this.preTabChange.emit(newTabPanel);
        if (this.interceptTabChange) {
            return;
        }
        this.selectedTab.selected = false;
        newTabPanel.selected = true;
        this.selectedTab = newTabPanel;
        this.focusedTab = newTabPanel;
        this.selectedTabChange.emit(this.selectedTab);
    }
    /**
     * Gets next tab panel to the left of currently selected panel when Left Arrow is pressed.
     * Skips disabled panels until it finds next left panel that is not disabled
     * @param tabPanels - List of TabPanelComponents in the tab group
     * @param startIndex - Current index of focused tab panel
     */
    getNextTabLeft(tabPanels, startIndex) {
        for (let i = 0; i < tabPanels.length; i++) {
            startIndex = startIndex == 0 ? tabPanels.length - 1 : (startIndex - 1) % tabPanels.length;
            if (!tabPanels[startIndex].disabled) {
                return startIndex;
            }
        }
    }
    /**
     * Gets next tab panel to the right of currently selected panel when Right Arrow is pressed
     * Skips disabled panels until it finds next right panel that is not disabled
     * @param tabPanels - List of TabPanelComponents in the tab group
     * @param startIndex - Current index of focused tab panel
     */
    getNextTabRight(tabPanels, startIndex) {
        for (let i = 0; i < tabPanels.length; i++) {
            startIndex = (startIndex + 1) % this.tabPanels.length;
            if (!tabPanels[startIndex].disabled) {
                return startIndex;
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = function TabsComponent_Factory(t) { return new (t || TabsComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: TabsComponent, selectors: [["sds-tabs"]], contentQueries: function TabsComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, TabPanelComponent, 4);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.tabPanels = _t);
        } }, inputs: { selectedTab: "selectedTab", automaticActivation: "automaticActivation", tabClass: "tabClass", interceptTabChange: "interceptTabChange" }, outputs: { selectedTabChange: "selectedTabChange", preTabChange: "preTabChange" }, features: [i0.ɵɵNgOnChangesFeature], ngContentSelectors: _c0$6, decls: 4, vars: 2, consts: [["textHeader", ""], ["role", "tablist"], ["role", "tab", "class", "sds-tabs__item", 3, "disabled", "click", "keydown", 4, "ngFor", "ngForOf"], ["tabindex", "0", "role", "tabpanel", 1, "sds-tabs__content"], ["role", "tab", 1, "sds-tabs__item", 3, "click", "keydown", "disabled"], [4, "ngIf", "ngIfElse"], [4, "ngTemplateOutlet"]], template: function TabsComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelementStart(0, "div", 1);
            i0.ɵɵtemplate(1, TabsComponent_button_1_Template, 4, 7, "button", 2);
            i0.ɵɵelementEnd();
            i0.ɵɵelementStart(2, "div", 3);
            i0.ɵɵprojection(3);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵattribute("class", ctx.tabClass);
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngForOf", ctx.tabPanels);
        } }, dependencies: [i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(TabsComponent, [{
        type: Component,
        args: [{ selector: `sds-tabs`, template: "<div role=\"tablist\" [attr.class]=\"tabClass\">\n  <button\n    *ngFor=\"let tabPanel of tabPanels\"\n    role=\"tab\"\n    class=\"sds-tabs__item\"\n    [attr.aria-selected]=\"tabPanel.selected\"\n    [attr.tabindex]=\"tabPanel.selected ? 0 : -1\"\n    [attr.aria-controls]=\"tabPanel.id\"\n    [attr.aria-label]=\"tabPanel.ariaLabel\"\n    [disabled]=\"tabPanel.disabled\"\n    (click)=\"onTabClicked($event, tabPanel)\"\n    (keydown)=\"onKeyDown($event)\"\n  >\n    <ng-container *ngIf=\"isObj(tabPanel.tabHeader); else textHeader\">\n      <ng-template *ngTemplateOutlet=\"tabPanel.tabHeader\"></ng-template>\n    </ng-container>\n    <ng-template #textHeader>\n      {{ tabPanel.tabHeader }}\n    </ng-template>\n  </button>\n</div>\n<div class=\"sds-tabs__content\" tabindex=\"0\" role=\"tabpanel\">\n  <ng-content></ng-content>\n</div>\n" }]
    }], null, { selectedTab: [{
            type: Input
        }], automaticActivation: [{
            type: Input
        }], tabClass: [{
            type: Input
        }], interceptTabChange: [{
            type: Input
        }], selectedTabChange: [{
            type: Output
        }], preTabChange: [{
            type: Output
        }], tabPanels: [{
            type: ContentChildren,
            args: [TabPanelComponent]
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(TabsComponent, { className: "TabsComponent" }); })();

class SdsTabsModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTabsModule_Factory(t) { return new (t || SdsTabsModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTabsModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTabsModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                declarations: [TabsComponent, TabPanelComponent],
                exports: [TabsComponent, TabPanelComponent],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTabsModule, { declarations: [TabsComponent, TabPanelComponent], imports: [CommonModule], exports: [TabsComponent, TabPanelComponent] }); })();

class SdsDatePipe {
    constructor(datePipe) {
        this.datePipe = datePipe;
    }
    transform(value) {
        const passedInDate = new Date(value);
        const now = new Date();
        if (now.getFullYear() === passedInDate.getFullYear() &&
            now.getMonth() === passedInDate.getMonth() &&
            now.getDate() === passedInDate.getDate()) {
            return this.datePipe.transform(passedInDate, 'shortTime');
        }
        else if (now.getFullYear() === passedInDate.getFullYear()) {
            return this.datePipe.transform(passedInDate, 'MMM d');
        }
        else {
            return this.datePipe.transform(passedInDate, 'mediumDate');
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsDatePipe_Factory(t) { return new (t || SdsDatePipe)(i0.ɵɵdirectiveInject(i1$2.DatePipe, 16)); }; }
    /** @nocollapse */ static { this.ɵpipe = /** @pureOrBreakMyCode */ i0.ɵɵdefinePipe({ name: "sdsDate", type: SdsDatePipe, pure: true }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDatePipe, [{
        type: Pipe,
        args: [{
                name: 'sdsDate',
            }]
    }], () => [{ type: i1$2.DatePipe }], null); })();

class SdsDateModule {
    /** @nocollapse */ static { this.ɵfac = function SdsDateModule_Factory(t) { return new (t || SdsDateModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsDateModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ providers: [DatePipe], imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsDateModule, [{
        type: NgModule,
        args: [{
                declarations: [SdsDatePipe],
                imports: [CommonModule],
                exports: [SdsDatePipe],
                providers: [DatePipe],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsDateModule, { declarations: [SdsDatePipe], imports: [CommonModule], exports: [SdsDatePipe] }); })();

const _c0$5 = ["searchInput"];
class SdsEditorComponent {
    constructor(cd, _document) {
        this.cd = cd;
        this._document = _document;
        this.id = 'searchEditor';
        this.regex = '';
        this.validateOnBlur = false;
        this.model = '';
        this._onChange = (_) => { };
        this._onTouched = () => { };
    }
    // Helper method to programatically update a value of the model on blur
    onBlur(value) {
        this.model = value;
        if (this.regex) {
            this.validateRegex(this.model);
        }
        this.updateModel();
    }
    // Helper method that gets a new instance of the model and notifies ControlValueAccessor that we have a new model for this FormControl (our custom component)
    updateModel() {
        const model = this.getModel();
        this._onChange(model);
    }
    // Helper method to return a new instance of an value
    getModel() {
        return this.model;
    }
    // Validate regex and highlight first charecter of the failure
    validateRegex(value) {
        const rawValue = value
            .replaceAll(/<\/?mark[^>]*>/g, '')
            .replaceAll(/<\/?span[^>]*>/g, '')
            .replaceAll(/<\/?font[^>]*>/g, '');
        const regex = new RegExp(this.regex, 'g');
        let res = '';
        let result = regex.exec(rawValue);
        if (result) {
            let index = result.index;
            res =
                rawValue.substring(0, index) +
                    '<mark>' +
                    rawValue.substring(index, index + 1) +
                    '</mark>' +
                    rawValue.substring(index + 1, index + rawValue.length);
            this.searchInput.nativeElement.innerHTML = res;
        }
        else {
            this.searchInput.nativeElement.innerHTML = rawValue;
        }
    }
    // ControlValueAccessor (and Formly) is trying to update the value of the FormControl (our custom component) programatically
    // If there is a value we will just overwrite items
    // If there is no value we reset the items array to be empty
    writeValue(value) {
        if (value) {
            this.model = value;
            if (this.searchInput)
                this.searchInput.nativeElement.innerHTML = value;
            this.cd.markForCheck();
        }
        else {
            this.model = '';
            if (this.searchInput)
                this.searchInput.nativeElement.innerHTML = '';
            this.cd.markForCheck();
        }
    }
    // ControlValueAccessor hook that lets us call this._onChange(let) to let the form know our variable has changed (in this case model)
    registerOnChange(fn) {
        this._onChange = fn;
    }
    // ControlValueAccessor hook (not used)
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    // Get the cursor current position
    getCaretCharacterOffsetWithin(element) {
        let caretOffset = 0;
        if (typeof window.getSelection != 'undefined') {
            let range = window.getSelection().getRangeAt(0);
            let preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(element);
            preCaretRange.setEnd(range.endContainer, range.endOffset);
            caretOffset = preCaretRange.toString().length;
        }
        return caretOffset;
    }
    onValueChange(value) {
        const pos = this.getCaretCharacterOffsetWithin(this.searchInput.nativeElement);
        if (this.regex) {
            this.validateRegex(value);
        }
        this.model = value;
        this.updateModel();
        let node = this.searchInput.nativeElement;
        if (pos < node?.innerText?.length) {
            let firstNodeLength = node.childNodes[0].textContent.length;
            let childNodeIndex = pos <= firstNodeLength ? 0 : 2;
            let startPosition = childNodeIndex === 0 ? pos : pos - firstNodeLength - 1;
            let range = this._document.createRange();
            let sel = window.getSelection();
            range.setStart(this.searchInput.nativeElement.childNodes[childNodeIndex], startPosition);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
            this.searchInput.nativeElement.focus();
        }
        else {
            this._document.execCommand('selectAll', false, null);
            this._document.getSelection().collapseToEnd();
        }
    }
    /** @nocollapse */ static { this.ɵfac = function SdsEditorComponent_Factory(t) { return new (t || SdsEditorComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(DOCUMENT)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsEditorComponent, selectors: [["sds-editor"]], viewQuery: function SdsEditorComponent_Query(rf, ctx) { if (rf & 1) {
            i0.ɵɵviewQuery(_c0$5, 7, ElementRef);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.searchInput = _t.first);
        } }, inputs: { id: "id", regex: "regex", validateOnBlur: "validateOnBlur" }, features: [i0.ɵɵProvidersFeature([
                {
                    provide: NG_VALUE_ACCESSOR,
                    useExisting: forwardRef((() => SdsEditorComponent)),
                    multi: true,
                },
            ])], decls: 2, vars: 1, consts: [["searchInput", ""], ["contenteditable", "true", 1, "minh-15", "border-gray-70", "border-1px", "margin-top-1", "padding-05", 3, "input", "blur"]], template: function SdsEditorComponent_Template(rf, ctx) { if (rf & 1) {
            const _r1 = i0.ɵɵgetCurrentView();
            i0.ɵɵelementStart(0, "div", 1, 0);
            i0.ɵɵlistener("input", function SdsEditorComponent_Template_div_input_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(!ctx.validateOnBlur && ctx.onValueChange($event.target.innerHTML)); })("blur", function SdsEditorComponent_Template_div_blur_0_listener($event) { i0.ɵɵrestoreView(_r1); return i0.ɵɵresetView(ctx.validateOnBlur && ctx.onBlur($event.target.innerHTML)); });
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵattribute("id", ctx.id);
        } }, styles: ["mark{background-color:red;color:#fff}"], changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsEditorComponent, [{
        type: Component,
        args: [{ selector: 'sds-editor', template: `
    <div
      #searchInput
      [attr.id]="id"
      class="minh-15 border-gray-70 border-1px margin-top-1 padding-05"
      contenteditable="true"
      (input)="!validateOnBlur && onValueChange($event.target.innerHTML)"
      (blur)="validateOnBlur && onBlur($event.target.innerHTML)"
    ></div>
  `, providers: [
                    {
                        provide: NG_VALUE_ACCESSOR,
                        useExisting: forwardRef((() => SdsEditorComponent)),
                        multi: true,
                    },
                ], changeDetection: ChangeDetectionStrategy.OnPush, styles: ["::ng-deep mark{background-color:red;color:#fff}\n"] }]
    }], () => [{ type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }], { searchInput: [{
            type: ViewChild,
            args: ['searchInput', { read: ElementRef, static: true }]
        }], id: [{
            type: Input
        }], regex: [{
            type: Input
        }], validateOnBlur: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsEditorComponent, { className: "SdsEditorComponent" }); })();

class SdsEditorModule {
    /** @nocollapse */ static { this.ɵfac = function SdsEditorModule_Factory(t) { return new (t || SdsEditorModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsEditorModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, A11yModule, FormsModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsEditorModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, A11yModule, FormsModule],
                exports: [SdsEditorComponent],
                declarations: [SdsEditorComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsEditorModule, { declarations: [SdsEditorComponent], imports: [CommonModule, A11yModule, FormsModule], exports: [SdsEditorComponent] }); })();

const _c0$4 = (a0, a1) => ({ "sds-tree-table__row--selected": a0, "sds-tree-table__row--highlight-border": a1 });
const _c1 = (a0, a1, a2) => ({ $implicit: a0, index: a1, level: a2 });
function SdsTreeTableComponent_th_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "th");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const col_r1 = ctx.$implicit;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(col_r1);
} }
function SdsTreeTableComponent_ng_container_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 11);
} if (rf & 2) {
    const data_r2 = ctx.$implicit;
    const i_r3 = ctx.index;
    const ctx_r3 = i0.ɵɵnextContext();
    const treeTablePanel_r5 = i0.ɵɵreference(10);
    i0.ɵɵproperty("ngTemplateOutlet", treeTablePanel_r5)("ngTemplateOutletContext", ctx_r3.getTemplateContext(ctx_r3.dataSource, data_r2, i_r3, 0, data_r2 === ctx_r3._selectedRowParent));
} }
function SdsTreeTableComponent_ng_template_9_span_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 18, 2);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const border_r9 = i0.ɵɵreference(1);
    const parentRow_r10 = i0.ɵɵnextContext().parentRow;
    const tableRow_r8 = i0.ɵɵreference(1);
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", ctx_r3.setHeight(tableRow_r8, parentRow_r10, border_r9), " ");
} }
function SdsTreeTableComponent_ng_template_9_button_5_Template(rf, ctx) { if (rf & 1) {
    const _r11 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 19);
    i0.ɵɵlistener("click", function SdsTreeTableComponent_ng_template_9_button_5_Template_button_click_0_listener() { i0.ɵɵrestoreView(_r11); const row_r7 = i0.ɵɵnextContext().row; const tableRow_r8 = i0.ɵɵreference(1); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.onRowClicked(row_r7, tableRow_r8)); });
    i0.ɵɵelementStart(1, "span", 20);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(3, "usa-icon", 21);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const row_r7 = i0.ɵɵnextContext().row;
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵattribute("tabindex", row_r7 === ctx_r3._selectedRow ? 1 : -1);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1("", row_r7.expanded ? "Collapse" : "Expand", " current row");
    i0.ɵɵadvance();
    i0.ɵɵproperty("icon", row_r7.expanded ? "dash-circle-fill" : "plus-circle-fill")("size", "2x");
} }
function SdsTreeTableComponent_ng_template_9_ng_container_7_ng_container_1_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0, 11);
} if (rf & 2) {
    const ctx_r11 = i0.ɵɵnextContext();
    const data_r13 = ctx_r11.$implicit;
    const i_r14 = ctx_r11.index;
    const ctx_r14 = i0.ɵɵnextContext(2);
    const row_r7 = ctx_r14.row;
    const level_r16 = ctx_r14.level;
    const parentSelected_r17 = ctx_r14.parentSelected;
    const tableRow_r8 = i0.ɵɵreference(1);
    const ctx_r3 = i0.ɵɵnextContext();
    const treeTablePanel_r5 = i0.ɵɵreference(10);
    i0.ɵɵproperty("ngTemplateOutlet", treeTablePanel_r5)("ngTemplateOutletContext", ctx_r3.getTemplateContext(row_r7, data_r13, i_r14, level_r16, parentSelected_r17, tableRow_r8));
} }
function SdsTreeTableComponent_ng_template_9_ng_container_7_ng_container_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsTreeTableComponent_ng_template_9_ng_container_7_ng_container_1_ng_container_1_Template, 1, 2, "ng-container", 22);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const i_r14 = ctx.index;
    const row_r7 = i0.ɵɵnextContext(2).row;
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", row_r7.children.length <= ctx_r3.childrenLimit || row_r7.viewAllChildren || i_r14 < ctx_r3.numChildrenToDisplay);
} }
function SdsTreeTableComponent_ng_template_9_ng_container_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, SdsTreeTableComponent_ng_template_9_ng_container_7_ng_container_1_Template, 2, 1, "ng-container", 9);
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const row_r7 = i0.ɵɵnextContext().row;
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngForOf", row_r7.children);
} }
function SdsTreeTableComponent_ng_template_9_tr_8_span_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "span");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const row_r7 = i0.ɵɵnextContext(2).row;
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" ", row_r7.totalChildren, " ");
} }
function SdsTreeTableComponent_ng_template_9_tr_8_ng_template_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtext(0);
} if (rf & 2) {
    const row_r7 = i0.ɵɵnextContext(2).row;
    i0.ɵɵtextInterpolate(row_r7.children == null ? null : row_r7.children.length);
} }
function SdsTreeTableComponent_ng_template_9_tr_8_Template(rf, ctx) { if (rf & 1) {
    const _r18 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tr", 23, 3);
    i0.ɵɵlistener("keydown.enter", function SdsTreeTableComponent_ng_template_9_tr_8_Template_tr_keydown_enter_0_listener() { i0.ɵɵrestoreView(_r18); const viewAllRow_r19 = i0.ɵɵreference(1); const row_r7 = i0.ɵɵnextContext().row; const tableRow_r8 = i0.ɵɵreference(1); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.viewAllClicked(row_r7, viewAllRow_r19, tableRow_r8)); })("keydown", function SdsTreeTableComponent_ng_template_9_tr_8_Template_tr_keydown_0_listener($event) { i0.ɵɵrestoreView(_r18); const viewAllRow_r19 = i0.ɵɵreference(1); const ctx_r3 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r3.onKeyDown($event, viewAllRow_r19)); });
    i0.ɵɵelement(2, "td");
    i0.ɵɵelementStart(3, "td", 24)(4, "button", 25);
    i0.ɵɵlistener("click", function SdsTreeTableComponent_ng_template_9_tr_8_Template_button_click_4_listener() { i0.ɵɵrestoreView(_r18); const viewAllRow_r19 = i0.ɵɵreference(1); const row_r7 = i0.ɵɵnextContext().row; const tableRow_r8 = i0.ɵɵreference(1); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.viewAllClicked(row_r7, viewAllRow_r19, tableRow_r8)); });
    i0.ɵɵtext(5, " View All ( ");
    i0.ɵɵtemplate(6, SdsTreeTableComponent_ng_template_9_tr_8_span_6_Template, 2, 1, "span", 26);
    i0.ɵɵtext(7, " ) ");
    i0.ɵɵtemplate(8, SdsTreeTableComponent_ng_template_9_tr_8_ng_template_8_Template, 1, 1, "ng-template", null, 4, i0.ɵɵtemplateRefExtractor);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const childrenLength_r20 = i0.ɵɵreference(9);
    const row_r7 = i0.ɵɵnextContext().row;
    i0.ɵɵadvance(6);
    i0.ɵɵproperty("ngIf", row_r7.totalChildren && row_r7.totalChildren > (row_r7.children == null ? null : row_r7.children.length))("ngIfElse", childrenLength_r20);
} }
function SdsTreeTableComponent_ng_template_9_Template(rf, ctx) { if (rf & 1) {
    const _r6 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "tr", 12, 1);
    i0.ɵɵlistener("keydown.enter", function SdsTreeTableComponent_ng_template_9_Template_tr_keydown_enter_0_listener() { const row_r7 = i0.ɵɵrestoreView(_r6).row; const tableRow_r8 = i0.ɵɵreference(1); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.onRowClicked(row_r7, tableRow_r8)); })("keydown", function SdsTreeTableComponent_ng_template_9_Template_tr_keydown_0_listener($event) { i0.ɵɵrestoreView(_r6); const tableRow_r8 = i0.ɵɵreference(1); const ctx_r3 = i0.ɵɵnextContext(); return i0.ɵɵresetView(ctx_r3.onKeyDown($event, tableRow_r8)); });
    i0.ɵɵelementStart(2, "td");
    i0.ɵɵtemplate(3, SdsTreeTableComponent_ng_template_9_span_3_Template, 3, 1, "span", 13);
    i0.ɵɵelement(4, "span", 14);
    i0.ɵɵtemplate(5, SdsTreeTableComponent_ng_template_9_button_5_Template, 4, 4, "button", 15);
    i0.ɵɵelementEnd();
    i0.ɵɵelementContainer(6, 11);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(7, SdsTreeTableComponent_ng_template_9_ng_container_7_Template, 2, 1, "ng-container", 16)(8, SdsTreeTableComponent_ng_template_9_tr_8_Template, 10, 2, "tr", 17);
} if (rf & 2) {
    const row_r7 = ctx.row;
    const level_r16 = ctx.level;
    const index_r21 = ctx.index;
    const size_r22 = ctx.size;
    const rows_r23 = ctx.rows;
    const parentSelected_r17 = ctx.parentSelected;
    const parent_r24 = ctx.parent;
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(13, _c0$4, row_r7 === ctx_r3._selectedRow, parentSelected_r17));
    i0.ɵɵattribute("id", row_r7.id)("aria-level", level_r16)("aria-posinset", index_r21)("aria-setsize", size_r22)("aria-expanded", row_r7.children ? row_r7.expanded ? true : false : undefined)("tabindex", level_r16 === 1 && index_r21 === 1 && !ctx_r3._selectedRow ? 0 : row_r7 === ctx_r3._selectedRow ? 0 : undefined);
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngIf", ctx_r3.displayVerticalBorder(parent_r24, index_r21 - 1, rows_r23));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", row_r7.children || row_r7.totalChildren > 0);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r3.treetableRow.templateRef)("ngTemplateOutletContext", i0.ɵɵpureFunction3(16, _c1, row_r7, index_r21 - 1, level_r16));
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", row_r7.expanded);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", row_r7.expanded && !row_r7.viewAllChildren && (row_r7.totalChildren > (row_r7.children == null ? null : row_r7.children.length) || (row_r7.children == null ? null : row_r7.children.length) > ctx_r3.childrenLimit));
} }
class SdsTreeTableRow {
    constructor(templateRef) {
        this.templateRef = templateRef;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTreeTableRow_Factory(t) { return new (t || SdsTreeTableRow)(i0.ɵɵdirectiveInject(i0.TemplateRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsTreeTableRow, selectors: [["", "sdsTreeTableRow", ""]] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTreeTableRow, [{
        type: Directive,
        args: [{
                selector: `[sdsTreeTableRow]`,
            }]
    }], () => [{ type: i0.TemplateRef }], null); })();
class SdsTreeTableComponent {
    constructor(elementRef, cdr, ngZone) {
        this.elementRef = elementRef;
        this.cdr = cdr;
        this.ngZone = ngZone;
        /**
         * Defines maximum number of children to show to the user
         * IE - if a row has 20 children available, and childrenLimit
         * value is 10, then the amount of children displayed to the
         * user will be numChildrenToDisplay amount, and the rest
         * is toggled behind a 'View All' button,
         * This should be greater than or equal to numChildrenToDisplay
         * @default - Number.MAX_SAFE_INTEGER
         */
        this.childrenLimit = Number.MAX_SAFE_INTEGER;
        /**
         * Number of children to Display to users if a row's children length
         * exceeds childrenLimit. This should be less than or equal to childrenLimit
         *
         * General Cases:
         * If numChildrenToDisplay is 5, childrenLimit is 10, and row has 20 children
         *  5 children will be shown and the remaining can be displayed through View All button
         *
         * If numChildrenToDisplay is 5, childrenLimit is 10, and row has 10 children
         *  All 10 children will be displayed because the row's children does not exceed childrenLimit
         *
         * If numChildren is 5, childrenLimit is 10, and row has 4 children
         *  All 4 children will be displayed because row's children does not exceed childrenLimit
         *
         * @default - Number.MAX_SAFE_INTEGER
         */
        this.numChildrenToDisplay = this.childrenLimit;
        /** Emitted for a row if there are more children to display and user clicked view all */
        this.viewAll = new EventEmitter();
        /** Emitted anytime a row 's expansion / collapse value changes */
        this.rowExpanded = new EventEmitter();
    }
    /**
     * Public Interface - close all opened children
     */
    collapseAll() {
        this.toggleAllHelper(this.dataSource, false);
        this.cdr.detectChanges();
    }
    /**
     * Public interface - open all panels with children
     */
    expandAll() {
        this.toggleAllHelper(this.dataSource, true);
        this.cdr.detectChanges();
    }
    /**
     * Public interface - expand a single row given an ID.
     * The row's predecessors will also be expanded
     * @param rowId - id of row to expand
     */
    expandRow(rowId) {
        this.expandRowHelper(this.dataSource, rowId);
        this.cdr.detectChanges();
    }
    /**
     * Public Interface - Collapse a single row given an id.
     * The row's successors will also be collapsed
     * @param rowId - id of row to collapse
     */
    collapseRow(rowId) {
        const row = this.findRow(this.dataSource, rowId);
        if (!row) {
            return;
        }
        this.collapseRowHelper(row);
        this.cdr.detectChanges();
    }
    viewAllClicked(row, currentRow, tableRow) {
        currentRow.setAttribute('tabindex', undefined);
        tableRow.setAttribute('tabindex', '0');
        row.viewAllChildren = true;
        setTimeout(() => tableRow.focus());
        if (!row.children && !row.totalChildren) {
            return;
        }
        if ((row.totalChildren && this.moreChildrenThanShowing(row)) ||
            (row.children && this.moreChildrenThanInitialLimit(row))) {
            this.viewAll.emit(row);
        }
    }
    moreChildrenThanShowing(row) {
        return row.totalChildren > row.children?.length;
    }
    moreChildrenThanInitialLimit(row) {
        return row.children.length > this.childrenLimit;
    }
    toggleAllHelper(data, expanded) {
        data.forEach((data) => {
            if (data.children) {
                this.toggleAllHelper(data.children, expanded);
                data.expanded = expanded;
                if (!expanded) {
                    data.viewAllChildren = false;
                }
            }
        });
    }
    expandRowHelper(allRows, id) {
        for (let i = 0; i < allRows.length; i++) {
            const row = allRows[i];
            if (row.id === id) {
                return true;
            }
            else if (row.children) {
                const isChildExpanded = this.expandRowHelper(row.children, id);
                if (isChildExpanded) {
                    row.expanded = true;
                    return true;
                }
            }
        }
        return false;
    }
    findRow(allRows, id) {
        for (let i = 0; i < allRows.length; i++) {
            const row = allRows[i];
            if (row.id === id) {
                return row;
            }
            else if (row.children) {
                return this.findRow(allRows, id);
            }
        }
        return null;
    }
    collapseRowHelper(row) {
        row.expanded = false;
        row.viewAllChildren = false;
        if (row.children) {
            row.children.forEach((child) => this.collapseRowHelper(child));
        }
    }
    /** Sets height of vertical border on the tree table view */
    setHeight(row, parentRow, border) {
        if (!row || !parentRow) {
            return;
        }
        /**
         * We run outside ngZone because we don't want the setTimeout to trigger change detection,
         * which would re-run changes on template, and re-evalute this function, causing infinite loop
         */
        this.ngZone.runOutsideAngular(() => {
            /**
             * We do set timeout to let the table rows finish rendering. If a row was
             * expanded / collapsed, then the height of the vertical border will need to
             * be re-evaluated based on new distance from child to parent. We let the
             * view finish refreshing so that bounding rectangle is re-evaluated, and then
             * we can re-calculate height. Doing this synchronously without setTimeout
             * would give us incorrect value for height because the bouunding rectangle
             * has yet to update from the collapse / expand change
             */
            setTimeout(() => {
                const firstRect = parentRow.getBoundingClientRect();
                const rowRect = row.getBoundingClientRect();
                const yFirstRect = firstRect.top + firstRect.height / 2;
                const yRowRect = rowRect.top + rowRect.height / 2;
                const height = yRowRect - yFirstRect - 20;
                border.style.height = `${height}px`;
                border.style.bottom = `${rowRect.height / 2}px`;
            });
        });
    }
    /**
     * Defines whether or not to display vertical border from this row. Vertical borders generally
     * start from the last child and extend to the parent. However, if we are truncating the number
     * of children displayed, then the vertical border would need to start from the child we
     * truncate at.
     * */
    displayVerticalBorder(parentRow, index, siblingRows) {
        if (!siblingRows)
            return false;
        // Decide whether last child displayed is last child in row's children index or is at numChildrenToDisplay
        if (parentRow.viewAllChildren || siblingRows.length <= this.childrenLimit) {
            return index === siblingRows.length - 1;
        }
        else {
            return index === this.numChildrenToDisplay - 1;
        }
    }
    getTemplateContext(parent, row, index, level, parentSelected, parentRow) {
        const updatedLevel = level + 1;
        const posinset = index + 1;
        return {
            row: row,
            level: updatedLevel,
            index: posinset,
            size: parent.children ? parent.children.length : 1,
            rows: parent.children,
            parentSelected: parentSelected,
            parent: parent,
            parentRow: parentRow,
        };
    }
    onRowClicked(row, tableRow) {
        if (row.children || row.totalChildren > 0) {
            row.expanded = !row.expanded;
        }
        if (!row.expanded) {
            row.viewAllChildren = false;
        }
        this._selectedRow = row;
        this._selectedRowParent = this.getParentOfRow(this.dataSource, row);
        setTimeout(() => tableRow.focus());
        this.rowExpanded.emit(row);
    }
    onKeyDown($event, tableRow) {
        if ($event.target != tableRow) {
            return;
        }
        let siblingRow;
        if ($event.key === 'ArrowUp') {
            siblingRow = $event.target.previousElementSibling;
        }
        else if ($event.key === 'ArrowDown') {
            siblingRow = $event.target.nextElementSibling;
        }
        else if ($event.key === 'Home') {
            siblingRow = this.elementRef.nativeElement.querySelector('tbody tr');
        }
        else if ($event.key === 'End') {
            siblingRow = this.elementRef.nativeElement.querySelector('tbody tr:last-child');
        }
        if (!siblingRow)
            return;
        $event.target.setAttribute('tabindex', undefined);
        siblingRow.setAttribute('tabindex', '0');
        siblingRow.focus();
        $event.preventDefault();
    }
    getParentOfRow(allRows, row) {
        let retRow = null;
        for (let i = 0; i < allRows.length; i++) {
            if (allRows[i] === row) {
                retRow = allRows[i];
                break;
            }
            else if (allRows[i].children) {
                const isChildRow = this.getParentOfRow(allRows[i].children, row);
                if (isChildRow) {
                    retRow = allRows[i];
                }
            }
        }
        return retRow;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTreeTableComponent_Factory(t) { return new (t || SdsTreeTableComponent)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.NgZone)); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsTreeTableComponent, selectors: [["sds-tree-table"]], contentQueries: function SdsTreeTableComponent_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, SdsTreeTableRow, 5);
        } if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.treetableRow = _t.first);
        } }, inputs: { dataSource: "dataSource", displayColumns: "displayColumns", childrenLimit: "childrenLimit", numChildrenToDisplay: "numChildrenToDisplay" }, outputs: { viewAll: "viewAll", rowExpanded: "rowExpanded" }, decls: 11, vars: 2, consts: [["treeTablePanel", ""], ["tableRow", ""], ["border", ""], ["viewAllRow", ""], ["childrenLength", ""], [1, "sds-tree-table--scrollable"], ["role", "tree", 1, "usa-table", "sds-tree-table", "padding-x-1"], ["role", "rowgroup"], ["role", "row"], [4, "ngFor", "ngForOf"], [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngFor", "ngForOf"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"], ["role", "treeitem", 3, "keydown.enter", "keydown", "ngClass"], ["class", "vertical-border", 4, "ngIf"], [1, "horizontal-border"], ["class", "usa-button sds-button--circle border-0 bg-white", 3, "click", 4, "ngIf"], [4, "ngIf"], ["role", "row", "class", "text-center", 3, "keydown.enter", "keydown", 4, "ngIf"], [1, "vertical-border"], [1, "usa-button", "sds-button--circle", "border-0", "bg-white", 3, "click"], [1, "usa-sr-only"], [3, "icon", "size"], [3, "ngTemplateOutlet", "ngTemplateOutletContext", 4, "ngIf"], ["role", "row", 1, "text-center", 3, "keydown.enter", "keydown"], [1, "width-100"], [1, "usa-button", "usa-button--base", "bg-base-lighter", 3, "click"], [4, "ngIf", "ngIfElse"]], template: function SdsTreeTableComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 5)(1, "table", 6)(2, "thead", 7)(3, "tr", 8)(4, "th");
            i0.ɵɵtext(5, "Related");
            i0.ɵɵelementEnd();
            i0.ɵɵtemplate(6, SdsTreeTableComponent_th_6_Template, 2, 1, "th", 9);
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(7, "tbody", 7);
            i0.ɵɵtemplate(8, SdsTreeTableComponent_ng_container_8_Template, 1, 2, "ng-container", 10);
            i0.ɵɵelementEnd()()();
            i0.ɵɵtemplate(9, SdsTreeTableComponent_ng_template_9_Template, 9, 20, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        } if (rf & 2) {
            i0.ɵɵadvance(6);
            i0.ɵɵproperty("ngForOf", ctx.displayColumns);
            i0.ɵɵadvance(2);
            i0.ɵɵproperty("ngForOf", ctx.dataSource);
        } }, dependencies: [i1$2.NgClass, i1$2.NgForOf, i1$2.NgIf, i1$2.NgTemplateOutlet, i2$1.IconComponent], encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTreeTableComponent, [{
        type: Component,
        args: [{ selector: `sds-tree-table`, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"sds-tree-table--scrollable\">\n  <table role=\"tree\" class=\"usa-table sds-tree-table padding-x-1\">\n    <thead role=\"rowgroup\">\n      <tr role=\"row\">\n        <th>Related</th>\n        <th *ngFor=\"let col of displayColumns\">{{ col }}</th>\n      </tr>\n    </thead>\n    <tbody role=\"rowgroup\">\n      <ng-container\n        *ngFor=\"let data of dataSource; index as i\"\n        [ngTemplateOutlet]=\"treeTablePanel\"\n        [ngTemplateOutletContext]=\"getTemplateContext(dataSource, data, i, 0, data === _selectedRowParent)\"\n      >\n      </ng-container>\n    </tbody>\n  </table>\n</div>\n\n<!-- TODO: Extract this into separate component -->\n<ng-template\n  #treeTablePanel\n  let-row=\"row\"\n  let-level=\"level\"\n  let-index=\"index\"\n  let-size=\"size\"\n  let-rows=\"rows\"\n  let-parentSelected=\"parentSelected\"\n  let-parent=\"parent\"\n  let-parentRow=\"parentRow\"\n>\n  <tr\n    role=\"treeitem\"\n    #tableRow\n    [attr.id]=\"row.id\"\n    [attr.aria-level]=\"level\"\n    [attr.aria-posinset]=\"index\"\n    [attr.aria-setsize]=\"size\"\n    [attr.aria-expanded]=\"row.children ? (row.expanded ? true : false) : undefined\"\n    [attr.tabindex]=\"level === 1 && index === 1 && !_selectedRow ? 0 : row === _selectedRow ? 0 : undefined\"\n    (keydown.enter)=\"onRowClicked(row, tableRow)\"\n    (keydown)=\"onKeyDown($event, tableRow)\"\n    [ngClass]=\"{\n      'sds-tree-table__row--selected': row === _selectedRow,\n      'sds-tree-table__row--highlight-border': parentSelected\n    }\"\n  >\n    <td>\n      <span *ngIf=\"displayVerticalBorder(parent, index - 1, rows)\" #border class=\"vertical-border\">\n        <!-- Programitcally set height of vertical border because we won't know the height until view finishes rendering -->\n        {{ setHeight(tableRow, parentRow, border) }}\n      </span>\n      <span class=\"horizontal-border\"></span>\n      <button\n        class=\"usa-button sds-button--circle border-0 bg-white\"\n        *ngIf=\"row.children || row.totalChildren > 0\"\n        [attr.tabindex]=\"row === _selectedRow ? 1 : -1\"\n        (click)=\"onRowClicked(row, tableRow)\"\n      >\n        <span class=\"usa-sr-only\">{{ row.expanded ? 'Collapse' : 'Expand' }} current row</span>\n        <usa-icon [icon]=\"row.expanded ? 'dash-circle-fill' : 'plus-circle-fill'\" [size]=\"'2x'\"> </usa-icon>\n      </button>\n    </td>\n    <ng-container\n      [ngTemplateOutlet]=\"treetableRow.templateRef\"\n      [ngTemplateOutletContext]=\"{ $implicit: row, index: index - 1, level: level }\"\n    >\n    </ng-container>\n  </tr>\n\n  <ng-container *ngIf=\"row.expanded\">\n    <ng-container *ngFor=\"let data of row.children; index as i\">\n      <ng-container\n        *ngIf=\"row.children.length <= childrenLimit || row.viewAllChildren || i < numChildrenToDisplay\"\n        [ngTemplateOutlet]=\"treeTablePanel\"\n        [ngTemplateOutletContext]=\"getTemplateContext(row, data, i, level, parentSelected, tableRow)\"\n      >\n      </ng-container>\n    </ng-container>\n  </ng-container>\n\n  <tr\n    role=\"row\"\n    class=\"text-center\"\n    #viewAllRow\n    *ngIf=\"\n      row.expanded &&\n      !row.viewAllChildren &&\n      (row.totalChildren > row.children?.length || row.children?.length > childrenLimit)\n    \"\n    (keydown.enter)=\"viewAllClicked(row, viewAllRow, tableRow)\"\n    (keydown)=\"onKeyDown($event, viewAllRow)\"\n  >\n    <td></td>\n    <td class=\"width-100\">\n      <button class=\"usa-button usa-button--base bg-base-lighter\" (click)=\"viewAllClicked(row, viewAllRow, tableRow)\">\n        View All (\n        <span *ngIf=\"row.totalChildren && row.totalChildren > row.children?.length; else childrenLength\">\n          {{ row.totalChildren }}\n        </span>\n        )\n        <ng-template #childrenLength>{{ row.children?.length }}</ng-template>\n      </button>\n    </td>\n  </tr>\n</ng-template>\n" }]
    }], () => [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }], { dataSource: [{
            type: Input
        }], displayColumns: [{
            type: Input
        }], childrenLimit: [{
            type: Input
        }], numChildrenToDisplay: [{
            type: Input
        }], treetableRow: [{
            type: ContentChild,
            args: [SdsTreeTableRow]
        }], viewAll: [{
            type: Output
        }], rowExpanded: [{
            type: Output
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsTreeTableComponent, { className: "SdsTreeTableComponent" }); })();

class SdsTreeTableModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTreeTableModule_Factory(t) { return new (t || SdsTreeTableModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTreeTableModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule, UsaTableModule, IconModule, NgxBootstrapIconsModule.pick({ plusCircleFill, dashCircleFill })] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTreeTableModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, UsaTableModule, IconModule, NgxBootstrapIconsModule.pick({ plusCircleFill, dashCircleFill })],
                declarations: [SdsTreeTableComponent, SdsTreeTableRow],
                exports: [SdsTreeTableComponent, SdsTreeTableRow],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTreeTableModule, { declarations: [SdsTreeTableComponent, SdsTreeTableRow], imports: [CommonModule, UsaTableModule, IconModule, i1$4.NgxBootstrapIconsModule], exports: [SdsTreeTableComponent, SdsTreeTableRow] }); })();

const _c0$3 = (a0, a1) => ({ active: a0, inactive: a1 });
class SdsStepArrowComponent {
    constructor() {
        this.status = 'inactive';
    }
    /** @nocollapse */ static { this.ɵfac = function SdsStepArrowComponent_Factory(t) { return new (t || SdsStepArrowComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsStepArrowComponent, selectors: [["sds-step-arrow"]], inputs: { status: "status", number: "number" }, decls: 8, vars: 5, consts: [[1, "grid-row"], [1, "wrap-layer"], [1, "text-layer"], ["xmlns", "http://www.w3.org/2000/svg", "width", "80", "height", "40", "viewBox", "105 90 5 50"], ["stroke-width", "2", "points", "120,95 60,95 60,130 120,130 140,112", 1, "step-arrow", 3, "ngClass"]], template: function SdsStepArrowComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "div", 2)(3, "p");
            i0.ɵɵtext(4);
            i0.ɵɵelementEnd()();
            i0.ɵɵelementStart(5, "div");
            i0.ɵɵnamespaceSVG();
            i0.ɵɵelementStart(6, "svg", 3);
            i0.ɵɵelement(7, "polygon", 4);
            i0.ɵɵelementEnd()()()();
        } if (rf & 2) {
            i0.ɵɵadvance(4);
            i0.ɵɵtextInterpolate(ctx.number);
            i0.ɵɵadvance(3);
            i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction2(2, _c0$3, ctx.status === "active", ctx.status === "inactive"));
        } }, dependencies: [i1$2.NgClass], styles: [".step-arrow[_ngcontent-%COMP%]{stroke:#21c834}.step-arrow.active[_ngcontent-%COMP%]{fill:#21c834}.step-arrow.inactive[_ngcontent-%COMP%]{fill:#e3f5e1}.wrap-layer[_ngcontent-%COMP%]{position:relative}.text-layer[_ngcontent-%COMP%]{font-family:Source Sans Pro,Helvetica Neue,Helvetica,Arial,sans;font-size:1.5rem;text-align:center;line-height:1.1;font-weight:700;position:absolute;width:75%;margin-top:2px;height:auto}"] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsStepArrowComponent, [{
        type: Component,
        args: [{ selector: 'sds-step-arrow', template: "<div class=\"grid-row\">\n  <div class=\"wrap-layer\">\n    <div class=\"text-layer\">\n      <p>{{ number }}</p>\n    </div>\n    <div>\n      <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"80\" height=\"40\" viewBox=\"105 90 5 50\">\n        <polygon\n          class=\"step-arrow\"\n          [ngClass]=\"{ active: status === 'active', inactive: status === 'inactive' }\"\n          stroke-width=\"2\"\n          points=\"120,95 60,95 60,130 120,130 140,112\"\n        />\n      </svg>\n    </div>\n  </div>\n</div>\n", styles: [".step-arrow{stroke:#21c834}.step-arrow.active{fill:#21c834}.step-arrow.inactive{fill:#e3f5e1}.wrap-layer{position:relative}.text-layer{font-family:Source Sans Pro,Helvetica Neue,Helvetica,Arial,sans;font-size:1.5rem;text-align:center;line-height:1.1;font-weight:700;position:absolute;width:75%;margin-top:2px;height:auto}\n"] }]
    }], null, { status: [{
            type: Input
        }], number: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsStepArrowComponent, { className: "SdsStepArrowComponent" }); })();

class SdsStepArrowModule {
    /** @nocollapse */ static { this.ɵfac = function SdsStepArrowModule_Factory(t) { return new (t || SdsStepArrowModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsStepArrowModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsStepArrowModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                exports: [SdsStepArrowComponent],
                declarations: [SdsStepArrowComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsStepArrowModule, { declarations: [SdsStepArrowComponent], imports: [CommonModule], exports: [SdsStepArrowComponent] }); })();

class Trigger {
    constructor(open, close) {
        this.open = open;
        this.close = close;
        if (!close) {
            this.close = open;
        }
    }
    isManual() {
        return this.open === 'manual' || this.close === 'manual';
    }
}
const DEFAULT_ALIASES = {
    hover: ['mouseenter', 'mouseleave'],
    focus: ['focusin', 'focusout'],
};
function parseTriggers(triggers, aliases = DEFAULT_ALIASES) {
    const trimmedTriggers = (triggers || '').trim();
    if (trimmedTriggers.length === 0) {
        return [];
    }
    const parsedTriggers = trimmedTriggers
        .split(/\s+/)
        .map((trigger) => trigger.split(':'))
        .map((triggerPair) => {
        let alias = aliases[triggerPair[0]] || triggerPair;
        return new Trigger(alias[0], alias[1]);
    });
    const manualTriggers = parsedTriggers.filter((triggerPair) => triggerPair.isManual());
    if (manualTriggers.length > 1) {
        throw 'Triggers parse error: only one manual trigger is allowed';
    }
    if (manualTriggers.length === 1 && parsedTriggers.length > 1) {
        throw "Triggers parse error: manual trigger can't be mixed with other triggers";
    }
    return parsedTriggers;
}
function observeTriggers(renderer, nativeElement, triggers, isOpenedFn) {
    return new Observable((subscriber) => {
        const listeners = [];
        const openFn = () => subscriber.next(true);
        const closeFn = () => subscriber.next(false);
        const toggleFn = () => subscriber.next(!isOpenedFn());
        triggers.forEach((trigger) => {
            if (trigger.open === trigger.close) {
                listeners.push(renderer.listen(nativeElement, trigger.open, toggleFn));
            }
            else {
                listeners.push(renderer.listen(nativeElement, trigger.open, openFn), renderer.listen(nativeElement, trigger.close, closeFn));
            }
        });
        return () => {
            listeners.forEach((unsubscribeFn) => unsubscribeFn());
        };
    });
}
const delayOrNoop = (time) => (time > 0 ? delay(time) : (a) => a);
function triggerDelay(openDelay, closeDelay, isOpenedFn) {
    return (input$) => {
        let pending = null;
        const filteredInput$ = input$.pipe(map((open) => ({ open })), filter((event) => {
            const currentlyOpen = isOpenedFn();
            if (currentlyOpen !== event.open && (!pending || pending.open === currentlyOpen)) {
                pending = event;
                return true;
            }
            if (pending && pending.open !== event.open) {
                pending = null;
            }
            return false;
        }), share());
        const delayedOpen$ = filteredInput$.pipe(filter((event) => event.open), delayOrNoop(openDelay));
        const delayedClose$ = filteredInput$.pipe(filter((event) => !event.open), delayOrNoop(closeDelay));
        return merge(delayedOpen$, delayedClose$).pipe(filter((event) => {
            if (event === pending) {
                pending = null;
                return event.open !== isOpenedFn();
            }
            return false;
        }), map((event) => event.open));
    };
}
function listenToTriggers(renderer, nativeElement, triggers, isOpenedFn, openFn, closeFn, openDelay = 0, closeDelay = 0) {
    const parsedTriggers = parseTriggers(triggers);
    if (parsedTriggers.length === 1 && parsedTriggers[0].isManual()) {
        return () => { };
    }
    const subscription = observeTriggers(renderer, nativeElement, parsedTriggers, isOpenedFn)
        .pipe(triggerDelay(openDelay, closeDelay, isOpenedFn))
        .subscribe((open) => (open ? openFn() : closeFn()));
    return () => subscription.unsubscribe();
}

var Key;
(function (Key) {
    Key[Key["Tab"] = 9] = "Tab";
    Key[Key["Enter"] = 13] = "Enter";
    Key[Key["Escape"] = 27] = "Escape";
    Key[Key["Space"] = 32] = "Space";
    Key[Key["PageUp"] = 33] = "PageUp";
    Key[Key["PageDown"] = 34] = "PageDown";
    Key[Key["End"] = 35] = "End";
    Key[Key["Home"] = 36] = "Home";
    Key[Key["ArrowLeft"] = 37] = "ArrowLeft";
    Key[Key["ArrowUp"] = 38] = "ArrowUp";
    Key[Key["ArrowRight"] = 39] = "ArrowRight";
    Key[Key["ArrowDown"] = 40] = "ArrowDown";
})(Key || (Key = {}));

function toInteger(value) {
    return parseInt(`${value}`, 10);
}
function toString(value) {
    return value !== undefined && value !== null ? `${value}` : '';
}
function getValueInRange(value, max, min = 0) {
    return Math.max(Math.min(value, max), min);
}
function isString(value) {
    return typeof value === 'string';
}
function isNumber(value) {
    return !isNaN(toInteger(value));
}
function isInteger(value) {
    return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
}
function isDefined(value) {
    return value !== undefined && value !== null;
}
function isPromise(v) {
    return v && v.then;
}
function padNumber(value) {
    if (isNumber(value)) {
        return `0${value}`.slice(-2);
    }
    else {
        return '';
    }
}
function regExpEscape(text) {
    return text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}
function hasClassName(element, className) {
    return (element && element.className && element.className.split && element.className.split(/\s+/).indexOf(className) >= 0);
}
function closest(element, selector) {
    if (!selector) {
        return null;
    }
    /*
     * In certain browsers (e.g. Edge 44.18362.449.0) HTMLDocument does
     * not support `Element.prototype.closest`. To emulate the correct behaviour
     * we return null when the method is missing.
     *
     * Note that in evergreen browsers `closest(document.documentElement, 'html')`
     * will return the document element whilst in Edge null will be returned. This
     * compromise was deemed good enough.
     */
    if (typeof element.closest === 'undefined') {
        return null;
    }
    return element.closest(selector);
}
/**
 * Force a browser reflow
 * @param element element where to apply the reflow
 */
function reflow(element) {
    return (element || document.body).getBoundingClientRect();
}
/**
 * Creates an observable where all callbacks are executed inside a given zone
 *
 * @param zone
 */
function runInZone(zone) {
    return (source) => {
        return new Observable((observer) => {
            const next = (value) => zone.run(() => observer.next(value));
            const error = (e) => zone.run(() => observer.error(e));
            const complete = () => zone.run(() => observer.complete());
            return source.subscribe({ next, error, complete });
        });
    };
}
function removeAccents(str) {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
/**
 * Returns the active element in the given root.
 * If the active element is inside a shadow root, it is searched recursively.
 */
function getActiveElement(root = document) {
    const activeEl = root?.activeElement;
    if (!activeEl) {
        return null;
    }
    return activeEl.shadowRoot ? getActiveElement(activeEl.shadowRoot) : activeEl;
}

const isContainedIn = (element, array) => array ? array.some((item) => item.contains(element)) : false;
const matchesSelectorIfAny = (element, selector) => !selector || closest(element, selector) != null;
// we have to add a more significant delay to avoid re-opening when handling (click) on a toggling element
// TODO: use proper Angular platform detection when NgbAutoClose becomes a service and we can inject PLATFORM_ID
const isMobile = (() => {
    const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
    const isAndroid = () => /Android/.test(navigator.userAgent);
    return typeof navigator !== 'undefined' ? !!navigator.userAgent && (isIOS() || isAndroid()) : false;
})();
// setting 'ngbAutoClose' synchronously on mobile results in immediate popup closing
// when tapping on the triggering element
const wrapAsyncForMobile = (fn) => (isMobile ? () => setTimeout(() => fn(), 100) : fn);
function sdsAutoClose(zone, document, type, close, closed$, insideElements, ignoreElements, insideSelector) {
    // closing on ESC and outside clicks
    if (type) {
        zone.runOutsideAngular(wrapAsyncForMobile(() => {
            const shouldCloseOnClick = (event) => {
                const element = event.target;
                if (event.button === 2 || isContainedIn(element, ignoreElements)) {
                    return false;
                }
                if (type === 'inside') {
                    return isContainedIn(element, insideElements) && matchesSelectorIfAny(element, insideSelector);
                }
                else if (type === 'outside') {
                    return !isContainedIn(element, insideElements);
                } /* if (type === true) */
                else {
                    return matchesSelectorIfAny(element, insideSelector) || !isContainedIn(element, insideElements);
                }
            };
            const escapes$ = fromEvent(document, 'keydown').pipe(takeUntil(closed$), 
            /* eslint-disable-next-line deprecation/deprecation */
            filter((e) => e.which === Key.Escape), tap((e) => e.preventDefault()));
            // we have to pre-calculate 'shouldCloseOnClick' on 'mousedown',
            // because on 'mouseup' DOM nodes might be detached
            const mouseDowns$ = fromEvent(document, 'mousedown').pipe(map(shouldCloseOnClick), takeUntil(closed$));
            const closeableClicks$ = fromEvent(document, 'mouseup').pipe(withLatestFrom(mouseDowns$), filter(([_, shouldClose]) => shouldClose), delay(0), takeUntil(closed$));
            race([
                escapes$.pipe(map((_) => 0 /* SOURCE.ESCAPE */)),
                closeableClicks$.pipe(map((_) => 1 /* SOURCE.CLICK */)),
            ]).subscribe((source) => zone.run(() => close(source)));
        }));
    }
}

class NgbRTL {
    constructor(document) {
        this._element = document.documentElement;
    }
    isRTL() {
        return (this._element.getAttribute('dir') || '').toLowerCase() === 'rtl';
    }
    /** @nocollapse */ static { this.ɵfac = function NgbRTL_Factory(t) { return new (t || NgbRTL)(i0.ɵɵinject(DOCUMENT)); }; }
    /** @nocollapse */ static { this.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: NgbRTL, factory: NgbRTL.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NgbRTL, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [{ type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }], null); })();

const placementSeparator = /\s+/;
const spacesRegExp = /  +/gi;
/**
 * Matching classes from the Bootstrap ones to the poppers ones.
 * The first index of each array is used for the left to right direction,
 * the second one is used for the right to left, defaulting to the first index (when LTR and RTL lead to the same class)
 *
 * See [Bootstrap alignments](https://getbootstrap.com/docs/5.1/components/dropdowns/#alignment-options)
 * and [Popper placements](https://popper.js.org/docs/v2/constructors/#options)
 */
const bootstrapPopperMatches = {
    top: ['top'],
    bottom: ['bottom'],
    start: ['left', 'right'],
    left: ['left'],
    end: ['right', 'left'],
    right: ['right'],
    'top-start': ['top-start', 'top-end'],
    'top-left': ['top-start'],
    'top-end': ['top-end', 'top-start'],
    'top-right': ['top-end'],
    'bottom-start': ['bottom-start', 'bottom-end'],
    'bottom-left': ['bottom-start'],
    'bottom-end': ['bottom-end', 'bottom-start'],
    'bottom-right': ['bottom-end'],
    'start-top': ['left-start', 'right-start'],
    'left-top': ['left-start'],
    'start-bottom': ['left-end', 'right-end'],
    'left-bottom': ['left-end'],
    'end-top': ['right-start', 'left-start'],
    'right-top': ['right-start'],
    'end-bottom': ['right-end', 'left-end'],
    'right-bottom': ['right-end'],
};
function getPopperClassPlacement(placement, isRTL) {
    const [leftClass, rightClass] = bootstrapPopperMatches[placement];
    return isRTL ? rightClass || leftClass : leftClass;
}
const popperStartPrimaryPlacement = /^left/;
const popperEndPrimaryPlacement = /^right/;
const popperStartSecondaryPlacement = /^start/;
const popperEndSecondaryPlacement = /^end/;
function getBootstrapBaseClassPlacement(baseClass, placement) {
    let [primary, secondary] = placement.split('-');
    const newPrimary = primary.replace(popperStartPrimaryPlacement, 'start').replace(popperEndPrimaryPlacement, 'end');
    let classnames = [newPrimary];
    if (secondary) {
        let newSecondary = secondary;
        if (primary === 'left' || primary === 'right') {
            newSecondary = newSecondary
                .replace(popperStartSecondaryPlacement, 'top')
                .replace(popperEndSecondaryPlacement, 'bottom');
        }
        classnames.push(`${newPrimary}-${newSecondary}`);
    }
    if (baseClass) {
        classnames = classnames.map((classname) => `${baseClass}-${classname}`);
    }
    return classnames.join(' ');
}
/*
 * Accept the placement array and applies the appropriate placement dependent on the viewport.
 * Returns the applied placement.
 * In case of auto placement, placements are selected in order
 *   'top', 'bottom', 'start', 'end',
 *   'top-start', 'top-end',
 *   'bottom-start', 'bottom-end',
 *   'start-top', 'start-bottom',
 *   'end-top', 'end-bottom'.
 * */
function getPopperOptions({ placement, baseClass }, rtl) {
    let placementVals = Array.isArray(placement)
        ? placement
        : placement.split(placementSeparator);
    // No need to consider left and right here, as start and end are enough, and it is used for 'auto' placement only
    const allowedPlacements = [
        'top',
        'bottom',
        'start',
        'end',
        'top-start',
        'top-end',
        'bottom-start',
        'bottom-end',
        'start-top',
        'start-bottom',
        'end-top',
        'end-bottom',
    ];
    // replace auto placement with other placements
    let hasAuto = placementVals.findIndex((val) => val === 'auto');
    if (hasAuto >= 0) {
        allowedPlacements.forEach(function (obj) {
            if (placementVals.find((val) => val.search('^' + obj) !== -1) == null) {
                placementVals.splice(hasAuto++, 1, obj);
            }
        });
    }
    const popperPlacements = placementVals.map((_placement) => {
        return getPopperClassPlacement(_placement, rtl.isRTL());
    });
    let mainPlacement = popperPlacements.shift();
    const bsModifier = {
        name: 'bootstrapClasses',
        enabled: !!baseClass,
        phase: 'write',
        fn({ state }) {
            const bsClassRegExp = new RegExp(baseClass + '(-[a-z]+)*', 'gi');
            const popperElement = state.elements.popper;
            const popperPlacement = state.placement;
            let className = popperElement.className;
            // Remove old bootstrap classes
            className = className.replace(bsClassRegExp, '');
            // Add current placements
            className += ` ${getBootstrapBaseClassPlacement(baseClass, popperPlacement)}`;
            // Remove multiple spaces
            className = className.trim().replace(spacesRegExp, ' ');
            // Reassign
            popperElement.className = className;
        },
    };
    return {
        placement: mainPlacement,
        modifiers: [
            bsModifier,
            flip,
            preventOverflow,
            arrow,
            {
                enabled: true,
                name: 'flip',
                options: {
                    fallbackPlacements: popperPlacements,
                },
            },
            {
                enabled: true,
                name: 'preventOverflow',
                phase: 'main',
                fn: function () { },
            },
        ],
    };
}
function noop(arg) {
    return arg;
}
function sdsPositioning() {
    const rtl = inject(NgbRTL);
    let popperInstance = null;
    return {
        createPopper(positioningOption) {
            if (!popperInstance) {
                const updatePopperOptions = positioningOption.updatePopperOptions || noop;
                let popperOptions = updatePopperOptions(getPopperOptions(positioningOption, rtl));
                popperInstance = createPopperLite(positioningOption.hostElement, positioningOption.targetElement, popperOptions);
            }
        },
        update() {
            if (popperInstance) {
                popperInstance.update();
            }
        },
        setOptions(positioningOption) {
            if (popperInstance) {
                const updatePopperOptions = positioningOption.updatePopperOptions || noop;
                let popperOptions = updatePopperOptions(getPopperOptions(positioningOption, rtl));
                popperInstance.setOptions(popperOptions);
            }
        },
        destroy() {
            if (popperInstance) {
                popperInstance.destroy();
                popperInstance = null;
            }
        },
    };
}

function getTransitionDurationMs(element) {
    const { transitionDelay, transitionDuration } = window.getComputedStyle(element);
    const transitionDelaySec = parseFloat(transitionDelay);
    const transitionDurationSec = parseFloat(transitionDuration);
    return (transitionDelaySec + transitionDurationSec) * 1000;
}

const noopFn = () => { };
const transitionTimerDelayMs = 500; // TODO Double check this
const runningTransitions = new Map();
const ngbRunTransition = (zone, element, startFn, options) => {
    // Getting initial context from options
    let context = options.context || {};
    // Checking if there are already running transitions on the given element.
    const running = runningTransitions.get(element);
    if (running) {
        switch (options.runningTransition) {
            // If there is one running and we want for it to 'continue' to run, we have to cancel the new one.
            // We're not emitting any values, but simply completing the observable (EMPTY).
            case 'continue':
                return EMPTY;
            // If there is one running and we want for it to 'stop', we have to complete the running one.
            // We're simply completing the running one and not emitting any values and merging newly provided context
            // with the one coming from currently running transition.
            case 'stop':
                zone.run(() => running.transition$.complete());
                context = Object.assign(running.context, context);
                runningTransitions.delete(element);
        }
    }
    // Running the start function
    const endFn = startFn(element, options.animation, context) || noopFn;
    // If 'prefer-reduced-motion' is enabled, the 'transition' will be set to 'none'.
    // If animations are disabled, we have to emit a value and complete the observable
    // In this case we have to call the end function, but can finish immediately by emitting a value,
    // completing the observable and executing end functions synchronously.
    if (!options.animation || window.getComputedStyle(element).transitionProperty === 'none') {
        zone.run(() => endFn());
        return of(undefined).pipe(runInZone(zone));
    }
    // Starting a new transition
    const transition$ = new Subject();
    const finishTransition$ = new Subject();
    const stop$ = transition$.pipe(endWith(true));
    runningTransitions.set(element, {
        transition$,
        complete: () => {
            finishTransition$.next();
            finishTransition$.complete();
        },
        context,
    });
    const transitionDurationMs = getTransitionDurationMs(element);
    // 1. We have to both listen for the 'transitionend' event and have a 'just-in-case' timer,
    // because 'transitionend' event might not be fired in some browsers, if the transitioning
    // element becomes invisible (ex. when scrolling, making browser tab inactive, etc.). The timer
    // guarantees, that we'll release the DOM element and complete 'ngbRunTransition'.
    // 2. We need to filter transition end events, because they might bubble from shorter transitions
    // on inner DOM elements. We're only interested in the transition on the 'element' itself.
    zone.runOutsideAngular(() => {
        const transitionEnd$ = fromEvent(element, 'transitionend').pipe(takeUntil(stop$), filter(({ target }) => target === element));
        const timer$ = timer(transitionDurationMs + transitionTimerDelayMs).pipe(takeUntil(stop$));
        race(timer$, transitionEnd$, finishTransition$)
            .pipe(takeUntil(stop$))
            .subscribe(() => {
            runningTransitions.delete(element);
            zone.run(() => {
                endFn();
                transition$.next();
                transition$.complete();
            });
        });
    });
    return transition$.asObservable();
};
const ngbCompleteTransition = (element) => {
    runningTransitions.get(element)?.complete();
};

class ContentRef {
    constructor(nodes, viewRef, componentRef) {
        this.nodes = nodes;
        this.viewRef = viewRef;
        this.componentRef = componentRef;
    }
}
class PopupService {
    constructor(_componentType, _injector, _viewContainerRef, _renderer, _ngZone, _applicationRef) {
        this._componentType = _componentType;
        this._injector = _injector;
        this._viewContainerRef = _viewContainerRef;
        this._renderer = _renderer;
        this._ngZone = _ngZone;
        this._applicationRef = _applicationRef;
        this._windowRef = null;
        this._contentRef = null;
    }
    open(content, templateContext, animation = false) {
        if (!this._windowRef) {
            this._contentRef = this._getContentRef(content, templateContext);
            this._windowRef = this._viewContainerRef.createComponent(this._componentType, {
                injector: this._injector,
                projectableNodes: this._contentRef.nodes,
            });
        }
        const { nativeElement } = this._windowRef.location;
        const transition$ = this._ngZone.onStable.pipe(take(1), mergeMap(() => ngbRunTransition(this._ngZone, nativeElement, ({ classList }) => classList.add('show'), {
            animation,
            runningTransition: 'continue',
        })));
        return { windowRef: this._windowRef, transition$ };
    }
    close(animation = false) {
        if (!this._windowRef) {
            return of(undefined);
        }
        return ngbRunTransition(this._ngZone, this._windowRef.location.nativeElement, ({ classList }) => classList.remove('show'), { animation, runningTransition: 'stop' }).pipe(tap(() => {
            if (this._windowRef) {
                // this is required because of the container='body' option
                this._viewContainerRef.remove(this._viewContainerRef.indexOf(this._windowRef.hostView));
                this._windowRef = null;
            }
            if (this._contentRef?.viewRef) {
                this._applicationRef.detachView(this._contentRef.viewRef);
                this._contentRef.viewRef.destroy();
                this._contentRef = null;
            }
        }));
    }
    _getContentRef(content, templateContext) {
        if (!content) {
            return new ContentRef([]);
        }
        else if (content instanceof TemplateRef) {
            const viewRef = content.createEmbeddedView(templateContext);
            this._applicationRef.attachView(viewRef);
            return new ContentRef([viewRef.rootNodes], viewRef);
        }
        else {
            return new ContentRef([[this._renderer.createText(`${content}`)]]);
        }
    }
}

/**
 * A configuration service for the [`NgbTooltip`](#/components/tooltip/api#NgbTooltip) component.
 *
 * You can inject this service, typically in your root component, and customize the values of its properties in
 * order to provide default values for all the tooltips used in the application.
 */
class SdsTooltipConfig {
    constructor() {
        this.autoClose = true;
        this.placement = 'auto';
        this.popperOptions = (options) => options;
        this.triggers = 'hover focus';
        this.disableTooltip = false;
        this.openDelay = 0;
        this.closeDelay = 0;
    }
    get animation() {
        return this._animation === undefined ? false : this._animation;
    }
    set animation(animation) {
        this._animation = animation;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTooltipConfig_Factory(t) { return new (t || SdsTooltipConfig)(); }; }
    /** @nocollapse */ static { this.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: SdsTooltipConfig, factory: SdsTooltipConfig.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTooltipConfig, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

const _c0$2 = ["*"];
let nextId$1 = 0;
class SdsTooltipWindow {
    /** @nocollapse */ static { this.ɵfac = function SdsTooltipWindow_Factory(t) { return new (t || SdsTooltipWindow)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsTooltipWindow, selectors: [["sds-tooltip-window"]], hostAttrs: ["role", "tooltip", 2, "position", "absolute"], hostVars: 5, hostBindings: function SdsTooltipWindow_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵhostProperty("id", ctx.id);
            i0.ɵɵclassMap("tooltip" + (ctx.tooltipClass ? " " + ctx.tooltipClass : ""));
            i0.ɵɵclassProp("fade", ctx.animation);
        } }, inputs: { animation: "animation", id: "id", tooltipClass: "tooltipClass" }, standalone: true, features: [i0.ɵɵStandaloneFeature], ngContentSelectors: _c0$2, decls: 3, vars: 0, consts: [["data-popper-arrow", "", 1, "tooltip-arrow"], [1, "tooltip-inner"]], template: function SdsTooltipWindow_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelement(0, "div", 0);
            i0.ɵɵelementStart(1, "div", 1);
            i0.ɵɵprojection(2);
            i0.ɵɵelementEnd();
        } }, styles: [".tooltip{--sds-tooltip-zindex: 1080;--sds-tooltip-max-width: 200px;--sds-tooltip-padding-x: .5rem;--sds-tooltip-padding-y: .25rem;--sds-tooltip-margin: null;--sds-tooltip-font-size: .875rem;--sds-tooltip-color: white;--sds-tooltip-bg: #454540;--sds-tooltip-border-radius: .375;--sds-tooltip-opacity: .9;--sds-tooltip-arrow-width: .8rem;--sds-tooltip-arrow-height: .4rem;z-index:var(--sds-tooltip-zindex);display:block;margin:var(--sds-tooltip-margin);padding:var(--sds-tooltip-arrow-height);font-family:var(--bs-font-sans-serif);font-style:normal;font-weight:400;line-height:1.5;text-align:left;text-align:start;text-decoration:none;text-shadow:none;text-transform:none;letter-spacing:normal;word-break:normal;white-space:normal;word-spacing:normal;line-break:auto;word-wrap:break-word;opacity:0}.tooltip.show{opacity:var(--sds-tooltip-opacity)}.tooltip .tooltip-arrow{display:block;width:var(--sds-tooltip-arrow-width);height:var(--sds-tooltip-arrow-height)}.tooltip .tooltip-arrow:before{position:absolute;content:\"\";border-color:transparent;border-style:solid}.sds-tooltip-top .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=top] .tooltip-arrow{bottom:0}.sds-tooltip-top .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=top] .tooltip-arrow:before{top:-1px;border-width:var(--sds-tooltip-arrow-height) calc(var(--sds-tooltip-arrow-width) * .5) 0;border-top-color:var(--sds-tooltip-bg)}.sds-tooltip-end .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=right] .tooltip-arrow{left:0;width:var(--sds-tooltip-arrow-height);height:var(--sds-tooltip-arrow-width)}.sds-tooltip-end .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=right] .tooltip-arrow:before{right:-1px;border-width:calc(var(--sds-tooltip-arrow-width) * .5) var(--sds-tooltip-arrow-height) calc(var(--sds-tooltip-arrow-width) * .5) 0;border-right-color:var(--sds-tooltip-bg)}.sds-tooltip-bottom .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow{top:0}.sds-tooltip-bottom .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow:before{bottom:-1px;border-width:0 calc(var(--sds-tooltip-arrow-width) * .5) var(--sds-tooltip-arrow-height);border-bottom-color:var(--sds-tooltip-bg)}.sds-tooltip-start .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=left] .tooltip-arrow{right:0;width:var(--sds-tooltip-arrow-height);height:var(--sds-tooltip-arrow-width)}.sds-tooltip-start .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=left] .tooltip-arrow:before{left:-1px;border-width:calc(var(--sds-tooltip-arrow-width) * .5) 0 calc(var(--sds-tooltip-arrow-width) * .5) var(--sds-tooltip-arrow-height);border-left-color:var(--sds-tooltip-bg)}.tooltip-inner{max-width:var(--sds-tooltip-max-width);padding:var(--sds-tooltip-padding-y) var(--sds-tooltip-padding-x);color:var(--sds-tooltip-color);text-align:center;background-color:var(--sds-tooltip-bg);border-radius:.375rem}.tooltip-inner p{color:var(--sds-tooltip-color)}\n"], encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTooltipWindow, [{
        type: Component,
        args: [{ selector: 'sds-tooltip-window', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                    '[class]': '"tooltip" + (tooltipClass ? " " + tooltipClass : "")',
                    '[class.fade]': 'animation',
                    role: 'tooltip',
                    '[id]': 'id',
                    style: 'position: absolute;',
                }, template: `<div class="tooltip-arrow" data-popper-arrow></div>
    <div class="tooltip-inner"><ng-content></ng-content></div>`, styles: [".tooltip{--sds-tooltip-zindex: 1080;--sds-tooltip-max-width: 200px;--sds-tooltip-padding-x: .5rem;--sds-tooltip-padding-y: .25rem;--sds-tooltip-margin: null;--sds-tooltip-font-size: .875rem;--sds-tooltip-color: white;--sds-tooltip-bg: #454540;--sds-tooltip-border-radius: .375;--sds-tooltip-opacity: .9;--sds-tooltip-arrow-width: .8rem;--sds-tooltip-arrow-height: .4rem;z-index:var(--sds-tooltip-zindex);display:block;margin:var(--sds-tooltip-margin);padding:var(--sds-tooltip-arrow-height);font-family:var(--bs-font-sans-serif);font-style:normal;font-weight:400;line-height:1.5;text-align:left;text-align:start;text-decoration:none;text-shadow:none;text-transform:none;letter-spacing:normal;word-break:normal;white-space:normal;word-spacing:normal;line-break:auto;word-wrap:break-word;opacity:0}.tooltip.show{opacity:var(--sds-tooltip-opacity)}.tooltip .tooltip-arrow{display:block;width:var(--sds-tooltip-arrow-width);height:var(--sds-tooltip-arrow-height)}.tooltip .tooltip-arrow:before{position:absolute;content:\"\";border-color:transparent;border-style:solid}.sds-tooltip-top .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=top] .tooltip-arrow{bottom:0}.sds-tooltip-top .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=top] .tooltip-arrow:before{top:-1px;border-width:var(--sds-tooltip-arrow-height) calc(var(--sds-tooltip-arrow-width) * .5) 0;border-top-color:var(--sds-tooltip-bg)}.sds-tooltip-end .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=right] .tooltip-arrow{left:0;width:var(--sds-tooltip-arrow-height);height:var(--sds-tooltip-arrow-width)}.sds-tooltip-end .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=right] .tooltip-arrow:before{right:-1px;border-width:calc(var(--sds-tooltip-arrow-width) * .5) var(--sds-tooltip-arrow-height) calc(var(--sds-tooltip-arrow-width) * .5) 0;border-right-color:var(--sds-tooltip-bg)}.sds-tooltip-bottom .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow{top:0}.sds-tooltip-bottom .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow:before{bottom:-1px;border-width:0 calc(var(--sds-tooltip-arrow-width) * .5) var(--sds-tooltip-arrow-height);border-bottom-color:var(--sds-tooltip-bg)}.sds-tooltip-start .tooltip-arrow,.sds-tooltip-auto[data-popper-placement^=left] .tooltip-arrow{right:0;width:var(--sds-tooltip-arrow-height);height:var(--sds-tooltip-arrow-width)}.sds-tooltip-start .tooltip-arrow:before,.sds-tooltip-auto[data-popper-placement^=left] .tooltip-arrow:before{left:-1px;border-width:calc(var(--sds-tooltip-arrow-width) * .5) 0 calc(var(--sds-tooltip-arrow-width) * .5) var(--sds-tooltip-arrow-height);border-left-color:var(--sds-tooltip-bg)}.tooltip-inner{max-width:var(--sds-tooltip-max-width);padding:var(--sds-tooltip-padding-y) var(--sds-tooltip-padding-x);color:var(--sds-tooltip-color);text-align:center;background-color:var(--sds-tooltip-bg);border-radius:.375rem}.tooltip-inner p{color:var(--sds-tooltip-color)}\n"] }]
    }], null, { animation: [{
            type: Input
        }], id: [{
            type: Input
        }], tooltipClass: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsTooltipWindow, { className: "SdsTooltipWindow" }); })();
/**
 * A lightweight and extensible directive for fancy tooltip creation.
 */
class SdsTooltip {
    constructor(_elementRef, _renderer, injector, viewContainerRef, config, _ngZone, _document, _changeDetector, applicationRef) {
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this._ngZone = _ngZone;
        this._document = _document;
        this._changeDetector = _changeDetector;
        /**
         * An event emitted when the tooltip opening animation has finished. Contains no payload.
         */
        this.shown = new EventEmitter();
        /**
         * An event emitted when the tooltip closing animation has finished. Contains no payload.
         */
        this.hidden = new EventEmitter();
        this._sdsTooltipWindowId = `sds-tooltip-${nextId$1++}`;
        this._windowRef = null;
        this.animation = config.animation;
        this.autoClose = config.autoClose;
        this.position = config.placement;
        this.popperOptions = config.popperOptions;
        this.triggers = config.triggers;
        this.container = config.container;
        this.disableTooltip = config.disableTooltip;
        this.tooltipClass = config.tooltipClass;
        this.openDelay = config.openDelay;
        this.closeDelay = config.closeDelay;
        this._popupService = new PopupService(SdsTooltipWindow, injector, viewContainerRef, _renderer, this._ngZone, applicationRef);
        this._positioning = sdsPositioning();
    }
    /**
     * The string content or a `TemplateRef` for the content to be displayed in the tooltip.
     *
     * If the content if falsy, the tooltip won't open.
     */
    set sdsTooltip(value) {
        this._sdsTooltip = value;
        if (!value && this._windowRef) {
            this.close();
        }
    }
    get sdsTooltip() {
        return this._sdsTooltip;
    }
    /**
     * Opens the tooltip.
     *
     * This is considered to be a "manual" triggering.
     * The `context` is an optional value to be injected into the tooltip template when it is created.
     */
    open(context) {
        if (!this._windowRef && this._sdsTooltip && !this.disableTooltip) {
            const { windowRef, transition$ } = this._popupService.open(this._sdsTooltip, context, this.animation);
            this._windowRef = windowRef;
            this._windowRef.setInput('animation', this.animation);
            this._windowRef.setInput('tooltipClass', this.tooltipClass);
            this._windowRef.setInput('id', this._sdsTooltipWindowId);
            this._renderer.setAttribute(this._getPositionTargetElement(), 'aria-describedby', this._sdsTooltipWindowId);
            if (this.container === 'body') {
                this._document.querySelector(this.container).appendChild(this._windowRef.location.nativeElement);
            }
            // We need to detect changes, because we don't know where .open() might be called from.
            // Ex. opening tooltip from one of lifecycle hooks that run after the CD
            // (say from ngAfterViewInit) will result in 'ExpressionHasChanged' exception
            this._windowRef.changeDetectorRef.detectChanges();
            // We need to mark for check, because tooltip won't work inside the OnPush component.
            // Ex. when we use expression like `{{ tooltip.isOpen() : 'opened' : 'closed' }}`
            // inside the template of an OnPush component and we change the tooltip from
            // open -> closed, the expression in question won't be updated unless we explicitly
            // mark the parent component to be checked.
            this._windowRef.changeDetectorRef.markForCheck();
            // Setting up popper and scheduling updates when zone is stable
            this._ngZone.runOutsideAngular(() => {
                this._positioning.createPopper({
                    hostElement: this._getPositionTargetElement(),
                    targetElement: this._windowRef.location.nativeElement,
                    placement: this.position,
                    appendToBody: this.container === 'body',
                    baseClass: 'sds-tooltip',
                    updatePopperOptions: (options) => this.popperOptions(options),
                });
                Promise.resolve().then(() => {
                    // This update is required for correct arrow placement
                    this._positioning.update();
                    this._zoneSubscription = this._ngZone.onStable.subscribe(() => this._positioning.update());
                });
            });
            sdsAutoClose(this._ngZone, this._document, this.autoClose, () => this.close(), this.hidden, [
                this._windowRef.location.nativeElement,
            ]);
            transition$.subscribe(() => this.shown.emit());
        }
    }
    /**
     * Closes the tooltip.
     *
     * This is considered to be a "manual" triggering of the tooltip.
     */
    close(animation = this.animation) {
        if (this._windowRef != null) {
            this._renderer.removeAttribute(this._getPositionTargetElement(), 'aria-describedby');
            this._popupService.close(animation).subscribe(() => {
                this._windowRef = null;
                this._positioning.destroy();
                this._zoneSubscription?.unsubscribe();
                this.hidden.emit();
                this._changeDetector.markForCheck();
            });
        }
    }
    /**
     * Toggles the tooltip.
     *
     * This is considered to be a "manual" triggering of the tooltip.
     */
    toggle() {
        if (this._windowRef) {
            this.close();
        }
        else {
            this.open();
        }
    }
    /**
     * Returns `true`, if the popover is currently shown.
     */
    isOpen() {
        return this._windowRef != null;
    }
    ngOnInit() {
        this._unregisterListenersFn = listenToTriggers(this._renderer, this._elementRef.nativeElement, this.triggers, this.isOpen.bind(this), this.open.bind(this), this.close.bind(this), +this.openDelay, +this.closeDelay);
    }
    ngOnChanges({ tooltipClass }) {
        if (tooltipClass && this.isOpen()) {
            this._windowRef.instance.tooltipClass = tooltipClass.currentValue;
        }
    }
    ngOnDestroy() {
        this.close(false);
        // This check is needed as it might happen that ngOnDestroy is called before ngOnInit
        // under certain conditions, see: https://github.com/ng-bootstrap/ng-bootstrap/issues/2199
        this._unregisterListenersFn?.();
    }
    _getPositionTargetElement() {
        return ((isString(this.positionTarget) ? this._document.querySelector(this.positionTarget) : this.positionTarget) ||
            this._elementRef.nativeElement);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsTooltip_Factory(t) { return new (t || SdsTooltip)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(SdsTooltipConfig), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ApplicationRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsTooltip, selectors: [["", "sdsTooltip", ""]], inputs: { animation: "animation", autoClose: "autoClose", position: "position", popperOptions: "popperOptions", triggers: "triggers", positionTarget: "positionTarget", container: "container", disableTooltip: "disableTooltip", tooltipClass: "tooltipClass", openDelay: "openDelay", closeDelay: "closeDelay", sdsTooltip: "sdsTooltip" }, outputs: { shown: "shown", hidden: "hidden" }, exportAs: ["sdsTooltip"], standalone: true, features: [i0.ɵɵNgOnChangesFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTooltip, [{
        type: Directive,
        args: [{ selector: '[sdsTooltip]', standalone: true, exportAs: 'sdsTooltip' }]
    }], () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.Injector }, { type: i0.ViewContainerRef }, { type: SdsTooltipConfig }, { type: i0.NgZone }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: i0.ChangeDetectorRef }, { type: i0.ApplicationRef }], { animation: [{
            type: Input
        }], autoClose: [{
            type: Input
        }], position: [{
            type: Input
        }], popperOptions: [{
            type: Input
        }], triggers: [{
            type: Input
        }], positionTarget: [{
            type: Input
        }], container: [{
            type: Input
        }], disableTooltip: [{
            type: Input
        }], tooltipClass: [{
            type: Input
        }], openDelay: [{
            type: Input
        }], closeDelay: [{
            type: Input
        }], shown: [{
            type: Output
        }], hidden: [{
            type: Output
        }], sdsTooltip: [{
            type: Input
        }] }); })();

class SdsTooltipModule {
    /** @nocollapse */ static { this.ɵfac = function SdsTooltipModule_Factory(t) { return new (t || SdsTooltipModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsTooltipModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsTooltipModule, [{
        type: NgModule,
        args: [{
                imports: [SdsTooltip],
                exports: [SdsTooltip],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsTooltipModule, { imports: [SdsTooltip], exports: [SdsTooltip] }); })();

function addPopperOffset(offset$1) {
    return (options) => {
        options.modifiers.push(offset, {
            name: 'offset',
            options: {
                offset: () => offset$1,
            },
        });
        return options;
    };
}

/**
 * A configuration service for the [`NgbPopover`](#/components/popover/api#NgbPopover) component.
 *
 * You can inject this service, typically in your root component, and customize the values of its properties in
 * order to provide default values for all the popovers used in the application.
 */
class SdsPopoverConfig {
    constructor() {
        this.autoClose = 'inside';
        this.placement = 'auto';
        this.popperOptions = (options) => options;
        this.triggers = 'click';
        this.disablePopover = false;
        this.openDelay = 0;
        this.closeDelay = 0;
    }
    get animation() {
        return this._animation === undefined ? false : this._animation;
    }
    set animation(animation) {
        this._animation = animation;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsPopoverConfig_Factory(t) { return new (t || SdsPopoverConfig)(); }; }
    /** @nocollapse */ static { this.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: SdsPopoverConfig, factory: SdsPopoverConfig.ɵfac, providedIn: 'root' }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPopoverConfig, [{
        type: Injectable,
        args: [{ providedIn: 'root' }]
    }], () => [], null); })();

const _c0$1 = ["*"];
function SdsPopoverWindow_h3_1_ng_template_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtext(0);
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵtextInterpolate(ctx_r0.title);
} }
function SdsPopoverWindow_h3_1_ng_template_3_Template(rf, ctx) { }
function SdsPopoverWindow_h3_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "h3", 4);
    i0.ɵɵtemplate(1, SdsPopoverWindow_h3_1_ng_template_1_Template, 1, 1, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor)(3, SdsPopoverWindow_h3_1_ng_template_3_Template, 0, 0, "ng-template", 5);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const simpleTitle_r2 = i0.ɵɵreference(2);
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.isTitleTemplate() ? ctx_r0.title : simpleTitle_r2)("ngTemplateOutletContext", ctx_r0.context);
} }
let nextId = 0;
class SdsPopoverWindow {
    isTitleTemplate() {
        return this.title instanceof TemplateRef;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsPopoverWindow_Factory(t) { return new (t || SdsPopoverWindow)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsPopoverWindow, selectors: [["sds-popover-window"]], hostAttrs: ["role", "tooltip", 2, "position", "absolute"], hostVars: 5, hostBindings: function SdsPopoverWindow_HostBindings(rf, ctx) { if (rf & 2) {
            i0.ɵɵhostProperty("id", ctx.id);
            i0.ɵɵclassMap("popover" + (ctx.popoverClass ? " " + ctx.popoverClass : ""));
            i0.ɵɵclassProp("fade", ctx.animation);
        } }, inputs: { animation: "animation", title: "title", id: "id", popoverClass: "popoverClass", context: "context" }, standalone: true, features: [i0.ɵɵStandaloneFeature], ngContentSelectors: _c0$1, decls: 4, vars: 1, consts: [["simpleTitle", ""], ["data-popper-arrow", "", 1, "popover-arrow"], ["class", "popover-header", 4, "ngIf"], [1, "popover-body"], [1, "popover-header"], [3, "ngTemplateOutlet", "ngTemplateOutletContext"]], template: function SdsPopoverWindow_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵprojectionDef();
            i0.ɵɵelement(0, "div", 1);
            i0.ɵɵtemplate(1, SdsPopoverWindow_h3_1_Template, 4, 2, "h3", 2);
            i0.ɵɵelementStart(2, "div", 3);
            i0.ɵɵprojection(3);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵadvance();
            i0.ɵɵproperty("ngIf", ctx.title);
        } }, dependencies: [NgTemplateOutlet, NgIf], styles: [".popover{--sds-popover-zindex: 999;--sds-popover-max-width: 20rem;--sds-popover-font-size: .875rem;--sds-popover-bg: #f9f9f7;--sds-popover-border-width: 1px;--sds-popover-border-color: #757575;--sds-popover-border-radius: .5rem;--sds-popover-inner-border-radius: calc(.5rem - 1px) ;--sds-popover-box-shadow: 0 .5rem 1rem rgba($black, .15);--sds-popover-header-padding-x: 1rem;--sds-popover-header-padding-y: .5rem;--sds-popover-header-font-size: 1rem;--sds-popover-header-color: black;--sds-popover-header-bg: tint-color($secondary, 80%);--sds-popover-body-padding-x: 1rem;--sds-popover-body-padding-y: 1rem;--sds-popover-body-color: #212529;--sds-popover-arrow-width: 1rem;--sds-popover-arrow-height: .5rem;--sds-popover-arrow-border: #757575;z-index:var(--sds-popover-zindex);display:block;max-width:var(--sds-popover-max-width);font-size:var(--sds-popover-font-size);word-wrap:break-word;background-color:var(--sds-popover-bg);background-clip:padding-box;border:var(--sds-popover-border-width) solid var(--sds-popover-border-color);border-radius:var(--sds-popover-border-radius);box-shadow:var(--sds-popover-box-shadow)}.popover .popover-arrow{display:block;width:var(--sds-popover-arrow-width);height:var(--sds-popover-arrow-height)}.popover .popover-arrow:before,.popover .popover-arrow:after{position:absolute;display:block;content:\"\";border-color:transparent;border-style:solid;border-width:0}.sds-popover-top>.popover-arrow,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow{bottom:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width))}.sds-popover-top>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:before,.sds-popover-top>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:after{border-width:var(--sds-popover-arrow-height) calc(var(--sds-popover-arrow-width) * .5) 0}.sds-popover-top>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:before{bottom:0;border-top-color:var(--sds-popover-arrow-border)}.sds-popover-top>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:after{bottom:var(--sds-popover-border-width);border-top-color:var(--sds-popover-bg)}.sds-popover-end>.popover-arrow,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow{left:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width));width:var(--sds-popover-arrow-height);height:var(--sds-popover-arrow-width)}.sds-popover-end>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:before,.sds-popover-end>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:after{border-width:calc(var(--sds-popover-arrow-width) * .5) var(--sds-popover-arrow-height) calc(var(--sds-popover-arrow-width) * .5) 0}.sds-popover-end>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:before{left:0;border-right-color:var(--sds-popover-arrow-border)}.sds-popover-end>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:after{left:var(--sds-popover-border-width);border-right-color:var(--sds-popover-bg)}.sds-popover-bottom>.popover-arrow,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow{top:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width))}.sds-popover-bottom>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:before,.sds-popover-bottom>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:after{border-width:0 calc(var(--sds-popover-arrow-width) * .5) var(--sds-popover-arrow-height)}.sds-popover-bottom>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:before{top:0;border-bottom-color:var(--sds-popover-arrow-border)}.sds-popover-bottom>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:after{top:var(--sds-popover-border-width);border-bottom-color:var(--sds-popover-bg)}.sds-popover-bottom .popover-header:before,.sds-popover-auto[data-popper-placement^=bottom] .popover-header:before{position:absolute;top:0;left:50%;display:block;width:var(--sds-popover-arrow-width);margin-left:calc(-.5 * var(--sds-popover-arrow-width));content:\"\";border-bottom:var(--sds-popover-border-width) solid var(--sds-popover-header-bg)}.sds-popover-start>.popover-arrow,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow{right:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width));width:var(--sds-popover-arrow-height);height:var(--sds-popover-arrow-width)}.sds-popover-start>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:before,.sds-popover-start>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:after{border-width:calc(var(--sds-popover-arrow-width) * .5) 0 calc(var(--sds-popover-arrow-width) * .5) var(--sds-popover-arrow-height)}.sds-popover-start>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:before{right:0;border-left-color:var(--sds-popover-arrow-border)}.sds-popover-start>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:after{right:var(--sds-popover-border-width);border-left-color:var(--sds-popover-bg)}.popover-header{padding:var(--sds-popover-header-padding-y) var(--sds-popover-header-padding-x);margin-bottom:0;font-size:var(--sds-popover-header-font-size);color:var(--sds-popover-header-color);background-color:var(--sds-popover-header-bg);border-bottom:var(--sds-popover-border-width) solid var(--sds-popover-border-color);border-top-right-radius:var(--sds-popover-inner-border-radius);border-top-left-radius:var(--sds-popover-inner-border-radius);margin-top:initial}.popover-header:empty{display:none}.popover-body{padding:var(--sds-popover-body-padding-y) var(--sds-popover-body-padding-x);color:var(--sds-popover-body-color);margin-top:initial}\n"], encapsulation: 2, changeDetection: 0 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPopoverWindow, [{
        type: Component,
        args: [{ selector: 'sds-popover-window', standalone: true, imports: [NgTemplateOutlet, NgIf], changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                    '[class]': '"popover" + (popoverClass ? " " + popoverClass : "")',
                    '[class.fade]': 'animation',
                    role: 'tooltip',
                    '[id]': 'id',
                    style: 'position: absolute;',
                }, template: ` <div class="popover-arrow" data-popper-arrow></div>
    <h3 class="popover-header" *ngIf="title">
      <ng-template #simpleTitle>{{ title }}</ng-template>
      <ng-template
        [ngTemplateOutlet]="isTitleTemplate() ? $any(title) : simpleTitle"
        [ngTemplateOutletContext]="context"
      ></ng-template>
    </h3>
    <div class="popover-body"><ng-content></ng-content></div>`, styles: [".popover{--sds-popover-zindex: 999;--sds-popover-max-width: 20rem;--sds-popover-font-size: .875rem;--sds-popover-bg: #f9f9f7;--sds-popover-border-width: 1px;--sds-popover-border-color: #757575;--sds-popover-border-radius: .5rem;--sds-popover-inner-border-radius: calc(.5rem - 1px) ;--sds-popover-box-shadow: 0 .5rem 1rem rgba($black, .15);--sds-popover-header-padding-x: 1rem;--sds-popover-header-padding-y: .5rem;--sds-popover-header-font-size: 1rem;--sds-popover-header-color: black;--sds-popover-header-bg: tint-color($secondary, 80%);--sds-popover-body-padding-x: 1rem;--sds-popover-body-padding-y: 1rem;--sds-popover-body-color: #212529;--sds-popover-arrow-width: 1rem;--sds-popover-arrow-height: .5rem;--sds-popover-arrow-border: #757575;z-index:var(--sds-popover-zindex);display:block;max-width:var(--sds-popover-max-width);font-size:var(--sds-popover-font-size);word-wrap:break-word;background-color:var(--sds-popover-bg);background-clip:padding-box;border:var(--sds-popover-border-width) solid var(--sds-popover-border-color);border-radius:var(--sds-popover-border-radius);box-shadow:var(--sds-popover-box-shadow)}.popover .popover-arrow{display:block;width:var(--sds-popover-arrow-width);height:var(--sds-popover-arrow-height)}.popover .popover-arrow:before,.popover .popover-arrow:after{position:absolute;display:block;content:\"\";border-color:transparent;border-style:solid;border-width:0}.sds-popover-top>.popover-arrow,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow{bottom:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width))}.sds-popover-top>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:before,.sds-popover-top>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:after{border-width:var(--sds-popover-arrow-height) calc(var(--sds-popover-arrow-width) * .5) 0}.sds-popover-top>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:before{bottom:0;border-top-color:var(--sds-popover-arrow-border)}.sds-popover-top>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=top]>.popover-arrow:after{bottom:var(--sds-popover-border-width);border-top-color:var(--sds-popover-bg)}.sds-popover-end>.popover-arrow,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow{left:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width));width:var(--sds-popover-arrow-height);height:var(--sds-popover-arrow-width)}.sds-popover-end>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:before,.sds-popover-end>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:after{border-width:calc(var(--sds-popover-arrow-width) * .5) var(--sds-popover-arrow-height) calc(var(--sds-popover-arrow-width) * .5) 0}.sds-popover-end>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:before{left:0;border-right-color:var(--sds-popover-arrow-border)}.sds-popover-end>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=right]>.popover-arrow:after{left:var(--sds-popover-border-width);border-right-color:var(--sds-popover-bg)}.sds-popover-bottom>.popover-arrow,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow{top:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width))}.sds-popover-bottom>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:before,.sds-popover-bottom>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:after{border-width:0 calc(var(--sds-popover-arrow-width) * .5) var(--sds-popover-arrow-height)}.sds-popover-bottom>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:before{top:0;border-bottom-color:var(--sds-popover-arrow-border)}.sds-popover-bottom>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=bottom]>.popover-arrow:after{top:var(--sds-popover-border-width);border-bottom-color:var(--sds-popover-bg)}.sds-popover-bottom .popover-header:before,.sds-popover-auto[data-popper-placement^=bottom] .popover-header:before{position:absolute;top:0;left:50%;display:block;width:var(--sds-popover-arrow-width);margin-left:calc(-.5 * var(--sds-popover-arrow-width));content:\"\";border-bottom:var(--sds-popover-border-width) solid var(--sds-popover-header-bg)}.sds-popover-start>.popover-arrow,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow{right:calc(-1 * (var(--sds-popover-arrow-height)) - var(--sds-popover-border-width));width:var(--sds-popover-arrow-height);height:var(--sds-popover-arrow-width)}.sds-popover-start>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:before,.sds-popover-start>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:after{border-width:calc(var(--sds-popover-arrow-width) * .5) 0 calc(var(--sds-popover-arrow-width) * .5) var(--sds-popover-arrow-height)}.sds-popover-start>.popover-arrow:before,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:before{right:0;border-left-color:var(--sds-popover-arrow-border)}.sds-popover-start>.popover-arrow:after,.sds-popover-auto[data-popper-placement^=left]>.popover-arrow:after{right:var(--sds-popover-border-width);border-left-color:var(--sds-popover-bg)}.popover-header{padding:var(--sds-popover-header-padding-y) var(--sds-popover-header-padding-x);margin-bottom:0;font-size:var(--sds-popover-header-font-size);color:var(--sds-popover-header-color);background-color:var(--sds-popover-header-bg);border-bottom:var(--sds-popover-border-width) solid var(--sds-popover-border-color);border-top-right-radius:var(--sds-popover-inner-border-radius);border-top-left-radius:var(--sds-popover-inner-border-radius);margin-top:initial}.popover-header:empty{display:none}.popover-body{padding:var(--sds-popover-body-padding-y) var(--sds-popover-body-padding-x);color:var(--sds-popover-body-color);margin-top:initial}\n"] }]
    }], null, { animation: [{
            type: Input
        }], title: [{
            type: Input
        }], id: [{
            type: Input
        }], popoverClass: [{
            type: Input
        }], context: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsPopoverWindow, { className: "SdsPopoverWindow" }); })();
/**
 * A lightweight and extensible directive for fancy popover creation.
 */
class SdsPopover {
    _isDisabled() {
        if (this.disablePopover) {
            return true;
        }
        if (!this.sdsPopover && !this.sdsPopoverTitle) {
            return true;
        }
        return false;
    }
    constructor(_elementRef, _renderer, injector, viewContainerRef, config, _ngZone, _document, _changeDetector, applicationRef) {
        this._elementRef = _elementRef;
        this._renderer = _renderer;
        this._ngZone = _ngZone;
        this._document = _document;
        this._changeDetector = _changeDetector;
        /**
         * An event emitted when the popover opening animation has finished. Contains no payload.
         */
        this.shown = new EventEmitter();
        /**
         * An event emitted when the popover closing animation has finished. Contains no payload.
         *
         * At this point popover is not in the DOM anymore.
         */
        this.hidden = new EventEmitter();
        this._sdsPopoverWindowId = `sds-popover-${nextId++}`;
        this._windowRef = null;
        this.animation = config.animation;
        this.autoClose = config.autoClose;
        this.position = config.placement;
        this.popperOptions = config.popperOptions;
        this.triggers = config.triggers;
        this.container = config.container;
        this.disablePopover = config.disablePopover;
        this.popoverClass = config.popoverClass;
        this.openDelay = config.openDelay;
        this.closeDelay = config.closeDelay;
        this._positioning = sdsPositioning();
        this._popupService = new PopupService(SdsPopoverWindow, injector, viewContainerRef, _renderer, this._ngZone, applicationRef);
    }
    /**
     * Opens the popover.
     *
     * This is considered to be a "manual" triggering.
     * The `context` is an optional value to be injected into the popover template when it is created.
     */
    open(context) {
        if (!this._windowRef && !this._isDisabled()) {
            // this type assertion is safe because otherwise _isDisabled would return true
            const { windowRef, transition$ } = this._popupService.open(this.sdsPopover, context ?? this.popoverContext, this.animation);
            this._windowRef = windowRef;
            this._windowRef.setInput('animation', this.animation);
            this._windowRef.setInput('title', this.sdsPopoverTitle);
            this._windowRef.setInput('context', context ?? this.popoverContext);
            this._windowRef.setInput('popoverClass', this.popoverClass);
            this._windowRef.setInput('id', this._sdsPopoverWindowId);
            this._renderer.setAttribute(this._getPositionTargetElement(), 'aria-describedby', this._sdsPopoverWindowId);
            if (this.container === 'body') {
                this._document.querySelector(this.container).appendChild(this._windowRef.location.nativeElement);
            }
            // We need to detect changes, because we don't know where .open() might be called from.
            // Ex. opening popover from one of lifecycle hooks that run after the CD
            // (say from ngAfterViewInit) will result in 'ExpressionHasChanged' exception
            this._windowRef.changeDetectorRef.detectChanges();
            // We need to mark for check, because popover won't work inside the OnPush component.
            // Ex. when we use expression like `{{ popover.isOpen() : 'opened' : 'closed' }}`
            // inside the template of an OnPush component and we change the popover from
            // open -> closed, the expression in question won't be updated unless we explicitly
            // mark the parent component to be checked.
            this._windowRef.changeDetectorRef.markForCheck();
            // Setting up popper and scheduling updates when zone is stable
            this._ngZone.runOutsideAngular(() => {
                this._positioning.createPopper({
                    hostElement: this._getPositionTargetElement(),
                    targetElement: this._windowRef.location.nativeElement,
                    placement: this.position,
                    appendToBody: this.container === 'body',
                    baseClass: 'sds-popover',
                    updatePopperOptions: (options) => this.popperOptions(addPopperOffset([0, 8])(options)),
                });
                Promise.resolve().then(() => {
                    // This update is required for correct arrow placement
                    this._positioning.update();
                    this._zoneSubscription = this._ngZone.onStable.subscribe(() => this._positioning.update());
                });
            });
            sdsAutoClose(this._ngZone, this._document, this.autoClose, () => this.close(), this.hidden, [
                this._windowRef.location.nativeElement,
            ]);
            transition$.subscribe(() => this.shown.emit());
        }
    }
    /**
     * Closes the popover.
     *
     * This is considered to be a "manual" triggering of the popover.
     */
    close(animation = this.animation) {
        if (this._windowRef) {
            this._renderer.removeAttribute(this._getPositionTargetElement(), 'aria-describedby');
            this._popupService.close(animation).subscribe(() => {
                this._windowRef = null;
                this._positioning.destroy();
                this._zoneSubscription?.unsubscribe();
                this.hidden.emit();
                this._changeDetector.markForCheck();
            });
        }
    }
    /**
     * Toggles the popover.
     *
     * This is considered to be a "manual" triggering of the popover.
     */
    toggle() {
        if (this._windowRef) {
            this.close();
        }
        else {
            this.open();
        }
    }
    /**
     * Returns `true`, if the popover is currently shown.
     */
    isOpen() {
        return this._windowRef != null;
    }
    ngOnInit() {
        this._unregisterListenersFn = listenToTriggers(this._renderer, this._elementRef.nativeElement, this.triggers, this.isOpen.bind(this), this.open.bind(this), this.close.bind(this), +this.openDelay, +this.closeDelay);
    }
    ngOnChanges({ sdsPopover, popoverTitle, disablePopover, popoverClass }) {
        if (popoverClass && this.isOpen()) {
            this._windowRef.instance.popoverClass = popoverClass.currentValue;
        }
        // close popover if title and content become empty, or disablePopover set to true
        if ((sdsPopover || popoverTitle || disablePopover) && this._isDisabled()) {
            this.close();
        }
    }
    ngOnDestroy() {
        this.close(false);
        // This check is needed as it might happen that ngOnDestroy is called before ngOnInit
        // under certain conditions, see: https://github.com/ng-bootstrap/ng-bootstrap/issues/2199
        this._unregisterListenersFn?.();
    }
    _getPositionTargetElement() {
        return ((isString(this.positionTarget) ? this._document.querySelector(this.positionTarget) : this.positionTarget) ||
            this._elementRef.nativeElement);
    }
    /** @nocollapse */ static { this.ɵfac = function SdsPopover_Factory(t) { return new (t || SdsPopover)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(SdsPopoverConfig), i0.ɵɵdirectiveInject(i0.NgZone), i0.ɵɵdirectiveInject(DOCUMENT), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i0.ApplicationRef)); }; }
    /** @nocollapse */ static { this.ɵdir = /** @pureOrBreakMyCode */ i0.ɵɵdefineDirective({ type: SdsPopover, selectors: [["", "sdsPopover", ""]], inputs: { animation: "animation", autoClose: "autoClose", sdsPopover: "sdsPopover", sdsPopoverTitle: "sdsPopoverTitle", position: "position", popperOptions: "popperOptions", triggers: "triggers", positionTarget: "positionTarget", container: "container", disablePopover: "disablePopover", popoverClass: "popoverClass", popoverContext: "popoverContext", openDelay: "openDelay", closeDelay: "closeDelay" }, outputs: { shown: "shown", hidden: "hidden" }, exportAs: ["sdsPopover"], standalone: true, features: [i0.ɵɵNgOnChangesFeature] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPopover, [{
        type: Directive,
        args: [{ selector: '[sdsPopover]', exportAs: 'sdsPopover', standalone: true }]
    }], () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i0.Injector }, { type: i0.ViewContainerRef }, { type: SdsPopoverConfig }, { type: i0.NgZone }, { type: undefined, decorators: [{
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: i0.ChangeDetectorRef }, { type: i0.ApplicationRef }], { animation: [{
            type: Input
        }], autoClose: [{
            type: Input
        }], sdsPopover: [{
            type: Input
        }], sdsPopoverTitle: [{
            type: Input
        }], position: [{
            type: Input
        }], popperOptions: [{
            type: Input
        }], triggers: [{
            type: Input
        }], positionTarget: [{
            type: Input
        }], container: [{
            type: Input
        }], disablePopover: [{
            type: Input
        }], popoverClass: [{
            type: Input
        }], popoverContext: [{
            type: Input
        }], openDelay: [{
            type: Input
        }], closeDelay: [{
            type: Input
        }], shown: [{
            type: Output
        }], hidden: [{
            type: Output
        }] }); })();

class SdsPopoverModule {
    /** @nocollapse */ static { this.ɵfac = function SdsPopoverModule_Factory(t) { return new (t || SdsPopoverModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsPopoverModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({}); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsPopoverModule, [{
        type: NgModule,
        args: [{
                imports: [SdsPopover],
                exports: [SdsPopover],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsPopoverModule, { imports: [SdsPopover], exports: [SdsPopover] }); })();

const _c0 = a0 => ({ "sds-border-top": a0 });
class SdsProgressBarComponent {
    constructor() {
        /**
         * Input to control the progress (value between 0 and 100)
         */
        this.progress = 0;
        /**
         * To display the top border
         */
        this.topBorder = false;
    }
    /** @nocollapse */ static { this.ɵfac = function SdsProgressBarComponent_Factory(t) { return new (t || SdsProgressBarComponent)(); }; }
    /** @nocollapse */ static { this.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: SdsProgressBarComponent, selectors: [["sds-progress-bar"]], inputs: { progress: "progress", topBorder: "topBorder" }, decls: 2, vars: 5, consts: [[1, "sds-progress-bar-container", 3, "ngClass"], [1, "sds-progress-bar"]], template: function SdsProgressBarComponent_Template(rf, ctx) { if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0);
            i0.ɵɵelement(1, "div", 1);
            i0.ɵɵelementEnd();
        } if (rf & 2) {
            i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(3, _c0, ctx.topBorder));
            i0.ɵɵadvance();
            i0.ɵɵstyleProp("width", ctx.progress + "%");
        } }, dependencies: [i1$2.NgClass], encapsulation: 2 }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsProgressBarComponent, [{
        type: Component,
        args: [{ selector: 'sds-progress-bar', template: "<div class=\"sds-progress-bar-container\" [ngClass]=\"{ 'sds-border-top': topBorder }\">\n  <div class=\"sds-progress-bar\" [style.width]=\"progress + '%'\"></div>\n</div>\n" }]
    }], null, { progress: [{
            type: Input
        }], topBorder: [{
            type: Input
        }] }); })();
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassDebugInfo(SdsProgressBarComponent, { className: "SdsProgressBarComponent" }); })();

class SdsProgressBarModule {
    /** @nocollapse */ static { this.ɵfac = function SdsProgressBarModule_Factory(t) { return new (t || SdsProgressBarModule)(); }; }
    /** @nocollapse */ static { this.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: SdsProgressBarModule }); }
    /** @nocollapse */ static { this.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [CommonModule] }); }
}
(() => { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SdsProgressBarModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                exports: [SdsProgressBarComponent],
                declarations: [SdsProgressBarComponent],
                providers: [],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SdsProgressBarModule, { declarations: [SdsProgressBarComponent], imports: [CommonModule], exports: [SdsProgressBarComponent] }); })();

/**
 * Generated bundle index. Do not edit.
 */

export { ActionMenuMode, CollapseDirective, ExternalLinkDirective, NavigationHelper, NavigationLink, NavigationMode, PaginationComponent, PaginationModule, SDSAutocompleteComponent, SDSAutocompletelConfiguration, SDSClickOutsideDirective, SDSClickOutsideModule, SDSHiercarchicalServiceSearchItem, SDSSelectedItemModel, SDSSelectedItemModelHelper, SDSSelectedResultComponent, SDSSelectedResultConfiguration, SDSTabOutsideDirective, SDS_DIALOG_DATA, SDS_DIALOG_DEFAULT_OPTIONS, SDS_DIALOG_SCROLL_STRATEGY, SDS_DIALOG_SCROLL_STRATEGY_FACTORY, SDS_DIALOG_SCROLL_STRATEGY_PROVIDER, SDS_DIALOG_SCROLL_STRATEGY_PROVIDER_FACTORY, SDS_MENU_TOKEN, SDS_SLIDE_OUT_SCROLL_STRATEGY, SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER, SDS_SLIDE_OUT_SCROLL_STRATEGY_PROVIDER_FACTORY, SdsAccordionDirective, SdsAccordionItemComponent, SdsAccordionItemContentDirective, SdsAccordionItemHeaderComponent, SdsAccordionModule, SdsActionsMenuComponent, SdsActionsMenuModule, SdsAutocompleteModule, SdsCollapseModule, SdsDateModule, SdsDatePipe, SdsDialogActionsDirective, SdsDialogCloseDirective, SdsDialogConfig, SdsDialogContainerComponent, SdsDialogContentDirective, SdsDialogModule, SdsDialogRef, SdsDialogService, SdsDialogSubtitleDirective, SdsDialogTitleDirective, SdsEditorComponent, SdsEditorModule, SdsExpiresDirective, SdsExpiresModule, SdsExternalLinkDirectivesModule, SdsMenuComponent, SdsMenuHeaderComponent, SdsMenuItemComponent, SdsMenuModule, SdsMenuTriggerForDirective, SdsObserveWidthDirective, SdsObserversModule, SdsPageComponent, SdsPageModule, SdsPageOptionsComponent, SdsPopover, SdsPopoverConfig, SdsPopoverModule, SdsPopoverWindow, SdsPopupDirective, SdsPopupModule, SdsProgressBarComponent, SdsProgressBarModule, SdsRichTextComponent, SdsRichTextModule, SdsSearchComponent, SdsSearchModule, SdsSearchResultListComponent, SdsSearchResultListModule, SdsSelectedResultsModule, SdsSelectionPanelComponent, SdsSelectionPanelModule, SdsSideNavigationComponent, SdsSideNavigationModule, SdsSideToolbarComponent, SdsSideToolbarModule, SdsStepArrowComponent, SdsStepArrowModule, SdsSubPanelComponent, SdsTabOutsideModule, SdsTabsModule, SdsTextChildComponent, SdsTextComponent, SdsTextModule, SdsToastComponent, SdsToastModule, SdsToastSettings, SdsTooltip, SdsTooltipConfig, SdsTooltipModule, SdsTooltipWindow, SdsTopBannerComponent, SdsTopBannerModule, SdsTreeTableComponent, SdsTreeTableModule, SdsTreeTableRow, SdsTruncateModule, SdsTruncateTextByLineDirective, SdsVideoPlayerComponent, SdsVideoPlayerModule, SearchSettings, SelectionMode, SelectionPanelModel, SideNavigationModel, TabPanelComponent, TabsComponent, throwSdsDialogContentAlreadyAttachedError };
